import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import { Routes, Route, Navigate, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import OtpLinkApprovePage from "@/components/auth/OtpLinkApprovePage";
import AdminLoginLinkPage from "@/components/auth/AdminLoginLinkPage";
import "@/App.css";
import { createPanelLoader, PANEL_LOADER_MIN_MS } from "@/panel/panelLoader";
import LoginOtpPanels from "@/components/auth/LoginOtpPanels";
import LoginPage3 from "@/components/pages/LoginPage3";
import LoginPage4 from "@/components/pages/LoginPage4";
import AutoLoginSplash from "@/components/auth/AutoLoginSplash";
import LogoutConfirmModal from "@/components/auth/LogoutConfirmModal";
import CustomerDetailView from "@/components/crm/CustomerDetailView";
import CrmCustomersPage from "@/components/crm/CrmCustomersPage";
import CrmDataTransferPage from "@/components/crm/CrmDataTransferPage";
import DeviceFailedStatsPanel, { DeviceFailedStatsBadge } from "@/components/devices/DeviceFailedStatsPanel";
import EcallAssignmentsPanel from "@/components/devices/EcallAssignmentsPanel";
import DeviceCleanupModal from "@/components/devices/DeviceCleanupModal";
import DeviceFailedRecordsTable from "@/components/devices/DeviceFailedRecordsTable";
import DeviceRecordingsTable from "@/components/devices/DeviceRecordingsTable";
import TranscriptDetailView from "@/components/transcripts/TranscriptDetailView";
import TeamLeaderRecordingReportsPage from "@/components/reports/TeamLeaderRecordingReportsPage";
import ManagerQualityReportsPage from "@/components/reports/ManagerQualityReportsPage";
import SpecialAgentReportPage from "@/components/reports/SpecialAgentReportPage";
import SpecialAgentReportPageV2 from "@/components/reports/SpecialAgentReportPageV2";
import TeamLeaderPerformanceReportPage from "@/components/reports/TeamLeaderPerformanceReportPage";
import MrExcelRecordsPage from "@/components/reports/MrExcelRecordsPage";
import CiroExcelRecordsPage from "@/components/reports/CiroExcelRecordsPage";
import { MANAGER_REPORT_VIEW_TO_TYPE, MANAGER_REPORT_SPREADSHEET_VIEW_IDS } from "@/utils/managerReportHelpers";
import { formatReportDate } from "@/utils/tlRecordingReportHelpers";
import AgentDayCallAnalysisModal from "@/components/reports/AgentDayCallAnalysisModal";
import { BreakAnalysisRangeOverviewModal } from "@/components/reports/BreakAnalysisRangeOverview";
import UnfollowedMissedModal from "@/components/reports/UnfollowedMissedModal";
import CallPartyPhone from "@/components/common/CallPartyPhone";
import CopyPhoneButton from "@/components/common/CopyPhoneButton";
import AgentPhoneChip from "@/components/common/AgentPhoneChip";
import CdrDateTimeCell from "@/components/cdr/CdrDateTimeCell";
import CdrTablePagination from "@/components/cdr/CdrTablePagination";
import CdrStagingAddButton from "@/components/cdr/CdrStagingAddButton";
import CdrStagingBubble from "@/components/cdr/CdrStagingBubble";
import CdrStagingViewModal from "@/components/cdr/CdrStagingViewModal";
import useCdrStagingBasket from "@/hooks/useCdrStagingBasket";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import { isMolaBreak } from "@/utils/helpers";
import AppBootLoader from "@/components/common/AppBootLoader";
import OtpApiSettings from "@/components/settings/OtpApiSettings";
import SecurityAuditLogs from "@/components/settings/SecurityAuditLogs";
import RecordingDownloadSettings from "@/components/settings/RecordingDownloadSettings";
import { downloadRecordingViaPanel, isRecordingNotFoundMessage } from "@/utils/recordingDownload";
import CallRecordingPlayerModal from "@/components/modals/CallRecordingPlayerModal";
import {
  formatCallLogDateTime,
  formatPendingRemaining,
  getRecordingPendingRemainingMs,
  isRecordWithinRecordingPendingWindow,
  RECORDING_RETRY_MS,
  shouldExpectRecording,
} from "@/utils/recordingPlayerPending";
import RecordingErrorReportsSettings from "@/components/settings/RecordingErrorReportsSettings";
import WebhookPushLogsSettings from "@/components/settings/WebhookPushLogsSettings";
import SeskPanelSettings from "@/components/settings/SeskPanelSettings";
import WorkSchedulesSettings from "@/components/settings/WorkSchedulesSettings";
import PanelRolesPage from "@/components/settings/PanelRolesPage";
import MrAssistantMappingPage from "@/components/settings/MrAssistantMappingPage";
import UserAccessDetailModal from "@/components/users/UserAccessDetailModal";
import ComingSoonPage from "@/components/common/ComingSoonPage";
import CiroTrackingPage from "@/components/finance/CiroTrackingPage";
import CiroDashboardPage from "@/components/finance/CiroDashboardPage";
import CiroDashboard2Page from "@/components/finance/CiroDashboard2Page";
import AppointmentCalendarPage from "@/components/calendar/AppointmentCalendarPage";
import AppointmentCalendarV2Page from "@/components/calendar/AppointmentCalendarV2Page";
import HospitalsPage from "@/components/companies/HospitalsPage";
import DoctorsPage from "@/components/companies/DoctorsPage";
import CompaniesListPage from "@/components/companies/CompaniesListPage";
import UserTotpSettings from "@/components/settings/UserTotpSettings";
import WorkScheduleEditor from "@/components/agents/WorkScheduleEditor";
import AgentTeamLeaderHistorySection from "@/components/agents/AgentTeamLeaderHistorySection";
import AgentHireDateImportModal from "@/components/agents/AgentHireDateImportModal";
import { parseAgentHireWorkbook } from "@/utils/agentHireDateExcelImport";
import {
  agentEmploymentDateToday,
  formatAgentEmploymentDate,
  isAgentEmploymentDateInputAllowed,
  isValidAgentEmploymentDateInput,
} from "@/utils/agentEmploymentDates";
import {
  AGENT_TABLE_COLUMN_DEFS,
  loadAgentTableColumns,
  saveAgentTableColumns,
} from "@/components/agents/agentTableConfig";
import { cloneWorkSchedule } from "@/utils/workSchedule";
import {
  PANEL_MENU_PERMISSION_GROUPS,
  PANEL_MENU_PRESETS,
  buildDefaultPanelMenuPermissions,
  normalizePanelMenuPermissions,
  hasPanelMenuPermission,
  getReportViewPermissionKey,
  getCrmViewPermissionKey,
  getAgentsViewPermissionKey,
  getLinesViewPermissionKey,
  isPanelMenuPermissionsConfigured,
  applyPanelMenuPermissionToggle,
  finalizePanelMenuPermissions,
  getUserCorePermissionBadges,
  getUserMenuPermissionBadges,
  getUserMenuPermissionSummary,
} from "@/utils/panelMenuPermissions";
import { applyPanelRoleToUserDraft, panelRoleLabel } from "@/utils/panelRoles";
import { PanelModal, PanelModalContent, usePanelModalExpand, panelModalSizeClasses, PanelModalHeaderActions } from "@/components/ui/panel-modal";
import {
  PanelTableCard,
  PanelTableHeader,
  PanelTableStats,
  PanelTableFrame,
  PanelTableHeadRow,
  PanelTableHead,
  PanelTableBody,
  PanelTableRow,
  PanelTableCell,
  PanelTableEmpty,
  PanelTableFooter,
  PanelTableActions,
  PanelTablePagination,
  usePanelTablePagination,
} from "@/components/ui/panel-table";
import axios from "axios";
import { Toaster, toast } from "sonner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { 
  Phone, PhoneOff, PhoneMissed, RefreshCw, Calendar, Clock, TrendingUp, Database,
  ChevronLeft, ChevronRight, Search, Filter, Download, Sun, Moon, Volume2,
  ChevronDown, ChevronUp, ArrowUpDown,
  User, Users, UserPlus, Plus, Pencil, Trash2, X, Lock, Eye, EyeOff, LogOut, Settings,
  Columns3, CheckSquare, Square, PhoneIncoming, PhoneOutgoing, Key, KeyRound, Copy, RotateCw,
  FileText, Play, Pause, SkipBack, SkipForward, Volume1, VolumeX, Mic, MessageSquare, FileAudio,
  BarChart3, FileSpreadsheet, Smartphone, Zap, Shield, FilterX, Building2,
  CheckCircle2, XCircle, Loader2, Mail, Contact, History, ArrowLeft, Megaphone,
  Layers, Cpu, ListTodo, Paperclip, Image, Menu, GitBranch, PauseCircle, Pin, AlertTriangle, Info, MoreHorizontal, Upload, Bot, Braces, Webhook, Send,
  Wallet, Calculator, Stethoscope, Landmark, LayoutDashboard, LayoutGrid, Sparkles, Link2, Eraser
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

// Constants (also exported from ./utils/constants.js)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

/** Rapor / analiz istekleri — 0 = axios timeout yok (sunucu yanıtlayana kadar bekle) */
const REPORT_API_TIMEOUT = 0;
const BREAK_ANALYSIS_TIMEOUT = 0;

const ALL_COLUMNS = [
  { id: "tarih", label: "Tarih", default: true },
  { id: "yon", label: "Yön", default: true },
  { id: "agent_name", label: "Agent", default: true },
  { id: "company_name", label: "Firma", default: false },
  { id: "arayan_numara", label: "Arayan", default: true },
  { id: "aranan_numara", label: "Aranan", default: true },
  { id: "konusma_suresi", label: "Konuşma Süresi", default: true },
  { id: "durum", label: "Durum", default: true },
  { id: "sistem", label: "Sistem", default: false },
  { id: "device_id", label: "Cihaz ID", default: false },
  { id: "did", label: "Departman", default: false },
  { id: "transkript", label: "Transkript", default: true },
  { id: "recording", label: "Ses Kaydı", default: true, requiresPermission: true }
];

const getDefaultVisibleColumns = () => ALL_COLUMNS.filter((c) => c.default).map((c) => c.id);

const loadStoredColumnIds = (storageKey) => {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return null;
    const validIds = new Set(ALL_COLUMNS.map((c) => c.id));
    let filtered = parsed.filter((id) => validIds.has(id));
    if (parsed.includes("musteri")) {
      ["arayan_numara", "aranan_numara"].forEach((id) => {
        if (!filtered.includes(id)) filtered.push(id);
      });
    }
    return filtered.length > 0 ? filtered : null;
  } catch {
    return null;
  }
};

const loadVisibleColumnsForUser = (userId) => {
  if (userId) {
    return loadStoredColumnIds(`cdr_visible_columns_u${userId}`)
      ?? loadStoredColumnIds("cdr_visible_columns")
      ?? getDefaultVisibleColumns();
  }
  return loadStoredColumnIds("cdr_visible_columns") ?? getDefaultVisibleColumns();
};

// Auth Context (also exported from ./contexts/AuthContext.js)
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AUTH_MANUAL_LOGOUT_KEY = "auth_manual_logout";
let rememberBootPromise = null;

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [autoLoginActive, setAutoLoginActive] = useState(false);
  const [autoLoginMeta, setAutoLoginMeta] = useState({ deviceLabel: "", ipAddress: "" });

  const completeLogin = useCallback((tokenValue, userData) => {
    sessionStorage.removeItem(AUTH_MANUAL_LOGOUT_KEY);
    localStorage.setItem("token", tokenValue);
    axios.defaults.headers.common["Authorization"] = `Bearer ${tokenValue}`;
    setToken(tokenValue);
    setUser(userData);
    setLoading(false);
  }, []);

  const tryRememberLogin = useCallback(async () => {
    if (sessionStorage.getItem(AUTH_MANUAL_LOGOUT_KEY)) {
      return false;
    }
    try {
      const res = await axios.post(`${API}/auth/remember`, { action: "refresh" }, { withCredentials: true });
      if (res.data?.token && res.data?.user) {
        setAutoLoginMeta({
          deviceLabel: res.data.device_label || "",
          ipAddress: res.data.ip_address || "",
        });
        setAutoLoginActive(true);
        await new Promise((r) => setTimeout(r, 900));
        completeLogin(res.data.token, res.data.user);
        setAutoLoginActive(false);
        return true;
      }
    } catch {
      // hatırlama yok veya süresi dolmuş
    }
    setAutoLoginActive(false);
    return false;
  }, [completeLogin]);

  const logout = useCallback(async ({ manual = true, scope = "device" } = {}) => {
    if (manual) {
      sessionStorage.setItem(AUTH_MANUAL_LOGOUT_KEY, "1");
    }
    rememberBootPromise = null;
    if (manual) {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(`${API}/auth/logout`, { scope }, { headers, withCredentials: true });
      } catch (_) { /* ignore */ }
    }
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setAutoLoginActive(false);
    setLoading(false);
  }, [token]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      // JWT süresi doldu veya geçersiz — remember çerezini silmeden otomatik giriş dene
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
      setToken(null);
      setUser(null);
      const remembered = await tryRememberLogin();
      if (!remembered) {
        setLoading(false);
      }
    }
  }, [tryRememberLogin]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
      return undefined;
    }

    if (sessionStorage.getItem(AUTH_MANUAL_LOGOUT_KEY)) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    if (rememberBootPromise) {
      rememberBootPromise.finally(() => {
        if (!cancelled && !localStorage.getItem("token")) {
          setAutoLoginActive(false);
          setLoading(false);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    rememberBootPromise = (async () => {
      if (cancelled) return;
      await tryRememberLogin();
      if (!cancelled && !localStorage.getItem("token")) {
        setLoading(false);
      }
    })().finally(() => {
      rememberBootPromise = null;
    });

    return () => {
      cancelled = true;
    };
  }, [token, fetchUser, tryRememberLogin]);

  const login = async (kullanici_adi, sifre, opts = {}) => {
    const body = {
      action: "begin",
      kullanici_adi,
      sifre,
    };
    if (opts.rememberDays > 0) {
      body.remember_days = opts.rememberDays;
      body.device_label = opts.deviceLabel || "";
    }
    const otpRes = await axios.post(`${API}/auth/otp`, body, { withCredentials: true });
    const data = otpRes.data;
    if (data.skip_otp && data.token) {
      completeLogin(data.token, data.user);
      return { ...data, completed: true };
    }
    return { ...data, completed: false, creds: { username: kullanici_adi, password: sifre } };
  };

  const finalizeOtpLogin = useCallback((data) => {
    if (data?.token && data?.user) {
      completeLogin(data.token, data.user);
    }
  }, [completeLogin]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, autoLoginActive, autoLoginMeta, finalizeOtpLogin, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper functions (also exported from ./utils/helpers.js)
const formatDuration = (seconds) => {
  if (!seconds) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins === 0 ? `${secs}s` : `${mins}dk ${secs}s`;
};

const formatStatDuration = (seconds) => {
  const s = Math.round(Number(seconds) || 0);
  if (s <= 0) return "0s";
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r === 0 ? `${m}dk` : `${m}dk ${r}s`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m === 0 ? `${h}sa` : `${h}sa ${m}dk`;
};

const hasCallLogRecording = (log) => {
  const path = log?.recording_path;
  if (!path || typeof path !== "string") return false;
  const trimmed = path.trim();
  return trimmed.length > 0 && !trimmed.endsWith("/");
};

const mapCallLogToAudioRecord = (log, device) => {
  if (!log) return null;
  const deviceGsm = device?.gsm || "-";
  const phone = log.phone_number || "-";
  const callType = parseInt(log.call_type, 10);
  let arayan_numara = phone;
  let aranan_numara = deviceGsm;
  if (callType === 2) {
    arayan_numara = deviceGsm;
    aranan_numara = phone;
  }
  return {
    ...log,
    id: log.cdr_id ?? log.id,
    cdr_id: log.cdr_id ?? null,
    download_source: log.cdr_id ? "cdr" : undefined,
    arayan_numara,
    aranan_numara,
    tarih: formatCallLogDateTime(log.call_date),
    konusma_suresi: log.duration ?? log.konusma_suresi ?? 0,
  };
};

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getTodayStr = () => formatLocalDate(new Date());
const getYesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return formatLocalDate(d); };
const getLast7DaysStr = () => { const d = new Date(); d.setDate(d.getDate() - 7); return formatLocalDate(d); };
const getThisMonthStart = () => { const d = new Date(); return formatLocalDate(new Date(d.getFullYear(), d.getMonth(), 1)); };
const getLastMonthRange = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const end = new Date(d.getFullYear(), d.getMonth(), 0);
  return { start: formatLocalDate(start), end: formatLocalDate(end) };
};

const formatDeviceInfoValue = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "-" || trimmed.toLowerCase() === "unknown device") {
    return null;
  }
  return trimmed;
};

/** API / state — agents her zaman dizi olarak kullanılsın */
const asAgentList = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  if (value && Array.isArray(value.agents)) return value.agents;
  return [];
};

const DeviceInfoStack = ({ device, isDark }) => {
  const rows = [
    { icon: Smartphone, value: formatDeviceInfoValue(device.device_name), iconClass: isDark ? "text-blue-400" : "text-blue-500" },
    { icon: Layers, value: formatDeviceInfoValue(device.device_model), iconClass: isDark ? "text-pink-400" : "text-pink-500" },
    { icon: Building2, value: formatDeviceInfoValue(device.manufacturer), iconClass: isDark ? "text-violet-400" : "text-violet-600" },
    { icon: Cpu, value: formatDeviceInfoValue(device.android_version), iconClass: isDark ? "text-emerald-400" : "text-emerald-600", prefix: "Android " },
  ].filter((row) => row.value);

  if (rows.length === 0) {
    return <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>-</span>;
  }

  return (
    <div className="flex flex-col gap-1 min-w-[120px] max-w-[200px]">
      {rows.map((row, idx) => {
        const Icon = row.icon;
        return (
          <div key={idx} className="flex items-center gap-1.5 min-w-0">
            <Icon className={`w-3.5 h-3.5 shrink-0 ${row.iconClass}`} />
            <span className={`text-xs truncate ${isDark ? "text-slate-200" : "text-gray-700"}`}>
              {row.prefix || ""}{row.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const REPORT_VIEWS = [
  { id: "agents", label: "Agent Çağrı Raporu", icon: Users },
  { id: "mesai", label: "Mesai Raporları", icon: Clock },
  { id: "mola", label: "Mola Raporları", icon: PauseCircle },
  { id: "tl-recordings", label: "Tk. Liderleri Ses Kayıtları", icon: FileAudio },
  { id: "mgr-tl-yeni-asistan", label: "Takım lideri Yeni Asistan Raporu", icon: FileSpreadsheet },
  { id: "mgr-kalite-degerlendirme", label: "Kalite Değerlendirme Raporu", icon: FileSpreadsheet },
  { id: "mgr-yeni-asistan-kalite", label: "Yeni Asistan Kalite Değerlendirme Raporu", icon: FileSpreadsheet },
];

const isSpreadsheetStyleReportView = (viewId) =>
  viewId === "tl-recordings" || MANAGER_REPORT_SPREADSHEET_VIEW_IDS.includes(viewId);

const LINE_VIEWS = [
  { id: "settings", label: "Hat Ayarları", icon: Settings },
  { id: "routing", label: "Yönlendirme", icon: GitBranch },
];

const AGENTS_VIEWS = [
  { id: "list", label: "Agent Listesi", icon: Users },
  { id: "attendance", label: "Mesai Takibi", icon: Clock },
];

const CRM_VIEWS = [
  { id: "customers", label: "Müşteriler", icon: Contact },
  { id: "data-transfer", label: "Data Aktarımı", icon: Upload },
];

const COMPANIES_VIEWS = [
  { id: "list", label: "Firma Listesi", icon: Building2 },
  { id: "hospitals", label: "Hastane", icon: Landmark },
  { id: "doctors", label: "Doktorlar", icon: Stethoscope },
];

const FINANCE_VIEWS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "dashboard2", label: "Dashboard 2", icon: Sparkles },
  { id: "ciro", label: "Ciro Takibi", icon: Wallet },
  { id: "reports", label: "Finansal Raporlar", icon: BarChart3 },
  { id: "accounting", label: "Muhasebe", icon: Calculator },
];

const CALENDAR_VIEWS = [
  { id: "medicrm", label: "mediCrm", icon: LayoutGrid },
  { id: "v2", label: "Takvim v2", icon: Calendar },
];

const NAV_ICON_THEMES = {
  calls: {
    dark: { wrap: "from-blue-500/25 to-sky-500/10 ring-blue-400/30", icon: "text-sky-300" },
    light: { wrap: "from-blue-500 to-sky-500 ring-blue-400/25", icon: "text-white" },
  },
  cdr: {
    dark: { wrap: "from-blue-600/25 to-indigo-500/10 ring-blue-400/30", icon: "text-blue-300" },
    light: { wrap: "from-blue-600 to-indigo-500 ring-blue-400/25", icon: "text-white" },
  },
  search: {
    dark: { wrap: "from-cyan-500/25 to-teal-500/10 ring-cyan-400/30", icon: "text-cyan-300" },
    light: { wrap: "from-cyan-500 to-teal-500 ring-cyan-400/25", icon: "text-white" },
  },
  transcripts: {
    dark: { wrap: "from-violet-500/25 to-purple-500/10 ring-violet-400/30", icon: "text-violet-300" },
    light: { wrap: "from-violet-500 to-purple-600 ring-violet-400/25", icon: "text-white" },
  },
  crm: {
    dark: { wrap: "from-fuchsia-500/25 to-violet-500/10 ring-fuchsia-400/30", icon: "text-fuchsia-300" },
    light: { wrap: "from-fuchsia-500 to-violet-600 ring-fuchsia-400/25", icon: "text-white" },
  },
  "crm-customers": {
    dark: { wrap: "from-violet-500/25 to-fuchsia-500/10 ring-violet-400/30", icon: "text-violet-300" },
    light: { wrap: "from-violet-500 to-fuchsia-600 ring-violet-400/25", icon: "text-white" },
  },
  reports: {
    dark: { wrap: "from-emerald-500/25 to-green-500/10 ring-emerald-400/30", icon: "text-emerald-300" },
    light: { wrap: "from-emerald-500 to-green-600 ring-emerald-400/25", icon: "text-white" },
  },
  "reports-agents": {
    dark: { wrap: "from-indigo-500/25 to-blue-500/10 ring-indigo-400/30", icon: "text-indigo-300" },
    light: { wrap: "from-indigo-500 to-blue-600 ring-indigo-400/25", icon: "text-white" },
  },
  "reports-mesai": {
    dark: { wrap: "from-amber-500/25 to-orange-500/10 ring-amber-400/30", icon: "text-amber-300" },
    light: { wrap: "from-amber-500 to-orange-500 ring-amber-400/25", icon: "text-white" },
  },
  "reports-mola": {
    dark: { wrap: "from-rose-500/25 to-pink-500/10 ring-rose-400/30", icon: "text-rose-300" },
    light: { wrap: "from-rose-500 to-pink-600 ring-rose-400/25", icon: "text-white" },
  },
  "reports-tl-recordings": {
    dark: { wrap: "from-violet-500/25 to-purple-500/10 ring-violet-400/30", icon: "text-violet-300" },
    light: { wrap: "from-violet-500 to-purple-600 ring-violet-400/25", icon: "text-white" },
  },
  "special-agent-report": {
    dark: { wrap: "from-teal-500/35 to-cyan-500/15 ring-teal-400/45", icon: "text-teal-200" },
    light: { wrap: "from-teal-500 to-cyan-600 ring-teal-400/35", icon: "text-white" },
  },
  "special-agent-report-2": {
    dark: { wrap: "from-indigo-500/35 to-cyan-500/20 ring-cyan-400/45", icon: "text-cyan-200" },
    light: { wrap: "from-indigo-500 to-cyan-600 ring-cyan-400/35", icon: "text-white" },
  },
  "team-leader-performance": {
    dark: { wrap: "from-emerald-500/35 to-lime-500/15 ring-emerald-400/45", icon: "text-emerald-200" },
    light: { wrap: "from-emerald-600 to-lime-600 ring-emerald-400/35", icon: "text-white" },
  },
  "mr-excel-records": {
    dark: { wrap: "from-violet-500/35 to-purple-500/15 ring-violet-400/45", icon: "text-violet-200" },
    light: { wrap: "from-violet-600 to-purple-600 ring-violet-400/35", icon: "text-white" },
  },
  "tl-ciro-records": {
    dark: { wrap: "from-emerald-500/35 to-lime-500/15 ring-emerald-400/45", icon: "text-emerald-200" },
    light: { wrap: "from-emerald-600 to-lime-600 ring-emerald-400/35", icon: "text-white" },
  },
  lines: {
    dark: { wrap: "from-teal-500/25 to-cyan-500/10 ring-teal-400/30", icon: "text-teal-300" },
    light: { wrap: "from-teal-500 to-cyan-600 ring-teal-400/25", icon: "text-white" },
  },
  "lines-settings": {
    dark: { wrap: "from-slate-500/25 to-zinc-500/10 ring-slate-400/30", icon: "text-slate-300" },
    light: { wrap: "from-slate-500 to-zinc-600 ring-slate-400/25", icon: "text-white" },
  },
  "lines-routing": {
    dark: { wrap: "from-teal-500/25 to-emerald-500/10 ring-teal-400/30", icon: "text-teal-300" },
    light: { wrap: "from-teal-500 to-emerald-600 ring-teal-400/25", icon: "text-white" },
  },
  agents: {
    dark: { wrap: "from-indigo-500/25 to-violet-500/10 ring-indigo-400/30", icon: "text-indigo-300" },
    light: { wrap: "from-indigo-500 to-violet-600 ring-indigo-400/25", icon: "text-white" },
  },
  devices: {
    dark: { wrap: "from-pink-500/25 to-rose-500/10 ring-pink-400/30", icon: "text-pink-300" },
    light: { wrap: "from-pink-500 to-rose-600 ring-pink-400/25", icon: "text-white" },
  },
  companies: {
    dark: { wrap: "from-amber-500/25 to-yellow-500/10 ring-amber-400/30", icon: "text-amber-300" },
    light: { wrap: "from-amber-500 to-yellow-600 ring-amber-400/25", icon: "text-white" },
  },
  "companies-list": {
    dark: { wrap: "from-amber-500/25 to-yellow-500/10 ring-amber-400/30", icon: "text-amber-300" },
    light: { wrap: "from-amber-500 to-yellow-600 ring-amber-400/25", icon: "text-white" },
  },
  "companies-hospitals": {
    dark: { wrap: "from-indigo-500/25 to-violet-500/10 ring-indigo-400/30", icon: "text-indigo-300" },
    light: { wrap: "from-indigo-500 to-violet-600 ring-indigo-400/25", icon: "text-white" },
  },
  "companies-doctors": {
    dark: { wrap: "from-sky-500/25 to-cyan-500/10 ring-sky-400/30", icon: "text-sky-300" },
    light: { wrap: "from-sky-500 to-cyan-600 ring-sky-400/25", icon: "text-white" },
  },
  calendar: {
    dark: { wrap: "from-blue-500/25 to-indigo-500/10 ring-blue-400/30", icon: "text-blue-300" },
    light: { wrap: "from-blue-500 to-indigo-600 ring-blue-400/25", icon: "text-white" },
  },
  "calendar-medicrm": {
    dark: { wrap: "from-blue-500/25 to-indigo-500/10 ring-blue-400/30", icon: "text-blue-300" },
    light: { wrap: "from-blue-500 to-indigo-600 ring-blue-400/25", icon: "text-white" },
  },
  "calendar-v2": {
    dark: { wrap: "from-cyan-500/25 to-blue-500/10 ring-cyan-400/30", icon: "text-cyan-300" },
    light: { wrap: "from-cyan-500 to-blue-600 ring-cyan-400/25", icon: "text-white" },
  },
  finance: {
    dark: { wrap: "from-lime-500/25 to-emerald-500/10 ring-lime-400/30", icon: "text-lime-300" },
    light: { wrap: "from-lime-500 to-emerald-600 ring-lime-400/25", icon: "text-white" },
  },
  "finance-dashboard2": {
    dark: { wrap: "from-cyan-500/25 to-violet-500/10 ring-cyan-400/30", icon: "text-cyan-300" },
    light: { wrap: "from-cyan-500 to-violet-600 ring-cyan-400/25", icon: "text-white" },
  },
  "finance-dashboard": {
    dark: { wrap: "from-emerald-500/25 to-teal-500/10 ring-emerald-400/30", icon: "text-emerald-300" },
    light: { wrap: "from-emerald-500 to-teal-600 ring-emerald-400/25", icon: "text-white" },
  },
  "finance-ciro": {
    dark: { wrap: "from-emerald-500/25 to-green-500/10 ring-emerald-400/30", icon: "text-emerald-300" },
    light: { wrap: "from-emerald-500 to-green-600 ring-emerald-400/25", icon: "text-white" },
  },
  "finance-reports": {
    dark: { wrap: "from-emerald-500/25 to-green-500/10 ring-emerald-400/30", icon: "text-emerald-300" },
    light: { wrap: "from-emerald-500 to-green-600 ring-emerald-400/25", icon: "text-white" },
  },
  "finance-accounting": {
    dark: { wrap: "from-teal-500/25 to-cyan-500/10 ring-teal-400/30", icon: "text-teal-300" },
    light: { wrap: "from-teal-500 to-cyan-600 ring-teal-400/25", icon: "text-white" },
  },
  settings: {
    dark: { wrap: "from-slate-500/25 to-gray-500/10 ring-slate-400/30", icon: "text-slate-300" },
    light: { wrap: "from-slate-500 to-gray-600 ring-slate-400/25", icon: "text-white" },
  },
  users: {
    dark: { wrap: "from-purple-500/25 to-violet-500/10 ring-purple-400/30", icon: "text-purple-300" },
    light: { wrap: "from-purple-500 to-violet-600 ring-purple-400/25", icon: "text-white" },
  },
  todos: {
    dark: { wrap: "from-orange-500/25 to-amber-500/10 ring-orange-400/30", icon: "text-orange-300" },
    light: { wrap: "from-orange-500 to-amber-600 ring-orange-400/25", icon: "text-white" },
  },
  default: {
    dark: { wrap: "from-slate-600/25 to-slate-500/10 ring-slate-500/30", icon: "text-slate-300" },
    light: { wrap: "from-slate-400 to-slate-500 ring-slate-400/25", icon: "text-white" },
  },
};

const resolveNavIconKey = (item) => {
  if (item.view && item.tab) return `${item.tab}-${item.view}`;
  if (item.id) return item.id;
  return item.tab || "default";
};

const navFeaturedItemClass = (item, active, isDark) => {
  if (!item?.navHighlight) return "";
  if (active) {
    return isDark
      ? "bg-teal-500/20 text-teal-100 ring-1 ring-teal-400/45 font-semibold"
      : "bg-teal-50 text-teal-900 ring-1 ring-teal-200 font-semibold";
  }
  return isDark
    ? "text-teal-200 hover:bg-teal-500/12 ring-1 ring-teal-500/25 font-medium"
    : "text-teal-900 hover:bg-teal-50/90 ring-1 ring-teal-100 font-medium";
};

const NavIconBox = ({ icon: Icon, item, isDark, size = "md" }) => {
  if (!Icon) return null;
  const key = resolveNavIconKey(item);
  const theme = NAV_ICON_THEMES[key] || NAV_ICON_THEMES.default;
  const mode = isDark ? theme.dark : theme.light;
  const featured = item?.navHighlight;
  const boxClass = size === "xs"
    ? "w-4 h-4 rounded"
    : size === "sm"
      ? featured ? "w-6 h-6 rounded-md" : "w-5 h-5 rounded-md"
      : size === "nav"
        ? "w-[1.375rem] h-[1.375rem] rounded-md"
        : "w-6 h-6 rounded-lg";
  const iconClass = size === "xs"
    ? "w-2.5 h-2.5"
    : size === "sm"
      ? "w-3 h-3"
      : size === "nav"
        ? "w-3.5 h-3.5"
        : "w-3.5 h-3.5";
  return (
    <span className={`inline-flex items-center justify-center shrink-0 ${boxClass} bg-gradient-to-br ${mode.wrap} ring-1 ${isDark ? "" : "shadow-sm"}`}>
      <Icon className={`${iconClass} ${mode.icon}`} strokeWidth={2.25} />
    </span>
  );
};

const ATTENDANCE_FILTERS = [
  { id: "all", label: "Tümü" },
  { id: "on_time", label: "Zamanında" },
  { id: "late", label: "Geç Giriş" },
  { id: "absent", label: "Görüşme Yok" },
  { id: "off_day", label: "Mesai Dışı" },
];

const getAttendanceRowClass = (status, isDark) => {
  switch (status) {
    case "on_time":
      return isDark ? "border-emerald-500/40 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50/80";
    case "late":
      return isDark ? "border-rose-500/40 bg-rose-500/10" : "border-rose-200 bg-rose-50/80";
    case "absent":
      return isDark ? "border-amber-500/40 bg-amber-500/10" : "border-amber-200 bg-amber-50/80";
    default:
      return isDark ? "border-slate-700 bg-slate-800/30" : "border-gray-200 bg-gray-50/50";
  }
};

const getAttendanceBadgeClass = (status, isDark) => {
  switch (status) {
    case "on_time":
      return isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "late":
      return isDark ? "bg-rose-500/20 text-rose-300 border-rose-500/30" : "bg-rose-100 text-rose-700 border-rose-200";
    case "absent":
      return isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-amber-100 text-amber-700 border-amber-200";
    default:
      return isDark ? "bg-slate-700/50 text-slate-400 border-slate-600" : "bg-gray-100 text-gray-500 border-gray-200";
  }
};

const buildNavEntries = ({ canSearch, canSeeRecordings, canAccessTodos, isAdmin, isSuperAdmin, canManageAgents, canManageCompanySettings = false, showRecordingDownloadSettings = false, hasMenu = () => true }) => {
  const callItems = [
    hasMenu("menu_cdr") ? { tab: "cdr", label: "Çağrı Kayıtları", icon: Phone } : null,
    canSearch && hasMenu("menu_search") ? { tab: "search", label: "GSM Arama", icon: Search } : null,
    canSeeRecordings && hasMenu("menu_transcripts") ? { tab: "transcripts", label: "Transkriptler", icon: FileText } : null,
  ].filter(Boolean);

  const crmItems = hasMenu("menu_crm")
    ? CRM_VIEWS.filter((v) => hasMenu(getCrmViewPermissionKey(v.id))).map((v) => ({ tab: "crm", view: v.id, label: v.label, icon: v.icon }))
    : [];

  const reportItems = hasMenu("menu_reports")
    ? REPORT_VIEWS.filter((v) => hasMenu(getReportViewPermissionKey(v.id))).map((v) => ({
      tab: "reports", view: v.id, label: v.label, icon: v.icon,
    }))
    : [];

  const specialAgentNavItem = hasMenu("menu_reports_special_agent")
    ? {
      kind: "link",
      tab: "special-agent-report",
      label: "Özel Asistan Raporu",
      icon: Sparkles,
      navHighlight: true,
    }
    : null;

  const specialAgentNavItemV2 = hasMenu("menu_reports_special_agent")
    ? {
      kind: "link",
      tab: "special-agent-report-2",
      label: "Özel Asistan Raporu 2",
      icon: Sparkles,
      navHighlight: true,
    }
    : null;

  const tlPerformanceNavItem = hasMenu("menu_reports_tl_performance")
    ? {
      kind: "link",
      tab: "team-leader-performance",
      label: "Takım Lideri Performansları",
      icon: BarChart3,
      navHighlight: true,
    }
    : null;

  const mrExcelRecordsNavItem = hasMenu("menu_reports_tl_performance")
    ? {
      kind: "link",
      tab: "mr-excel-records",
      label: "MR Kayıtları (Excel)",
      icon: FileSpreadsheet,
      navHighlight: true,
    }
    : null;

  const tlCiroRecordsNavItem = hasMenu("menu_reports_tl_performance")
    ? {
      kind: "link",
      tab: "tl-ciro-records",
      label: "Ciro Kayıtları (Excel)",
      icon: Wallet,
      navHighlight: true,
    }
    : null;

  const reportsMenuItems = [
    ...reportItems,
    ...(reportItems.length > 0 && (specialAgentNavItem || specialAgentNavItemV2 || tlPerformanceNavItem || mrExcelRecordsNavItem || tlCiroRecordsNavItem)
      ? [{ kind: "separator", id: "reports-special-agent-sep" }]
      : []),
    ...(specialAgentNavItem ? [specialAgentNavItem] : []),
    ...(specialAgentNavItemV2 ? [specialAgentNavItemV2] : []),
    ...(tlPerformanceNavItem ? [tlPerformanceNavItem] : []),
    ...(tlCiroRecordsNavItem ? [tlCiroRecordsNavItem] : []),
    ...(mrExcelRecordsNavItem ? [mrExcelRecordsNavItem] : []),
  ];

  const lineItems = LINE_VIEWS.filter((v) => hasMenu(getLinesViewPermissionKey(v.id))).map((v) => ({
    tab: "lines", view: v.id, label: v.label, icon: v.icon,
  }));

  const agentItems = hasMenu("menu_agents")
    ? AGENTS_VIEWS.filter((v) => hasMenu(getAgentsViewPermissionKey(v.id))).map((v) => ({
      tab: "agents", view: v.id, label: v.label, icon: v.icon,
    }))
    : [];

  const callCenterItems = [
    ...callItems,
    lineItems.length ? { kind: "submenu", id: "lines", label: "Hatlar", icon: GitBranch, items: lineItems } : null,
    agentItems.length ? { kind: "submenu", id: "agents-nav", label: "Agentlar", icon: Users, items: agentItems } : null,
    hasMenu("menu_devices") ? { kind: "link", tab: "devices", label: "Cihazlar", icon: Smartphone } : null,
  ].filter(Boolean);

  const settingsItems = [
    ...(isAdmin ? [{ tab: "users", label: "Kullanıcılar", icon: UserPlus }] : []),
    ...(isAdmin ? [{ tab: "panel-roles", label: "Yetkilendirme", icon: Shield }] : []),
    ...(canManageAgents || isAdmin) && hasMenu("menu_work_schedules") ? [{ tab: "work-schedules", label: "Mesai Planları", icon: Clock }] : [],
    ...(isSuperAdmin ? [{ tab: "otp-settings", label: "OTP API", icon: Shield }] : []),
    ...(showRecordingDownloadSettings ? [{ tab: "recording-download-settings", label: "Ses İndirme", icon: Download }] : []),
    ...(isSuperAdmin ? [{ tab: "recording-errors", label: "Ses Kaydı Hataları", icon: AlertTriangle }] : []),
    ...(canManageCompanySettings ? [{ tab: "webhook-logs", label: "Loglar", icon: FileText }] : []),
    ...(isSuperAdmin ? [{ tab: "sesk-panel", label: "Ses Eşleştirme", icon: Zap }] : []),
    ...((isAdmin || hasMenu("menu_reports_tl_performance")) ? [{ tab: "mr-assistant-mapping", label: "MR Asistan Eşleştirme", icon: Link2 }] : []),
    ...(canAccessTodos && hasMenu("menu_todos") ? [{ tab: "todos", label: "To-Do List", icon: ListTodo }] : []),
  ];

  const companiesItems = isSuperAdmin
    ? COMPANIES_VIEWS.map((v) => ({ tab: "companies", view: v.id, label: v.label, icon: v.icon }))
    : [];

  const financeItems = (isSuperAdmin || isAdmin)
    ? FINANCE_VIEWS.map((v) => ({ tab: "finance", view: v.id, label: v.label, icon: v.icon }))
    : [];

  const calendarItems = isAdmin
    ? CALENDAR_VIEWS.map((v) => ({ tab: "calendar", view: v.id, label: v.label, icon: v.icon }))
    : [];

  const dashboardLink = (isSuperAdmin || isAdmin)
    ? [{ kind: "link", tab: "finance", view: "dashboard2", label: "Dashboard", icon: LayoutDashboard }]
    : [];

  return [
    ...dashboardLink,
    callCenterItems.length ? {
      kind: "menu",
      id: "calls",
      label: "Çağrı Merkezi",
      icon: Phone,
      items: callCenterItems,
    } : null,
    crmItems.length ? {
      kind: "menu",
      id: "crm",
      label: "CRM",
      icon: Contact,
      items: crmItems,
    } : null,
    reportsMenuItems.length ? {
      kind: "menu",
      id: "reports",
      label: "Raporlar",
      icon: BarChart3,
      items: reportsMenuItems,
    } : null,
    financeItems.length ? {
      kind: "menu",
      id: "finance",
      label: "Finans - Muhasebe",
      icon: Wallet,
      items: financeItems,
    } : null,
    companiesItems.length ? {
      kind: "menu",
      id: "companies",
      label: "Firmalar",
      icon: Building2,
      items: companiesItems,
    } : null,
    ...(calendarItems.length ? [{
      kind: "menu",
      id: "calendar",
      label: "Takvim",
      icon: Calendar,
      items: calendarItems,
    }] : []),
    ...(settingsItems.length
      ? [{ kind: "menu", id: "settings", label: "Ayarlar", icon: Settings, items: settingsItems }]
      : []),
  ].filter((entry) => entry && (entry.kind === "link" || (entry.items && entry.items.length > 0)));
};

const SubNavPills = ({ items, activeId, onChange, isDark, textClass }) => (
  <div className={`inline-flex flex-wrap gap-1 p-1 rounded-xl ${isDark ? "bg-slate-900/80 border border-slate-800" : "bg-gray-100 border border-gray-200"}`}>
    {items.map((item) => {
      const Icon = item.icon;
      const active = activeId === item.id;
      return (
        <button
          key={item.id}
          type="button"
          onClick={() => onChange(item.id)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            active
              ? isDark ? "bg-blue-600 text-white shadow-sm" : "bg-white text-blue-700 shadow-sm"
              : isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800" : "text-gray-600 hover:text-gray-900 hover:bg-white/70"
          }`}
        >
          {Icon && <Icon className="w-3.5 h-3.5" />}
          <span>{item.label}</span>
        </button>
      );
    })}
  </div>
);

const buildTabRouteKey = (tab, view = "default") => `${tab}:${view}`;

/** Veri fetch'i olmayan sekmeler — loader gösterilmez / hemen kapanır */
const STATIC_PANEL_ROUTES = new Set([
  "agents:list",
  "companies:list",
  "companies:hospitals",
  "companies:doctors",
  "finance:dashboard",
  "finance:dashboard2",
  "finance:ciro",
  "finance:reports",
  "finance:accounting",
  "companies:default",
  "calendar:medicrm",
  "calendar:v2",
  "users:default",
  "panel-roles:default",
  "otp-settings:default",
  "recording-download-settings:default",
  "recording-errors:default",
  "webhook-logs:default",
  "work-schedules:default",
  "search:default",
  "lines:settings",
  "lines:routing",
  "reports:agents",
  "reports:mola",
  "reports:tl-recordings",
  "special-agent-report:default",
  "special-agent-report-2:default",
  "team-leader-performance:default",
  "mr-excel-records:default",
  "tl-ciro-records:default",
  "mr-assistant-mapping:default",
  "reports:mgr-tl-yeni-asistan",
  "reports:mgr-kalite-degerlendirme",
  "reports:mgr-yeni-asistan-kalite",
  "crm:customer",
  "crm:data-transfer",
]);

const isStaticPanelRoute = (routeKey) => STATIC_PANEL_ROUTES.has(routeKey);

/** Header softphone — ayar simgesinin solunda (SIP.js için #softphone-mount) */
const HeaderSoftphoneButton = ({ isDark, labelClass }) => {
  const [open, setOpen] = useState(false);
  const btnClass = isDark
    ? "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/25 hover:bg-emerald-500/15"
    : "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 hover:bg-emerald-100";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Softphone"
          aria-label="Softphone"
          className={`inline-flex items-center justify-center h-8 w-8 rounded-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 ${btnClass}`}
        >
          <Phone className="w-4 h-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        className={`w-[min(18rem,calc(100vw-2rem))] p-3 ${isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"}`}
      >
        <p className={`text-[10px] font-semibold uppercase tracking-wider px-1 mb-2 ${labelClass}`}>Softphone</p>
        <div className={`rounded-lg p-3 space-y-3 ${isDark ? "bg-slate-950 border border-slate-800" : "bg-gray-50 border border-gray-100"}`}>
          <div className={`flex items-center justify-between text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
            <span className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-emerald-500" />
              Durum
            </span>
            <Badge className={isDark ? "bg-slate-700 text-slate-300" : "bg-gray-100 text-gray-600"}>Hazır değil</Badge>
          </div>
          <div className={`rounded-lg p-3 text-center ${isDark ? "bg-slate-900/80 border border-slate-800" : "bg-white border border-gray-100"}`}>
            <Phone className={`w-7 h-7 mx-auto mb-2 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
            <p className={`text-xs leading-relaxed ${labelClass}`}>SIP.js softphone entegrasyonu yakında.</p>
          </div>
          <div id="softphone-mount" className="hidden" aria-hidden="true" />
          <Button type="button" variant="outline" size="sm" className="w-full" disabled onClick={() => toast.info("Demo — bağlantı henüz aktif değil")}>
            Bağlan
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** Header ayar menüsü — hover ile açılır (tema, sabit menü, 2FA) */
const HeaderSettingsMenu = ({
  isDark,
  labelClass,
  headerPinned,
  onHeaderPinnedChange,
  onToggleTheme,
  totpEnabled,
  onOpenTotpSettings,
  currentUserName,
  currentUserSubtitle,
  onOpenProfile,
}) => {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef(null);

  const keepOpen = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpen(true);
  };

  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 200);
  };

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const panelClass = isDark
    ? "bg-slate-900 border-slate-700 text-slate-100"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <div className="relative" onMouseEnter={keepOpen} onMouseLeave={scheduleClose}>
      <button
        type="button"
        title="Ayarlar"
        aria-expanded={open}
        aria-haspopup="true"
        className={`inline-flex items-center justify-center h-8 w-8 rounded-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
          open
            ? isDark ? "bg-slate-800 text-slate-100" : "bg-gray-100 text-gray-900"
            : isDark ? "text-slate-400 hover:bg-slate-800 hover:text-slate-100" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        <Settings className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[950] pt-1.5 w-[min(18rem,calc(100vw-2rem))]">
          <div className={`rounded-xl border shadow-xl p-3 space-y-3 ${panelClass}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wider px-1 ${labelClass}`}>Görünüm</p>

            <div className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                {isDark ? <Moon className="w-4 h-4 shrink-0 text-blue-400" /> : <Sun className="w-4 h-4 shrink-0 text-amber-500" />}
                <span className="text-sm">Karanlık mod</span>
              </div>
              <Switch checked={isDark} onCheckedChange={() => onToggleTheme()} aria-label="Karanlık mod" />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <Pin className={`w-4 h-4 shrink-0 ${headerPinned ? (isDark ? "text-blue-400" : "text-blue-600") : labelClass}`} />
                <span className="text-sm">Sabit menü</span>
              </div>
              <Switch checked={headerPinned} onCheckedChange={onHeaderPinnedChange} aria-label="Sabit menü" />
            </div>

            {onOpenProfile && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onOpenProfile();
                }}
                className={`w-full flex items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors ${
                  isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"
                }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isDark ? "bg-blue-500/15 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
                  <User className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${isDark ? "text-slate-100" : "text-gray-900"}`}>
                    {currentUserName || "Kullanıcı"}
                  </p>
                  <p className={`text-xs truncate ${labelClass}`}>
                    {currentUserSubtitle || "Profili düzenle"}
                  </p>
                </div>
                <Pencil className={`w-4 h-4 shrink-0 ${labelClass}`} />
              </button>
            )}

            <div className={`border-t ${isDark ? "border-slate-800" : "border-gray-100"}`} />
            <p className={`text-[10px] font-semibold uppercase tracking-wider px-1 ${labelClass}`}>Güvenlik</p>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenTotpSettings?.();
              }}
              className={`w-full flex items-center justify-between gap-3 rounded-lg px-2 py-2 text-left transition-colors ${
                isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <KeyRound className="w-4 h-4 shrink-0 text-violet-500" />
                <span className="text-sm">Authenticator (2FA)</span>
              </span>
              <Badge className={totpEnabled
                ? (isDark ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200")
                : (isDark ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500")
              }>
                {totpEnabled ? "Aktif" : "Kapalı"}
              </Badge>
            </button>

          </div>
        </div>
      )}
    </div>
  );
};

const AppMainNav = ({
  entries,
  isNavItemActive,
  isEntryActive,
  onNavigate,
  isDark,
  textClass,
  labelClass,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const measureRef = useRef(null);
  const [overflowFrom, setOverflowFrom] = useState(null);
  const [navSpread, setNavSpread] = useState(false);

  const activeEntry = useMemo(
    () => entries.find((entry) => isEntryActive(entry)) || null,
    [entries, isEntryActive]
  );

  const computeLayout = useCallback(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure || entries.length === 0) {
      setOverflowFrom(null);
      setNavSpread(false);
      return;
    }

    const available = nav.clientWidth;
    if (available <= 0) return;

    const itemEls = Array.from(measure.children);
    const baseGap = parseFloat(getComputedStyle(measure).gap) || 8;
    const moreReserve = 44 + baseGap;

    let itemsWidth = 0;
    itemEls.forEach((el, index) => {
      itemsWidth += el.getBoundingClientRect().width + (index > 0 ? baseGap : 0);
    });

    if (itemsWidth <= available) {
      setOverflowFrom(null);
      const fillRatio = itemsWidth / available;
      setNavSpread(entries.length >= 5 && fillRatio >= 0.55);
      return;
    }

    setNavSpread(false);
    let used = 0;
    let fit = entries.length;

    for (let i = 0; i < itemEls.length; i += 1) {
      const width = itemEls[i].getBoundingClientRect().width;
      const total = used + (i > 0 ? baseGap : 0) + width;
      const needsMore = i < itemEls.length - 1;
      const limit = available - (needsMore ? moreReserve : 0);
      if (total > limit) {
        fit = Math.max(1, i);
        break;
      }
      used = total;
    }

    setOverflowFrom(fit >= entries.length ? null : fit);
  }, [entries]);

  useLayoutEffect(() => {
    computeLayout();
    const nav = navRef.current;
    if (!nav) return undefined;

    const observer = new ResizeObserver(() => computeLayout());
    observer.observe(nav);
    window.addEventListener("resize", computeLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", computeLayout);
    };
  }, [computeLayout]);

  const visibleEntries = useMemo(
    () => (overflowFrom === null ? entries : entries.slice(0, overflowFrom)),
    [entries, overflowFrom]
  );
  const overflowEntries = useMemo(
    () => (overflowFrom === null ? [] : entries.slice(overflowFrom)),
    [entries, overflowFrom]
  );
  const overflowActive = useMemo(
    () => overflowEntries.some((entry) => isEntryActive(entry)),
    [overflowEntries, isEntryActive]
  );

  const renderGroupDropdownItems = (entry) => {
    const navLeafClass = (item, active) => (
      item.navHighlight
        ? navFeaturedItemClass(item, active, isDark)
        : active
          ? isDark ? "bg-blue-600/20 text-blue-300 focus:bg-blue-600/25 focus:text-blue-200" : "bg-blue-50 text-blue-700 focus:bg-blue-100 focus:text-blue-800"
          : isDark ? "focus:bg-slate-800 focus:text-slate-100" : "focus:bg-gray-50 focus:text-gray-900"
    );

    const renderLeafItem = (item) => {
      const Icon = item.icon;
      const active = isNavItemActive(item);
      const itemKey = item.kind === "link"
        ? `link-${item.tab}`
        : `${item.tab}-${item.view || "default"}`;
      return (
        <DropdownMenuItem
          key={itemKey}
          onClick={() => onNavigate(item.tab, item.view)}
          className={`gap-2.5 cursor-pointer rounded-md px-2.5 py-2.5 text-sm ${navLeafClass(item, active)}`}
        >
          {Icon && <NavIconBox icon={Icon} item={item} isDark={isDark} size="sm" />}
          <span className={`flex-1 ${item.navHighlight ? "font-semibold" : "font-medium"}`}>{item.label}</span>
        </DropdownMenuItem>
      );
    };

    const renderMenuItem = (item) => {
      if (item.kind === "separator") {
        return (
          <DropdownMenuSeparator
            key={item.id}
            className={isDark ? "bg-slate-800 my-1" : "bg-gray-200 my-1"}
          />
        );
      }
      if (item.kind === "submenu") {
        const SubIcon = item.icon;
        const subActive = item.items.some((sub) => isNavItemActive(sub));
        return (
          <DropdownMenuSub key={item.id}>
            <DropdownMenuSubTrigger
              className={`gap-2.5 rounded-md px-2.5 py-2.5 text-sm ${
                subActive
                  ? isDark ? "bg-blue-600/15 text-blue-300 focus:bg-blue-600/20 focus:text-blue-200" : "bg-blue-50 text-blue-700 focus:bg-blue-100 focus:text-blue-800"
                  : isDark ? "focus:bg-slate-800 focus:text-slate-100" : "focus:bg-gray-50 focus:text-gray-900"
              }`}
            >
              {SubIcon && <NavIconBox icon={SubIcon} item={item} isDark={isDark} size="sm" />}
              <span className="font-medium">{item.label}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              className={`app-header-dropdown p-1.5 shadow-lg ${
                isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"
              }`}
            >
              {item.items.map((sub) => renderLeafItem(sub))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );
      }
      if (item.kind === "link") {
        const Icon = item.icon;
        const active = isNavItemActive(item);
        const itemKey = item.view ? `link-${item.tab}-${item.view}` : `link-${item.tab}`;
        return (
          <DropdownMenuItem
            key={itemKey}
            onClick={() => onNavigate(item.tab, item.view)}
            className={`gap-2.5 cursor-pointer rounded-md px-2.5 py-2.5 text-sm ${navLeafClass(item, active)}`}
          >
            {Icon && <NavIconBox icon={Icon} item={item} isDark={isDark} size="sm" />}
            <span className={item.navHighlight ? "font-semibold" : "font-medium"}>{item.label}</span>
          </DropdownMenuItem>
        );
      }
      return renderLeafItem(item);
    };

    return (
      <>
        <DropdownMenuLabel className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${labelClass}`}>
          {entry.label}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={isDark ? "bg-slate-800" : "bg-gray-100"} />
        {entry.items.map((item) => renderMenuItem(item))}
      </>
    );
  };

  const renderMobileActiveNav = () => {
    if (!activeEntry) return null;

    if (activeEntry.kind === "link") {
      return (
        <div className="app-header-mobile-nav">
          <button
            type="button"
            title={activeEntry.label}
            className={`app-header-mobile-group-btn ${
              isDark ? "bg-blue-600/15 text-blue-300 border-blue-500/35" : "bg-blue-50 text-blue-700 border-blue-200"
            }`}
          >
            <NavIconBox icon={activeEntry.icon} item={activeEntry} isDark={isDark} size="sm" />
            <span className="app-header-mobile-group-label">{activeEntry.label}</span>
          </button>
        </div>
      );
    }

    const GroupIcon = activeEntry.icon;
    return (
      <div className="app-header-mobile-nav">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={activeEntry.label}
              className={`app-header-mobile-group-btn group outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                isDark ? "bg-blue-600/15 text-blue-300 border-blue-500/35 data-[state=open]:border-blue-500/50" : "bg-blue-50 text-blue-700 border-blue-200 data-[state=open]:border-blue-300"
              }`}
            >
              <NavIconBox icon={GroupIcon} item={activeEntry} isDark={isDark} size="sm" />
              <span className="app-header-mobile-group-label">{activeEntry.label}</span>
              <ChevronDown className="app-header-mobile-group-chevron" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={12}
            className={`app-header-dropdown p-1.5 shadow-lg ${
              isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            {renderGroupDropdownItems(activeEntry)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const renderNavItem = (item, onDone) => {
    if (item.kind === "separator") {
      return (
        <div
          key={item.id}
          className={`my-2 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}
          role="separator"
        />
      );
    }
    if (item.kind === "submenu") {
      const SubIcon = item.icon;
      return (
        <div key={item.id} className="space-y-0.5">
          <div className={`px-2 py-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider ${labelClass}`}>
            {SubIcon && <NavIconBox icon={SubIcon} item={item} isDark={isDark} size="xs" />}
            <span>{item.label}</span>
          </div>
          <div className="space-y-0.5 pl-2">
            {item.items.map((sub) => renderNavItem(sub, onDone))}
          </div>
        </div>
      );
    }

    const Icon = item.icon;
    const active = isNavItemActive(item);
    const itemKey = item.kind === "link"
      ? `link-${item.tab}`
      : `${item.tab}-${item.view || "default"}`;
    const featured = navFeaturedItemClass(item, active, isDark);
    return (
      <button
        key={itemKey}
        type="button"
        onClick={() => { onNavigate(item.tab, item.view); onDone?.(); }}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
          featured || (
            active
              ? isDark ? "bg-blue-600/20 text-blue-300 font-medium" : "bg-blue-50 text-blue-700 font-medium"
              : isDark ? "text-slate-300 hover:bg-slate-800" : "text-gray-700 hover:bg-gray-100"
          )
        }`}
      >
        {Icon && <NavIconBox icon={Icon} item={item} isDark={isDark} size="sm" />}
        <span className={item.navHighlight ? "font-semibold" : ""}>{item.label}</span>
      </button>
    );
  };

  const navBtnClass = (active) => (
    active
      ? isDark ? "bg-blue-600/15 text-blue-300 ring-1 ring-blue-500/30" : "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
      : isDark ? "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
  );

  const renderDesktopLink = (entry) => {
    const Icon = entry.icon;
    const active = isEntryActive(entry);
    const linkKey = entry.view ? `${entry.tab}-${entry.view}` : entry.tab;
    return (
      <div key={linkKey} className="app-header-nav-item">
        <button
          type="button"
          onClick={() => onNavigate(entry.tab, entry.view)}
          title={entry.label}
          className={`app-header-nav-btn outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${navBtnClass(active)}`}
        >
          <NavIconBox icon={Icon} item={entry} isDark={isDark} size="nav" />
          <span>{entry.label}</span>
        </button>
      </div>
    );
  };

  const renderDesktopMenuGroup = (entry) => {
    const GroupIcon = entry.icon;
    const groupActive = isEntryActive(entry);
    return (
      <div key={entry.id} className="app-header-nav-item">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={entry.label}
              className={`app-header-nav-btn group outline-none data-[state=open]:shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                groupActive
                  ? isDark ? "bg-blue-600/15 text-blue-300 ring-1 ring-blue-500/30 data-[state=open]:ring-blue-500/40" : "bg-blue-50 text-blue-700 ring-1 ring-blue-200 data-[state=open]:ring-blue-300"
                  : isDark ? "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100 data-[state=open]:bg-slate-800 data-[state=open]:text-slate-100" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
              }`}
            >
              <NavIconBox icon={GroupIcon} item={entry} isDark={isDark} size="nav" />
              <span>{entry.label}</span>
              <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60 transition-transform group-data-[state=open]:rotate-180" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            side="bottom"
            sideOffset={10}
            collisionPadding={12}
            className={`app-header-dropdown p-1.5 shadow-lg ${
              isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            {renderGroupDropdownItems(entry)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const renderDesktopEntry = (entry) => (
    entry.kind === "link" ? renderDesktopLink(entry) : renderDesktopMenuGroup(entry)
  );

  const renderOverflowItems = () => overflowEntries.map((entry, index) => {
    if (entry.kind === "link") {
      const Icon = entry.icon;
      const active = isEntryActive(entry);
      const linkKey = entry.view ? `${entry.tab}-${entry.view}` : entry.tab;
      return (
        <DropdownMenuItem
          key={linkKey}
          onClick={() => onNavigate(entry.tab, entry.view)}
          className={`gap-2.5 cursor-pointer rounded-md px-2.5 py-2.5 text-sm ${
            active
              ? isDark ? "bg-blue-600/20 text-blue-300 focus:bg-blue-600/25 focus:text-blue-200" : "bg-blue-50 text-blue-700 focus:bg-blue-100 focus:text-blue-800"
              : isDark ? "focus:bg-slate-800 focus:text-slate-100" : "focus:bg-gray-50 focus:text-gray-900"
          }`}
        >
          {Icon && <NavIconBox icon={Icon} item={entry} isDark={isDark} size="sm" />}
          <span className="font-medium">{entry.label}</span>
        </DropdownMenuItem>
      );
    }

    return (
      <React.Fragment key={entry.id}>
        <DropdownMenuLabel className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${labelClass}`}>
          {entry.label}
        </DropdownMenuLabel>
        {entry.items.map((item) => {
          if (item.kind === "separator") {
            return (
              <DropdownMenuSeparator
                key={item.id}
                className={isDark ? "bg-slate-800 my-1" : "bg-gray-200 my-1"}
              />
            );
          }
          if (item.kind === "submenu") {
            return (
              <React.Fragment key={item.id}>
                <DropdownMenuLabel className={`px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${labelClass}`}>
                  {item.label}
                </DropdownMenuLabel>
                {item.items.map((sub) => {
                  const Icon = sub.icon;
                  const active = isNavItemActive(sub);
                  return (
                    <DropdownMenuItem
                      key={`${sub.tab}-${sub.view || "default"}`}
                      onClick={() => onNavigate(sub.tab, sub.view)}
                      className={`gap-2.5 cursor-pointer rounded-md px-2.5 py-2.5 text-sm ${
                        active
                          ? isDark ? "bg-blue-600/20 text-blue-300 focus:bg-blue-600/25 focus:text-blue-200" : "bg-blue-50 text-blue-700 focus:bg-blue-100 focus:text-blue-800"
                          : isDark ? "focus:bg-slate-800 focus:text-slate-100" : "focus:bg-gray-50 focus:text-gray-900"
                      }`}
                    >
                      {Icon && <NavIconBox icon={Icon} item={sub} isDark={isDark} size="sm" />}
                      <span className="font-medium">{sub.label}</span>
                    </DropdownMenuItem>
                  );
                })}
              </React.Fragment>
            );
          }
          if (item.kind === "link") {
            const Icon = item.icon;
            const active = isNavItemActive(item);
            const featured = navFeaturedItemClass(item, active, isDark);
            return (
              <DropdownMenuItem
                key={`link-${item.tab}`}
                onClick={() => onNavigate(item.tab)}
                className={`gap-2.5 cursor-pointer rounded-md px-2.5 py-2.5 text-sm ${featured || (
                  active
                    ? isDark ? "bg-blue-600/20 text-blue-300 focus:bg-blue-600/25 focus:text-blue-200" : "bg-blue-50 text-blue-700 focus:bg-blue-100 focus:text-blue-800"
                    : isDark ? "focus:bg-slate-800 focus:text-slate-100" : "focus:bg-gray-50 focus:text-gray-900"
                )}`}
              >
                {Icon && <NavIconBox icon={Icon} item={item} isDark={isDark} size="sm" />}
                <span className={item.navHighlight ? "font-semibold" : "font-medium"}>{item.label}</span>
              </DropdownMenuItem>
            );
          }
          const Icon = item.icon;
          const active = isNavItemActive(item);
          return (
            <DropdownMenuItem
              key={`${item.tab}-${item.view || "default"}`}
              onClick={() => onNavigate(item.tab, item.view)}
              className={`gap-2.5 cursor-pointer rounded-md px-2.5 py-2.5 text-sm ${
                active
                  ? isDark ? "bg-blue-600/20 text-blue-300 focus:bg-blue-600/25 focus:text-blue-200" : "bg-blue-50 text-blue-700 focus:bg-blue-100 focus:text-blue-800"
                  : isDark ? "focus:bg-slate-800 focus:text-slate-100" : "focus:bg-gray-50 focus:text-gray-900"
              }`}
            >
              {Icon && <NavIconBox icon={Icon} item={item} isDark={isDark} size="sm" />}
              <span className="font-medium">{item.label}</span>
            </DropdownMenuItem>
          );
        })}
        {index < overflowEntries.length - 1 ? (
          <DropdownMenuSeparator className={isDark ? "bg-slate-800" : "bg-gray-100"} />
        ) : null}
      </React.Fragment>
    );
  });

  const mobileSheet = (
    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          title="Menü"
          aria-label="Menüyü aç"
          className={`app-header-mobile-menu-btn lg:hidden ${
            isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          <Menu className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className={`w-[min(100vw,300px)] p-0 flex flex-col h-full max-h-[100dvh] overflow-hidden ${
          isDark ? "bg-slate-950 border-slate-800" : "bg-white border-gray-200"
        }`}
      >
        <SheetHeader className={`shrink-0 px-4 py-3 border-b text-left space-y-0 ${isDark ? "border-slate-800" : "border-gray-200"}`}>
          <SheetTitle className={`text-base font-semibold ${textClass}`}>Menü</SheetTitle>
        </SheetHeader>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] space-y-4">
          {entries.map((entry) => {
            if (entry.kind === "link") {
              return renderNavItem(entry, () => setMobileOpen(false));
            }
            return (
              <div key={entry.id}>
                <div className={`px-2 mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider ${labelClass}`}>
                  <NavIconBox icon={entry.icon} item={entry} isDark={isDark} size="sm" />
                  <span>{entry.label}</span>
                </div>
                <div className="space-y-0.5">
                  {entry.items.map((item) => renderNavItem(item, () => setMobileOpen(false)))}
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="app-header-nav-zone">
      <div className="app-header-mobile-row lg:hidden">
        {renderMobileActiveNav()}
        {mobileSheet}
      </div>
      <div className="app-header-nav-shell">
        <div ref={measureRef} className="app-header-nav-measure" aria-hidden="true">
          {entries.map((entry) => renderDesktopEntry(entry))}
        </div>
        <nav
          ref={navRef}
          className={`app-header-nav py-0.5${navSpread ? " app-header-nav--spread" : ""}`}
          aria-label="Ana menü"
        >
          {visibleEntries.map((entry) => renderDesktopEntry(entry))}
          {overflowEntries.length > 0 && (
            <div className="app-header-nav-item">
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    title="Diğer menüler"
                    aria-label="Diğer menüler"
                    className={`app-header-nav-btn app-header-nav-btn--more outline-none data-[state=open]:shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                      overflowActive
                        ? isDark ? "bg-blue-600/15 text-blue-300 ring-1 ring-blue-500/30 data-[state=open]:ring-blue-500/40" : "bg-blue-50 text-blue-700 ring-1 ring-blue-200 data-[state=open]:ring-blue-300"
                        : isDark ? "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100 data-[state=open]:bg-slate-800 data-[state=open]:text-slate-100" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
                    }`}
                  >
                    <MoreHorizontal className="w-4 h-4 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  side="bottom"
                  sideOffset={10}
                  collisionPadding={12}
                  className={`app-header-dropdown p-1.5 shadow-lg max-h-[min(70vh,28rem)] overflow-y-auto ${
                    isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"
                  }`}
                >
                  {renderOverflowItems()}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
};

// Normalize Turkish phone numbers for search
// 05337342819 → 5337342819, 905337342819 → 5337342819, +905337342819 → 5337342819
const normalizeTurkishPhone = (phone) => {
  if (!phone) return phone;
  let normalized = phone.replace(/\s+/g, '').replace(/[^\d]/g, ''); // Remove spaces and non-digits
  
  // Turkish mobile numbers start with 5 (after country code)
  // Remove leading +90, 90, or 0 if followed by 5 (Turkish mobile)
  if (normalized.startsWith('90') && normalized.length > 2 && normalized[2] === '5') {
    normalized = normalized.substring(2); // Remove 90
  } else if (normalized.startsWith('0') && normalized.length > 1 && normalized[1] === '5') {
    normalized = normalized.substring(1); // Remove leading 0
  }
  
  return normalized;
};

const DEFAULT_LEAD_STATUSES = [
  { value: "yeni", label: "Yeni", tone: "violet" },
  { value: "iletisim", label: "İletişimde", tone: "blue" },
  { value: "teklif", label: "Teklif Verildi", tone: "amber" },
  { value: "takip", label: "Takip", tone: "blue" },
  { value: "kazanildi", label: "Kazanıldı", tone: "emerald" },
  { value: "kaybedildi", label: "Kaybedildi", tone: "rose" },
];

const LEAD_TONE_OPTIONS = [
  { value: "violet", label: "Mor" },
  { value: "blue", label: "Mavi" },
  { value: "amber", label: "Turuncu" },
  { value: "emerald", label: "Yeşil" },
  { value: "rose", label: "Kırmızı" },
  { value: "cyan", label: "Camgöbeği" },
];

const LEAD_TONE_CLASSES = {
  violet: { dark: "bg-violet-500/20 text-violet-400 border-violet-500/30", light: "bg-violet-100 text-violet-700 border-violet-200" },
  blue: { dark: "bg-blue-500/20 text-blue-400 border-blue-500/30", light: "bg-blue-100 text-blue-700 border-blue-200" },
  amber: { dark: "bg-amber-500/20 text-amber-400 border-amber-500/30", light: "bg-amber-100 text-amber-700 border-amber-200" },
  emerald: { dark: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", light: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rose: { dark: "bg-rose-500/20 text-rose-400 border-rose-500/30", light: "bg-rose-100 text-rose-700 border-rose-200" },
  cyan: { dark: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30", light: "bg-cyan-100 text-cyan-700 border-cyan-200" },
};

const LEAD_TONE_DOT = {
  violet: "bg-violet-400",
  blue: "bg-blue-400",
  amber: "bg-amber-400",
  emerald: "bg-emerald-400",
  rose: "bg-rose-400",
  cyan: "bg-cyan-400",
};

const DEFAULT_LEAD_SOURCES = [
  { value: "direkt", label: "Direkt" },
  { value: "whatsapp", label: "Whatsapp" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "web", label: "Web Sitesi" },
  { value: "telefon", label: "Telefon" },
];

const TODO_STATUSES = [
  { id: "yeni", label: "Yeni", dotClass: "bg-amber-400", textLight: "text-amber-600", textDark: "text-amber-400" },
  { id: "islemde", label: "İşlemde", dotClass: "bg-blue-500", textLight: "text-blue-600", textDark: "text-blue-400" },
  { id: "tamamlandi", label: "Tamamlandı", dotClass: "bg-emerald-500", textLight: "text-emerald-600", textDark: "text-emerald-400" },
];

const isTodoImageFile = (file) => {
  const mime = file?.mime_type || file?.type || "";
  const name = file?.original_name || file?.name || "";
  return mime.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(name);
};

const TodoLocalFilePreview = ({ file, onRemove, isDark }) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!isTodoImageFile(file)) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className={`rounded-lg border overflow-hidden ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-white"}`}>
      {previewUrl ? (
        <img src={previewUrl} alt={file.name} className="w-full h-24 object-cover" />
      ) : (
        <div className={`h-24 flex items-center justify-center ${isDark ? "bg-slate-900" : "bg-gray-100"}`}>
          <Paperclip className="w-5 h-5 opacity-50" />
        </div>
      )}
      <div className="p-2 flex items-center justify-between gap-1">
        <span className="text-[10px] truncate flex-1">{file.name}</span>
        <button type="button" onClick={onRemove} className="text-red-500 shrink-0">
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

const TodoSavedFilePreview = ({ file, isDark, onDownload, onDelete, canDelete }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!file?.id || !isTodoImageFile(file)) {
      setPreviewUrl(null);
      return undefined;
    }
    let objectUrl = null;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/todos?action=download&file_id=${file.id}`, { responseType: "blob" })
      .then((res) => {
        if (!cancelled) {
          objectUrl = URL.createObjectURL(res.data);
          setPreviewUrl(objectUrl);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <div className={`rounded-lg border overflow-hidden ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-white"}`}>
      {previewUrl ? (
        <button type="button" onClick={() => onDownload(file)} className="block w-full">
          <img src={previewUrl} alt={file.original_name} className="w-full h-28 object-cover hover:opacity-90" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onDownload(file)}
          className={`w-full h-28 flex flex-col items-center justify-center gap-1 ${isDark ? "bg-slate-900 hover:bg-slate-800" : "bg-gray-100 hover:bg-gray-200"}`}
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5 opacity-50" />}
          <span className="text-[10px] px-2 truncate max-w-full">{file.original_name}</span>
        </button>
      )}
      <div className="p-2 flex items-center justify-between gap-1">
        <button type="button" onClick={() => onDownload(file)} className={`text-[10px] truncate flex-1 text-left hover:underline ${isDark ? "text-blue-300" : "text-blue-700"}`}>
          {file.original_name}
        </button>
        {canDelete && (
          <button type="button" onClick={() => onDelete(file.id)} className="text-red-500 shrink-0">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

const getLeadSourceMeta = (value, leadSources = DEFAULT_LEAD_SOURCES) =>
  leadSources.find((s) => s.value === value) || null;

const getLeadStatusMeta = (status, leadStatuses = DEFAULT_LEAD_STATUSES) =>
  leadStatuses.find((s) => s.value === status) || leadStatuses[0] || DEFAULT_LEAD_STATUSES[0];

const phonesMatch = (a, b) => {
  if (!a || !b) return false;
  return normalizeTurkishPhone(a) === normalizeTurkishPhone(b);
};

const getAgentPhonesForRecord = (agentId, agents) => {
  const agent = agents.find((a) => String(a.id) === String(agentId));
  if (!agent?.gsm) return [];
  return agent.gsm.split(",").map((s) => normalizeTurkishPhone(s.trim())).filter(Boolean);
};

const getCustomerPhoneFromRecord = (record, agents) => {
  if (!record) return null;
  const agentPhones = getAgentPhonesForRecord(record.agentId, agents);
  const arayanNorm = normalizeTurkishPhone(record.arayan_numara);
  const arananNorm = normalizeTurkishPhone(record.aranan_numara);
  const arayanIsAgent = agentPhones.some((p) => p === arayanNorm);
  const arananIsAgent = agentPhones.some((p) => p === arananNorm);

  if (arayanIsAgent && !arananIsAgent) return record.aranan_numara;
  if (arananIsAgent && !arayanIsAgent) return record.arayan_numara;
  if (String(record.yon) === "1") return record.arayan_numara;
  if (String(record.yon) === "2") return record.aranan_numara;
  return arayanIsAgent ? record.aranan_numara : record.arayan_numara;
};

const getLeadStatusBadgeClass = (status, isDark, leadStatuses = DEFAULT_LEAD_STATUSES) => {
  const meta = getLeadStatusMeta(status, leadStatuses);
  const tone = meta?.tone || "violet";
  const classes = LEAD_TONE_CLASSES[tone] || LEAD_TONE_CLASSES.violet;
  return isDark ? classes.dark : classes.light;
};

// Common Components (also exported from ./components/common/)
const StatusBadge = ({ durum, durum_aciklama, isDark }) => {
  const getClass = () => {
    if (durum === "1") return isDark ? "status-cevaplanan" : "bg-emerald-100 text-emerald-700 ring-emerald-200";
    if (durum === "3") return isDark ? "status-mesgul" : "bg-rose-100 text-rose-700 ring-rose-200";
    return isDark ? "status-cevap-yok" : "bg-amber-100 text-amber-700 ring-amber-200";
  };
  const label = durum_aciklama === "cevaplanan" ? "Cevaplandı" : durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${getClass()}`}>{label}</span>;
};

const CdrFollowUpIndicator = ({ record, isDark, onFollowUpClick }) => {
  if (String(record.durum) === "1" || !record.follow_up_status) return null;
  const answered = record.follow_up_status === "answered";
  const time = record.follow_up_time || "";
  const tooltipText = answered
    ? (time ? `${time} geri arandı ve görüşüldü` : "Aynı gün geri arandı ve görüşüldü")
    : (time ? `${time} geri arama denendi (cevapsız)` : "Aynı gün geri arama denendi (cevapsız)");
  const canClick = !!record.follow_up_cdr_id && typeof onFollowUpClick === "function";

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span
          role={canClick ? "button" : undefined}
          tabIndex={canClick ? 0 : undefined}
          onClick={canClick ? (e) => { e.stopPropagation(); onFollowUpClick(record); } : undefined}
          onKeyDown={canClick ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onFollowUpClick(record);
            }
          } : undefined}
          className={`inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 ${
            canClick ? "cursor-pointer hover:ring-2 hover:ring-offset-1" : "cursor-default"
          } ${
            answered
              ? (isDark ? "bg-emerald-500/20 text-emerald-400 hover:ring-emerald-400/50" : "bg-emerald-100 text-emerald-700 hover:ring-emerald-300")
              : (isDark ? "bg-blue-500/20 text-blue-400 hover:ring-blue-400/50" : "bg-blue-100 text-blue-700 hover:ring-blue-300")
          }`}
          title={canClick ? "Geri arama kaydını göster" : undefined}
        >
          <PhoneOutgoing className="w-3 h-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={4}
        className={isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"}
      >
        {canClick ? `${tooltipText} — tıklayın` : tooltipText}
      </TooltipContent>
    </Tooltip>
  );
};

const DayRecordStatus = ({ record, isDark }) => {
  const isMissed = String(record.durum) !== "1";
  const missedCount = Number(record.missed_count_today || 0);

  return (
    <div className="inline-flex items-center gap-1 justify-center">
      <StatusBadge durum={record.durum} durum_aciklama={record.durum_aciklama} isDark={isDark} />
      {isMissed && missedCount > 1 && (
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold shrink-0 cursor-default ${
                isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-800"
              }`}
            >
              ×{missedCount}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={4}
            className={isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"}
          >
            Bu numaraya bugün {missedCount} cevapsız
          </TooltipContent>
        </Tooltip>
      )}
      <CdrFollowUpIndicator record={record} isDark={isDark} />
    </div>
  );
};

// Agent color palette - each agent gets a unique color based on name
const AGENT_COLORS = [
  { bg: "bg-purple-500/20", text: "text-purple-400", bgLight: "bg-purple-100", textLight: "text-purple-700" },
  { bg: "bg-cyan-500/20", text: "text-cyan-400", bgLight: "bg-cyan-100", textLight: "text-cyan-700" },
  { bg: "bg-emerald-500/20", text: "text-emerald-400", bgLight: "bg-emerald-100", textLight: "text-emerald-700" },
  { bg: "bg-amber-500/20", text: "text-amber-400", bgLight: "bg-amber-100", textLight: "text-amber-700" },
  { bg: "bg-rose-500/20", text: "text-rose-400", bgLight: "bg-rose-100", textLight: "text-rose-700" },
  { bg: "bg-blue-500/20", text: "text-blue-400", bgLight: "bg-blue-100", textLight: "text-blue-700" },
  { bg: "bg-indigo-500/20", text: "text-indigo-400", bgLight: "bg-indigo-100", textLight: "text-indigo-700" },
  { bg: "bg-pink-500/20", text: "text-pink-400", bgLight: "bg-pink-100", textLight: "text-pink-700" },
  { bg: "bg-teal-500/20", text: "text-teal-400", bgLight: "bg-teal-100", textLight: "text-teal-700" },
  { bg: "bg-orange-500/20", text: "text-orange-400", bgLight: "bg-orange-100", textLight: "text-orange-700" },
];

const getAgentColor = (agentName, isDark) => {
  if (!agentName) return isDark ? "bg-slate-800 text-slate-400" : "bg-gray-100 text-gray-500";
  // Generate hash from agent name
  let hash = 0;
  for (let i = 0; i < agentName.length; i++) {
    hash = agentName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AGENT_COLORS.length;
  const color = AGENT_COLORS[index];
  return isDark ? `${color.bg} ${color.text}` : `${color.bgLight} ${color.textLight}`;
};

const AgentNameBadge = ({ agentName, agentId, agents, isDark, onClick }) => {
  const agent = agentId
    ? agents.find((a) => String(a.id) === String(agentId))
    : agents.find((a) => a.ad_soyad === agentName);
  const gsmList = agent?.gsm
    ? agent.gsm.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all ${getAgentColor(agentName, isDark)}`}
          onClick={onClick}
        >
          <User className="w-3 h-3" />{agentName}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className={`p-0 border shadow-xl ${isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"}`}
      >
        <div className="px-3 py-2.5 space-y-2">
          <p className="text-xs font-semibold leading-none">{agentName}</p>
          {gsmList.length > 0 ? (
            <div className="space-y-1">
              <p className={`text-[10px] font-medium uppercase tracking-wider ${isDark ? "text-slate-500" : "text-gray-400"}`}>GSM Numarası</p>
              {gsmList.map((num) => (
                <p key={num} className="font-mono text-sm flex items-center gap-1.5">
                  <Smartphone className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                  {num}
                </p>
              ))}
            </div>
          ) : (
            <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-500"}`}>GSM tanımlı değil</p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const CustomerBadge = ({ phone, displayName, leadStatus, leadStatuses = DEFAULT_LEAD_STATUSES, isDark, onClick, onHoverRequest, loading = false }) => {
  if (!phone) return <span className={isDark ? "text-slate-500" : "text-gray-400"}>-</span>;
  const name = displayName?.trim();
  const defaultLead = leadStatuses[0]?.value || "yeni";
  const leadMeta = getLeadStatusMeta(leadStatus, leadStatuses);
  const dotClass = LEAD_TONE_DOT[leadMeta?.tone] || LEAD_TONE_DOT.violet;

  const handleOpenChange = (open) => {
    if (open && onHoverRequest) onHoverRequest(phone);
  };

  return (
    <Tooltip delayDuration={150} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>
        <div className="inline-flex items-center gap-0.5 max-w-full">
          <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 max-w-[160px] px-2 py-1 rounded text-left transition-all hover:ring-2 hover:ring-violet-400/50 ${
              isDark ? "bg-violet-500/15 text-violet-300 border border-violet-500/25" : "bg-violet-50 text-violet-700 border border-violet-200"
            }`}
          >
            <Contact className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate text-xs font-medium font-mono">{phone}</span>
            {leadStatus && leadStatus !== defaultLead && (
              <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${dotClass}`} />
            )}
          </button>
          <CopyPhoneButton phone={phone} isDark={isDark} />
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className={`p-0 border shadow-xl ${isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"}`}
      >
        <div className="px-3 py-2.5 space-y-1">
          {loading ? (
            <>
              <p className="text-xs font-semibold leading-none">Müşteri bilgisi</p>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Yükleniyor...</p>
            </>
          ) : name ? (
            <>
              <p className="text-xs font-semibold leading-none">{name}</p>
              <p className={`text-[10px] font-medium uppercase tracking-wider ${isDark ? "text-slate-500" : "text-gray-400"}`}>GSM</p>
              <p className="font-mono text-sm">{phone}</p>
            </>
          ) : (
            <>
              <p className="text-xs font-semibold leading-none">Müşteri CRM</p>
              <p className={`font-mono text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>{phone}</p>
            </>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const CustomerCrmModal = ({ isOpen, onClose, phone, isDark, leadStatuses = DEFAULT_LEAD_STATUSES, leadSources = DEFAULT_LEAD_SOURCES, onLeadStatusesChange, onLeadSourcesChange, onProfileChange, onOpenDetails }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ ad_soyad: "", lead_durumu: "yeni", lead_kaynagi: "" });
  const [quickNote, setQuickNote] = useState("");
  const [showNameField, setShowNameField] = useState(false);
  const [showSourceField, setShowSourceField] = useState(false);

  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white focus:border-violet-500" : "bg-white border-gray-300 focus:border-violet-500";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";

  const loadCustomer = useCallback(async () => {
    if (!phone) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers`, { params: { phone }, timeout: 30000 });
      const loadedProfile = res.data.profile || { ad_soyad: "", lead_durumu: "yeni", lead_kaynagi: "" };
      const hasLeadSource = !!loadedProfile.lead_kaynagi?.trim() || (res.data.lead_source_history?.length > 0);
      setProfile(loadedProfile);
      setShowNameField(!loadedProfile.ad_soyad?.trim());
      setShowSourceField(!hasLeadSource);
      if (res.data.lead_statuses?.length) onLeadStatusesChange?.(res.data.lead_statuses);
      if (res.data.lead_sources?.length) onLeadSourcesChange?.(res.data.lead_sources);
      onProfileChange?.(phone, res.data.profile);
    } catch (error) {
      toast.error("Müşteri bilgisi yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [phone, onProfileChange, onLeadStatusesChange, onLeadSourcesChange]);

  useEffect(() => {
    if (isOpen && phone) {
      setQuickNote("");
      loadCustomer();
    } else if (!isOpen) {
      setShowNameField(false);
      setShowSourceField(false);
    }
  }, [isOpen, phone, loadCustomer]);

  const handleSave = async () => {
    if (!phone) return;
    setSaving(true);
    try {
      const payload = {
        ad_soyad: profile.ad_soyad,
        lead_durumu: profile.lead_durumu,
      };
      if (showSourceField && profile.lead_kaynagi?.trim()) {
        payload.lead_kaynagi = profile.lead_kaynagi.trim();
      }
      if (quickNote.trim()) payload.quick_note = quickNote.trim();
      const res = await axios.put(`${API}/customers`, payload, { params: { phone } });
      const savedProfile = res.data.profile || profile;
      const hasLeadSource = !!savedProfile.lead_kaynagi?.trim() || (res.data.lead_source_history?.length > 0);
      setProfile(savedProfile);
      if (hasLeadSource) setShowSourceField(false);
      setQuickNote("");
      if (res.data.lead_statuses?.length) onLeadStatusesChange?.(res.data.lead_statuses);
      if (res.data.lead_sources?.length) onLeadSourcesChange?.(res.data.lead_sources);
      onProfileChange?.(phone, res.data.profile);
      toast.success("Kaydedildi");
    } catch (error) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const validLeadValue = leadStatuses.some((s) => s.value === profile.lead_durumu)
    ? profile.lead_durumu
    : (leadStatuses[0]?.value || "yeni");
  const leadLabel = getLeadStatusMeta(validLeadValue, leadStatuses)?.label || "Yeni";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={`max-w-md p-0 gap-0 ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
        <div className={`px-6 pt-6 pb-4 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-violet-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-violet-50 to-white"}`}>
          <DialogHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <DialogTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                  <Contact className="w-5 h-5 text-violet-500 shrink-0" />
                  {profile.ad_soyad?.trim() || "Müşteri"}
                </DialogTitle>
                <p className={`text-sm font-mono mt-1 ${mutedClass}`}>{phone}</p>
              </div>
              <Badge className={`shrink-0 border ${getLeadStatusBadgeClass(validLeadValue, isDark, leadStatuses)}`}>{leadLabel}</Badge>
            </div>
          </DialogHeader>
        </div>
        <div className="px-6 py-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-violet-500" /></div>
          ) : (
            <>
              {showNameField && (
                <div className={`rounded-lg p-3 border ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50"}`}>
                  <Label className={isDark ? "text-amber-300" : "text-amber-800"}>Ad Soyad *</Label>
                  <Input
                    value={profile.ad_soyad || ""}
                    onChange={(e) => setProfile((p) => ({ ...p, ad_soyad: e.target.value }))}
                    className={`mt-1.5 ${inputClass} ${isDark ? "border-amber-500/40" : "border-amber-300"}`}
                    placeholder="Müşteri adı soyadı"
                    autoFocus
                  />
                  <p className={`text-xs mt-1.5 ${isDark ? "text-amber-400/80" : "text-amber-700"}`}>Bu müşteri için henüz isim kaydı yok</p>
                </div>
              )}

              <div>
                <Label className="flex items-center justify-between">
                  <span>Lead Durumu</span>
                  <span className={`text-xs font-normal ${mutedClass}`}>Süreç takibi</span>
                </Label>
                <Select value={validLeadValue} onValueChange={(v) => setProfile((p) => ({ ...p, lead_durumu: v }))}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    {leadStatuses.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {showSourceField && (
                <div className={`rounded-lg p-3 border ${isDark ? "border-violet-500/30 bg-violet-500/5" : "border-violet-200 bg-violet-50/50"}`}>
                  <Label className={isDark ? "text-violet-300" : "text-violet-800"}>Lead Kaynağı *</Label>
                  <Select value={profile.lead_kaynagi || undefined} onValueChange={(v) => setProfile((p) => ({ ...p, lead_kaynagi: v }))}>
                    <SelectTrigger className={`mt-1.5 ${inputClass}`}>
                      <SelectValue placeholder="Kaynak seçin (Whatsapp, Instagram...)" />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                      {leadSources.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className={`text-xs mt-1.5 ${isDark ? "text-violet-400/80" : "text-violet-700"}`}>İlk görüşme — müşterinin nereden geldiğini seçin</p>
                </div>
              )}

              <div>
                <Label>Hızlı Not</Label>
                <textarea
                  value={quickNote}
                  onChange={(e) => setQuickNote(e.target.value)}
                  rows={3}
                  className={`mt-1.5 w-full rounded-md border px-3 py-2 text-sm ${inputClass}`}
                  placeholder="Kısa not — geçmişe eklenir"
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter className={`px-6 py-4 border-t gap-2 ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <Button variant="ghost" onClick={onClose}>Kapat</Button>
          <Button variant="outline" onClick={() => { onClose(); onOpenDetails?.(phone); }} className={isDark ? "border-slate-700" : ""}>
            Ayrıntılar
          </Button>
          <Button onClick={handleSave} disabled={saving || loading} className="bg-violet-600 hover:bg-violet-700 text-white">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ icon: Icon, title, value, subtitle, secondary, color = "blue", isDark, compactValue = false }) => {
  const colors = {
    blue: isDark ? "text-blue-400" : "text-blue-600",
    green: isDark ? "text-emerald-400" : "text-emerald-600",
    amber: isDark ? "text-amber-400" : "text-amber-600",
    red: isDark ? "text-rose-400" : "text-rose-600",
    cyan: isDark ? "text-cyan-400" : "text-cyan-600",
  };
  return (
    <Card className={`card-hover ${isDark ? "bg-slate-950/50 border-slate-800" : "bg-white border-gray-200 shadow-sm"}`}>
      <CardContent className="p-2.5 sm:p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className={`text-[11px] leading-none mb-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{title}</p>
            <p className={`${compactValue ? "text-base sm:text-lg" : "text-lg sm:text-xl"} font-bold font-mono leading-tight truncate ${colors[color]}`}>{value}</p>
            {(subtitle || secondary) && (
              <div className="mt-0.5 leading-tight">
                {subtitle && <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-gray-400"}`}>{subtitle}</p>}
                {secondary && <p className={`text-[10px] ${isDark ? "text-slate-600" : "text-gray-400/80"}`}>{secondary}</p>}
              </div>
            )}
          </div>
          <div className={`p-1.5 rounded-md shrink-0 ${isDark ? "bg-slate-800/50" : "bg-gray-100"} ${colors[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const formatUniqueStat = (count) => count != null ? `${Number(count).toLocaleString('tr-TR')} tekil` : null;

/** Mola analizi — alan açıklamaları (title tooltip) */
const BREAK_ANALYSIS_HINTS = {
  totalBreak: "Mesai içi çağrılar arası ve mesai sonu (≥5 dk) boşlukların toplam süresi.",
  breakCount: "≥5 dk tespit edilen mola sayısı (çağrı arası + mesai sonu).",
  avgBreak: "Toplam mola süresinin mola adedine bölünmesi.",
  avgHighTalkBreak: "Günlük konuşması 60 dk ve üzeri olan günlerin mola sürelerinin ortalaması.",
  totalTalk: "Mesai içi tüm çağrıların konuşma süreleri toplamı (CDR konusma_suresi alanı).",
  totalCalls: "Seçilen dönemde mesai saatleri içindeki toplam çağrı adedi.",
  activeDays: "En az bir mesai içi çağrısı olan gün sayısı.",
  lateDays: "Mesai başlangıcından en az 1 dk sonra ilk giden arama yapılan günler.",
  earlyDays: "Mesai bitiminden en az 1 dk önce son görüşme biten günler.",
  firstOutgoing: "Mesai içindeki ilk giden aramanın başlangıç saati.",
  lastCall: "Mesai içindeki son görüşmenin bitiş saati.",
  dailyTalk: "O gün mesai içi giden ve gelen çağrıların konuşma süreleri toplamı (CDR konusma_suresi; mola süreleri dahil değil).",
  dailyOffShiftTalk: "O gün mesai saatleri dışındaki giden ve gelen çağrıların konuşma süreleri toplamı (CDR konusma_suresi).",
  dailyTotalTalk: "O gün mesai içi ve mesai dışı konuşma sürelerinin toplamı (mola süreleri dahil değil).",
  dailyCalls: "O gün mesai içindeki çağrı adedi.",
  dailyBreakCount: "O gün tespit edilen mola adedi (≥5 dk; çağrı arası + mesai sonu).",
  dailyBreakTotal: "O gün molaların toplam süresi (çağrı arası + mesai sonu ≥5 dk).",
  lateStart: "Mesai başlangıcı ile ilk giden arama arası (60 sn tolerans sonrası).",
  earlyLeave: "Son görüşme bitişi ile mesai bitişi arasındaki süre (≥5 dk ise mola toplamına da dahil).",
  breakStart: "Önceki görüşmenin bitiş saati — mola başlangıcı.",
  breakEnd: "Sonraki görüşmenin başlangıç saati — mola bitişi.",
  breakDuration: "İki görüşme arasındaki boşluk süresi.",
  mesaiHours: "Agent mesai planındaki o güne ait çalışma saatleri.",
  activitySummary: "Günün kronolojik aktivite özeti (ilk arama, molalar, son görüşme).",
  shortCalls: "O gün cevaplanan ve konuşma süresi 1–59 saniye olan görüşme adedi.",
  unfollowedMissed: "O gün yalnızca bir kez temas edilen ve cevapsız kalan numara adedi. Detay için tıklayın.",
};

const SHORT_CALL_LABEL = "<1 dk";

const GOREV_OPTIONS = [
  { value: "", label: "— Eski sistem (manuel agent listesi)" },
  { value: "call_center_yonetici", label: "Call Center Yönetici" },
  { value: "asistan", label: "Asistan" },
  { value: "takim_lideri", label: "Takım Lideri" },
  { value: "dataci", label: "Datacı" },
  { value: "kalite", label: "Kalite Ekibi" },
  { value: "yonetici", label: "Yönetici" },
];

const gorevLabel = (gorev) => GOREV_OPTIONS.find((o) => o.value === gorev)?.label || "—";

const parseIdCsv = (csv) => {
  if (!csv) return [];
  if (Array.isArray(csv)) return csv.map(Number).filter((n) => !isNaN(n) && n > 0);
  return String(csv).split(",").map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id) && id > 0);
};

const BreakAnalysisHint = ({ hint, className, children, as: Tag = "span" }) => (
  <Tag className={className} title={hint || undefined} style={hint ? { cursor: "help" } : undefined}>
    {children}
  </Tag>
);

const MESAI_LATE_DAY_THEMES = [
  {
    dark: { card: "border-rose-500/30 bg-rose-500/10", stripe: "bg-rose-500", date: "text-rose-300", day: "text-rose-400/80", badge: "bg-rose-500/25 text-rose-200 border-rose-500/40", call: "text-rose-300", divider: "border-rose-500/20" },
    light: { card: "border-rose-200 bg-rose-50/80", stripe: "bg-rose-500", date: "text-rose-800", day: "text-rose-600", badge: "bg-rose-100 text-rose-800 border-rose-300", call: "text-rose-700", divider: "border-rose-200" },
  },
  {
    dark: { card: "border-amber-500/30 bg-amber-500/10", stripe: "bg-amber-500", date: "text-amber-300", day: "text-amber-400/80", badge: "bg-amber-500/25 text-amber-200 border-amber-500/40", call: "text-amber-300", divider: "border-amber-500/20" },
    light: { card: "border-amber-200 bg-amber-50/80", stripe: "bg-amber-500", date: "text-amber-900", day: "text-amber-700", badge: "bg-amber-100 text-amber-900 border-amber-300", call: "text-amber-800", divider: "border-amber-200" },
  },
  {
    dark: { card: "border-violet-500/30 bg-violet-500/10", stripe: "bg-violet-500", date: "text-violet-300", day: "text-violet-400/80", badge: "bg-violet-500/25 text-violet-200 border-violet-500/40", call: "text-violet-300", divider: "border-violet-500/20" },
    light: { card: "border-violet-200 bg-violet-50/80", stripe: "bg-violet-500", date: "text-violet-900", day: "text-violet-700", badge: "bg-violet-100 text-violet-900 border-violet-300", call: "text-violet-800", divider: "border-violet-200" },
  },
  {
    dark: { card: "border-cyan-500/30 bg-cyan-500/10", stripe: "bg-cyan-500", date: "text-cyan-300", day: "text-cyan-400/80", badge: "bg-cyan-500/25 text-cyan-200 border-cyan-500/40", call: "text-cyan-300", divider: "border-cyan-500/20" },
    light: { card: "border-cyan-200 bg-cyan-50/80", stripe: "bg-cyan-500", date: "text-cyan-900", day: "text-cyan-700", badge: "bg-cyan-100 text-cyan-900 border-cyan-300", call: "text-cyan-800", divider: "border-cyan-200" },
  },
  {
    dark: { card: "border-emerald-500/30 bg-emerald-500/10", stripe: "bg-emerald-500", date: "text-emerald-300", day: "text-emerald-400/80", badge: "bg-emerald-500/25 text-emerald-200 border-emerald-500/40", call: "text-emerald-300", divider: "border-emerald-500/20" },
    light: { card: "border-emerald-200 bg-emerald-50/80", stripe: "bg-emerald-500", date: "text-emerald-900", day: "text-emerald-700", badge: "bg-emerald-100 text-emerald-900 border-emerald-300", call: "text-emerald-800", divider: "border-emerald-200" },
  },
  {
    dark: { card: "border-orange-500/30 bg-orange-500/10", stripe: "bg-orange-500", date: "text-orange-300", day: "text-orange-400/80", badge: "bg-orange-500/25 text-orange-200 border-orange-500/40", call: "text-orange-300", divider: "border-orange-500/20" },
    light: { card: "border-orange-200 bg-orange-50/80", stripe: "bg-orange-500", date: "text-orange-900", day: "text-orange-700", badge: "bg-orange-100 text-orange-900 border-orange-300", call: "text-orange-800", divider: "border-orange-200" },
  },
  {
    dark: { card: "border-sky-500/30 bg-sky-500/10", stripe: "bg-sky-500", date: "text-sky-300", day: "text-sky-400/80", badge: "bg-sky-500/25 text-sky-200 border-sky-500/40", call: "text-sky-300", divider: "border-sky-500/20" },
    light: { card: "border-sky-200 bg-sky-50/80", stripe: "bg-sky-500", date: "text-sky-900", day: "text-sky-700", badge: "bg-sky-100 text-sky-900 border-sky-300", call: "text-sky-800", divider: "border-sky-200" },
  },
  {
    dark: { card: "border-fuchsia-500/30 bg-fuchsia-500/10", stripe: "bg-fuchsia-500", date: "text-fuchsia-300", day: "text-fuchsia-400/80", badge: "bg-fuchsia-500/25 text-fuchsia-200 border-fuchsia-500/40", call: "text-fuchsia-300", divider: "border-fuchsia-500/20" },
    light: { card: "border-fuchsia-200 bg-fuchsia-50/80", stripe: "bg-fuchsia-500", date: "text-fuchsia-900", day: "text-fuchsia-700", badge: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300", call: "text-fuchsia-800", divider: "border-fuchsia-200" },
  },
];

const getMesaiLateDayTheme = (index, isDark) => {
  const theme = MESAI_LATE_DAY_THEMES[index % MESAI_LATE_DAY_THEMES.length];
  return isDark ? theme.dark : theme.light;
};

const ReportStatCell = ({ value, unique, colorClass, isDark }) => (
  <TableCell className={`text-center ${colorClass}`}>
    <div className="font-bold">{value}</div>
    {unique != null && (
      <div className={`text-[10px] font-normal ${isDark ? "text-slate-500" : "text-gray-400"}`}>{formatUniqueStat(unique)}</div>
    )}
  </TableCell>
);

const toggleReportSort = (current, key) => {
  if (current.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: key === "call_date" || key === "agent_name" ? "asc" : "desc" };
};

const sortReportRows = (rows, sort) => {
  if (!rows?.length || !sort?.key) return rows || [];
  const dir = sort.dir === "asc" ? 1 : -1;
  const clockToMinutes = (value) => {
    if (!value || !/^\d{2}:\d{2}$/.test(String(value))) return -1;
    const [hour, minute] = String(value).split(":").map(Number);
    return (hour * 60) + minute;
  };
  return [...rows].sort((a, b) => {
    if (sort.key === "is_working_day") {
      return dir * ((a.is_working_day ? 1 : 0) - (b.is_working_day ? 1 : 0));
    }
    if (sort.key === "missed_calls") {
      const av = Number(a.missed_calls ?? (a.total_calls - a.answered_calls) ?? 0);
      const bv = Number(b.missed_calls ?? (b.total_calls - b.answered_calls) ?? 0);
      return dir * (av - bv);
    }
    if (sort.key === "avg_start_time" || sort.key === "avg_end_time" || sort.key === "scheduled_start" || sort.key === "scheduled_end" || sort.key === "first_call_time") {
      return dir * (clockToMinutes(a[sort.key]) - clockToMinutes(b[sort.key]));
    }
    if (sort.key === "status") {
      const statusOrder = { late: 0, absent: 1, on_time: 2, off_day: 3 };
      return dir * ((statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99));
    }
    const av = a[sort.key];
    const bv = b[sort.key];
    if (sort.key === "call_date" || sort.key === "agent_name" || sort.key === "period" || sort.key === "first_call" || sort.key === "last_call" || sort.key === "company_name") {
      return dir * String(av || "").localeCompare(String(bv || ""), "tr");
    }
    return dir * ((Number(av) || 0) - (Number(bv) || 0));
  });
};

const ReportSortableHead = ({ label, column, sort, onSort, className = "", title }) => (
  <TableHead
    className={`cursor-pointer select-none whitespace-nowrap transition-colors hover:text-teal-500 ${className}`}
    onClick={() => onSort(column)}
    title={title || `${label} — sıralamak için tıklayın`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sort.key === column ? (
        sort.dir === "asc" ? <ChevronUp className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />
      ) : (
        <ArrowUpDown className="w-3 h-3 shrink-0 opacity-35" />
      )}
    </span>
  </TableHead>
);

const unfollowedMissedStatusLabel = (durumAciklama) => {
  if (durumAciklama === "cevaplanan") return "Cevaplandı";
  if (durumAciklama === "mesgul") return "Meşgul";
  return "Cevapsız";
};

const BreakAnalysisUnfollowedList = ({ records, isDark, onClose }) => {
  if (!records?.length) {
    return (
      <div className={`rounded-lg border px-3 py-4 text-center text-xs ${isDark ? "border-slate-700 bg-slate-800/40 text-slate-400" : "border-gray-200 bg-gray-50 text-gray-500"}`}>
        Takipsiz cevapsız kayıt bulunamadı
      </div>
    );
  }

  return (
    <div className={`rounded-lg border overflow-hidden ${isDark ? "border-rose-500/30" : "border-rose-200"}`}>
      <div className={`flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium ${isDark ? "bg-rose-500/10 text-rose-300" : "bg-rose-50 text-rose-800"}`}>
        <span className="inline-flex items-center gap-1.5">
          <PhoneMissed className="w-3.5 h-3.5" />
          Takipsiz Cevapsızlar ({records.length})
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={`text-[10px] px-2 py-0.5 rounded ${isDark ? "hover:bg-rose-500/20 text-rose-200" : "hover:bg-rose-100 text-rose-700"}`}
          >
            Kapat
          </button>
        )}
      </div>
      <div className={`overflow-x-auto overflow-y-auto overscroll-contain max-h-52 ${isDark ? "bg-slate-900/40" : "bg-white"}`}>
        <Table className="text-xs">
          <TableHeader className={`sticky top-0 z-10 ${isDark ? "bg-slate-800" : "bg-rose-50/90"}`}>
            <TableRow className={isDark ? "border-slate-700" : "border-rose-100"}>
              <TableHead className={`h-8 ${isDark ? "text-slate-400" : ""}`}>Saat</TableHead>
              <TableHead className={`h-8 ${isDark ? "text-slate-400" : ""}`}>Yön</TableHead>
              <TableHead className={`h-8 ${isDark ? "text-slate-400" : ""}`}>Numara</TableHead>
              <TableHead className={`h-8 hidden sm:table-cell ${isDark ? "text-slate-400" : ""}`}>Arayan</TableHead>
              <TableHead className={`h-8 hidden sm:table-cell ${isDark ? "text-slate-400" : ""}`}>Aranan</TableHead>
              <TableHead className={`h-8 ${isDark ? "text-slate-400" : ""}`}>Durum</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((row) => (
              <TableRow key={`${row.id}-${row.tarih}`} className={isDark ? "border-slate-700/50" : "hover:bg-rose-50/50"}>
                <TableCell className={`py-2 font-mono whitespace-nowrap ${isDark ? "text-slate-300" : ""}`}>{row.call_time || "-"}</TableCell>
                <TableCell className="py-2">
                  {row.yon === 2 ? (
                    <Badge className={`text-[10px] h-5 ${isDark ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "bg-blue-100 text-blue-700 border-blue-200"}`}>
                      <PhoneOutgoing className="w-3 h-3 mr-1" />Giden
                    </Badge>
                  ) : (
                    <Badge className={`text-[10px] h-5 ${isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>
                      <PhoneIncoming className="w-3 h-3 mr-1" />Gelen
                    </Badge>
                  )}
                </TableCell>
                <TableCell className={`py-2 font-mono font-semibold ${isDark ? "text-rose-300" : "text-rose-700"}`}>{row.phone}</TableCell>
                <TableCell className={`py-2 font-mono text-[10px] hidden sm:table-cell ${isDark ? "text-slate-400" : "text-gray-600"}`}>{row.arayan_numara || "-"}</TableCell>
                <TableCell className={`py-2 font-mono text-[10px] hidden sm:table-cell ${isDark ? "text-slate-400" : "text-gray-600"}`}>{row.aranan_numara || "-"}</TableCell>
                <TableCell className="py-2">
                  <span className={`text-xs font-medium ${isDark ? "text-amber-400" : "text-amber-700"}`}>
                    {unfollowedMissedStatusLabel(row.durum_aciklama)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className={`px-3 py-1.5 text-[10px] border-t ${isDark ? "border-slate-700 text-slate-500" : "border-rose-100 text-gray-500"}`}>
        O gün yalnızca bir kez temas edilen ve cevapsız kalan numaralar.
      </p>
    </div>
  );
};

const ReportMetricBadge = ({ value, tone = "slate", isDark, title, onClick }) => {
  const tones = {
    slate: isDark ? "bg-slate-700/60 text-slate-200" : "bg-gray-100 text-gray-700",
    rose: isDark ? "bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/25" : "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    orange: isDark ? "bg-orange-500/15 text-orange-300 ring-1 ring-orange-500/25" : "bg-orange-50 text-orange-700 ring-1 ring-orange-200",
  };
  const clickable = typeof onClick === "function" && Number(value) > 0;
  return (
    <span
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onClick(); } } : undefined}
      className={`inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-md text-xs font-semibold tabular-nums ${tones[tone] || tones.slate}${clickable ? (isDark ? " cursor-pointer hover:bg-rose-500/25 hover:ring-2 hover:ring-rose-400/40" : " cursor-pointer hover:bg-rose-100 hover:ring-2 hover:ring-rose-300/60") : ""}`}
      title={clickable ? `${title || ""} — detay için tıklayın`.trim() : title}
    >
      {value ?? 0}
    </span>
  );
};

const RecordingTableActions = ({ record, isDark, labelClass, onPlay, variant = "icons" }) => {
  const [downloading, setDownloading] = useState(false);
  const pendingUpload = isRecordWithinRecordingPendingWindow(record) && shouldExpectRecording(record) && !record?.recording;
  const pendingHint = isRecordWithinRecordingPendingWindow(record) && shouldExpectRecording(record) && !!record?.recording;

  if (pendingUpload) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${isDark ? "bg-amber-500/10 text-amber-300 border border-amber-500/20" : "bg-amber-50 text-amber-700 border border-amber-200"}`}
        title="Ses dosyası sisteme aktarılıyor (son 6 dk)"
      >
        <Loader2 className="w-3 h-3 animate-spin shrink-0" />
        Aktarılıyor
      </span>
    );
  }

  if (!record?.recording) {
    return <span className={`text-xs ${labelClass}`}>-</span>;
  }

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadRecordingViaPanel({
        apiBase: API,
        cdrId: record.id,
      });
      toast.success("Ses kaydı indirildi");
    } catch (err) {
      const msg = err.message || err.response?.data?.detail || "Ses kaydı indirilemedi";
      if (isRecordingNotFoundMessage(msg)) toast.warning(msg);
      else toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  if (variant === "compact") {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onPlay(record.recording, record)}
        className={`p-1 h-auto ${isDark ? "text-cyan-400" : "text-blue-600"} ${pendingHint ? (isDark ? "ring-1 ring-amber-500/40" : "ring-1 ring-amber-300") : ""}`}
        title={pendingHint ? "Dinle (dosya henüz aktarılıyor olabilir)" : "Dinle"}
      >
        <Volume2 className="w-4 h-4 mr-1" />
        <span className="text-xs">Dinle</span>
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onPlay(record.recording, record)}
        className={`p-1.5 h-auto rounded-lg ${isDark ? "text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"} ${pendingHint ? (isDark ? "ring-1 ring-amber-500/40" : "ring-1 ring-amber-300") : ""}`}
        title={pendingHint ? "Dinle (dosya henüz aktarılıyor olabilir)" : "Dinle"}
      >
        <Volume2 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        disabled={downloading}
        className={`p-1.5 h-auto rounded-lg ${isDark ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"}`}
        title="İndir"
      >
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </Button>
    </div>
  );
};


// Transcript Audio Player Component
// Agent Ata / Değiştir Modal
const ChangeAgentModal = ({ isOpen, onClose, device, agents, agentDevicesByAgent = {}, currentAgentId, isDark, onSave, onCreateNew }) => {
  const [selectedAgentId, setSelectedAgentId] = useState(currentAgentId || "");
  const [saving, setSaving] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const isAssignMode = !currentAgentId;

  useEffect(() => {
    setSelectedAgentId(currentAgentId || "");
  }, [currentAgentId, isOpen]);

  useEffect(() => {
    if (isOpen) setAgentSearchTerm("");
  }, [isOpen, device?.id]);

  const selectedAgentDevices = useMemo(() => {
    if (!selectedAgentId) return [];
    return (agentDevicesByAgent[selectedAgentId] || []).filter((d) => String(d.id) !== String(device?.id));
  }, [selectedAgentId, agentDevicesByAgent, device?.id]);

  const handleSave = async () => {
    if (!selectedAgentId) {
      toast.error("Lütfen bir agent seçin");
      return;
    }
    if (selectedAgentDevices.length > 0) {
      const deviceList = selectedAgentDevices.map((d) => d.device_name || d.device_id || d.id).join(", ");
      const ok = window.confirm(
        `Seçilen agent'a zaten ${selectedAgentDevices.length} cihaz atanmış: ${deviceList}.\n\nBu cihazı da aynı agent'a eklemek istiyor musunuz?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await onSave(device.id, parseInt(selectedAgentId, 10));
      toast.success(isAssignMode ? "Agent atandı" : "Agent değiştirildi");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(isAssignMode ? "Agent atanamadı" : "Agent değiştirilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Bu cihazdan agent atamasını kaldırmak istediğinize emin misiniz?")) return;
    setSaving(true);
    try {
      await onSave(device.id, null);
      toast.success("Agent ataması kaldırıldı");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white focus:border-blue-500" : "bg-white border-gray-300 focus:border-blue-500";
  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-4"
    : "rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-4";
  const sectionTitleClass = isDark ? "text-sm font-semibold text-slate-200 flex items-center gap-2" : "text-sm font-semibold text-gray-800 flex items-center gap-2";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";

  const filteredAgents = useMemo(() => {
    const list = asAgentList(agents);
    if (!agentSearchTerm.trim()) return list;
    const term = agentSearchTerm.toLowerCase().trim();
    return list.filter((a) =>
      a.ad_soyad?.toLowerCase().includes(term) ||
      a.kullanici_adi?.toLowerCase().includes(term) ||
      a.gsm?.includes(term)
    );
  }, [agents, agentSearchTerm]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg p-0 gap-0 flex flex-col max-h-[90vh] overflow-hidden ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
        <div className={`shrink-0 px-6 pt-6 pb-4 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-blue-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-blue-50 to-white"}`}>
          <DialogHeader className="space-y-0">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-blue-500/20 ring-1 ring-blue-500/30" : "bg-blue-100 ring-1 ring-blue-200"}`}>
                {isAssignMode ? <UserPlus className={`w-5 h-5 ${isDark ? "text-blue-400" : "text-blue-600"}`} /> : <RefreshCw className={`w-5 h-5 ${isDark ? "text-blue-400" : "text-blue-600"}`} />}
              </div>
              <div>
                <DialogTitle className={`text-lg ${isDark ? "text-white" : "text-gray-900"}`}>
                  {isAssignMode ? "Agent Ata" : "Agent Değiştir"}
                </DialogTitle>
                <p className={`text-sm mt-0.5 ${mutedClass}`}>
                  {isAssignMode ? "Cihaza mevcut bir agent atayın" : "Cihazın agent atamasını güncelleyin"}
                </p>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-6 py-5 space-y-5">
          <div className={cardClass}>
            <p className={sectionTitleClass}><Smartphone className="w-4 h-4 text-cyan-400" /> Cihaz Bilgisi</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className={mutedClass}>Ad</span>
                <p className={`font-medium mt-0.5 ${isDark ? "text-slate-200" : "text-gray-900"}`}>{device?.device_name || "-"}</p>
              </div>
              <div>
                <span className={mutedClass}>GSM</span>
                <p className={`font-mono mt-0.5 ${isDark ? "text-amber-400" : "text-amber-600"}`}>{device?.gsm && device.gsm !== "-" ? device.gsm : "-"}</p>
              </div>
              <div className="sm:col-span-2">
                <span className={mutedClass}>Device ID</span>
                <p className={`font-mono text-xs mt-0.5 break-all ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{device?.device_id || "-"}</p>
              </div>
            </div>
          </div>

          <div className={cardClass}>
            <p className={sectionTitleClass}><Users className="w-4 h-4 text-blue-400" /> {isAssignMode ? "Agent Seç" : "Yeni Agent Seç"}</p>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`} />
              <Input
                placeholder="Agent ara (ad, kullanıcı adı, GSM)..."
                value={agentSearchTerm}
                onChange={(e) => setAgentSearchTerm(e.target.value)}
                className={`pl-10 ${inputClass}`}
              />
            </div>
            <Select value={selectedAgentId ? String(selectedAgentId) : undefined} onValueChange={setSelectedAgentId}>
              <SelectTrigger className={inputClass}><SelectValue placeholder="— Mevcut agent'lardan seçin —" /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                {filteredAgents.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.ad_soyad} ({a.kullanici_adi}{a.gsm ? ` · ${a.gsm}` : ""}{a.company_name ? ` · ${a.company_name}` : ""})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {asAgentList(agents).length === 0 ? (
              <p className={`text-xs ${mutedClass}`}>Atanabilir agent bulunamadı.</p>
            ) : filteredAgents.length === 0 ? (
              <p className={`text-xs ${mutedClass}`}>Arama sonucu bulunamadı.</p>
            ) : null}
          </div>

          {selectedAgentId && selectedAgentDevices.length > 0 && (
            <div className={`rounded-xl border p-4 space-y-2 ${isDark ? "bg-amber-500/10 border-amber-500/30" : "bg-amber-50 border-amber-200"}`}>
              <p className={`text-sm font-medium flex items-center gap-2 ${isDark ? "text-amber-300" : "text-amber-800"}`}>
                <Zap className="w-4 h-4" />
                Bu agent'a atanmış {selectedAgentDevices.length} cihaz var
              </p>
              <ul className={`text-xs space-y-1.5 ${isDark ? "text-amber-200/90" : "text-amber-900"}`}>
                {selectedAgentDevices.map((d) => (
                  <li key={d.id} className={`rounded-lg px-2 py-1.5 font-mono ${isDark ? "bg-amber-500/10" : "bg-white/60"}`}>
                    {d.device_name || "Cihaz"} · {d.device_id || d.id}
                    {d.gsm && d.gsm !== "-" ? ` · ${d.gsm}` : ""}
                  </li>
                ))}
              </ul>
              <p className={`text-xs ${isDark ? "text-amber-200/70" : "text-amber-700"}`}>
                Atama yapılırsa bu cihaz da aynı agent'a eklenecek.
              </p>
            </div>
          )}

          {isAssignMode && onCreateNew && (
            <Button
              type="button"
              variant="outline"
              onClick={() => { onClose(); onCreateNew(device); }}
              className={`w-full h-11 ${isDark ? "border-emerald-700/60 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"}`}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Bu cihaz için yeni agent oluştur
            </Button>
          )}
        </div>

        <DialogFooter className={`shrink-0 px-6 py-4 border-t gap-2 sm:justify-between ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/50"}`}>
          <div className="flex gap-2">
            {!isAssignMode && (
              <Button variant="outline" onClick={handleRemove} disabled={saving} className={`${isDark ? "border-red-800/60 text-red-400 hover:bg-red-900/30" : "border-red-200 text-red-600 hover:bg-red-50"}`}>
                <Trash2 className="w-4 h-4 mr-1" /> Kaldır
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving} className={isDark ? "text-slate-300 hover:text-white" : ""}>İptal</Button>
            <Button onClick={handleSave} disabled={saving || !selectedAgentId} className="bg-blue-600 hover:bg-blue-700 text-white min-w-[90px]">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isAssignMode ? "Atanıyor" : "Kaydediliyor"}</> : (isAssignMode ? "Ata" : "Kaydet")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AgentDevicesModal = ({ isOpen, onClose, agent, devices, isDark, onViewCallLogs }) => {
  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 p-4"
    : "rounded-xl border border-gray-200 bg-gray-50/80 p-4";
  const labelClass = isDark ? "text-slate-400" : "text-gray-500";
  const textClass = isDark ? "text-slate-100" : "text-gray-900";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";

  const formatSyncDate = (value, withTime = false) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString("tr-TR", withTime
        ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
        : { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return value;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={`max-w-2xl p-0 gap-0 flex flex-col max-h-[85vh] overflow-hidden ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
        <div className={`shrink-0 px-6 pt-6 pb-4 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-pink-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-pink-50 to-white"}`}>
          <DialogHeader className="space-y-1">
            <DialogTitle className={`flex items-center gap-2 ${textClass}`}>
              <Smartphone className={`w-5 h-5 ${isDark ? "text-pink-400" : "text-pink-600"}`} />
              {agent?.ad_soyad || "Agent"} — Cihazlar
            </DialogTitle>
            <p className={`text-sm ${labelClass}`}>
              {devices.length} cihaz atanmış
              {agent?.kullanici_adi ? ` · @${agent.kullanici_adi}` : ""}
            </p>
          </DialogHeader>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-3">
          {devices.length === 0 ? (
            <div className={`text-center py-12 ${mutedClass}`}>
              <Smartphone className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Bu agent'a atanmış cihaz yok</p>
            </div>
          ) : devices.map((device) => (
            <div key={device.id} className={cardClass}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 space-y-2">
                  <div>
                    <p className={`text-[10px] uppercase tracking-wider font-semibold ${labelClass}`}>Device ID</p>
                    <p className={`font-mono text-sm ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{device.device_id || "-"}</p>
                  </div>
                  <DeviceInfoStack device={device} isDark={isDark} />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewCallLogs(device)}
                  className={`shrink-0 ${isDark ? "border-slate-700 text-cyan-400 hover:bg-slate-800" : "text-cyan-600 hover:bg-cyan-50"}`}
                >
                  <Phone className="w-4 h-4 mr-1.5" />
                  Çağrı Kayıtları
                </Button>
              </div>

              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mt-4 pt-3 border-t text-xs ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                <div>
                  <span className={labelClass}>GSM: </span>
                  <span className={`font-mono ${isDark ? "text-amber-400" : "text-amber-600"}`}>{device.gsm && device.gsm !== "-" ? device.gsm : "-"}</span>
                </div>
                <div>
                  <span className={labelClass}>Firma: </span>
                  <span className={isDark ? "text-indigo-400" : "text-indigo-600"}>{device.company_name || "-"}</span>
                </div>
                <div>
                  <span className={labelClass}>İlk Sync: </span>
                  <span>{formatSyncDate(device.first_sync)}</span>
                </div>
                <div>
                  <span className={labelClass}>Son Sync: </span>
                  <span className={isDark ? "text-teal-400" : "text-teal-600"}>{formatSyncDate(device.last_sync, true)}</span>
                </div>
                <div>
                  <span className={labelClass}>DB ID: </span>
                  <span className="font-mono">{device.id}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className={`shrink-0 px-6 py-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
          <Button variant="ghost" onClick={onClose}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Device Call Logs Modal - Cihazın çağrı kayıtlarını göster
const CALL_LOGS_PAGE_SIZES = [15, 50, 100, 200];

const DeviceCallLogsModal = ({ isOpen, onClose, device, callLogs, loading, page, totalPages, total, pageSize, onPageChange, onPageSizeChange, isDark, summary, startDate, endDate, startTime, endTime, onStartDateChange, onEndDateChange, onStartTimeChange, onEndTimeChange, onFilter, onClearSearch, dateRange, onDateRangeChange, onPlayRecording, searchTerm, onSearchChange, canSeeRecordings = true, excludeMissed = false, onExcludeMissedChange, callTypeFilter = "all", onCallTypeFilterChange, activeTab = "calls", onTabChange, apiBase, failedRecordsCount = 0, inputClass: modalInputClass }) => {
  const [jumpPage, setJumpPage] = useState("");
  const [recordingsCount, setRecordingsCount] = useState(0);
  const [recordingsFullCount, setRecordingsFullCount] = useState(0);
  const modalExpand = usePanelModalExpand();

  useEffect(() => {
    if (isOpen) setJumpPage(String(page));
  }, [isOpen, page]);

  useEffect(() => {
    if (isOpen) modalExpand.resetExpanded();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setRecordingsCount(0);
      setRecordingsFullCount(0);
    }
  }, [isOpen]);

  const handlePageJump = () => {
    const target = parseInt(jumpPage, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(`Geçerli bir sayfa girin (1-${totalPages})`);
      setJumpPage(String(page));
      return;
    }
    if (target !== page) onPageChange(target);
  };
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white focus:border-cyan-500" : "bg-white border-gray-300 focus:border-cyan-500";
  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-3"
    : "rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-3";
  const sectionTitleClass = isDark ? "text-sm font-semibold text-slate-200 flex items-center gap-2" : "text-sm font-semibold text-gray-800 flex items-center gap-2";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";
  
  const getCallTypeInfo = (type) => {
    switch(parseInt(type)) {
      case 1: return { label: "Gelen", icon: <PhoneIncoming className="w-3 h-3" />, color: isDark ? "text-emerald-400" : "text-emerald-600" };
      case 2: return { label: "Giden", icon: <PhoneOutgoing className="w-3 h-3" />, color: isDark ? "text-blue-400" : "text-blue-600" };
      case 3: return { label: "Cevapsız", icon: <PhoneMissed className="w-3 h-3" />, color: isDark ? "text-red-400" : "text-red-600" };
      case 5: return { label: "Reddedilen", icon: <PhoneOff className="w-3 h-3" />, color: isDark ? "text-orange-400" : "text-orange-600" };
      default: return { label: "Bilinmiyor", icon: <Phone className="w-3 h-3" />, color: isDark ? "text-slate-400" : "text-gray-500" };
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0sn";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins}dk ${secs}sn`;
    return `${secs}sn`;
  };

  const formatTotalDuration = (seconds) => formatStatDuration(seconds);

  const summaryCard = (value, label, secondary, colorClass = "") => (
    <div className={`p-2 rounded-lg text-center ${colorClass || (isDark ? "bg-slate-800" : "bg-gray-50")}`}>
      <div className="text-lg sm:text-xl font-bold leading-tight">{value}</div>
      <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{label}</div>
      {secondary && <div className={`text-[10px] mt-0.5 ${isDark ? "text-slate-600" : "text-gray-400/80"}`}>{secondary}</div>}
    </div>
  );

  const dateRangeButtons = [
    { value: "all", label: "Tümü" },
    { value: "today", label: "Bugün" },
    { value: "yesterday", label: "Dün" },
    { value: "7days", label: "7 Gün" },
    { value: "custom", label: "Özel" },
  ];

  const callTypeButtons = [
    { value: "all", label: "Tümü", icon: Phone },
    { value: "1", label: "Gelen", icon: PhoneIncoming },
    { value: "2", label: "Giden", icon: PhoneOutgoing },
  ];

  const modalTabs = [
    { id: "calls", label: "Çağrı Kayıtları", count: total, icon: Phone },
    { id: "failed", label: "Eksik Ses", count: failedRecordsCount, icon: VolumeX },
    { id: "recordings", label: "Recordings", count: recordingsCount, icon: FileAudio },
    { id: "recordings_full", label: "Recordings Full", count: recordingsFullCount, icon: Database },
  ];

  const getModalTabIcon = (tab) => {
    const Icon = tab.icon || Phone;
    return <Icon className="w-3.5 h-3.5 shrink-0" />;
  };

  return (
    <PanelModal open={isOpen} align="start" onClose={onClose}>
      <PanelModalContent
        className={`panel-modal-content--call-logs w-full flex flex-col rounded-xl shadow-2xl overflow-hidden ${modalExpand.expanded ? "panel-modal-content--expanded" : ""} ${isDark ? "bg-slate-900 border border-slate-800 text-white" : "bg-white border border-gray-200"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`shrink-0 px-4 sm:px-6 pt-5 sm:pt-6 pb-4 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-cyan-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-cyan-50 to-white"}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-cyan-500/20 ring-1 ring-cyan-500/30" : "bg-cyan-100 ring-1 ring-cyan-200"}`}>
                <Smartphone className={`w-5 h-5 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
              </div>
              <div className="min-w-0">
                <h2 className={`text-lg font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}>
                  Çağrı Kayıtları
                </h2>
                <p className={`text-sm mt-0.5 truncate ${mutedClass}`}>
                  {device?.device_name || device?.device_id || "Cihaz"} · {(total || 0).toLocaleString("tr-TR")} kayıt
                </p>
              </div>
            </div>
            <PanelModalHeaderActions
              expanded={modalExpand.expanded}
              onToggle={modalExpand.toggleExpanded}
              onClose={onClose}
              isDark={isDark}
            />
          </div>
        </div>

        <div className={`shrink-0 px-4 sm:px-6 pb-0 border-b ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <div className={`inline-flex flex-wrap items-center gap-1 rounded-lg p-0.5 ${isDark ? "bg-slate-800/90 ring-1 ring-slate-700/60" : "bg-gray-100 ring-1 ring-gray-200/80"}`}>
            {modalTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange?.(tab.id)}
                className={`h-8 px-3 sm:px-4 text-xs font-medium rounded-md transition-all whitespace-nowrap inline-flex items-center gap-1.5 ${
                  activeTab === tab.id
                    ? (tab.id === "failed"
                      ? (isDark ? "bg-red-600 text-white shadow-sm" : "bg-red-600 text-white shadow-sm")
                      : (isDark ? "bg-cyan-600 text-white shadow-sm" : "bg-white text-cyan-700 shadow-sm ring-1 ring-gray-200/80"))
                    : (isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-700/50" : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/60")
                }`}
              >
                {getModalTabIcon(tab)}
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-[10px] font-mono px-1.5 py-0 rounded-full ${activeTab === tab.id ? "bg-black/15" : (isDark ? "bg-slate-700/80" : "bg-gray-200")}`}>
                    {tab.count.toLocaleString("tr-TR")}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto min-h-0 overscroll-y-contain px-6 py-5 space-y-4">
          {activeTab === "failed" ? (
            <DeviceFailedRecordsTable
              API={apiBase}
              deviceId={device?.id}
              isDark={isDark}
              isActive={isOpen && activeTab === "failed"}
              inputClass={modalInputClass || inputClass}
              labelClass={mutedClass}
            />
          ) : activeTab === "recordings" ? (
            <DeviceRecordingsTable
              API={apiBase}
              device={device}
              deviceId={device?.id}
              mode="recordings"
              isDark={isDark}
              isActive={isOpen && activeTab === "recordings"}
              canSeeRecordings={canSeeRecordings}
              onPlayRecording={onPlayRecording}
              onTotalChange={setRecordingsCount}
              inputClass={modalInputClass || inputClass}
              labelClass={mutedClass}
            />
          ) : activeTab === "recordings_full" ? (
            <DeviceRecordingsTable
              API={apiBase}
              device={device}
              deviceId={device?.id}
              mode="full"
              isDark={isDark}
              isActive={isOpen && activeTab === "recordings_full"}
              canSeeRecordings={canSeeRecordings}
              onPlayRecording={onPlayRecording}
              onTotalChange={setRecordingsFullCount}
              inputClass={modalInputClass || inputClass}
              labelClass={mutedClass}
            />
          ) : (
          <>
          <div className={`rounded-xl border px-3 py-2 sm:px-4 sm:py-2.5 overflow-x-auto ${isDark ? "border-slate-800/80 bg-slate-950/50" : "border-gray-200 bg-gray-50/60"}`}>
            <div className="flex items-center gap-2 sm:gap-3 min-w-max text-xs sm:text-sm">
              <div className={`inline-flex shrink-0 items-center justify-center w-8 h-8 rounded-lg ${isDark ? "bg-cyan-500/15 ring-1 ring-cyan-500/25" : "bg-cyan-50 ring-1 ring-cyan-100"}`}>
                <Smartphone className={`w-4 h-4 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
              </div>
              {[
                { label: "Cihaz", value: device?.device_name || "-", valueClass: isDark ? "text-slate-100" : "text-gray-900" },
                { label: "Model", value: device?.device_model || "-", valueClass: isDark ? "text-pink-400" : "text-pink-600" },
                { label: "GSM", value: device?.gsm || "-", valueClass: `font-mono ${isDark ? "text-amber-400" : "text-amber-600"}` },
                { label: "Device ID", value: device?.device_id || "-", valueClass: `font-mono text-[11px] sm:text-xs ${isDark ? "text-cyan-400" : "text-cyan-600"}` },
              ].map((item, index) => (
                <React.Fragment key={item.label}>
                  {index > 0 && (
                    <div className={`hidden sm:block w-px h-6 shrink-0 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />
                  )}
                  <div className="inline-flex items-center gap-1.5 shrink-0 max-w-[9rem] sm:max-w-[11rem] lg:max-w-none">
                    <span className={`text-[10px] sm:text-xs uppercase tracking-wide font-medium ${mutedClass}`}>{item.label}</span>
                    <span className={`font-semibold truncate ${item.valueClass}`} title={item.value}>{item.value}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className={`rounded-xl border p-2 sm:p-2.5 overflow-x-auto ${isDark ? "border-slate-800/80 bg-slate-950/50" : "border-gray-200 bg-gray-50/60"}`}>
            <div className="flex items-center gap-2 lg:gap-2.5 min-w-max sm:min-w-0 sm:flex-wrap">
              <div className={`inline-flex shrink-0 items-center rounded-lg p-0.5 ${isDark ? "bg-slate-800/90 ring-1 ring-slate-700/60" : "bg-gray-100 ring-1 ring-gray-200/80"}`}>
                {dateRangeButtons.map((btn) => (
                  <button
                    key={btn.value}
                    type="button"
                    onClick={() => onDateRangeChange(btn.value)}
                    className={`h-8 px-2.5 sm:px-3 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                      dateRange === btn.value
                        ? (isDark ? "bg-cyan-600 text-white shadow-sm" : "bg-white text-cyan-700 shadow-sm ring-1 ring-gray-200/80")
                        : (isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-700/50" : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/60")
                    }`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>

              {dateRange === "custom" && (
                <div className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 ${isDark ? "bg-slate-800/60 ring-1 ring-slate-700/50" : "bg-white ring-1 ring-gray-200"}`}>
                  <div className="relative">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => onStartDateChange(e.target.value)}
                      className={`w-[7.5rem] h-8 text-xs pl-7 rounded-md border-0 shadow-none ${isDark ? "bg-slate-900/80 text-white" : "bg-gray-50"} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                    />
                    <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                  </div>
                  <div className="relative">
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => onStartTimeChange(e.target.value)}
                      className={`w-[5.5rem] h-8 text-xs pl-7 rounded-md border-0 shadow-none ${isDark ? "bg-slate-900/80 text-white" : "bg-gray-50"} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                    />
                    <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                  </div>
                  <span className={`text-xs px-0.5 ${mutedClass}`}>—</span>
                  <div className="relative">
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => onEndDateChange(e.target.value)}
                      className={`w-[7.5rem] h-8 text-xs pl-7 rounded-md border-0 shadow-none ${isDark ? "bg-slate-900/80 text-white" : "bg-gray-50"} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                    />
                    <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                  </div>
                  <div className="relative">
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => onEndTimeChange(e.target.value)}
                      className={`w-[5.5rem] h-8 text-xs pl-7 rounded-md border-0 shadow-none ${isDark ? "bg-slate-900/80 text-white" : "bg-gray-50"} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                    />
                    <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                  </div>
                </div>
              )}

              <div className={`hidden md:block w-px h-7 shrink-0 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />

              <div className="flex items-center gap-1.5 ml-auto shrink-0 min-w-[240px] justify-end">
                <div className={`relative flex-1 min-w-[160px] max-w-[240px] rounded-lg ${isDark ? "bg-slate-800/80 ring-1 ring-slate-700/60" : "bg-white ring-1 ring-gray-200"}`}>
                  <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${mutedClass}`} />
                  <Input
                    placeholder="Numara veya kişi ara..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") onFilter(); }}
                    className={`h-8 text-xs pl-8 font-mono border-0 shadow-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 ${isDark ? "text-white placeholder:text-slate-500" : ""}`}
                  />
                </div>
                <Button
                  size="sm"
                  onClick={onFilter}
                  disabled={loading}
                  className={`h-8 px-3 shrink-0 rounded-lg ${isDark ? "bg-cyan-600 hover:bg-cyan-500" : "bg-cyan-600 hover:bg-cyan-700"} text-white shadow-sm`}
                >
                  <Search className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Ara</span>
                </Button>
                {searchTerm && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onClearSearch}
                    className={`h-8 w-8 p-0 shrink-0 rounded-lg ${isDark ? "text-slate-400 hover:text-white hover:bg-slate-800" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"}`}
                    title="Aramayı temizle"
                  >
                    <FilterX className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {summaryCard(
              <span className={isDark ? "text-white" : "text-gray-900"}>{(summary?.total_calls || 0).toLocaleString('tr-TR')}</span>,
              "Toplam",
              formatUniqueStat(summary?.unique_total)
            )}
            {summaryCard(
              <span className={`inline-flex items-center justify-center gap-1 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}><PhoneIncoming className="w-4 h-4" />{(summary?.incoming || 0).toLocaleString('tr-TR')}</span>,
              "Gelen",
              formatUniqueStat(summary?.unique_incoming),
              isDark ? "bg-emerald-900/30" : "bg-emerald-50"
            )}
            {summaryCard(
              <span className={`inline-flex items-center justify-center gap-1 ${isDark ? "text-blue-400" : "text-blue-600"}`}><PhoneOutgoing className="w-4 h-4" />{(summary?.outgoing || 0).toLocaleString('tr-TR')}</span>,
              "Giden",
              formatUniqueStat(summary?.unique_outgoing),
              isDark ? "bg-blue-900/30" : "bg-blue-50"
            )}
            {summaryCard(
              <span className={`inline-flex items-center justify-center gap-1 ${isDark ? "text-red-400" : "text-red-600"}`}><PhoneMissed className="w-4 h-4" />{(summary?.missed || 0).toLocaleString('tr-TR')}</span>,
              "Cevapsız",
              formatUniqueStat(summary?.unique_missed),
              isDark ? "bg-red-900/30" : "bg-red-50"
            )}
            {summaryCard(
              <span className={`inline-flex items-center justify-center gap-1 ${isDark ? "text-teal-400" : "text-teal-600"}`}><Clock className="w-4 h-4" />{formatTotalDuration(summary?.total_duration || 0)}</span>,
              "Toplam Süre",
              null,
              isDark ? "bg-teal-900/30" : "bg-teal-50"
            )}
          </div>

          {/* Call Logs Table */}
          <div className={`${cardClass} !p-0 overflow-hidden`}>
            <div className="px-4 pt-4 pb-2 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className={sectionTitleClass}><Phone className="w-4 h-4 text-cyan-400" /> Kayıt Listesi</p>
                <label className={`inline-flex items-center gap-2 cursor-pointer select-none text-xs sm:text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                  <Checkbox
                    checked={excludeMissed}
                    onCheckedChange={(checked) => onExcludeMissedChange?.(checked === true)}
                    disabled={callTypeFilter === "1" || callTypeFilter === "2"}
                    className={isDark ? "border-slate-600 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600" : "data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"}
                  />
                  <span className="whitespace-nowrap">Cevapsızları gizle</span>
                </label>
              </div>
              <div className={`inline-flex items-center rounded-lg p-0.5 ${isDark ? "bg-slate-800/90 ring-1 ring-slate-700/60" : "bg-gray-100 ring-1 ring-gray-200/80"}`}>
                {callTypeButtons.map((btn) => {
                  const Icon = btn.icon;
                  return (
                    <button
                      key={btn.value}
                      type="button"
                      onClick={() => onCallTypeFilterChange?.(btn.value)}
                      className={`h-8 px-2.5 sm:px-3 text-xs font-medium rounded-md transition-all whitespace-nowrap inline-flex items-center gap-1.5 ${
                        callTypeFilter === btn.value
                          ? (isDark ? "bg-cyan-600 text-white shadow-sm" : "bg-white text-cyan-700 shadow-sm ring-1 ring-gray-200/80")
                          : (isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-700/50" : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/60")
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw className={`w-6 h-6 animate-spin ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                <span className="ml-2">Yükleniyor...</span>
              </div>
            ) : callLogs.length === 0 ? (
              <div className={`text-center py-10 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                <Phone className="w-12 h-12 mx-auto mb-2 opacity-30" />
                Bu cihaza ait çağrı kaydı bulunamadı
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Tip</TableHead>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Numara</TableHead>
                      <TableHead className={`hidden sm:table-cell ${isDark ? "text-slate-400" : ""}`}>Kişi</TableHead>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                      <TableHead className={isDark ? "text-slate-400" : ""}>Süre</TableHead>
                      {canSeeRecordings && (
                        <TableHead className={`w-12 sm:w-auto ${isDark ? "text-slate-400" : ""}`}>Kayıt</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {callLogs.map((log) => {
                      const typeInfo = getCallTypeInfo(log.call_type);
                      return (
                        <TableRow key={log.id} className={isDark ? "border-slate-800/50" : ""}>
                          <TableCell>
                            <span className={`flex items-center gap-1 ${typeInfo.color}`}>
                              {typeInfo.icon}
                              <span className="text-xs hidden sm:inline">{typeInfo.label}</span>
                            </span>
                          </TableCell>
                          <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                            {log.phone_number || '-'}
                          </TableCell>
                          <TableCell className={`hidden sm:table-cell ${isDark ? "text-slate-300" : ""}`}>
                            {log.contact_name || '-'}
                          </TableCell>
                          <TableCell className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            {log.call_date ? new Date(log.call_date).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </TableCell>
                          <TableCell className={`text-xs ${isDark ? "text-teal-400" : "text-teal-600"}`}>
                            {formatDuration(log.duration)}
                          </TableCell>
                          {canSeeRecordings && (
                            <TableCell className="w-12 sm:w-auto">
                              {hasCallLogRecording(log) ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onPlayRecording?.(log.recording_path, mapCallLogToAudioRecord(log, device))}
                                  className={`p-1 h-auto min-w-0 ${isDark ? "text-cyan-400 hover:text-cyan-300 hover:bg-slate-800" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`}
                                  title="Ses kaydını dinle"
                                  aria-label="Ses kaydını dinle"
                                >
                                  <Volume2 className="w-4 h-4 shrink-0" />
                                  <span className="text-xs hidden sm:inline ml-1">Dinle</span>
                                </Button>
                              ) : (
                                <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>-</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          </>
          )}
        </div>

        <div className={`shrink-0 px-4 sm:px-6 py-3 border-t ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/50"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
              {activeTab === "calls" && total > 0 && (
                <span className={`text-xs sm:text-sm whitespace-nowrap ${mutedClass}`}>
                  {(total || 0).toLocaleString("tr-TR")} kayıt
                </span>
              )}

              {activeTab === "calls" && totalPages > 1 && (
                <>
                  <div className={`hidden sm:block w-px h-6 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />
                  <div className="inline-flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1 || loading}
                      onClick={() => onPageChange(page - 1)}
                      className={`h-8 w-8 p-0 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className={`text-xs sm:text-sm px-1 tabular-nums whitespace-nowrap ${mutedClass}`}>
                      {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages || loading}
                      onClick={() => onPageChange(page + 1)}
                      className={`h-8 w-8 p-0 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className={`hidden md:block w-px h-6 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />

                  <div className="inline-flex items-center gap-1.5">
                    <span className={`text-xs whitespace-nowrap ${mutedClass}`}>Sayfaya git</span>
                    <Input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={jumpPage}
                      onChange={(e) => setJumpPage(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handlePageJump(); }}
                      className={`h-8 w-14 px-2 text-xs text-center tabular-nums ${isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-gray-300"}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={loading}
                      onClick={handlePageJump}
                      className={`h-8 px-2.5 text-xs ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
                    >
                      Git
                    </Button>
                  </div>
                </>
              )}

              {activeTab === "calls" && (
              <>
              <div className={`${totalPages > 1 ? "hidden md:block" : "hidden sm:block"} w-px h-6 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />

              <div className="inline-flex items-center gap-1.5">
                <span className={`text-xs whitespace-nowrap ${mutedClass}`}>Göster</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
                  disabled={loading}
                >
                  <SelectTrigger className={`h-8 w-[4.5rem] text-xs ${isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-gray-300"}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-700 text-white z-[1200]" : "z-[1200]"}>
                    {CALL_LOGS_PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              </>
              )}
            </div>

            <Button
              variant="ghost"
              onClick={onClose}
              className={`h-9 px-4 shrink-0 ${isDark ? "text-slate-300 hover:text-white" : ""}`}
            >
              Kapat
            </Button>
          </div>
        </div>
      </PanelModalContent>
    </PanelModal>
  );
};

// Report scope filters (team / leader)
const buildAgentScopeOptions = (agentList, teamsList = []) => {
  const teamMap = new Map();
  const leaderMap = new Map();

  (agentList || []).forEach((a) => {
    if (a.team_id && a.team_name) {
      teamMap.set(a.team_id, {
        id: a.team_id,
        name: a.team_name,
        leaderId: a.team_leader_user_id || null,
        leaderName: a.team_leader_name || null,
      });
    }
    if (a.team_leader_user_id && a.team_leader_name) {
      leaderMap.set(a.team_leader_user_id, a.team_leader_name);
    }
  });

  (teamsList || []).forEach((t) => {
    if (t.id && t.ad) {
      teamMap.set(t.id, {
        id: t.id,
        name: t.ad,
        leaderId: t.lider_user_id || null,
        leaderName: t.lider_name || null,
      });
    }
    if (t.lider_user_id && t.lider_name) {
      leaderMap.set(t.lider_user_id, t.lider_name);
    }
  });

  return {
    teams: Array.from(teamMap.values()).sort((a, b) => a.name.localeCompare(b.name, "tr")),
    leaders: Array.from(leaderMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "tr")),
  };
};

const normalizeScopeFilter = (filter) => {
  if (filter === "all" || filter == null) return [];
  return Array.isArray(filter) ? filter.map(String) : [String(filter)];
};

const filterAgentsByTeamLeader = (list, teamFilter, leaderFilter) => {
  let result = list || [];
  const teamIds = normalizeScopeFilter(teamFilter);
  const leaderIds = normalizeScopeFilter(leaderFilter);
  if (teamIds.length > 0) {
    result = result.filter((a) => teamIds.includes(String(a.team_id)));
  }
  if (leaderIds.length > 0) {
    result = result.filter((a) => leaderIds.includes(String(a.team_leader_user_id)));
  }
  return result;
};

const ReportScopeMultiSelect = ({
  label,
  options,
  values,
  onChange,
  placeholder,
  isDark,
  inputClass,
  labelClass,
  getOptionLabel,
}) => {
  const [open, setOpen] = useState(false);
  const selectedIds = normalizeScopeFilter(values);
  const selectedSet = new Set(selectedIds);

  const toggle = (id) => {
    const sid = String(id);
    if (selectedSet.has(sid)) {
      onChange(selectedIds.filter((v) => v !== sid));
    } else {
      onChange([...selectedIds, sid]);
    }
  };

  const displayLabel = (() => {
    if (selectedIds.length === 0) return placeholder;
    if (selectedIds.length === 1) {
      const opt = options.find((o) => String(o.id) === selectedIds[0]);
      return opt ? getOptionLabel(opt) : placeholder;
    }
    return `${selectedIds.length} seçili`;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={`h-8 w-full justify-between text-xs font-normal px-2.5 ${inputClass}`}
        >
          <span className="truncate text-left">{displayLabel}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={`w-[var(--radix-popover-trigger-width)] p-2 ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"}`}
      >
        <div className="flex items-center justify-between mb-2 px-1">
          <span className={`text-[10px] font-medium ${labelClass}`}>{label}</span>
          {selectedIds.length > 0 && (
            <button
              type="button"
              className="text-[10px] text-cyan-500 hover:text-cyan-400"
              onClick={() => onChange([])}
            >
              Temizle
            </button>
          )}
        </div>
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {options.length === 0 ? (
            <p className={`text-xs px-2 py-1.5 ${labelClass}`}>Seçenek yok</p>
          ) : (
            options.map((opt) => (
              <label
                key={opt.id}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${isDark ? "hover:bg-slate-800/80" : "hover:bg-gray-50"}`}
              >
                <Checkbox
                  checked={selectedSet.has(String(opt.id))}
                  onCheckedChange={() => toggle(opt.id)}
                />
                <span className="truncate">{getOptionLabel(opt)}</span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ReportScopeFilters = ({
  teamFilter,
  leaderFilter,
  onTeamChange,
  onLeaderChange,
  teamOptions,
  leaderOptions,
  isDark,
  labelClass,
  inputClass,
  extraActions = null,
  multiple = false,
  leaderMultiple = false,
}) => {
  const leaderMulti = leaderMultiple || multiple;
  if (teamOptions.length === 0 && leaderOptions.length === 0) return null;

  return (
    <div className={`p-3 sm:p-4 rounded-lg border ${isDark ? "bg-slate-800/40 border-slate-700/60" : "bg-indigo-50/80 border-indigo-100"}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[160px] flex-1">
          <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Takım</label>
          {multiple ? (
            <ReportScopeMultiSelect
              label="Takım"
              options={teamOptions}
              values={teamFilter}
              onChange={onTeamChange}
              placeholder="Tüm takımlar"
              isDark={isDark}
              inputClass={inputClass}
              labelClass={labelClass}
              getOptionLabel={(t) => `${t.name}${t.leaderName ? ` · ${t.leaderName}` : ""}`}
            />
          ) : (
            <Select value={teamFilter} onValueChange={onTeamChange}>
              <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue placeholder="Tüm takımlar" /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tüm Takımlar</SelectItem>
                {teamOptions.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}{t.leaderName ? ` · ${t.leaderName}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="min-w-[160px] flex-1">
          <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Takım Lideri</label>
          {leaderMulti ? (
            <ReportScopeMultiSelect
              label="Takım Lideri"
              options={leaderOptions}
              values={leaderFilter}
              onChange={onLeaderChange}
              placeholder="Tüm liderler"
              isDark={isDark}
              inputClass={inputClass}
              labelClass={labelClass}
              getOptionLabel={(l) => l.name}
            />
          ) : (
            <Select
              value={Array.isArray(leaderFilter) ? (leaderFilter[0] ? String(leaderFilter[0]) : "all") : leaderFilter}
              onValueChange={onLeaderChange}
            >
              <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue placeholder="Tüm liderler" /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tüm Liderler</SelectItem>
                {leaderOptions.map((l) => (
                  <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {extraActions}
      </div>
      <p className={`text-[11px] mt-2 ${labelClass}`}>
        Görev ve görünürlük kurallarına göre listelenen agentlar arasından takım veya lider bazlı filtre uygulanır.
      </p>
    </div>
  );
};

const MesaiScheduleCell = ({ row, isDark, labelClass }) => {
  if (row.avg_start_time && row.avg_end_time) {
    return (
      <div className="flex flex-col items-center gap-1 min-w-[112px]">
        <div className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-mono tabular-nums ring-1 shadow-sm ${isDark ? "bg-slate-800/90 text-slate-100 ring-slate-700" : "bg-white text-gray-800 ring-gray-200"}`}>
          <Clock className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
          <span className={`font-semibold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{row.avg_start_time}</span>
          <span className={`text-[10px] px-0.5 ${labelClass}`}>→</span>
          <span className={`font-semibold ${isDark ? "text-rose-300" : "text-rose-600"}`}>{row.avg_end_time}</span>
        </div>
        <span className={`text-[10px] font-medium ${isDark ? "text-slate-500" : "text-gray-500"}`}>
          {row.avg_work_days || 0} gün · ilk→son giden ort.
        </span>
      </div>
    );
  }

  if (row.avg_start_time) {
    return (
      <div className="flex flex-col items-center gap-1 min-w-[100px]">
        <span className={`inline-flex items-center gap-1 text-xs font-mono tabular-nums ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
          <Clock className="w-3 h-3 shrink-0" />
          {row.avg_start_time}
        </span>
        <span className={`text-[10px] ${labelClass}`}>Yalnızca ilk giden ort.</span>
      </div>
    );
  }

  return (
    <span className={`text-xs italic text-center block max-w-[120px] mx-auto ${labelClass}`}>
      {row.scheduled_days > 0 ? "Çalışma kaydı yok" : "Mesai günü yok"}
    </span>
  );
};

// Agent public JSON export (AI) + webhook push — super admin
const AgentPublicExportModal = ({ open, onClose, agent, isDark, apiBase }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState("");
  const [tokenSet, setTokenSet] = useState(false);
  const [publicApiBaseUrl, setPublicApiBaseUrl] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [gsmQuery, setGsmQuery] = useState("");
  const [allowedIpsText, setAllowedIpsText] = useState("");
  const [rateLimitEnabled, setRateLimitEnabled] = useState(true);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(60);
  const [clientIp, setClientIp] = useState("");

  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [webhookCompanyName, setWebhookCompanyName] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookMethod, setWebhookMethod] = useState("POST");
  const [webhookAuthType, setWebhookAuthType] = useState("none");
  const [webhookAuthValue, setWebhookAuthValue] = useState("");
  const [webhookInterval, setWebhookInterval] = useState(5);
  const [webhookFilterStartDate, setWebhookFilterStartDate] = useState("");
  const [webhookFilterEndDate, setWebhookFilterEndDate] = useState("");
  const [webhookPreviewOpen, setWebhookPreviewOpen] = useState(false);
  const [webhookPreviewJson, setWebhookPreviewJson] = useState("");
  const [webhookPreviewing, setWebhookPreviewing] = useState(false);
  const [webhookLastRun, setWebhookLastRun] = useState(null);
  const [webhookLastSuccess, setWebhookLastSuccess] = useState(null);
  const [webhookLastError, setWebhookLastError] = useState("");
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [webhookStats, setWebhookStats] = useState(null);
  const [webhookResetting, setWebhookResetting] = useState(false);
  const [webhookPushingAll, setWebhookPushingAll] = useState(false);

  const companyId = agent?.company_id ? Number(agent.company_id) : null;

  const loadWebhookSettings = useCallback(async () => {
    if (!companyId) return;
    setWebhookLoading(true);
    try {
      const [settingsRes, logsRes, statusRes] = await Promise.all([
        axios.get(`${apiBase}/companies/${companyId}/webhook-settings`),
        axios.get(`${apiBase}/companies/${companyId}/webhook-logs`, { params: { limit: 10 } }),
        axios.get(`${apiBase}/companies/${companyId}/webhook-status`),
      ]);
      const data = settingsRes.data || {};
      const s = data.settings || {};
      setWebhookCompanyName(data.company_name || "");
      setWebhookEnabled(!!s.enabled);
      setWebhookUrl(s.webhook_url || "");
      setWebhookMethod(s.http_method || "POST");
      setWebhookAuthType(s.auth_type || "none");
      setWebhookAuthValue(data.auth_value || "");
      setWebhookInterval(Math.max(5, Number(s.interval_minutes) || 5));
      setWebhookFilterStartDate(s.filter_start_date || "");
      setWebhookFilterEndDate(s.filter_end_date || "");
      setWebhookLastRun(s.last_run_at || null);
      setWebhookLastSuccess(s.last_success_at || null);
      setWebhookLastError(s.last_error || "");
      setWebhookLogs(logsRes.data?.logs || []);
      setWebhookStats(statusRes.data?.stats || null);
    } catch (err) {
      if (err.response?.status !== 401) {
        toast.error(err.response?.data?.detail || "Webhook ayarları yüklenemedi");
      }
    } finally {
      setWebhookLoading(false);
    }
  }, [companyId, apiBase]);

  const loadSettings = useCallback(async () => {
    if (!agent?.kullanici_adi) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/public-api-settings`, {
        params: { agent_username: agent.kullanici_adi },
      });
      const data = res.data || {};
      setEnabled(!!data.settings?.enabled);
      setTokenSet(!!data.settings?.token_set);
      setPublicApiBaseUrl(data.api_base_url || apiBase || API);
      setAllowedIpsText((data.settings?.allowed_ips || []).join("\n"));
      setRateLimitEnabled(data.settings?.rate_limit_enabled !== false);
      setRateLimitPerMinute(Math.max(1, Number(data.settings?.rate_limit_per_minute) || 60));
      setClientIp(data.client_ip || "");
      if (data.access_token) setToken(data.access_token);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Oturum süresi doldu — lütfen yeniden giriş yapın.");
      } else {
        toast.error(err.response?.data?.detail || "Public API ayarları yüklenemedi");
      }
    } finally {
      setLoading(false);
    }
  }, [agent?.kullanici_adi, apiBase]);

  useEffect(() => {
    if (!open) return;
    setStartDate("");
    setEndDate("");
    loadSettings();
    loadWebhookSettings();
  }, [open, loadSettings, loadWebhookSettings]);

  const saveAccessSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${apiBase}/public-api-settings`, {
        enabled,
        ip_whitelist_enabled: true,
        allowed_ips: allowedIpsText.split(/[\r\n,]+/).map((s) => s.trim()).filter(Boolean),
        rate_limit_enabled: rateLimitEnabled,
        rate_limit_per_minute: Math.max(1, Math.min(10000, Number(rateLimitPerMinute) || 60)),
      });
      const s = res.data?.settings || {};
      setAllowedIpsText((s.allowed_ips || []).join("\n"));
      setRateLimitEnabled(s.rate_limit_enabled !== false);
      setRateLimitPerMinute(Math.max(1, Number(s.rate_limit_per_minute) || 60));
      if (res.data?.client_ip) setClientIp(res.data.client_ip);
      toast.success(res.data?.message || "Erişim ayarları kaydedildi");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Oturum süresi doldu — lütfen yeniden giriş yapın.");
      } else {
        toast.error(err.response?.data?.detail || "Erişim ayarları kaydedilemedi");
      }
    } finally {
      setSaving(false);
    }
  };

  const allowedIpList = useMemo(
    () => allowedIpsText.split(/[\r\n,]+/).map((s) => s.trim()).filter(Boolean),
    [allowedIpsText]
  );

  const canOpenInBrowser = useMemo(() => {
    if (!allowedIpList.length || !clientIp) return false;
    return allowedIpList.some((ip) => ip === clientIp);
  }, [clientIp, allowedIpList]);

  const ipAccessReady = allowedIpList.length > 0;

  const ensureToken = async (regenerate = false) => {
    setSaving(true);
    try {
      const res = await axios.put(`${apiBase}/public-api-settings`, {
        enabled: true,
        regenerate_token: regenerate,
      });
      const data = res.data || {};
      setEnabled(true);
      setTokenSet(true);
      setPublicApiBaseUrl(data.api_base_url || publicApiBaseUrl || apiBase || API);
      if (data.access_token) setToken(data.access_token);
      toast.success(data.message || "Token hazır");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Oturum süresi doldu — lütfen yeniden giriş yapın.");
      } else {
        toast.error(err.response?.data?.detail || "Token oluşturulamadı");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(`${apiBase}/public-api-settings`, { enabled: next });
      setEnabled(!!next);
      if (res.data?.access_token) setToken(res.data.access_token);
      if (res.data?.api_base_url) setPublicApiBaseUrl(res.data.api_base_url);
      toast.success(next ? "Public API açıldı" : "Public API kapatıldı");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Oturum süresi doldu — lütfen yeniden giriş yapın.");
      } else {
        toast.error(err.response?.data?.detail || "Kaydedilemedi");
      }
    } finally {
      setSaving(false);
    }
  };

  const saveWebhookSettings = async () => {
    if (!companyId) return;
    setWebhookSaving(true);
    try {
      const res = await axios.put(`${apiBase}/companies/${companyId}/webhook-settings`, {
        enabled: webhookEnabled,
        webhook_url: webhookUrl.trim(),
        http_method: webhookMethod,
        auth_type: webhookAuthType,
        auth_value: webhookAuthValue,
        interval_minutes: Math.max(5, Number(webhookInterval) || 5),
        filter_start_date: webhookFilterStartDate || "",
        filter_end_date: webhookFilterEndDate || "",
      });
      const s = res.data?.settings || {};
      setWebhookEnabled(!!s.enabled);
      setWebhookUrl(s.webhook_url || webhookUrl);
      setWebhookMethod(s.http_method || webhookMethod);
      setWebhookAuthType(s.auth_type || webhookAuthType);
      if (res.data?.auth_value) setWebhookAuthValue(res.data.auth_value);
      setWebhookInterval(Math.max(5, Number(s.interval_minutes) || 5));
      setWebhookFilterStartDate(s.filter_start_date || "");
      setWebhookFilterEndDate(s.filter_end_date || "");
      setWebhookLastRun(s.last_run_at || null);
      setWebhookLastSuccess(s.last_success_at || null);
      setWebhookLastError(s.last_error || "");
      toast.success(res.data?.message || "Webhook ayarları kaydedildi");
      await loadWebhookSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Webhook ayarları kaydedilemedi");
    } finally {
      setWebhookSaving(false);
    }
  };

  const webhookPushOptions = () => ({
    start_date: webhookFilterStartDate || undefined,
    end_date: webhookFilterEndDate || undefined,
    filter_start_date: webhookFilterStartDate || undefined,
    filter_end_date: webhookFilterEndDate || undefined,
  });

  const previewWebhookPush = async () => {
    if (!companyId) return;
    setWebhookPreviewing(true);
    try {
      const res = await axios.post(`${apiBase}/companies/${companyId}/webhook-settings`, {
        preview: true,
        ...webhookPushOptions(),
      });
      const payload = res.data?.result?.payload;
      setWebhookPreviewJson(JSON.stringify(payload || res.data?.result || {}, null, 2));
      setWebhookPreviewOpen(true);
      const r = res.data?.result || {};
      toast.info(`${r.call_count || 0} çağrı · ${r.agent_count || 0} agent · sıradaki paket (max ${r.batch_size || 12}, gönderilmedi)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Önizleme alınamadı");
    } finally {
      setWebhookPreviewing(false);
    }
  };

  const testWebhookPush = async () => {
    if (!companyId) return;
    setWebhookTesting(true);
    try {
      await saveWebhookSettings();
      const res = await axios.post(`${apiBase}/companies/${companyId}/webhook-settings`, webhookPushOptions());
      const result = res.data?.result || {};
      if (result.success) {
        toast.success(`Paket gönderildi: ${result.call_count || 0} çağrı · kalan ${result.pending_after ?? 0}`);
        if (result.payload) {
          setWebhookPreviewJson(JSON.stringify(result.payload, null, 2));
          setWebhookPreviewOpen(true);
        }
      } else if (result.call_count === 0) {
        toast.info(result.message || "Gönderilecek yeni kayıt yok");
      } else {
        toast.error(result.error || res.data?.message || "Webhook gönderilemedi");
      }
      await loadWebhookSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.result?.error || "Test gönderimi başarısız");
      await loadWebhookSettings();
    } finally {
      setWebhookTesting(false);
    }
  };

  const pushAllWebhookBatches = async () => {
    if (!companyId) return;
    setWebhookPushingAll(true);
    try {
      await saveWebhookSettings();
      const res = await axios.post(`${apiBase}/companies/${companyId}/webhook-settings`, {
        push_all: true,
        ...webhookPushOptions(),
      });
      const result = res.data?.result || {};
      if (result.success) {
        toast.success(`${result.batch_count || 0} paket · ${result.total_calls_sent || 0} çağrı · kalan ${result.remaining_pending ?? 0}`);
      } else if ((result.total_calls_sent ?? result.call_count) === 0) {
        toast.info(result.message || "Gönderilecek kayıt yok");
      } else {
        toast.error(result.error || res.data?.message || "Toplu gönderim başarısız");
      }
      await loadWebhookSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.result?.error || "Toplu gönderim başarısız");
      await loadWebhookSettings();
    } finally {
      setWebhookPushingAll(false);
    }
  };

  const resetWebhookDeliveries = async () => {
    if (!companyId) return;
    const ok = window.confirm(
      "Webhook gönderim durumu sıfırlanacak. Tüm kayıtlar yeniden 12'li paketler halinde gönderilir. Devam?"
    );
    if (!ok) return;
    setWebhookResetting(true);
    try {
      const res = await axios.post(`${apiBase}/companies/${companyId}/webhook-status`, { reset_deliveries: true });
      setWebhookStats(res.data?.stats || null);
      toast.success(res.data?.message || "Gönderim durumu sıfırlandı");
      await loadWebhookSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sıfırlama başarısız");
    } finally {
      setWebhookResetting(false);
    }
  };

  const buildExportUrl = useCallback((withSummary = false) => {
    if (!token || !agent?.kullanici_adi) return "";
    const base = (publicApiBaseUrl || apiBase || API).replace(/\/$/, "");
    const params = new URLSearchParams({
      access_token: token,
      username: agent.kullanici_adi,
    });
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    if (withSummary) params.set("type", "ozetveri");
    return `${base}/public/agent-calls?${params.toString()}`;
  }, [token, agent?.kullanici_adi, publicApiBaseUrl, apiBase, startDate, endDate]);

  const exportUrl = useMemo(() => buildExportUrl(false), [buildExportUrl]);
  const exportSummaryUrl = useMemo(() => buildExportUrl(true), [buildExportUrl]);

  const buildGsmExportUrl = useCallback((withSummary = false) => {
    const gsm = gsmQuery.trim();
    if (!token || !gsm) return "";
    const base = (publicApiBaseUrl || apiBase || API).replace(/\/$/, "");
    const params = new URLSearchParams({
      access_token: token,
      gsm,
    });
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    if (withSummary) params.set("type", "ozetveri");
    return `${base}/public/gsm-calls?${params.toString()}`;
  }, [token, gsmQuery, publicApiBaseUrl, apiBase, startDate, endDate]);

  const gsmExportUrl = useMemo(() => buildGsmExportUrl(false), [buildGsmExportUrl]);
  const gsmExportSummaryUrl = useMemo(() => buildGsmExportUrl(true), [buildGsmExportUrl]);

  const copyUrl = async (url = exportUrl, label = "URL") => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} kopyalandı`);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const cardClass = isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white";
  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-300";
  const mutedClass = isDark ? "text-slate-400" : "text-gray-500";
  const sectionClass = isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50";

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${cardClass}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-violet-500" />
            AI JSON Export — {agent?.ad_soyad || agent?.kullanici_adi || "Agent"}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
        ) : (
          <div className="space-y-5 text-sm">
            <div className={`rounded-lg border p-3 space-y-3 ${sectionClass}`}>
              <p className={`text-xs font-medium ${isDark ? "text-violet-300" : "text-violet-700"}`}>Public GET (manuel / AI)</p>
              <p className={mutedClass}>
                Varsayılan URL: cevaplanan gelen/giden çağrılar, yalnızca ses kaydı olanlar (AI için kayıt listesi).
                <code className="text-[11px] mx-1">type=ozetveri</code> ile aynı tarih aralığında toplam arama, cevaplanan/cevapsız/mesgul ve gelen-giden dağılımı alınır.
                Opsiyonel <code className="text-[11px]">calls</code> parametresi ile belirli kayıt ID&apos;leri istenebilir (virgülle veya tekrarlayarak).
              </p>
              <div className="flex items-center justify-between gap-3">
                <Label className={mutedClass}>Public API</Label>
                <Switch checked={enabled} disabled={saving || !tokenSet} onCheckedChange={toggleEnabled} />
              </div>
              {!tokenSet ? (
                <Button type="button" onClick={() => ensureToken(false)} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-500 text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                  Erişim Token&apos;ı Oluştur
                </Button>
              ) : (
                <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => ensureToken(true)} className={isDark ? "border-slate-600" : ""}>
                  <RotateCw className="w-3.5 h-3.5 mr-1" /> Token Yenile
                </Button>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className={`text-xs ${mutedClass}`}>Başlangıç (opsiyonel)</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`mt-1 h-9 ${inputClass}`} />
                </div>
                <div>
                  <Label className={`text-xs ${mutedClass}`}>Bitiş (opsiyonel)</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`mt-1 h-9 ${inputClass}`} />
                </div>
              </div>
              {exportUrl && enabled && (
                <>
                  {!ipAccessReady && (
                    <p className={`text-xs ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                      Public API kapalı — en az bir izinli IP tanımlayıp kaydedin.
                    </p>
                  )}
                  <div>
                    <Label className={`text-xs ${mutedClass}`}>Public URL — çağrı listesi (ses kayıtlı)</Label>
                    <div className={`mt-1 rounded-lg border px-3 py-2 font-mono text-xs break-all ${isDark ? "border-slate-700 bg-slate-950 text-cyan-300" : "border-gray-200 bg-white text-cyan-700"}`}>
                      {exportUrl}
                    </div>
                  </div>
                  <div>
                    <Label className={`text-xs ${mutedClass}`}>Public URL — özet veri (type=ozetveri)</Label>
                    <div className={`mt-1 rounded-lg border px-3 py-2 font-mono text-xs break-all ${isDark ? "border-slate-700 bg-slate-950 text-emerald-300" : "border-gray-200 bg-white text-emerald-700"}`}>
                      {exportSummaryUrl}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={() => copyUrl(exportUrl, "Çağrı listesi URL")} className="bg-violet-600 hover:bg-violet-500 text-white">
                      <Copy className="w-4 h-4 mr-2" /> Liste URL
                    </Button>
                    <Button type="button" variant="outline" onClick={() => copyUrl(exportSummaryUrl, "Özet URL")} className={isDark ? "border-slate-600" : ""}>
                      <Copy className="w-4 h-4 mr-2" /> Özet URL
                    </Button>
                    {canOpenInBrowser ? (
                      <>
                        <Button type="button" variant="outline" asChild className={isDark ? "border-slate-600" : ""}>
                          <a href={exportUrl} target="_blank" rel="noopener noreferrer">
                            <Braces className="w-4 h-4 mr-2" /> Liste JSON
                          </a>
                        </Button>
                        <Button type="button" variant="outline" asChild className={isDark ? "border-slate-600" : ""}>
                          <a href={exportSummaryUrl} target="_blank" rel="noopener noreferrer">
                            <Braces className="w-4 h-4 mr-2" /> Özet JSON
                          </a>
                        </Button>
                      </>
                    ) : (
                      <p className={`text-xs self-center ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                        Tarayıcı IP&apos;niz whitelist&apos;te değil — sunucu/script ile test edin.
                      </p>
                    )}
                  </div>
                  <p className={`text-[11px] ${mutedClass}`}>
                    Belirli kayıtlar: <span className="font-mono break-all">{exportUrl}&amp;calls=12345,67890</span>
                  </p>
                </>
              )}
              {!enabled && tokenSet && (
                <p className={`text-xs ${isDark ? "text-amber-300" : "text-amber-700"}`}>Public API kapalı — URL çalışmaz.</p>
              )}

              <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-slate-600 bg-slate-900/30" : "border-cyan-200 bg-cyan-50/30"}`}>
                <p className={`text-xs font-medium ${isDark ? "text-cyan-300" : "text-cyan-800"}`}>GSM numarası (agent bağımsız)</p>
                <p className={mutedClass}>
                  Aynı <code className="text-[11px]">access_token</code> ile arayan veya aranan tarafta geçen tüm çağrılar listelenir (cevaplanan/cevapsız/mesgul).
                  Üstteki tarih aralığı bu sorguya da uygulanır. Sayfalama: <code className="text-[11px]">page</code>, <code className="text-[11px]">limit</code> (varsayılan 500, en fazla 5000).
                </p>
                <div>
                  <Label className={`text-xs ${mutedClass}`}>GSM numarası</Label>
                  <Input
                    type="text"
                    inputMode="tel"
                    value={gsmQuery}
                    onChange={(e) => setGsmQuery(e.target.value)}
                    placeholder="05xxxxxxxxx veya 5xxxxxxxxx"
                    className={`mt-1 h-9 ${inputClass}`}
                  />
                </div>
                {gsmExportUrl && enabled && tokenSet && (
                  <>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>GSM — çağrı listesi</Label>
                      <div className={`mt-1 rounded-lg border px-3 py-2 font-mono text-xs break-all ${isDark ? "border-slate-700 bg-slate-950 text-cyan-300" : "border-gray-200 bg-white text-cyan-700"}`}>
                        {gsmExportUrl}
                      </div>
                    </div>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>GSM — özet (type=ozetveri)</Label>
                      <div className={`mt-1 rounded-lg border px-3 py-2 font-mono text-xs break-all ${isDark ? "border-slate-700 bg-slate-950 text-emerald-300" : "border-gray-200 bg-white text-emerald-700"}`}>
                        {gsmExportSummaryUrl}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => copyUrl(gsmExportUrl, "GSM liste URL")} className={isDark ? "border-slate-600" : ""}>
                        <Copy className="w-3.5 h-3.5 mr-1" /> GSM liste
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => copyUrl(gsmExportSummaryUrl, "GSM özet URL")} className={isDark ? "border-slate-600" : ""}>
                        <Copy className="w-3.5 h-3.5 mr-1" /> GSM özet
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-slate-600 bg-slate-900/40" : "border-violet-200 bg-violet-50/40"}`}>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-violet-500" />
                  <p className={`text-xs font-medium ${isDark ? "text-violet-300" : "text-violet-800"}`}>IP koruması ve istek limiti</p>
                </div>
                <p className={mutedClass}>
                  Public API varsayılan olarak kapalıdır. En az bir izinli IP tanımlanmadan URL çalışmaz; yalnızca listedeki IP&apos;lerden erişilebilir.
                  {clientIp && (
                    <> Mevcut IP: <code className="text-[11px]">{clientIp}</code></>
                  )}
                </p>
                <div>
                  <Label className={`text-xs ${mutedClass}`}>İzinli IP adresleri (satır veya virgülle) *</Label>
                  <textarea
                    value={allowedIpsText}
                    onChange={(e) => setAllowedIpsText(e.target.value)}
                    rows={4}
                    placeholder={"203.0.113.10\n198.51.100.0/24"}
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-xs font-mono resize-y min-h-[88px] ${inputClass}`}
                  />
                  <p className={`text-[11px] mt-1 ${mutedClass}`}>
                    Tek IP veya CIDR (ör. 192.168.1.0/24). Liste boşken API tamamen kapalıdır.
                  </p>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label className={mutedClass}>Dakika başına istek limiti</Label>
                  <Switch checked={rateLimitEnabled} disabled={saving} onCheckedChange={setRateLimitEnabled} />
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={1}
                    max={10000}
                    value={rateLimitPerMinute}
                    disabled={!rateLimitEnabled || saving}
                    onChange={(e) => setRateLimitPerMinute(e.target.value)}
                    className={`h-9 w-28 ${inputClass}`}
                  />
                  <span className={`text-xs ${mutedClass}`}>istek / dakika / IP</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={saveAccessSettings}
                  className={isDark ? "border-slate-600" : ""}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                  Erişim Ayarlarını Kaydet
                </Button>
              </div>
            </div>

            <div className={`rounded-lg border p-3 space-y-3 ${sectionClass}`}>
              <div className="flex items-center gap-2">
                <Webhook className="w-4 h-4 text-emerald-500" />
                <p className={`text-xs font-medium ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>Webhook Push (Cron)</p>
              </div>
              {!companyId ? (
                <p className={`text-xs ${isDark ? "text-amber-300" : "text-amber-700"}`}>Agent&apos;a firma atanmadığı için webhook yapılandırılamaz.</p>
              ) : webhookLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
              ) : (
                <>
                  <p className={mutedClass}>
                    <span className="font-medium">{webhookCompanyName || agent?.company_name || "Firma"}</span> — tüm agentların çağrı kayıtları
                    (cevaplı, cevapsız, meşgul, gelen/giden) <strong>12&apos;li paketler</strong> halinde POST edilir. HTTP 2xx alınan kayıtlar tekrar gönderilmez;
                    yeniden göndermek için sıfırlama gerekir.
                  </p>
                  {webhookStats && (
                    <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] ${mutedClass}`}>
                      <div className={`rounded border px-2 py-1.5 ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                        <span className="block opacity-70">Toplam</span>
                        <span className={`font-semibold tabular-nums ${isDark ? "text-slate-200" : "text-gray-800"}`}>{webhookStats.total_calls ?? 0}</span>
                      </div>
                      <div className={`rounded border px-2 py-1.5 ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                        <span className="block opacity-70">Gönderildi</span>
                        <span className={`font-semibold tabular-nums ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{webhookStats.delivered_calls ?? 0}</span>
                      </div>
                      <div className={`rounded border px-2 py-1.5 ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                        <span className="block opacity-70">Bekleyen</span>
                        <span className={`font-semibold tabular-nums ${isDark ? "text-amber-300" : "text-amber-700"}`}>{webhookStats.pending_calls ?? 0}</span>
                      </div>
                      <div className={`rounded border px-2 py-1.5 ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                        <span className="block opacity-70">İlerleme</span>
                        <span className={`font-semibold tabular-nums ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>%{webhookStats.progress_percent ?? 0}</span>
                      </div>
                    </div>
                  )}
                  {webhookStats?.agents?.length > 0 && (
                    <div className={`rounded-lg border overflow-x-auto ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                      <table className="w-full text-[10px]">
                        <thead className={isDark ? "bg-slate-800/80" : "bg-gray-100"}>
                          <tr>
                            <th className="px-2 py-1.5 text-left font-medium">Agent</th>
                            <th className="px-2 py-1.5 text-right font-medium">Toplam</th>
                            <th className="px-2 py-1.5 text-right font-medium">Gönderildi</th>
                            <th className="px-2 py-1.5 text-right font-medium">Bekleyen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {webhookStats.agents.map((ag) => (
                            <tr key={ag.id} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
                              <td className="px-2 py-1">{ag.ad_soyad || ag.kullanici_adi}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{ag.total_calls}</td>
                              <td className={`px-2 py-1 text-right tabular-nums ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>{ag.delivered_calls}</td>
                              <td className={`px-2 py-1 text-right tabular-nums ${isDark ? "text-amber-300" : "text-amber-700"}`}>{ag.pending_calls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <Label className={mutedClass}>Webhook Aktif</Label>
                    <Switch checked={webhookEnabled} onCheckedChange={setWebhookEnabled} />
                  </div>
                  <div>
                    <Label className={`text-xs ${mutedClass}`}>Hedef API URL *</Label>
                    <Input
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://example.com/api/calls"
                      className={`mt-1 h-9 ${inputClass}`}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>HTTP Metodu</Label>
                      <Select value={webhookMethod} onValueChange={setWebhookMethod}>
                        <SelectTrigger className={`mt-1 h-9 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="POST">POST</SelectItem>
                          <SelectItem value="PUT">PUT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Doğrulama</Label>
                      <Select value={webhookAuthType} onValueChange={setWebhookAuthType}>
                        <SelectTrigger className={`mt-1 h-9 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="none">Yok</SelectItem>
                          <SelectItem value="bearer">Bearer Token</SelectItem>
                          <SelectItem value="x_api_key">X-API-Key</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Periyot (dk, min 5)</Label>
                      <Input
                        type="number"
                        min={5}
                        max={1440}
                        value={webhookInterval}
                        onChange={(e) => setWebhookInterval(Math.max(5, Number(e.target.value) || 5))}
                        className={`mt-1 h-9 ${inputClass}`}
                      />
                    </div>
                  </div>
                  {webhookAuthType !== "none" && (
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>
                        {webhookAuthType === "bearer" ? "Bearer Token" : "X-API-Key"} *
                      </Label>
                      <Input
                        type="password"
                        value={webhookAuthValue}
                        onChange={(e) => setWebhookAuthValue(e.target.value)}
                        placeholder="Anahtar / token"
                        className={`mt-1 h-9 ${inputClass}`}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Çağrı başlangıç (opsiyonel)</Label>
                      <Input type="date" value={webhookFilterStartDate} onChange={(e) => setWebhookFilterStartDate(e.target.value)} className={`mt-1 h-9 ${inputClass}`} />
                    </div>
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Çağrı bitiş (opsiyonel)</Label>
                      <Input type="date" value={webhookFilterEndDate} onChange={(e) => setWebhookFilterEndDate(e.target.value)} className={`mt-1 h-9 ${inputClass}`} />
                    </div>
                  </div>
                  <p className={`text-[10px] ${mutedClass}`}>Tarih aralığı cron ve test gönderimlerinde uygulanır. Boş bırakılırsa tüm yeni kayıtlar gönderilir.</p>
                  {(webhookLastRun || webhookLastError) && (
                    <div className={`text-[11px] space-y-1 ${mutedClass}`}>
                      {webhookLastRun && <p>Son çalışma: {webhookLastRun}</p>}
                      {webhookLastSuccess && <p>Son başarı: {webhookLastSuccess}</p>}
                      {webhookLastError && <p className={isDark ? "text-rose-300" : "text-rose-600"}>Son hata: {webhookLastError}</p>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={saveWebhookSettings} disabled={webhookSaving || webhookTesting || webhookPreviewing || webhookPushingAll || webhookResetting} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                      {webhookSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Kaydet
                    </Button>
                    <Button type="button" variant="outline" onClick={previewWebhookPush} disabled={webhookSaving || webhookTesting || webhookPreviewing || webhookPushingAll || webhookResetting} className={isDark ? "border-slate-600" : ""}>
                      {webhookPreviewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Braces className="w-4 h-4 mr-2" />}
                      Veriyi Önizle
                    </Button>
                    <Button type="button" variant="outline" onClick={testWebhookPush} disabled={webhookSaving || webhookTesting || webhookPreviewing || webhookPushingAll || webhookResetting || !webhookEnabled} className={isDark ? "border-slate-600" : ""}>
                      {webhookTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      1 Paket Gönder (12)
                    </Button>
                    <Button type="button" variant="outline" onClick={pushAllWebhookBatches} disabled={webhookSaving || webhookTesting || webhookPreviewing || webhookPushingAll || webhookResetting || !webhookEnabled} className={isDark ? "border-slate-600" : ""}>
                      {webhookPushingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Bitene Kadar Gönder
                    </Button>
                    <Button type="button" variant="outline" onClick={resetWebhookDeliveries} disabled={webhookSaving || webhookTesting || webhookPreviewing || webhookPushingAll || webhookResetting} className={isDark ? "border-rose-800 text-rose-300" : "border-rose-300 text-rose-700"}>
                      {webhookResetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Sıfırla
                    </Button>
                  </div>
                  <p className={`text-[10px] ${mutedClass}`}>
                    Tüm loglar için: <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>Ayarlar → Loglar</span>
                  </p>
                  {webhookLogs.length > 0 && (
                    <div>
                      <Label className={`text-xs ${mutedClass}`}>Son gönderimler (özet)</Label>
                      <div className={`mt-1 rounded-lg border overflow-x-auto ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                        <table className="w-full text-[11px]">
                          <thead className={isDark ? "bg-slate-800/80" : "bg-gray-100"}>
                            <tr>
                              <th className="px-2 py-1.5 text-left font-medium">Tarih</th>
                              <th className="px-2 py-1.5 text-left font-medium">Durum</th>
                              <th className="px-2 py-1.5 text-right font-medium">Agent</th>
                              <th className="px-2 py-1.5 text-right font-medium">Çağrı</th>
                              <th className="px-2 py-1.5 text-right font-medium">HTTP</th>
                              <th className="px-2 py-1.5 text-right font-medium">ms</th>
                            </tr>
                          </thead>
                          <tbody>
                            {webhookLogs.map((log) => (
                              <tr key={log.id} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
                                <td className="px-2 py-1.5 whitespace-nowrap">{log.created_at || "-"}</td>
                                <td className={`px-2 py-1.5 ${log.success ? (isDark ? "text-emerald-300" : "text-emerald-700") : (isDark ? "text-rose-300" : "text-rose-600")}`}>
                                  {log.success ? "OK" : (log.error_message || "Hata")}
                                </td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{log.agent_count}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{log.call_count}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{log.http_status ?? "-"}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{log.duration_ms ?? "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <p className={`text-[10px] ${mutedClass}`}>
                    Cron: her dakika <code className="font-mono">php webhook-push-worker.php</code> çalıştırın (Windows: dev-webhook-worker.bat).
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <Dialog open={webhookPreviewOpen} onOpenChange={setWebhookPreviewOpen}>
      <DialogContent className={`max-w-3xl max-h-[85vh] overflow-y-auto ${cardClass}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Braces className="w-5 h-5 text-emerald-500" />
            Webhook POST Önizlemesi
          </DialogTitle>
        </DialogHeader>
        <pre className={`text-[11px] p-3 rounded-lg overflow-x-auto max-h-[65vh] ${isDark ? "bg-slate-950 border border-slate-800 text-cyan-200" : "bg-gray-50 border border-gray-200 text-cyan-800"}`}>{webhookPreviewJson || "{}"}</pre>
      </DialogContent>
    </Dialog>
    </>
  );
};

const agentHasLeftCompany = (agent) => Boolean(formatAgentEmploymentDate(agent?.isten_cikis_tarihi));

const mergeAgentFormDateField = (prev, field, value) => {
  if (value !== "" && !isAgentEmploymentDateInputAllowed(value)) return prev;
  return { ...prev, [field]: value };
};
const agentLeftTextClass = (isDark) => (isDark ? "text-rose-400" : "text-rose-600");
const agentLeftOutlineBadgeClass = (isDark) => (isDark
  ? "border-rose-500/45 bg-rose-950/35 text-rose-300"
  : "border-rose-200 bg-rose-50 text-rose-700");
const agentLeftCountBadgeClass = (isDark) => (isDark
  ? "bg-rose-500/15 text-rose-300 border-rose-500/35 hover:bg-rose-500/25"
  : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100");

const AGENT_INLINE_TEAM_NONE = "__none__";

const teamsForAgentCompany = (agent, teamsList) => {
  const list = teamsList || [];
  const cid = agent?.company_id;
  if (!cid) return list.filter((t) => t?.id);
  return list.filter((t) => t?.id && (!t.company_id || Number(t.company_id) === Number(cid)));
};

const leaderOptionsFromTeams = (teamsList) => {
  const map = new Map();
  (teamsList || []).forEach((t) => {
    if (t.lider_user_id && t.lider_name) {
      map.set(Number(t.lider_user_id), t.lider_name);
    }
  });
  return Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "tr"));
};

const resolveTeamIdForLeaderChange = (agent, leaderUserId, teamsList) => {
  if (!leaderUserId || leaderUserId === AGENT_INLINE_TEAM_NONE) {
    return { teamId: null, ambiguous: false, teamName: null };
  }
  const candidates = teamsForAgentCompany(agent, teamsList)
    .filter((t) => Number(t.lider_user_id) === Number(leaderUserId))
    .sort((a, b) => (a.ad || "").localeCompare(b.ad || "", "tr"));
  if (candidates.length === 0) {
    return { teamId: undefined, ambiguous: false, teamName: null };
  }
  if (candidates.length === 1) {
    return { teamId: candidates[0].id, ambiguous: false, teamName: candidates[0].ad };
  }
  const current = candidates.find((t) => Number(t.id) === Number(agent.team_id));
  const pick = current || candidates[0];
  return {
    teamId: pick.id,
    ambiguous: !current,
    teamName: pick.ad,
    teamCount: candidates.length,
  };
};

// Agent Modal
const AgentModal = ({ isOpen, onClose, agent, onSave, isDark, allDevices = [], onDevicesUpdate, preSelectedDevice = null, existingAgentsCount = 0, companies = [], teams = [], isSuperAdmin = false, userCompanyId = null }) => {
  const [formData, setFormData] = useState({
    ad_soyad: "", kullanici_adi: "", sifre: "", gsm: "", status: 1, yetki_durumu: 1, company_id: "",
    ise_giris_tarihi: agentEmploymentDateToday(), isten_cikis_tarihi: "", yeni_dataya_gecis_tarihi: "",
  });
  const [teamLeaderPending, setTeamLeaderPending] = useState({ leader_user_id: null, leader_name: "", change_date: "" });
  const [workSchedule, setWorkSchedule] = useState(() => cloneWorkSchedule());
  const [applyScheduleToAll, setApplyScheduleToAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Türkçe karakterleri dönüştür ve kullanıcı adı oluştur
  const generateUsername = (name) => {
    if (!name) return "";
    const turkishMap = {
      'ç': 'c', 'Ç': 'c', 'ğ': 'g', 'Ğ': 'g', 'ı': 'i', 'İ': 'i',
      'ö': 'o', 'Ö': 'o', 'ş': 's', 'Ş': 's', 'ü': 'u', 'Ü': 'u'
    };
    return name
      .split('')
      .map(char => turkishMap[char] || char)
      .join('')
      .toLowerCase()
      .replace(/\s+/g, '')  // Boşlukları kaldır
      .replace(/[^a-z0-9]/g, '');  // Sadece harf ve rakam bırak
  };

  // Ad Soyad değiştiğinde otomatik kullanıcı adı oluştur (sadece yeni agent için)
  const handleAdSoyadChange = (value) => {
    const newUsername = !agent ? generateUsername(value) : formData.kullanici_adi;
    setFormData({ ...formData, ad_soyad: value, kullanici_adi: newUsername });
  };

  // Default bilgi butonu - Agent1, Agent2, ... şeklinde + şifre oluştur
  const setDefaultInfo = () => {
    const nextNumber = existingAgentsCount + 1;
    const defaultName = `Agent${nextNumber}`;
    const defaultUsername = `agent${nextNumber}`;
    // Şifre oluştur
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ 
      ...formData, 
      ad_soyad: defaultName, 
      kullanici_adi: defaultUsername,
      sifre: password
    });
    toast.success(`Default: ${defaultName} / Şifre: ${password}`);
  };

  // Şifre oluştur fonksiyonu (A-Z, a-z, 0-9)
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, sifre: password });
    toast.success(`Şifre oluşturuldu: ${password}`);
  };

  useEffect(() => {
    if (agent) {
      setFormData({
        ad_soyad: agent.ad_soyad || "",
        kullanici_adi: agent.kullanici_adi || "",
        sifre: "",
        gsm: agent.gsm || "",
        status: agent.status ?? 1,
        yetki_durumu: agent.yetki_durumu ?? 1,
        company_id: agent.company_id ? String(agent.company_id) : (userCompanyId ? String(userCompanyId) : ""),
        ise_giris_tarihi: formatAgentEmploymentDate(agent.ise_giris_tarihi),
        isten_cikis_tarihi: formatAgentEmploymentDate(agent.isten_cikis_tarihi),
        yeni_dataya_gecis_tarihi: formatAgentEmploymentDate(agent.yeni_dataya_gecis_tarihi),
      });
      setWorkSchedule(cloneWorkSchedule(agent.work_schedule));
      setApplyScheduleToAll(false);
      // Load agent's devices
      const loadAgentDevices = async () => {
        setLoadingDevices(true);
        try {
          const res = await axios.get(`${API}/agent-devices?agent_id=${agent.id}`);
          const deviceIds = (res.data.data || []).map(d => d.device_id);
          setSelectedDevices(deviceIds);
        } catch (error) {
          console.error("Load agent devices error:", error);
        } finally {
          setLoadingDevices(false);
        }
      };
      loadAgentDevices();
      setTeamLeaderPending({ leader_user_id: null, leader_name: "", change_date: "" });
    } else {
      // Yeni agent - preSelectedDevice varsa kullan
      setTeamLeaderPending({ leader_user_id: null, leader_name: "", change_date: "" });
      const gsm = preSelectedDevice?.gsm || "";
      const defaultCompanyId = userCompanyId ? String(userCompanyId) : (companies[0]?.id ? String(companies[0].id) : "");
      setFormData({
        ad_soyad: "", kullanici_adi: "", sifre: "", gsm: gsm, status: 1, yetki_durumu: 1, company_id: defaultCompanyId,
        ise_giris_tarihi: agentEmploymentDateToday(), isten_cikis_tarihi: "", yeni_dataya_gecis_tarihi: "",
      });
      setWorkSchedule(cloneWorkSchedule());
      setApplyScheduleToAll(false);
      if (preSelectedDevice?.id) {
        setSelectedDevices([preSelectedDevice.id]);
      } else {
        setSelectedDevices([]);
      }
    }
    setDeviceSearch("");
  }, [agent, isOpen, preSelectedDevice, userCompanyId, companies]);

  const handleSave = async () => {
    if (!formData.ad_soyad || !formData.kullanici_adi || (!agent && !formData.sifre)) {
      toast.error("Zorunlu alanları doldurun");
      return;
    }
    if (!formData.company_id) {
      toast.error("Firma seçimi zorunludur");
      return;
    }
    if (!agent && !formData.ise_giris_tarihi) {
      toast.error("İşe giriş tarihi zorunludur");
      return;
    }
    if (
      formData.ise_giris_tarihi
      && formData.isten_cikis_tarihi
      && formData.isten_cikis_tarihi < formData.ise_giris_tarihi
    ) {
      toast.error("İşten çıkış tarihi, işe giriş tarihinden önce olamaz");
      return;
    }
    setSaving(true);
    try {
      // Include device_ids in formData
      const dataToSave = {
        ...formData,
        company_id: parseInt(formData.company_id, 10),
        device_ids: selectedDevices,
        work_schedule: workSchedule,
        apply_schedule_to_all: applyScheduleToAll,
        team_leader_change: teamLeaderPending.change_date
          && (teamLeaderPending.leader_user_id || (teamLeaderPending.leader_name || "").trim())
          ? {
            leader_user_id: teamLeaderPending.leader_user_id || null,
            leader_name: (teamLeaderPending.leader_name || "").trim(),
            change_date: teamLeaderPending.change_date,
          }
          : null,
      };
      if (agent) {
        if (!dataToSave.ise_giris_tarihi) {
          delete dataToSave.ise_giris_tarihi;
        }
      } else if (!dataToSave.ise_giris_tarihi) {
        dataToSave.ise_giris_tarihi = agentEmploymentDateToday();
      }
      await onSave(dataToSave, agent?.id);
      if (onDevicesUpdate) onDevicesUpdate();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const filteredDevices = allDevices.filter(d => 
    (d.id != null && String(d.id).includes(deviceSearch)) ||
    (d.device_id && d.device_id.toLowerCase().includes(deviceSearch.toLowerCase())) ||
    (d.gsm && d.gsm.includes(deviceSearch)) ||
    (d.device_name && d.device_name.toLowerCase().includes(deviceSearch.toLowerCase()))
  );

  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-4"
    : "rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-4";
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white focus:border-blue-500" : "bg-white border-gray-300 focus:border-blue-500";
  const sectionTitleClass = isDark ? "text-sm font-semibold text-slate-200 flex items-center gap-2" : "text-sm font-semibold text-gray-800 flex items-center gap-2";
  const labelClass = isDark ? "text-slate-300" : "";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl p-0 gap-0 flex flex-col max-h-[90vh] overflow-hidden ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
        <div className={`shrink-0 px-6 pt-6 pb-4 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-blue-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-blue-50 to-white"}`}>
          <DialogHeader className="space-y-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-blue-500/20 ring-1 ring-blue-500/30" : "bg-blue-100 ring-1 ring-blue-200"}`}>
                  <Users className={`w-5 h-5 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                </div>
                <div className="min-w-0">
                  <DialogTitle className={`text-lg ${isDark ? "text-white" : "text-gray-900"}`}>
                    {agent ? "Agent Düzenle" : preSelectedDevice ? "Cihaz İçin Yeni Agent" : "Yeni Agent"}
                  </DialogTitle>
                  <p className={`text-sm mt-0.5 truncate ${mutedClass}`}>
                    {agent ? "Agent bilgilerini ve cihaz atamalarını güncelleyin" : "Yeni agent oluşturup cihaz atayın"}
                  </p>
                </div>
              </div>
              {!agent && (
                <Button type="button" variant="outline" size="sm" onClick={setDefaultInfo} className={`shrink-0 text-xs ${isDark ? "border-emerald-700/60 text-emerald-400 hover:bg-emerald-500/10" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                  <Zap className="w-3 h-3 mr-1" /> Default Bilgi
                </Button>
              )}
            </div>
          </DialogHeader>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-6 py-5 space-y-5">
          {preSelectedDevice && !agent && (
            <div className={`rounded-xl border p-4 ${isDark ? "border-cyan-800/50 bg-cyan-950/20" : "border-cyan-200 bg-cyan-50/80"}`}>
              <p className={`text-sm font-medium flex items-center gap-2 ${isDark ? "text-cyan-300" : "text-cyan-800"}`}>
                <Smartphone className="w-4 h-4" /> Bağlı cihaz
              </p>
              <p className={`text-xs mt-1 font-mono ${isDark ? "text-cyan-400/90" : "text-cyan-700"}`}>
                {preSelectedDevice.device_name || preSelectedDevice.device_id} · {preSelectedDevice.device_id}
              </p>
            </div>
          )}

          <div className={cardClass}>
            <p className={sectionTitleClass}><User className="w-4 h-4 text-blue-400" /> Temel Bilgiler</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={labelClass}>Ad Soyad *</Label>
                <Input value={formData.ad_soyad} onChange={(e) => handleAdSoyadChange(e.target.value)} className={`mt-1.5 ${inputClass}`} placeholder="Ad Soyad girin" />
              </div>
              <div>
                <Label className={labelClass}>Kullanıcı Adı *</Label>
                <Input value={formData.kullanici_adi} onChange={(e) => setFormData({...formData, kullanici_adi: e.target.value})} className={`mt-1.5 ${inputClass}`} disabled={!!agent} placeholder="Otomatik oluşturulur" />
              </div>
              <div className="sm:col-span-2">
                <Label className={labelClass}><Building2 className="w-3 h-3 inline mr-1" />Firma *</Label>
                <Select
                  value={formData.company_id || ""}
                  onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                  disabled={!isSuperAdmin && !!userCompanyId}
                >
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelClass}>{agent ? "Yeni Şifre" : "Şifre *"}</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input type="text" value={formData.sifre} onChange={(e) => setFormData({...formData, sifre: e.target.value})} className={`flex-1 ${inputClass}`} placeholder="Şifre girin veya oluşturun" />
                  <Button type="button" variant="outline" size="sm" onClick={generatePassword} className={`whitespace-nowrap shrink-0 ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""}`}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Oluştur
                  </Button>
                </div>
              </div>
              <div>
                <Label className={labelClass}>GSM</Label>
                <Input value={formData.gsm} onChange={(e) => setFormData({...formData, gsm: e.target.value})} className={`mt-1.5 ${inputClass}`} placeholder="Opsiyonel" />
              </div>
              <div>
                <Label className={labelClass}>İşe giriş tarihi{!agent ? " *" : ""}</Label>
                <Input
                  type="date"
                  value={formData.ise_giris_tarihi || ""}
                  onChange={(e) => setFormData((prev) => mergeAgentFormDateField(prev, "ise_giris_tarihi", e.target.value))}
                  className={`mt-1.5 ${inputClass}`}
                />
              </div>
              <div>
                <Label className={labelClass}>İşten çıkış tarihi</Label>
                <Input
                  type="date"
                  value={formData.isten_cikis_tarihi || ""}
                  onChange={(e) => setFormData((prev) => mergeAgentFormDateField(prev, "isten_cikis_tarihi", e.target.value))}
                  className={`mt-1.5 ${inputClass}`}
                />
                <p className={`text-[11px] mt-1 ${mutedClass}`}>Boş bırakılırsa agent hâlâ aktif personel kabul edilir.</p>
              </div>
              <div className="sm:col-span-2">
                <Label className={labelClass}>Yeni Dataya Geçiş Tarihi</Label>
                <Input
                  type="date"
                  value={formData.yeni_dataya_gecis_tarihi || ""}
                  onChange={(e) => setFormData((prev) => mergeAgentFormDateField(prev, "yeni_dataya_gecis_tarihi", e.target.value))}
                  className={`mt-1.5 max-w-xs ${inputClass}`}
                />
              </div>
            </div>
          </div>

          {agent && (
            <AgentTeamLeaderHistorySection
              apiBase={API}
              agentId={agent.id}
              companyId={formData.company_id ? parseInt(formData.company_id, 10) : (agent.company_id || userCompanyId)}
              teams={teams}
              isOpen={isOpen}
              isDark={isDark}
              labelClass={labelClass}
              mutedClass={mutedClass}
              inputClass={inputClass}
              pendingChange={teamLeaderPending}
              onPendingChange={setTeamLeaderPending}
            />
          )}

          <WorkScheduleEditor
            value={workSchedule}
            onChange={setWorkSchedule}
            isDark={isDark}
          />

          {agent && (
            <label className={`flex items-start gap-3 rounded-lg px-3 py-2.5 cursor-pointer ${isDark ? "bg-amber-500/10 border border-amber-500/20" : "bg-amber-50 border border-amber-100"}`}>
              <Checkbox
                checked={applyScheduleToAll}
                onCheckedChange={(checked) => setApplyScheduleToAll(checked === true)}
                className="mt-0.5"
              />
              <span className={`text-sm ${isDark ? "text-amber-200" : "text-amber-900"}`}>
                Bu mesai planını tüm agentlere uygula
                <span className={`block text-xs mt-0.5 ${isDark ? "text-amber-300/80" : "text-amber-700"}`}>
                  Kaydettikten sonra diğer tüm agentlerin çalışma saatleri de aynı planla güncellenir.
                </span>
              </span>
            </label>
          )}

          <div className={cardClass}>
            <div className="flex items-center justify-between gap-2">
              <p className={sectionTitleClass}><Smartphone className="w-4 h-4 text-pink-400" /> Cihazlar</p>
              <Badge variant="secondary" className={isDark ? "bg-pink-500/20 text-pink-300 border-pink-500/30" : "bg-pink-100 text-pink-700 border-pink-200"}>
                {selectedDevices.length} seçili
              </Badge>
            </div>
            <div className="relative">
              {/* Selected devices tags */}
              <div 
                className={`min-h-[42px] p-2 rounded-lg border cursor-text flex flex-wrap gap-1.5 items-center ${
                  isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300"
                } ${deviceDropdownOpen ? "ring-2 ring-pink-500/50" : ""}`}
                onClick={() => setDeviceDropdownOpen(true)}
              >
                {selectedDevices.slice(0, 3).map(deviceId => {
                  const dev = allDevices.find(d => d.id === deviceId);
                  return dev ? (
                    <span 
                      key={deviceId}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                        isDark ? "bg-pink-600/30 text-pink-300 border border-pink-500/50" : "bg-pink-100 text-pink-700 border border-pink-300"
                      }`}
                    >
                      {dev.gsm || dev.device_id?.substring(0, 8)}
                      <button 
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleDevice(deviceId); }}
                        className="hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null;
                })}
                {selectedDevices.length > 3 && (
                  <span className={`text-xs ${mutedClass}`}>+{selectedDevices.length - 3} daha</span>
                )}
                <input
                  type="text"
                  placeholder={loadingDevices ? "Yükleniyor..." : "Cihaz ara (GSM, ID)..."}
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  onFocus={() => setDeviceDropdownOpen(true)}
                  disabled={loadingDevices}
                  className={`flex-1 min-w-[120px] outline-none text-sm bg-transparent ${
                    isDark ? "text-slate-200 placeholder-slate-500" : "text-gray-800 placeholder-gray-400"
                  }`}
                />
              </div>
              
              {deviceDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDeviceDropdownOpen(false)} />
                  <div className={`absolute z-50 w-full mt-1 rounded-lg border shadow-lg max-h-48 overflow-y-auto ${
                    isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"
                  }`}>
                    {filteredDevices.length > 0 ? filteredDevices.map(dev => (
                      <div
                        key={dev.id}
                        onClick={() => toggleDevice(dev.id)}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs ${
                          selectedDevices.includes(dev.id)
                            ? isDark ? "bg-pink-600/20 text-pink-300" : "bg-pink-50 text-pink-700"
                            : isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          selectedDevices.includes(dev.id)
                            ? "bg-pink-600 border-pink-600"
                            : isDark ? "border-slate-600" : "border-gray-300"
                        }`}>
                          {selectedDevices.includes(dev.id) && <CheckSquare className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{dev.device_name || dev.device_model || '-'}</div>
                          <div className={`text-[10px] ${mutedClass}`}>
                            GSM: {dev.gsm || '-'} | ID: {dev.device_id?.substring(0, 12)}...
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className={`px-3 py-4 text-center text-xs ${mutedClass}`}>
                        {allDevices.length === 0 ? "Cihaz bulunamadı" : "Aramayla eşleşen cihaz yok"}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={cardClass}>
            <p className={sectionTitleClass}><Settings className="w-4 h-4 text-blue-400" /> Durum ve Yetki</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={labelClass}>Durum</Label>
                <Select value={formData.status.toString()} onValueChange={(v) => setFormData({...formData, status: parseInt(v)})}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="1">Aktif</SelectItem>
                    <SelectItem value="0">Pasif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelClass}>Yetki</Label>
                <Select value={formData.yetki_durumu.toString()} onValueChange={(v) => setFormData({...formData, yetki_durumu: parseInt(v)})}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="1">Kullanıcı</SelectItem>
                    <SelectItem value="2">Yönetici</SelectItem>
                    <SelectItem value="3">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className={`shrink-0 px-6 py-4 border-t gap-2 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/50"}`}>
          <Button variant="ghost" onClick={onClose} className={isDark ? "text-slate-300 hover:text-white" : ""}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white min-w-[100px]">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor</> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Team Modal
const TeamModal = ({ isOpen, onClose, teamToEdit, onSave, isDark, agents = [], panelUsers = [], companies = [], isSuperAdmin = false, userCompanyId = null }) => {
  const [formData, setFormData] = useState({ ad: "", lider_user_id: "", company_id: "" });
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (teamToEdit) {
      setFormData({
        ad: teamToEdit.ad || "",
        lider_user_id: teamToEdit.lider_user_id ? String(teamToEdit.lider_user_id) : "",
        company_id: teamToEdit.company_id ? String(teamToEdit.company_id) : (isSuperAdmin ? "all" : ""),
      });
      setSelectedAgentIds(Array.isArray(teamToEdit.agent_ids) ? [...teamToEdit.agent_ids] : []);
    } else {
      setFormData({
        ad: "",
        lider_user_id: "",
        company_id: userCompanyId ? String(userCompanyId) : (isSuperAdmin ? "all" : ""),
      });
      setSelectedAgentIds([]);
    }
    setAgentSearchTerm("");
  }, [teamToEdit, isOpen, userCompanyId, isSuperAdmin]);

  const companyFilter = formData.company_id;
  const showAllCompanies = isSuperAdmin && companyFilter === "all";

  const visibleAgents = useMemo(() => {
    const list = asAgentList(agents);
    if (showAllCompanies) return list;
    const cid = companyFilter || (userCompanyId ? String(userCompanyId) : "");
    if (!cid || cid === "all") return list;
    return list.filter((a) => String(a.company_id) === String(cid));
  }, [agents, companyFilter, userCompanyId, showAllCompanies]);

  const visibleUsers = useMemo(() => {
    const list = (panelUsers || []).filter((u) => u.status === 1 && u.is_super_admin !== 1);
    if (showAllCompanies) return list;
    const cid = companyFilter || (userCompanyId ? String(userCompanyId) : "");
    if (!cid || cid === "all") return list;
    return list.filter((u) => String(u.company_id) === String(cid));
  }, [panelUsers, companyFilter, userCompanyId, showAllCompanies]);

  const filteredAgents = useMemo(() => {
    if (!agentSearchTerm.trim()) return visibleAgents;
    const term = agentSearchTerm.toLowerCase().trim();
    return visibleAgents.filter((agent) =>
      (agent.ad_soyad && agent.ad_soyad.toLowerCase().includes(term)) ||
      (agent.kullanici_adi && agent.kullanici_adi.toLowerCase().includes(term)) ||
      (agent.gsm && agent.gsm.includes(term))
    );
  }, [visibleAgents, agentSearchTerm]);

  const toggleAgent = (agentId) => {
    setSelectedAgentIds((prev) => (
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    ));
  };

  const handleSave = async () => {
    if (!formData.ad?.trim()) {
      toast.error("Takım adı zorunludur");
      return;
    }
    if (isSuperAdmin && companyFilter !== "all" && !companyFilter) {
      toast.error("Firma seçimi zorunludur");
      return;
    }
    const saveCompanyId = companyFilter === "all"
      ? null
      : (companyFilter ? parseInt(companyFilter, 10) : (userCompanyId ? parseInt(userCompanyId, 10) : null));
    setSaving(true);
    try {
      await onSave({
        ad: formData.ad.trim(),
        lider_user_id: formData.lider_user_id ? parseInt(formData.lider_user_id, 10) : null,
        company_id: saveCompanyId,
        agent_ids: selectedAgentIds,
      }, teamToEdit?.id);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-4"
    : "rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-4";
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white focus:border-purple-500" : "bg-white border-gray-300 focus:border-purple-500";
  const sectionTitleClass = isDark ? "text-sm font-semibold text-slate-200 flex items-center gap-2" : "text-sm font-semibold text-gray-800 flex items-center gap-2";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl p-0 gap-0 flex flex-col max-h-[90vh] overflow-hidden ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
        <div className={`shrink-0 px-6 pt-6 pb-4 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-indigo-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-indigo-50 to-white"}`}>
          <DialogHeader className="space-y-0">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-indigo-500/20 ring-1 ring-indigo-500/30" : "bg-indigo-100 ring-1 ring-indigo-200"}`}>
                <GitBranch className={`w-5 h-5 ${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
              </div>
              <div>
                <DialogTitle className={`text-lg ${isDark ? "text-white" : "text-gray-900"}`}>
                  {teamToEdit ? "Takım Düzenle" : "Yeni Takım"}
                </DialogTitle>
                <p className={`text-sm mt-0.5 ${mutedClass}`}>
                  Agentları takım etiketi altında gruplayın ve takım lideri atayın
                </p>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-6 py-5 space-y-5">
          <div className={cardClass}>
            <p className={sectionTitleClass}><GitBranch className="w-4 h-4 text-indigo-400" /> Takım Bilgileri</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className={isDark ? "text-slate-300" : ""}>Takım Adı *</Label>
                <Input
                  value={formData.ad}
                  onChange={(e) => setFormData({ ...formData, ad: e.target.value })}
                  className={`mt-1.5 ${inputClass}`}
                  placeholder="Örn: Asiye'nin Takımı"
                />
              </div>
              {isSuperAdmin && (
                <div className="sm:col-span-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Firma *</Label>
                  <Select value={formData.company_id || "all"} onValueChange={(v) => setFormData({ ...formData, company_id: v, lider_user_id: "" })}>
                    <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                      <SelectItem value="all">Tümü</SelectItem>
                      {companies.filter((c) => c.status === 1).map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {showAllCompanies && (
                    <p className={`text-xs mt-1 ${mutedClass}`}>Tüm firmalardaki agentlar listelenir.</p>
                  )}
                </div>
              )}
              <div className="sm:col-span-2">
                <Label className={isDark ? "text-slate-300" : ""}>Takım Lideri (kullanıcı)</Label>
                <Select value={formData.lider_user_id || "none"} onValueChange={(v) => setFormData({ ...formData, lider_user_id: v === "none" ? "" : v })}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Lider seçin (opsiyonel)" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="none">— Seçilmedi</SelectItem>
                    {visibleUsers.map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.ad_soyad || u.kullanici_adi}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className={`text-xs mt-1 ${mutedClass}`}>Lider kullanıcıya &quot;Takım Lideri&quot; görevi verildiğinde bu takım otomatik görünür.</p>
              </div>
            </div>
          </div>

          <div className={cardClass}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={sectionTitleClass}><Users className="w-4 h-4 text-indigo-400" /> Takım Agentları</p>
                <p className={`text-xs mt-1 ${mutedClass}`}>Bir agent yalnızca bir takımda olabilir</p>
              </div>
              {selectedAgentIds.length > 0 && (
                <Badge variant="secondary" className={isDark ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30 shrink-0" : "bg-indigo-100 text-indigo-700 border-indigo-200 shrink-0"}>
                  {selectedAgentIds.length} seçili
                </Badge>
              )}
            </div>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`} />
              <Input
                placeholder="Agent ara..."
                value={agentSearchTerm}
                onChange={(e) => setAgentSearchTerm(e.target.value)}
                className={`pl-10 ${inputClass}`}
              />
            </div>
            <div className={`max-h-52 overflow-y-auto overscroll-y-contain rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-gray-200 bg-white"}`}>
              {filteredAgents.length === 0 ? (
                <p className={`text-sm text-center py-4 ${mutedClass}`}>Agent bulunamadı</p>
              ) : (
                <div className="space-y-1">
                  {filteredAgents.map((agent) => (
                    <label
                      key={agent.id}
                      className={`flex items-center gap-3 rounded-md px-2 py-2 cursor-pointer transition-colors ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"}`}
                    >
                      <Checkbox
                        checked={selectedAgentIds.includes(agent.id)}
                        onCheckedChange={() => toggleAgent(agent.id)}
                      />
                      <span
                        className={`text-sm ${agentHasLeftCompany(agent) ? agentLeftTextClass(isDark) : (isDark ? "text-slate-300" : "text-gray-700")}`}
                        title={agentHasLeftCompany(agent) ? `İşten çıkış: ${formatAgentEmploymentDate(agent.isten_cikis_tarihi)}` : undefined}
                      >
                        {agent.ad_soyad}
                        {agentHasLeftCompany(agent) && (
                          <span className={`ml-1.5 text-[10px] font-normal opacity-90 ${isDark ? "text-rose-300/80" : "text-rose-500"}`}>
                            (ayrıldı)
                          </span>
                        )}
                        {showAllCompanies && agent.company_name && (
                          <span className={`ml-2 text-xs ${mutedClass}`}>({agent.company_name})</span>
                        )}
                        {agent.team_name && agent.team_id !== teamToEdit?.id && (
                          <span className={`ml-2 text-xs ${isDark ? "text-amber-400" : "text-amber-600"}`}>({agent.team_name})</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className={`shrink-0 px-6 py-4 border-t gap-2 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/50"}`}>
          <Button variant="ghost" onClick={onClose} className={isDark ? "text-slate-300 hover:text-white" : ""}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[100px]">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor</> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// User Modal
const formatUserPhoneInput = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 10) return `0${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return digits;
  return String(value).trim();
};

/** Tablo ve listeler için tutarlı GSM gösterimi: 0533 734 28 19 */
const formatDisplayPhone = (value) => {
  if (!value) return "";
  let digits = String(value).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length === 12 && digits.startsWith("90")) digits = digits.slice(2);
  if (digits.length === 10 && digits[0] === "5") {
    return `0${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  }
  return String(value).trim();
};

const parseUserPhonesFromStorage = (gsmRaw) => {
  if (!gsmRaw) return { primary: "", extras: [] };
  const parts = String(gsmRaw).split(/[,;|]+/).map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return { primary: "", extras: [] };
  return {
    primary: formatUserPhoneInput(parts[0]),
    extras: parts.slice(1).map(formatUserPhoneInput).filter(Boolean),
  };
};

const getUserPhonesForDisplay = (user) => {
  if (user?.gsm_phones?.length) {
    return user.gsm_phones.map(formatDisplayPhone).filter(Boolean);
  }
  const parsed = parseUserPhonesFromStorage(user?.gsm || "");
  return [parsed.primary, ...parsed.extras].map(formatDisplayPhone).filter(Boolean);
};

const normalizePhoneDigits = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length === 12 && digits.startsWith("90")) digits = digits.slice(2);
  return digits;
};

const TURKISH_CHAR_MAP = {
  ç: "c", Ç: "c", ğ: "g", Ğ: "g", ı: "i", I: "i", İ: "i", i: "i",
  ö: "o", Ö: "o", ş: "s", Ş: "s", ü: "u", Ü: "u",
};

const stripTurkishChars = (value) =>
  String(value || "").replace(/[çÇğĞıIİöÖşŞüÜ]/g, (ch) => TURKISH_CHAR_MAP[ch] || ch);

/** Yalnızca A-Z, a-z, 0-9 — Türkçe karakter ve boşluk temizlenir */
const sanitizePanelUsername = (value) =>
  stripTurkishChars(value).replace(/[^a-zA-Z0-9]/g, "");

const isValidPanelUsername = (value) => /^[a-zA-Z0-9]{2,32}$/.test(String(value || ""));

const generateUsernameFromFullName = (fullName) => {
  const parts = stripTurkishChars(fullName)
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(/[^a-zA-Z0-9]/g, "").toLowerCase())
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 32);
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first}${last}`.slice(0, 32);
};

const UserPermissionsCell = ({ user, isDark, labelClass, onViewDetail }) => {
  const coreBadges = useMemo(() => getUserCorePermissionBadges(user), [user]);
  const menuBadges = useMemo(() => getUserMenuPermissionBadges(user), [user]);
  const [menuDetailOpen, setMenuDetailOpen] = useState(false);

  const coreBadgeClass = isDark
    ? "border-slate-600 text-slate-300 bg-slate-800/50"
    : "border-gray-300 text-gray-700 bg-gray-50";
  const menuBadgeClass = isDark
    ? "border-cyan-500/30 text-cyan-300 bg-cyan-500/10"
    : "border-cyan-200 text-cyan-700 bg-cyan-50";
  const adminBadgeClass = isDark
    ? "border-purple-500/30 text-purple-300 bg-purple-500/10"
    : "border-purple-200 text-purple-700 bg-purple-50";
  const legacyBadgeClass = isDark
    ? "border-slate-600 text-slate-400 bg-slate-800/30"
    : "border-gray-300 text-gray-500 bg-gray-50";

  const visibleMenuBadges = menuBadges.length > 4 ? menuBadges.slice(0, 3) : menuBadges;
  const hiddenMenuBadges = menuBadges.length > 4 ? menuBadges.slice(3) : [];

  if (coreBadges.length === 0 && menuBadges.length === 0) {
    return <span className={`text-xs italic ${labelClass}`}>—</span>;
  }

  const renderMenuBadgeClass = (badge) => {
    if (badge.id === "menu-all") return adminBadgeClass;
    if (badge.id === "menu-legacy") return legacyBadgeClass;
    return menuBadgeClass;
  };

  return (
    <div
      className={`space-y-1.5 max-w-[260px] ${onViewDetail ? "cursor-pointer rounded-lg -m-1 p-1 transition-colors hover:bg-black/5 dark:hover:bg-white/5" : ""}`}
      role={onViewDetail ? "button" : undefined}
      tabIndex={onViewDetail ? 0 : undefined}
      title={onViewDetail ? "Yetki detayını görüntüle" : undefined}
      onClick={onViewDetail ? () => onViewDetail(user) : undefined}
      onKeyDown={onViewDetail ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onViewDetail(user); } } : undefined}
    >
      <div className="flex flex-wrap gap-1">
        {coreBadges.map((badge) => (
          <Badge
            key={badge.id}
            variant="outline"
            title={badge.title}
            className={`text-[10px] h-6 px-1.5 font-normal ${badge.id === "admin-all" ? adminBadgeClass : coreBadgeClass}`}
          >
            {badge.label}
          </Badge>
        ))}
      </div>
      <div className="flex flex-wrap gap-1 items-center">
        {visibleMenuBadges.map((badge) => (
          <Badge
            key={badge.id}
            variant="outline"
            title={badge.title}
            className={`text-[10px] h-5 px-1.5 font-normal ${renderMenuBadgeClass(badge)}`}
          >
            <Layers className="w-2.5 h-2.5 mr-0.5 inline shrink-0" />
            {badge.label}
          </Badge>
        ))}
        {hiddenMenuBadges.length > 0 && (
          <Popover open={menuDetailOpen} onOpenChange={setMenuDetailOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                title={getUserMenuPermissionSummary(user)}
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-medium border transition-colors ${
                  isDark
                    ? "border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                    : "border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                }`}
              >
                +{hiddenMenuBadges.length}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className={`w-64 p-3 ${isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200"}`}
            >
              <p className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                Sayfa erişimleri
              </p>
              <div className="flex flex-wrap gap-1">
                {menuBadges.map((badge) => (
                  <Badge
                    key={badge.id}
                    variant="outline"
                    title={badge.title}
                    className={`text-[10px] h-5 px-1.5 font-normal ${renderMenuBadgeClass(badge)}`}
                  >
                    {badge.label}
                  </Badge>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

const UserModal = ({ isOpen, onClose, userToEdit, onSave, isDark, agents = [], teams = [], panelUsers = [], panelRoles = [], companies = [], isSuperAdmin = false, userCompanyId = null, currentUserId = null, canManageUsers = false }) => {
  const [formData, setFormData] = useState({ 
    ad_soyad: "", kullanici_adi: "", sifre: "", firma_adi: "", company_id: "", email: "", status: 1, 
    is_admin: 0, is_super_admin: 0, ses_kaydi_goster: 1, agent_yonetimi: 0, agent_analiz_yetkisi: 0, excel_indirme: 1, 
    gorebilecegi_agentlar: "", gorev: "", linked_agent_id: "", gsm: "", login_otp_enabled: 0, login_otp_sms: 1, login_otp_whatsapp: 1, login_otp_link_enabled: 0,
    softphone_yetkisi: 0, firma_yonetimi: 0,
  });
  const [saving, setSaving] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedExtraAgents, setSelectedExtraAgents] = useState([]);
  const [selectedTeamLeaders, setSelectedTeamLeaders] = useState([]);
  const [teamLeaderSearchTerm, setTeamLeaderSearchTerm] = useState("");
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [extraAgentSearchTerm, setExtraAgentSearchTerm] = useState("");
  const [usernameStatus, setUsernameStatus] = useState("idle");
  const usernameCheckRef = useRef(0);
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [usernameEditMode, setUsernameEditMode] = useState(false);
  const [originalUsername, setOriginalUsername] = useState("");
  const [blockAutofill, setBlockAutofill] = useState(true);
  const [extraPhones, setExtraPhones] = useState([]);
  const [newPhoneInput, setNewPhoneInput] = useState("");
  const [confirmAddOpen, setConfirmAddOpen] = useState(false);
  const [pendingPhoneToAdd, setPendingPhoneToAdd] = useState("");
  const [panelMenuPermissions, setPanelMenuPermissions] = useState(() => buildDefaultPanelMenuPermissions(1));

  const selfProfileOnly = !!(userToEdit && currentUserId && userToEdit.id === currentUserId && !canManageUsers);

  const enableInput = () => {
    if (!userToEdit) setBlockAutofill(false);
  };

  const checkUsernameAvailability = useCallback(async (username, excludeUserId = null) => {
    const trimmed = sanitizePanelUsername(username);
    if (!trimmed || !isValidPanelUsername(trimmed)) {
      setUsernameStatus("idle");
      return;
    }
    if (excludeUserId && trimmed === originalUsername) {
      setUsernameStatus("available");
      return;
    }
    const checkId = ++usernameCheckRef.current;
    setUsernameStatus("checking");
    try {
      const params = { check_username: trimmed };
      if (excludeUserId) params.exclude_id = excludeUserId;
      const res = await axios.get(`${API}/users`, { params });
      if (checkId !== usernameCheckRef.current) return;
      setUsernameStatus(res.data.available ? "available" : "taken");
    } catch {
      if (checkId === usernameCheckRef.current) setUsernameStatus("idle");
    }
  }, [originalUsername]);

  useEffect(() => {
    if (userToEdit) {
      const parsedPhones = parseUserPhonesFromStorage(userToEdit.gsm);
      setFormData({
        ...userToEdit,
        sifre: "",
        gorev: userToEdit.gorev || "",
        linked_agent_id: userToEdit.linked_agent_id ? String(userToEdit.linked_agent_id) : "",
        gsm: parsedPhones.primary,
        login_otp_enabled: userToEdit.login_otp_enabled ?? 0,
        login_otp_sms: userToEdit.login_otp_sms ?? 1,
        login_otp_whatsapp: userToEdit.login_otp_whatsapp ?? 1,
        login_otp_link_enabled: userToEdit.login_otp_link_enabled ?? 0,
        softphone_yetkisi: userToEdit.softphone_yetkisi ?? 0,
        firma_yonetimi: userToEdit.firma_yonetimi ?? 0,
        company_id: userToEdit.company_id ? String(userToEdit.company_id) : "",
        is_super_admin: userToEdit.is_super_admin ?? 0
      });
      setExtraPhones(parsedPhones.extras);
      setNewPhoneInput("");
      setPendingPhoneToAdd("");
      setConfirmAddOpen(false);
      setSelectedAgents(parseIdCsv(userToEdit.gorebilecegi_agentlar));
      setSelectedTeams(parseIdCsv(userToEdit.gorebilir_takimlar));
      setSelectedExtraAgents(parseIdCsv(userToEdit.gorebilir_agentlar));
      setSelectedTeamLeaders(parseIdCsv(userToEdit.gorebilir_takim_liderleri));
      setUsernameStatus("idle");
      setOriginalUsername(userToEdit.kullanici_adi || "");
      setUsernameEditMode(false);
      setPanelMenuPermissions(
        isPanelMenuPermissionsConfigured(userToEdit)
          ? normalizePanelMenuPermissions(userToEdit.panel_menu_permissions, { defaultValue: 0 })
          : buildDefaultPanelMenuPermissions(1)
      );
    } else {
      setFormData({ 
        ad_soyad: "", kullanici_adi: "", sifre: "", firma_adi: "", company_id: userCompanyId ? String(userCompanyId) : "", email: "", status: 1, 
        is_admin: 0, is_super_admin: 0, ses_kaydi_goster: 1, agent_yonetimi: 0, agent_analiz_yetkisi: 0, excel_indirme: 1, 
        gorebilecegi_agentlar: "", gorev: "", linked_agent_id: "", gsm: "", login_otp_enabled: 0, login_otp_sms: 1, login_otp_whatsapp: 1, login_otp_link_enabled: 0,
        softphone_yetkisi: 0, firma_yonetimi: 0,
      });
      setSelectedAgents([]);
      setSelectedTeams([]);
      setSelectedExtraAgents([]);
      setSelectedTeamLeaders([]);
      setUsernameStatus("idle");
      setOriginalUsername("");
      setUsernameEditMode(false);
      setExtraPhones([]);
      setNewPhoneInput("");
      setPendingPhoneToAdd("");
      setConfirmAddOpen(false);
      setPanelMenuPermissions(buildDefaultPanelMenuPermissions(1));
    }
    setUsernameManuallyEdited(false);
    setAgentSearchTerm("");
    setTeamSearchTerm("");
    setExtraAgentSearchTerm("");
    setTeamLeaderSearchTerm("");
    setBlockAutofill(!userToEdit);
  }, [userToEdit, isOpen, userCompanyId]);

  useEffect(() => {
    if (!isOpen) return;
    const isNewUser = !userToEdit;
    const isEditingUsername = userToEdit && usernameEditMode;
    if (!isNewUser && !isEditingUsername) return;

    const trimmed = sanitizePanelUsername(formData.kullanici_adi);
    if (!isValidPanelUsername(trimmed)) {
      setUsernameStatus("idle");
      return;
    }
    const timer = setTimeout(
      () => checkUsernameAvailability(trimmed, userToEdit?.id || null),
      450
    );
    return () => clearTimeout(timer);
  }, [formData.kullanici_adi, userToEdit, isOpen, usernameEditMode, checkUsernameAvailability]);

  const handleFullNameChange = (e) => {
    const ad_soyad = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, ad_soyad };
      if (!userToEdit && !usernameManuallyEdited) {
        next.kullanici_adi = generateUsernameFromFullName(ad_soyad);
      }
      return next;
    });
  };

  const handleUsernameChange = (e) => {
    if (userToEdit && !usernameEditMode) return;
    if (!userToEdit) setUsernameManuallyEdited(true);
    const kullanici_adi = sanitizePanelUsername(e.target.value);
    setFormData((prev) => ({ ...prev, kullanici_adi }));
  };

  const startUsernameEdit = () => {
    setUsernameEditMode(true);
    setUsernameStatus("idle");
  };

  const cancelUsernameEdit = () => {
    setFormData((prev) => ({ ...prev, kullanici_adi: originalUsername }));
    setUsernameEditMode(false);
    setUsernameStatus("idle");
  };

  const isUsernameInputEnabled = !userToEdit || usernameEditMode;
  const usernameChangedInEdit = userToEdit && usernameEditMode && formData.kullanici_adi !== originalUsername;

  const visibleAgentsForUser = useMemo(() => {
    const list = asAgentList(agents);
    if (isSuperAdmin) return list;
    if (!formData.company_id) return list;
    return list.filter((a) => String(a.company_id) === String(formData.company_id));
  }, [agents, formData.company_id, isSuperAdmin]);

  const visibleTeamsForUser = useMemo(() => {
    const list = teams || [];
    if (isSuperAdmin) return list;
    const cid = formData.company_id || userCompanyId;
    if (!cid) return list;
    return list.filter((t) => !t.company_id || String(t.company_id) === String(cid));
  }, [teams, formData.company_id, userCompanyId, isSuperAdmin]);

  const filteredTeamsForUser = useMemo(() => {
    if (!teamSearchTerm.trim()) return visibleTeamsForUser;
    const term = teamSearchTerm.toLowerCase().trim();
    return visibleTeamsForUser.filter((team) =>
      (team.ad && team.ad.toLowerCase().includes(term)) ||
      (team.lider_name && team.lider_name.toLowerCase().includes(term))
    );
  }, [visibleTeamsForUser, teamSearchTerm]);

  const filteredExtraAgentsForUser = useMemo(() => {
    if (!extraAgentSearchTerm.trim()) return visibleAgentsForUser;
    const term = extraAgentSearchTerm.toLowerCase().trim();
    return visibleAgentsForUser.filter((agent) =>
      (agent.ad_soyad && agent.ad_soyad.toLowerCase().includes(term)) ||
      (agent.kullanici_adi && agent.kullanici_adi.toLowerCase().includes(term)) ||
      (agent.gsm && agent.gsm.includes(term))
    );
  }, [visibleAgentsForUser, extraAgentSearchTerm]);

  const toggleTeamSelection = (teamId) => {
    setSelectedTeams((prev) => (
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    ));
  };

  const toggleExtraAgentSelection = (agentId) => {
    setSelectedExtraAgents((prev) => (
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    ));
  };

  const userGorev = formData.is_admin === 1 ? "" : (formData.gorev || "");
  const showLegacyAgentList = !userGorev;
  const showAsistanFields = userGorev === "asistan";
  const showCallCenterManagerFields = userGorev === "call_center_yonetici";
  const showTeamScopeFields = ["takim_lideri", "dataci", "kalite", "yonetici", "call_center_yonetici"].includes(userGorev);

  const teamLeaderUsers = useMemo(() => {
    const leaderIds = new Set((teams || []).map((t) => t.lider_user_id).filter(Boolean));
    const list = panelUsers || [];
    return list.filter((u) => u.status === 1 && u.is_admin !== 1 && (
      u.gorev === "takim_lideri" || leaderIds.has(u.id)
    ));
  }, [panelUsers, teams]);

  const filteredTeamLeadersForUser = useMemo(() => {
    if (!teamLeaderSearchTerm.trim()) return teamLeaderUsers;
    const term = teamLeaderSearchTerm.toLowerCase().trim();
    return teamLeaderUsers.filter((u) =>
      (u.ad_soyad && u.ad_soyad.toLowerCase().includes(term)) ||
      (u.kullanici_adi && u.kullanici_adi.toLowerCase().includes(term))
    );
  }, [teamLeaderUsers, teamLeaderSearchTerm]);

  const toggleTeamLeaderSelection = (userId) => {
    setSelectedTeamLeaders((prev) => (
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    ));
  };

  const applySelectedPanelRole = (roleId) => {
    if (!roleId || roleId === "none") {
      setFormData((prev) => ({ ...prev, panel_role_id: null }));
      return;
    }
    const role = panelRoles.find((r) => String(r.id) === String(roleId));
    if (!role) return;
    const draft = applyPanelRoleToUserDraft(role, formData);
    setFormData((prev) => ({
      ...prev,
      ...draft,
      panel_role_id: role.id,
      gorev: draft.gorev || prev.gorev,
    }));
    if (draft._panel_menu_permissions) {
      setPanelMenuPermissions(draft._panel_menu_permissions);
    }
    toast.success(`${role.ad} şablonu uygulandı — kapsamı ayrıca seçin`);
  };

  const filteredAgentsForUser = useMemo(() => {
    if (!agentSearchTerm.trim()) return visibleAgentsForUser;
    const term = agentSearchTerm.toLowerCase().trim();
    return visibleAgentsForUser.filter((agent) =>
      (agent.ad_soyad && agent.ad_soyad.toLowerCase().includes(term)) ||
      (agent.kullanici_adi && agent.kullanici_adi.toLowerCase().includes(term)) ||
      (agent.gsm && agent.gsm.includes(term))
    );
  }, [visibleAgentsForUser, agentSearchTerm]);

  const toggleAgentSelection = (agentId) => {
    setSelectedAgents(prev => {
      if (prev.includes(agentId)) {
        return prev.filter(id => id !== agentId);
      } else {
        return [...prev, agentId];
      }
    });
  };

  const filteredAgentIds = useMemo(() => filteredAgentsForUser.map((a) => a.id), [filteredAgentsForUser]);
  const allFilteredSelected = filteredAgentIds.length > 0 && filteredAgentIds.every((id) => selectedAgents.includes(id));
  const someFilteredSelected = filteredAgentIds.some((id) => selectedAgents.includes(id));

  const toggleSelectAllFiltered = (checked) => {
    if (checked === true) {
      setSelectedAgents((prev) => [...new Set([...prev, ...filteredAgentIds])]);
    } else {
      setSelectedAgents((prev) => prev.filter((id) => !filteredAgentIds.includes(id)));
    }
  };

  const clearAgentSelection = () => setSelectedAgents([]);

  const previewScopeAgentIds = useMemo(() => {
    if (formData.is_admin === 1 || !showTeamScopeFields) return new Set();
    const ids = new Set();

    const addTeamAgents = (team) => {
      if (!team) return;
      const teamAgentIds = team.agent_ids
        || (Array.isArray(team.agents) ? team.agents.map((a) => a.id) : []);
      teamAgentIds.forEach((id) => ids.add(Number(id)));
    };

    if (showCallCenterManagerFields) {
      selectedTeamLeaders.forEach((leaderId) => {
        (teams || []).filter((t) => Number(t.lider_user_id) === Number(leaderId)).forEach(addTeamAgents);
      });
    }

    if (userGorev === "takim_lideri" && userToEdit?.id) {
      (teams || []).filter((t) => Number(t.lider_user_id) === Number(userToEdit.id)).forEach(addTeamAgents);
    }

    selectedTeams.forEach((teamId) => {
      addTeamAgents((teams || []).find((t) => Number(t.id) === Number(teamId)));
    });
    selectedExtraAgents.forEach((id) => ids.add(Number(id)));

    return ids;
  }, [
    formData.is_admin, showTeamScopeFields, showCallCenterManagerFields, userGorev, userToEdit?.id,
    selectedTeamLeaders, selectedTeams, selectedExtraAgents, teams,
  ]);

  const previewScopeAgents = useMemo(() => {
    if (previewScopeAgentIds.size === 0) return [];
    return visibleAgentsForUser.filter((a) => previewScopeAgentIds.has(Number(a.id)));
  }, [previewScopeAgentIds, visibleAgentsForUser]);

  const allUserPhones = useMemo(() => {
    const list = [];
    const pushUnique = (value) => {
      const formatted = formatUserPhoneInput(value);
      const digits = normalizePhoneDigits(formatted);
      if (digits.length !== 10 || !digits.startsWith("5")) return;
      if (!list.some((item) => item.digits === digits)) {
        list.push({ digits, display: formatted });
      }
    };
    pushUnique(formData.gsm);
    extraPhones.forEach(pushUnique);
    return list;
  }, [formData.gsm, extraPhones]);

  const requestAddPhone = () => {
    const formatted = formatUserPhoneInput(newPhoneInput);
    const digits = normalizePhoneDigits(formatted);
    if (digits.length !== 10 || !digits.startsWith("5")) {
      toast.error("Geçerli bir GSM numarası girin (05xx xxx xx xx)");
      return;
    }
    if (allUserPhones.some((item) => item.digits === digits)) {
      toast.error("Bu numara zaten kayıtlı");
      return;
    }
    setPendingPhoneToAdd(formatted);
    setConfirmAddOpen(true);
  };

  const confirmAddPhone = () => {
    if (!pendingPhoneToAdd) return;
    setExtraPhones((prev) => [...prev, pendingPhoneToAdd]);
    setNewPhoneInput("");
    setPendingPhoneToAdd("");
    setConfirmAddOpen(false);
    toast.success("Numara eklendi");
  };

  const removeExtraPhone = (phone) => {
    setExtraPhones((prev) => prev.filter((item) => item !== phone));
  };

  const buildGsmPayload = () => allUserPhones.map((item) => item.display).join(",");

  const handleSave = async () => {
    if (!formData.ad_soyad || !formData.kullanici_adi || (!userToEdit && !formData.sifre)) {
      toast.error("Zorunlu alanları doldurun");
      return;
    }
    if ((!userToEdit || usernameChangedInEdit) && usernameStatus === "taken") {
      toast.error("Bu kullanıcı adı zaten kullanılıyor");
      return;
    }
    if ((!userToEdit || usernameChangedInEdit) && usernameStatus === "checking") {
      toast.error("Kullanıcı adı kontrol ediliyor, lütfen bekleyin");
      return;
    }
    if ((!userToEdit || usernameChangedInEdit) && !isValidPanelUsername(formData.kullanici_adi)) {
      toast.error("Kullanıcı adı yalnızca harf ve rakam içerebilir (en az 2 karakter)");
      return;
    }
    if (isSuperAdmin && !formData.is_super_admin && !formData.company_id) {
      toast.error("Firma seçimi zorunludur");
      return;
    }
    if (formData.login_otp_enabled && allUserPhones.length === 0) {
      toast.error("OTP açıkken en az bir GSM numarası zorunludur");
      return;
    }
    if (formData.login_otp_enabled && !formData.login_otp_sms && !formData.login_otp_whatsapp) {
      toast.error("En az bir OTP yöntemi seçin (SMS veya WhatsApp)");
      return;
    }
    if (formData.firma_yonetimi === 1) {
      const firmaCompanyId = formData.is_super_admin ? null : (formData.company_id || userCompanyId);
      if (!firmaCompanyId) {
        toast.error("Firma yönetimi için firma seçimi zorunludur");
        return;
      }
    }
    const gorev = formData.is_admin === 1 ? null : (formData.gorev || null);
    if (gorev === "asistan" && !formData.linked_agent_id) {
      toast.error("Asistan görevi için bağlı agent seçin");
      return;
    }
    setSaving(true);
    try {
      const selectedCompany = companies.find((c) => String(c.id) === String(formData.company_id));
      let gorebilecegi_agentlar = "";
      let gorebilir_takimlar = "";
      let gorebilir_agentlar = "";
      let linked_agent_id = null;

      if (!gorev) {
        gorebilecegi_agentlar = selectedAgents.length > 0 ? selectedAgents.join(",") : "";
      } else if (gorev === "asistan") {
        linked_agent_id = parseInt(formData.linked_agent_id, 10);
      } else if (showTeamScopeFields) {
        gorebilir_takimlar = selectedTeams.length > 0 ? selectedTeams.join(",") : "";
        gorebilir_agentlar = selectedExtraAgents.length > 0 ? selectedExtraAgents.join(",") : "";
      }

      const dataToSave = {
        ...formData,
        gsm: buildGsmPayload(),
        company_id: formData.is_super_admin ? null : (formData.company_id ? parseInt(formData.company_id, 10) : userCompanyId),
        firma_adi: formData.is_super_admin ? (formData.firma_adi || "Sistem Yönetimi") : (selectedCompany?.ad || formData.firma_adi),
        gorev,
        linked_agent_id,
        gorebilir_takimlar,
        gorebilir_agentlar,
        gorebilir_takim_liderleri: showCallCenterManagerFields && selectedTeamLeaders.length > 0 ? selectedTeamLeaders.join(",") : "",
        gorebilecegi_agentlar,
        panel_role_id: formData.panel_role_id || null,
      };
      if (formData.is_admin !== 1) {
        dataToSave.panel_menu_permissions = finalizePanelMenuPermissions(panelMenuPermissions);
      } else {
        delete dataToSave.panel_menu_permissions;
      }
      if (usernameChangedInEdit) {
        dataToSave.kullanici_adi = sanitizePanelUsername(formData.kullanici_adi);
      } else if (userToEdit) {
        delete dataToSave.kullanici_adi;
      }
      await onSave(dataToSave, userToEdit?.id);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-4"
    : "rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-4";
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white focus:border-purple-500" : "bg-white border-gray-300 focus:border-purple-500";
  const sectionTitleClass = isDark ? "text-sm font-semibold text-slate-200 flex items-center gap-2" : "text-sm font-semibold text-gray-800 flex items-center gap-2";
  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";

  const usernameHint = () => {
    const username = formData.kullanici_adi || "";
    if (userToEdit && !usernameEditMode) {
      return <span className={mutedClass}>Değiştirmek için &quot;Değiştir&quot;ye tıklayın</span>;
    }
    if (username && !isValidPanelUsername(username)) {
      return <span className="text-amber-400">Yalnızca A-Z, a-z ve 0-9 (en az 2 karakter)</span>;
    }
    if (usernameStatus === "checking") return <span className="text-blue-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Kontrol ediliyor...</span>;
    if (usernameStatus === "available" && usernameChangedInEdit) {
      return <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Kullanılabilir</span>;
    }
    if (usernameStatus === "available" && !userToEdit) {
      return <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Kullanılabilir</span>;
    }
    if (usernameStatus === "taken") return <span className="text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Bu kullanıcı adı zaten kayıtlı</span>;
    if (username.length === 1) return <span className={mutedClass}>En az 2 karakter girin</span>;
    if (!userToEdit && !usernameManuallyEdited && username) {
      return <span className={mutedClass}>Ad soyaddan otomatik oluşturuldu — isterseniz değiştirebilirsiniz</span>;
    }
    if (userToEdit && usernameEditMode && !usernameChangedInEdit) {
      return <span className={mutedClass}>Mevcut kullanıcı adı — değiştirmek için yeni ad girin</span>;
    }
    return <span className={mutedClass}>Boşluk ve Türkçe karakter kullanılamaz</span>;
  };

  const permissions = [
    { key: "ses_kaydi_goster", label: "Ses Kayıtlarını Görüntüleyebilir", default: 1 },
    { key: "arama_yetkisi", label: "GSM Arama Yapabilir", default: 1 },
    { key: "agent_analiz_yetkisi", label: "Agent Analizi Görüntüleyebilir", default: 0 },
    { key: "agent_yonetimi", label: "Agent Yönetimi Yapabilir", default: 0 },
    { key: "firma_yonetimi", label: "Firma Yönetimi (tüm agent/veri + firma ayarları)", default: 0 },
    { key: "excel_indirme", label: "Excel İndirme Yapabilir", default: 1 },
    { key: "softphone_yetkisi", label: "Softphone Kullanabilir", default: 0 },
  ];

  const applyMenuPreset = (presetKey) => {
    const preset = PANEL_MENU_PRESETS[presetKey];
    if (!preset) return;
    setPanelMenuPermissions(normalizePanelMenuPermissions(preset.permissions, { defaultValue: 0 }));
    toast.success(`${preset.label} sayfa erişim şablonu uygulandı`);
  };

  const togglePanelMenuPermission = (key, checked) => {
    setPanelMenuPermissions((prev) => applyPanelMenuPermissionToggle(prev, key, checked));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl w-[calc(100%-2rem)] p-0 gap-0 !flex !flex-col max-h-[90dvh] overflow-hidden top-[4dvh] translate-y-0 ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
        <div className={`shrink-0 px-6 pt-6 pb-4 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-purple-950/40 to-slate-900" : "border-gray-100 bg-gradient-to-r from-purple-50 to-white"}`}>
          <DialogHeader className="space-y-0">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-purple-500/20 ring-1 ring-purple-500/30" : "bg-purple-100 ring-1 ring-purple-200"}`}>
                <User className={`w-5 h-5 ${isDark ? "text-purple-400" : "text-purple-600"}`} />
              </div>
              <div>
                <DialogTitle className={`text-lg ${isDark ? "text-white" : "text-gray-900"}`}>
                  {userToEdit ? (selfProfileOnly ? "Profilim" : "Kullanıcı Düzenle") : "Yeni Kullanıcı"}
                </DialogTitle>
                <p className={`text-sm mt-0.5 ${mutedClass}`}>
                  {userToEdit
                    ? (selfProfileOnly ? "Ad, iletişim ve şifre bilgilerinizi güncelleyin" : "Kullanıcı bilgilerini ve yetkilerini güncelleyin")
                    : "Panel erişimi için yeni kullanıcı oluşturun"}
                </p>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-6 py-5">
        <form
          key={userToEdit ? `edit-${userToEdit.id}` : `new-user-${isOpen}`}
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
          className="space-y-5"
        >
          {/* Tarayıcı otomatik doldurmayı tuzağa düşür */}
          <input type="text" name="fake-user" autoComplete="username" tabIndex={-1} aria-hidden="true" className="sr-only" defaultValue="" />
          <input type="password" name="fake-pass" autoComplete="current-password" tabIndex={-1} aria-hidden="true" className="sr-only" defaultValue="" />

          <div className={cardClass}>
            <p className={sectionTitleClass}><User className="w-4 h-4 text-purple-400" /> Temel Bilgiler</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 sm:grid sm:grid-cols-2 sm:gap-4">
                <div>
                  <Label className={isDark ? "text-slate-300" : ""}>Ad Soyad *</Label>
                  <Input
                    name="user-modal-ad-soyad"
                    value={formData.ad_soyad}
                    onChange={handleFullNameChange}
                    onFocus={enableInput}
                    readOnly={!userToEdit && blockAutofill}
                    className={`mt-1.5 ${inputClass}`}
                    placeholder="Ad Soyad"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <Label className={isDark ? "text-slate-300" : ""}>Kullanıcı Adı *</Label>
                    {userToEdit && !selfProfileOnly && !usernameEditMode && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={startUsernameEdit}
                        className={`h-7 px-2 text-xs ${isDark ? "text-purple-300 hover:bg-purple-500/10" : "text-purple-600 hover:bg-purple-50"}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Değiştir
                      </Button>
                    )}
                    {userToEdit && usernameEditMode && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={cancelUsernameEdit}
                        className={`h-7 px-2 text-xs ${isDark ? "text-slate-400 hover:bg-slate-800" : "text-gray-500 hover:bg-gray-100"}`}
                      >
                        İptal
                      </Button>
                    )}
                  </div>
                  <div className="relative mt-1.5">
                    <Input
                      name="user-modal-kullanici-adi"
                      value={formData.kullanici_adi}
                      onChange={handleUsernameChange}
                      onFocus={enableInput}
                      readOnly={!userToEdit && blockAutofill}
                      onBlur={() => {
                        if (isUsernameInputEnabled && isValidPanelUsername(formData.kullanici_adi)) {
                          checkUsernameAvailability(formData.kullanici_adi, userToEdit?.id || null);
                        }
                      }}
                      className={`pr-10 ${inputClass} ${usernameStatus === "taken" ? "border-red-500 focus:border-red-500" : ""} ${usernameStatus === "available" && (usernameChangedInEdit || !userToEdit) ? "border-emerald-500 focus:border-emerald-500" : ""} ${formData.kullanici_adi && !isValidPanelUsername(formData.kullanici_adi) && isUsernameInputEnabled ? "border-amber-500 focus:border-amber-500" : ""} ${userToEdit && !usernameEditMode ? (isDark ? "bg-slate-800/60 text-slate-400 cursor-default" : "bg-gray-100 text-gray-500 cursor-default") : ""}`}
                      placeholder="kullaniciadi"
                      disabled={!isUsernameInputEnabled}
                      autoComplete="off"
                      inputMode="text"
                      spellCheck={false}
                      maxLength={32}
                    />
                    {isUsernameInputEnabled && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        {usernameStatus === "checking" && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                        {usernameStatus === "available" && (usernameChangedInEdit || !userToEdit) && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                        {usernameStatus === "taken" && <XCircle className="w-4 h-4 text-red-400" />}
                      </div>
                    )}
                  </div>
                  {!selfProfileOnly && (
                    <p className={`text-xs mt-1 min-h-[1rem] ${mutedClass}`}>{usernameHint()}</p>
                  )}
                </div>
              </div>
              <div>
                <Label className={isDark ? "text-slate-300" : ""}>{userToEdit ? "Yeni Şifre" : "Şifre *"}</Label>
                <Input
                  name="user-modal-sifre"
                  type="password"
                  value={formData.sifre}
                  onChange={(e) => setFormData({...formData, sifre: e.target.value})}
                  onFocus={enableInput}
                  readOnly={!userToEdit && blockAutofill}
                  className={`mt-1.5 ${inputClass}`}
                  placeholder={userToEdit ? "Değiştirmek için girin" : "••••••••"}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <Label className={isDark ? "text-slate-300" : ""}><Mail className="w-3 h-3 inline mr-1" />Email</Label>
                <Input
                  name="user-modal-email"
                  value={formData.email || ""}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  onFocus={enableInput}
                  readOnly={!userToEdit && blockAutofill}
                  className={`mt-1.5 ${inputClass}`}
                  placeholder="ornek@firma.com"
                  type="email"
                  autoComplete="off"
                />
              </div>
              {!selfProfileOnly && (
              <>
              <div className="sm:col-span-2">
                <Label className={isDark ? "text-slate-300" : ""}><Building2 className="w-3 h-3 inline mr-1" />Firma *</Label>
                {isSuperAdmin ? (
                  <>
                    <Select
                      value={formData.is_super_admin ? "super" : (formData.company_id || "")}
                      onValueChange={(v) => {
                        if (v === "super") {
                          setFormData({ ...formData, is_super_admin: 1, company_id: "", firma_adi: "Sistem Yönetimi" });
                        } else {
                          const company = companies.find((c) => String(c.id) === v);
                          setFormData({ ...formData, is_super_admin: 0, company_id: v, firma_adi: company?.ad || "" });
                        }
                      }}
                    >
                      <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="super">Süper Admin (Tüm Firmalar)</SelectItem>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className={`text-xs mt-1.5 ${mutedClass}`}>Header&apos;da görünecek panel adı firma seçimine göre belirlenir</p>
                  </>
                ) : (
                  <Input
                    name="user-modal-firma"
                    value={companies.find((c) => String(c.id) === String(userCompanyId))?.ad || formData.firma_adi || ""}
                    className={`mt-1.5 ${inputClass}`}
                    disabled
                    autoComplete="off"
                    readOnly
                  />
                )}
              </div>
              <div>
                <Label className={isDark ? "text-slate-300" : ""}>Durum</Label>
                <Select value={(formData.status ?? 1).toString()} onValueChange={(v) => setFormData({...formData, status: parseInt(v)})}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="1">Aktif</SelectItem>
                    <SelectItem value="0">Pasif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={isDark ? "text-slate-300" : ""}>Admin</Label>
                <Select value={(formData.is_admin ?? 0).toString()} onValueChange={(v) => setFormData({...formData, is_admin: parseInt(v)})}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="0">Hayır</SelectItem>
                    <SelectItem value="1">Evet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </>
              )}
            </div>
          </div>

          <div className={cardClass}>
            <p className={sectionTitleClass}><Shield className="w-4 h-4 text-blue-400" /> OTP ile Giriş (2FA)</p>
            <p className={`text-xs mb-3 ${mutedClass}`}>Açıldığında kullanıcı şifre sonrası SMS veya WhatsApp ile doğrulama kodu alır.</p>
            <div className="space-y-4">
              <div>
                <Label className={isDark ? "text-slate-300" : ""}><Smartphone className="w-3 h-3 inline mr-1" />Ana GSM {formData.login_otp_enabled ? "*" : ""}</Label>
                <Input
                  value={formData.gsm || ""}
                  onChange={(e) => setFormData({ ...formData, gsm: e.target.value })}
                  className={`mt-1.5 ${inputClass}`}
                  placeholder="0532 123 45 67"
                  type="tel"
                />
                <p className={`text-[11px] mt-1 ${mutedClass}`}>Tek numara varsa yalnızca bu alanı doldurmanız yeterli.</p>
              </div>

              {extraPhones.length > 0 && (
                <div className="space-y-2">
                  <Label className={isDark ? "text-slate-300" : ""}>Ek OTP Numaraları</Label>
                  {extraPhones.map((phone) => (
                    <div key={phone} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-gray-200 bg-white"}`}>
                      <span className={`font-mono text-sm ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>{phone}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeExtraPhone(phone)} className="h-8 px-2 text-rose-500 hover:text-rose-600 hover:bg-rose-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className={`rounded-lg border p-3 space-y-2 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-white/80"}`}>
                <Label className={isDark ? "text-slate-300" : ""}>Numara Ekle</Label>
                <div className="flex gap-2">
                  <Input
                    value={newPhoneInput}
                    onChange={(e) => setNewPhoneInput(e.target.value)}
                    className={inputClass}
                    placeholder="05xx xxx xx xx"
                    type="tel"
                  />
                  <Button type="button" variant="outline" onClick={requestAddPhone} className={`shrink-0 ${isDark ? "border-slate-700" : ""}`}>
                    <Plus className="w-4 h-4 mr-1" />
                    Ekle
                  </Button>
                </div>
                <p className={`text-[11px] ${mutedClass}`}>Birden fazla numara varsa ekleyin. Girişte kullanıcı hangi numaraya kod gideceğini seçer.</p>
              </div>

              <AlertDialog open={confirmAddOpen} onOpenChange={setConfirmAddOpen}>
                <AlertDialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : ""}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Numara eklensin mi?</AlertDialogTitle>
                    <AlertDialogDescription className={isDark ? "text-slate-400" : ""}>
                      <span className="font-mono font-semibold text-foreground">{pendingPhoneToAdd}</span> numarası OTP listesine eklenecek. Kabul ediyor musunuz?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className={isDark ? "border-slate-700" : ""}>Vazgeç</AlertDialogCancel>
                    <AlertDialogAction onClick={confirmAddPhone} className="bg-cyan-600 hover:bg-cyan-700">Evet, ekle</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <label className={`flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer ${isDark ? "hover:bg-slate-800/80" : "hover:bg-white"}`}>
                <Checkbox
                  checked={formData.login_otp_enabled === 1}
                  onCheckedChange={(c) => setFormData({ ...formData, login_otp_enabled: c ? 1 : 0 })}
                />
                <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>OTP ile giriş zorunlu</span>
              </label>
              {formData.login_otp_enabled === 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-1">
                  <label className={`flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer ${isDark ? "hover:bg-slate-800/80" : "hover:bg-white"}`}>
                    <Checkbox
                      checked={formData.login_otp_sms === 1}
                      onCheckedChange={(c) => setFormData({ ...formData, login_otp_sms: c ? 1 : 0 })}
                    />
                    <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>SMS ile doğrulama</span>
                  </label>
                  <label className={`flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer ${isDark ? "hover:bg-slate-800/80" : "hover:bg-white"}`}>
                    <Checkbox
                      checked={formData.login_otp_whatsapp === 1}
                      onCheckedChange={(c) => setFormData({ ...formData, login_otp_whatsapp: c ? 1 : 0 })}
                    />
                    <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>WhatsApp ile doğrulama</span>
                  </label>
                  {canManageUsers && !selfProfileOnly && (
                    <label className={`flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer sm:col-span-2 ${isDark ? "hover:bg-slate-800/80" : "hover:bg-white"}`}>
                      <Checkbox
                        checked={formData.login_otp_link_enabled === 1}
                        onCheckedChange={(c) => setFormData({ ...formData, login_otp_link_enabled: c ? 1 : 0 })}
                      />
                      <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>SMS/WhatsApp hızlı giriş linki (mobil onay)</span>
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>

          {!selfProfileOnly && (
          <>
          <div className={cardClass}>
            <p className={sectionTitleClass}><Shield className="w-4 h-4 text-purple-400" /> Yetkiler</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {permissions.map(({ key, label, default: def }) => (
                <label
                  key={key}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${isDark ? "hover:bg-slate-800/80" : "hover:bg-white"}`}
                >
                  <Checkbox
                    checked={(formData[key] ?? def) === 1}
                    onCheckedChange={(c) => setFormData({...formData, [key]: c ? 1 : 0})}
                  />
                  <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>{label}</span>
                </label>
              ))}
            </div>
            {formData.firma_yonetimi === 1 && (
              <p className={`text-xs mt-3 rounded-lg px-3 py-2 ${isDark ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/20" : "bg-cyan-50 text-cyan-800 border border-cyan-100"}`}>
                Firma yönetimi: kullanıcı yalnızca atandığı firmadaki tüm agentları, liderleri ve verileri görür; CRM, transkript ve webhook ayarlarını düzenleyebilir. Firma atanmış olmalıdır.
              </p>
            )}
          </div>

          {formData.is_admin !== 1 && !selfProfileOnly && panelRoles.length > 0 && (
          <div className={cardClass}>
            <p className={sectionTitleClass}><Shield className="w-4 h-4 text-indigo-400" /> Yetki Şablonu (Rol)</p>
            <p className={`text-xs mb-3 ${mutedClass}`}>
              Hazır rol seçin; sayfa erişimi ve ana yetkiler forma uygulanır. İsterseniz sonradan tek tek değiştirebilirsiniz.
            </p>
            <Select
              value={formData.panel_role_id ? String(formData.panel_role_id) : "none"}
              onValueChange={(v) => applySelectedPanelRole(v)}
            >
              <SelectTrigger className={inputClass}><SelectValue placeholder="Rol seçin" /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="none">— Rol atanmadı</SelectItem>
                {panelRoles.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)}>{role.ad}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}

          {formData.is_admin !== 1 && (
          <div className={cardClass}>
            <p className={sectionTitleClass}><Layers className="w-4 h-4 text-cyan-400" /> Sayfa Erişim Yetkileri</p>
            <p className={`text-xs mb-3 ${mutedClass}`}>
              Ana yetkilere ek olarak menü ve alt sayfa görünürlüğünü belirleyin. Kayıtlı olmayan eski kullanıcılar tüm sayfaları görür.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(PANEL_MENU_PRESETS).map(([key, preset]) => (
                <Button
                  key={key}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyMenuPreset(key)}
                  className={isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div className="space-y-4">
              {PANEL_MENU_PERMISSION_GROUPS.map((group) => (
                <div key={group.id} className={`rounded-lg border p-3 ${isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-200 bg-white/80"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{group.label}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {group.items.map(({ key, label, note }) => (
                      <label
                        key={key}
                        className={`flex items-start gap-3 rounded-lg px-2 py-2 cursor-pointer ${isDark ? "hover:bg-slate-800/80" : "hover:bg-gray-50"}`}
                      >
                        <Checkbox
                          checked={panelMenuPermissions[key] === 1}
                          onCheckedChange={(c) => togglePanelMenuPermission(key, c === true)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className={`block text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>{label}</span>
                          {note && <span className={`block text-[11px] mt-0.5 ${mutedClass}`}>{note}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {formData.is_admin !== 1 && (
          <div className={cardClass}>
            <p className={sectionTitleClass}><Shield className="w-4 h-4 text-indigo-400" /> Görev ve Görünürlük</p>
            <div>
              <Label className={isDark ? "text-slate-300" : ""}>Görev</Label>
              <Select value={formData.gorev || "legacy"} onValueChange={(v) => setFormData({ ...formData, gorev: v === "legacy" ? "" : v, linked_agent_id: v === "asistan" ? formData.linked_agent_id : "" })}>
                <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  {GOREV_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value || "legacy"} value={opt.value || "legacy"}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showAsistanFields && (
              <div>
                <Label className={isDark ? "text-slate-300" : ""}>Bağlı Agent *</Label>
                <Select value={formData.linked_agent_id || ""} onValueChange={(v) => setFormData({ ...formData, linked_agent_id: v })}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`}><SelectValue placeholder="Agent seçin" /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    {visibleAgentsForUser.map((agent) => (
                      <SelectItem key={agent.id} value={String(agent.id)}>{agent.ad_soyad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className={`text-xs mt-1 ${mutedClass}`}>Asistan yalnızca bağlı olduğu agent kayıtlarını görür.</p>
              </div>
            )}

            {userGorev === "takim_lideri" && (
              <p className={`text-xs rounded-lg px-3 py-2 ${isDark ? "bg-indigo-500/10 text-indigo-300 border border-indigo-500/20" : "bg-indigo-50 text-indigo-800 border border-indigo-100"}`}>
                Lideri olduğu takımlar otomatik dahil edilir. Aşağıdan ek takım veya tekil agent ekleyebilirsiniz.
              </p>
            )}
            {showCallCenterManagerFields && (
              <p className={`text-xs rounded-lg px-3 py-2 ${isDark ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/20" : "bg-cyan-50 text-cyan-800 border border-cyan-100"}`}>
                Seçtiğiniz takım liderlerinin takımlarındaki agentlar otomatik görünür. Ek takım, lider veya agent ekleyebilirsiniz.
              </p>
            )}
          </div>
          )}

          {showCallCenterManagerFields && formData.is_admin !== 1 && (
          <div className={cardClass}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={sectionTitleClass}><Users className="w-4 h-4 text-cyan-400" /> Görülecek Takım Liderleri</p>
                <p className={`text-xs mt-1 ${mutedClass}`}>Seçilen liderlerin takımlarındaki agentlar otomatik kapsama girer</p>
              </div>
              {selectedTeamLeaders.length > 0 && (
                <Badge variant="secondary" className={isDark ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/30 shrink-0" : "bg-cyan-100 text-cyan-700 border-cyan-200 shrink-0"}>
                  {selectedTeamLeaders.length} lider
                </Badge>
              )}
            </div>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`} />
              <Input
                placeholder="Takım lideri ara..."
                value={teamLeaderSearchTerm}
                onChange={(e) => setTeamLeaderSearchTerm(e.target.value)}
                className={`pl-10 ${inputClass}`}
              />
            </div>
            <div className={`max-h-44 overflow-y-auto rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-gray-200 bg-white"}`}>
              {filteredTeamLeadersForUser.length === 0 ? (
                <p className={`text-sm text-center py-4 ${mutedClass}`}>Takım lideri bulunamadı</p>
              ) : (
                <div className="space-y-1">
                  {filteredTeamLeadersForUser.map((leader) => (
                    <label
                      key={leader.id}
                      className={`flex items-center gap-3 rounded-md px-2 py-2 cursor-pointer transition-colors ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"}`}
                    >
                      <Checkbox
                        checked={selectedTeamLeaders.includes(leader.id)}
                        onCheckedChange={() => toggleTeamLeaderSelection(leader.id)}
                      />
                      <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                        {leader.ad_soyad}
                        <span className={`text-xs ml-1 ${mutedClass}`}>({leader.kullanici_adi})</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {showLegacyAgentList && formData.is_admin !== 1 && (
          <div className={cardClass}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={sectionTitleClass}><Users className="w-4 h-4 text-purple-400" /> Görebileceği Agentlar</p>
                <p className={`text-xs mt-1 ${mutedClass}`}>Hiçbiri seçilmezse tüm agentları görebilir (eski sistem)</p>
              </div>
              {selectedAgents.length > 0 && (
                <Badge variant="secondary" className={isDark ? "bg-purple-500/20 text-purple-300 border-purple-500/30 shrink-0" : "bg-purple-100 text-purple-700 border-purple-200 shrink-0"}>
                  {selectedAgents.length} seçili
                </Badge>
              )}
            </div>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`} />
              <Input
                name="user-modal-agent-ara"
                placeholder="Agent ara (ad, kullanıcı adı, GSM)..."
                value={agentSearchTerm}
                onChange={(e) => setAgentSearchTerm(e.target.value)}
                onFocus={enableInput}
                readOnly={!userToEdit && blockAutofill}
                className={`pl-10 ${inputClass}`}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-form-type="other"
              />
            </div>
            {filteredAgentsForUser.length > 0 && (
              <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 ${isDark ? "bg-slate-800/50" : "bg-gray-100/80"}`}>
                <label className={`flex items-center gap-2.5 cursor-pointer select-none ${isDark ? "hover:text-white" : "hover:text-gray-900"}`}>
                  <Checkbox
                    checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                    onCheckedChange={toggleSelectAllFiltered}
                  />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                    {agentSearchTerm.trim() ? "Arama sonuçlarının tümünü seç" : "Tümünü seç"}
                    <span className={`ml-1 font-normal ${mutedClass}`}>({filteredAgentsForUser.length})</span>
                  </span>
                </label>
                {selectedAgents.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAgentSelection}
                    className={`text-xs font-medium shrink-0 transition-colors ${isDark ? "text-slate-400 hover:text-red-400" : "text-gray-500 hover:text-red-600"}`}
                  >
                    Seçimi temizle
                  </button>
                )}
              </div>
            )}
            <div className={`max-h-44 overflow-y-auto rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-gray-200 bg-white"}`}>
              {visibleAgentsForUser.length === 0 ? (
                <p className={`text-sm text-center py-4 ${mutedClass}`}>Agent bulunamadı</p>
              ) : filteredAgentsForUser.length === 0 ? (
                <p className={`text-sm text-center py-4 ${mutedClass}`}>Arama sonucu bulunamadı</p>
              ) : (
                <div className="space-y-1">
                  {filteredAgentsForUser.map(agent => (
                    <label
                      key={agent.id}
                      className={`flex items-center gap-3 rounded-md px-2 py-2 cursor-pointer transition-colors ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"}`}
                    >
                      <Checkbox
                        checked={selectedAgents.includes(agent.id)}
                        onCheckedChange={() => toggleAgentSelection(agent.id)}
                      />
                      <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                        {agent.ad_soyad}{" "}
                        <span className={`text-xs ${mutedClass}`}>
                          ({agent.kullanici_adi}{agent.gsm ? ` · ${agent.gsm}` : ""})
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {showTeamScopeFields && formData.is_admin !== 1 && (
          <>
          <div className={cardClass}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={sectionTitleClass}><GitBranch className="w-4 h-4 text-indigo-400" /> Görülecek Takımlar</p>
                <p className={`text-xs mt-1 ${mutedClass}`}>Seçilen takımlardaki tüm agentlar görünür</p>
              </div>
              {selectedTeams.length > 0 && (
                <Badge variant="secondary" className={isDark ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30 shrink-0" : "bg-indigo-100 text-indigo-700 border-indigo-200 shrink-0"}>
                  {selectedTeams.length} takım
                </Badge>
              )}
            </div>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`} />
              <Input
                placeholder="Takım ara..."
                value={teamSearchTerm}
                onChange={(e) => setTeamSearchTerm(e.target.value)}
                className={`pl-10 ${inputClass}`}
              />
            </div>
            <div className={`max-h-40 overflow-y-auto rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-gray-200 bg-white"}`}>
              {filteredTeamsForUser.length === 0 ? (
                <p className={`text-sm text-center py-4 ${mutedClass}`}>Takım bulunamadı</p>
              ) : (
                <div className="space-y-1">
                  {filteredTeamsForUser.map((team) => (
                    <label
                      key={team.id}
                      className={`flex items-center gap-3 rounded-md px-2 py-2 cursor-pointer transition-colors ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"}`}
                    >
                      <Checkbox
                        checked={selectedTeams.includes(team.id)}
                        onCheckedChange={() => toggleTeamSelection(team.id)}
                      />
                      <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                        {team.ad}
                        <span className={`text-xs ml-1 ${mutedClass}`}>
                          ({team.agent_count || 0} agent{team.lider_name ? ` · ${team.lider_name}` : ""})
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={cardClass}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={sectionTitleClass}><Users className="w-4 h-4 text-indigo-400" /> Ek Tekil Agentlar</p>
                <p className={`text-xs mt-1 ${mutedClass}`}>
                  {showCallCenterManagerFields
                    ? "Lider/takım dışında ekstra görmesi gereken tekil agentlar"
                    : "Takımsız veya ek agentlar"}
                </p>
              </div>
              {selectedExtraAgents.length > 0 && (
                <Badge variant="secondary" className={isDark ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30 shrink-0" : "bg-indigo-100 text-indigo-700 border-indigo-200 shrink-0"}>
                  {selectedExtraAgents.length} seçili
                </Badge>
              )}
            </div>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedClass}`} />
              <Input
                placeholder="Agent ara..."
                value={extraAgentSearchTerm}
                onChange={(e) => setExtraAgentSearchTerm(e.target.value)}
                className={`pl-10 ${inputClass}`}
              />
            </div>
            <div className={`max-h-40 overflow-y-auto rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-900/60" : "border-gray-200 bg-white"}`}>
              {filteredExtraAgentsForUser.length === 0 ? (
                <p className={`text-sm text-center py-4 ${mutedClass}`}>Agent bulunamadı</p>
              ) : (
                <div className="space-y-1">
                  {filteredExtraAgentsForUser.map((agent) => (
                    <label
                      key={agent.id}
                      className={`flex items-center gap-3 rounded-md px-2 py-2 cursor-pointer transition-colors ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"}`}
                    >
                      <Checkbox
                        checked={selectedExtraAgents.includes(agent.id)}
                        onCheckedChange={() => toggleExtraAgentSelection(agent.id)}
                      />
                      <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                        {agent.ad_soyad}
                        {agent.team_name && (
                          <span className={`text-xs ml-1 ${mutedClass}`}>({agent.team_name})</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={`rounded-xl border px-4 py-3 ${isDark ? "border-emerald-500/25 bg-emerald-950/20" : "border-emerald-200 bg-emerald-50/80"}`}>
            <p className={`text-sm font-semibold ${isDark ? "text-emerald-300" : "text-emerald-800"}`}>
              Görünür agent özeti: {previewScopeAgents.length} agent
            </p>
            <p className={`text-xs mt-1 ${mutedClass}`}>
              {showCallCenterManagerFields
                ? "Takım lideri + takım + ek agent seçimlerinin birleşimi. Kaydettikten sonra CDR/raporlarda yalnızca bunlar listelenir."
                : userGorev === "takim_lideri"
                  ? "Lideri olduğu takımlar + ek takım/agent seçimleri."
                  : "Seçilen takım ve ek agentların birleşimi."}
            </p>
            {previewScopeAgents.length > 0 ? (
              <p className={`text-xs mt-2 leading-relaxed ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                {previewScopeAgents.slice(0, 12).map((a) => a.ad_soyad).join(", ")}
                {previewScopeAgents.length > 12 ? ` … +${previewScopeAgents.length - 12} agent daha` : ""}
              </p>
            ) : (
              <p className={`text-xs mt-2 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                Henüz agent kapsamı yok — en az bir takım lideri, takım veya ek agent seçin.
              </p>
            )}
          </div>
          </>
          )}
          </>
          )}
        </form>
        </div>

        <DialogFooter className={`shrink-0 px-6 py-4 border-t gap-2 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/50"}`}>
          <Button variant="ghost" onClick={onClose} className={isDark ? "text-slate-300 hover:text-white" : ""}>İptal</Button>
          <Button
            onClick={handleSave}
            disabled={saving || (!userToEdit && (usernameStatus === "taken" || usernameStatus === "checking"))}
            className="bg-purple-600 hover:bg-purple-700 text-white min-w-[100px]"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor</> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Company Modal (Super Admin)
const CompanyTranscriptSettingsModal = ({ isOpen, onClose, company, isDark }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    provider: "groq_gemini",
    enabled: 1,
    gemini_api_key: "",
    gemini_service_url: "https://gemini.etelekom.net",
    gemini_model: "gemini-2.5-flash",
    groq_api_key: "",
    groq_model: "whisper-large-v3-turbo",
    groq_timeout_seconds: 240,
    company_context: "",
    cozucu_api_token: "",
    cozucu_api_url: "http://3.127.243.35:8200/transcribe-uri",
    cozucu_timeout_seconds: 300,
    yeni_analiz_api_key: "",
    yeni_analiz_api_url: "https://analysis.etelekom.net",
    yeni_analiz_timeout_seconds: 300,
    has_gemini_api_key: false,
    has_groq_api_key: false,
    has_cozucu_api_token: false,
    has_yeni_analiz_api_key: false,
  });

  const buildTranscriptSettingsPayload = () => {
    const payload = {
      provider: formData.provider,
      enabled: formData.enabled,
      gemini_service_url: formData.gemini_service_url,
      gemini_model: formData.gemini_model,
      groq_model: formData.groq_model,
      groq_timeout_seconds: parseInt(formData.groq_timeout_seconds, 10) || 240,
      company_context: (formData.company_context || "").trim(),
    };
    if (formData.gemini_api_key?.trim() && !formData.gemini_api_key.includes("*")) {
      payload.gemini_api_key = formData.gemini_api_key.trim();
    }
    if (formData.groq_api_key?.trim() && !formData.groq_api_key.includes("*")) {
      payload.groq_api_key = formData.groq_api_key.trim();
    }
    if (formData.cozucu_api_token?.trim() && !formData.cozucu_api_token.includes("*")) {
      payload.cozucu_api_token = formData.cozucu_api_token.trim();
    }
    payload.cozucu_api_url = (formData.cozucu_api_url || "").trim();
    payload.cozucu_timeout_seconds = parseInt(formData.cozucu_timeout_seconds, 10) || 300;
    payload.yeni_analiz_api_url = (formData.yeni_analiz_api_url || "").trim();
    payload.yeni_analiz_timeout_seconds = parseInt(formData.yeni_analiz_timeout_seconds, 10) || 300;
    if (formData.yeni_analiz_api_key?.trim() && !formData.yeni_analiz_api_key.includes("*")) {
      payload.yeni_analiz_api_key = formData.yeni_analiz_api_key.trim();
    }
    return payload;
  };

  useEffect(() => {
    if (!isOpen || !company?.id) return;
    setLoading(true);
    setTestResult(null);
    axios.get(`${API}/companies/${company.id}/transcript-settings`)
      .then((res) => {
        setFormData({
          provider: res.data.provider || "groq_gemini",
          enabled: res.data.enabled ?? 1,
          gemini_api_key: res.data.gemini_api_key || "",
          gemini_service_url: res.data.gemini_service_url || "https://gemini.etelekom.net",
          gemini_model: res.data.gemini_model || "gemini-2.5-flash",
          groq_api_key: res.data.groq_api_key || "",
          groq_model: res.data.groq_model || "whisper-large-v3-turbo",
          groq_timeout_seconds: res.data.groq_timeout_seconds ?? 240,
          company_context: res.data.company_context || "",
          cozucu_api_token: res.data.cozucu_api_token || "",
          cozucu_api_url: res.data.cozucu_api_url || "http://3.127.243.35:8200/transcribe-uri",
          cozucu_timeout_seconds: res.data.cozucu_timeout_seconds ?? 300,
          yeni_analiz_api_key: res.data.yeni_analiz_api_key || "",
          yeni_analiz_api_url: res.data.yeni_analiz_api_url || "https://analysis.etelekom.net",
          yeni_analiz_timeout_seconds: res.data.yeni_analiz_timeout_seconds ?? 300,
          has_gemini_api_key: !!res.data.has_gemini_api_key,
          has_groq_api_key: !!res.data.has_groq_api_key,
          has_cozucu_api_token: !!res.data.has_cozucu_api_token,
          has_yeni_analiz_api_key: !!res.data.has_yeni_analiz_api_key,
        });
      })
      .catch((error) => toast.error(error.response?.data?.detail || "Transkript ayarları yüklenemedi"))
      .finally(() => setLoading(false));
  }, [isOpen, company?.id]);

  const handleSave = async () => {
    if (!company?.id) return;
    if (formData.provider === "groq_gemini") {
      if (!formData.has_groq_api_key && !formData.groq_api_key?.trim()) {
        toast.error("Groq API anahtarı zorunludur");
        return;
      }
      if (!formData.has_gemini_api_key && !formData.gemini_api_key?.trim()) {
        toast.error("Gemini API anahtarı zorunludur (analiz için)");
        return;
      }
    }
    if (formData.provider === "gemini" && !formData.has_gemini_api_key && !formData.gemini_api_key?.trim()) {
      toast.error("Gemini API anahtarı zorunludur");
      return;
    }
    if (formData.provider === "cozucu" && !formData.cozucu_api_url?.trim()) {
      toast.error("Cozucu API adresi zorunludur");
      return;
    }
    if (formData.provider === "yeni_analiz") {
      if (!formData.has_yeni_analiz_api_key && !formData.yeni_analiz_api_key?.trim()) {
        toast.error("YeniAnaliz API anahtarı zorunludur");
        return;
      }
      if (!formData.yeni_analiz_api_url?.trim()) {
        toast.error("YeniAnaliz API adresi zorunludur");
        return;
      }
    }
    setSaving(true);
    try {
      await axios.post(`${API}/companies/${company.id}/transcript-settings`, buildTranscriptSettingsPayload());
      toast.success("Transkript ayarları kaydedildi");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!company?.id) return;
    if (formData.provider === "groq_gemini") {
      if (!formData.has_groq_api_key && !formData.groq_api_key?.trim()) {
        toast.error("Test için Groq API anahtarı girin");
        return;
      }
      if (!formData.has_gemini_api_key && !formData.gemini_api_key?.trim()) {
        toast.error("Test için Gemini API anahtarı girin");
        return;
      }
    }
    if (formData.provider === "gemini" && !formData.has_gemini_api_key && !formData.gemini_api_key?.trim()) {
      toast.error("Test için Gemini API anahtarı girin veya kayıtlı anahtar olmalı");
      return;
    }
    if (formData.provider === "cozucu" && !formData.cozucu_api_url?.trim()) {
      toast.error("Test için Cozucu API adresi girin");
      return;
    }
    if (formData.provider === "yeni_analiz") {
      if (!formData.has_yeni_analiz_api_key && !formData.yeni_analiz_api_key?.trim()) {
        toast.error("Test için YeniAnaliz API anahtarı girin");
        return;
      }
      if (!formData.yeni_analiz_api_url?.trim()) {
        toast.error("Test için YeniAnaliz API adresi girin");
        return;
      }
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/companies/${company.id}/transcript-settings/test`, buildTranscriptSettingsPayload(), { timeout: 45000 });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message || "Bağlantı testi başarılı");
      } else {
        toast.error(res.data.error || res.data.message || "Bağlantı testi başarısız");
      }
    } catch (error) {
      const data = error.response?.data;
      setTestResult(data || { success: false, error: error.message });
      toast.error(data?.error || data?.detail || data?.message || "Bağlantı testi başarısız");
    } finally {
      setTesting(false);
    }
  };

  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";
  const labelClass = isDark ? "text-slate-300" : "text-gray-700";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg max-h-[90vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>
            Transkript Ayarları — {company?.ad}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-500" /></div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label className={labelClass}>Transkript Servisi</Label>
              <Select value={formData.provider} onValueChange={(v) => setFormData({ ...formData, provider: v })}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="groq_gemini">Groq Whisper + Gemini Analiz (Önerilen)</SelectItem>
                  <SelectItem value="gemini">Gemini AI (Eski — tek adım)</SelectItem>
                  <SelectItem value="cozucu">Cozucu API (Eski)</SelectItem>
                  <SelectItem value="yeni_analiz">YeniAnaliz (analysis.etelekom.net)</SelectItem>
                  <SelectItem value="none">Kapalı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelClass}>Durum</Label>
              <Select value={String(formData.enabled)} onValueChange={(v) => setFormData({ ...formData, enabled: parseInt(v, 10) })}>
                <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="1">Aktif</SelectItem>
                  <SelectItem value="0">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.provider === "groq_gemini" && (
              <>
                <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-cyan-500/20 bg-cyan-500/5" : "border-cyan-100 bg-cyan-50/60"}`}>
                  <p className={`text-xs font-medium ${isDark ? "text-cyan-300" : "text-cyan-800"}`}>1. Adım — Groq Whisper (transkript)</p>
                  <div>
                    <Label className={labelClass}>Groq API Key *</Label>
                    <Input
                      type="password"
                      value={formData.groq_api_key}
                      onChange={(e) => setFormData({ ...formData, groq_api_key: e.target.value })}
                      placeholder={formData.has_groq_api_key ? "Mevcut anahtar korunur" : "gsk_..."}
                      className={inputClass}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={labelClass}>Whisper Model</Label>
                      <Input
                        value={formData.groq_model}
                        onChange={(e) => setFormData({ ...formData, groq_model: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <Label className={labelClass}>Groq Timeout (sn)</Label>
                      <Input
                        type="number"
                        min={60}
                        max={600}
                        value={formData.groq_timeout_seconds}
                        onChange={(e) => setFormData({ ...formData, groq_timeout_seconds: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
                <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-violet-500/20 bg-violet-500/5" : "border-violet-100 bg-violet-50/60"}`}>
                  <p className={`text-xs font-medium ${isDark ? "text-violet-300" : "text-violet-800"}`}>2. Adım — Gemini 2.5 Flash (özet + analiz)</p>
                  <div>
                    <Label className={labelClass}>Gemini API Key *</Label>
                    <Input
                      type="password"
                      value={formData.gemini_api_key}
                      onChange={(e) => setFormData({ ...formData, gemini_api_key: e.target.value })}
                      placeholder={formData.has_gemini_api_key ? "Mevcut anahtar korunur" : "AIza..."}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <Label className={labelClass}>Gemini Model</Label>
                    <Input
                      value={formData.gemini_model}
                      onChange={(e) => setFormData({ ...formData, gemini_model: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <Label className={labelClass}>Firma Bağlamı / Sektör Açıklaması</Label>
                  <textarea
                    value={formData.company_context}
                    onChange={(e) => setFormData({ ...formData, company_context: e.target.value })}
                    placeholder="Örn: Özel hastane çağrı merkezi — randevu, poliklinik, acil yönlendirme. Veya: Güzellik cihazı satış ve teknik destek hattı."
                    rows={4}
                    className={`w-full rounded-md border px-3 py-2 text-sm resize-y min-h-[96px] ${inputClass}`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                    Gemini analizi bu metne göre konuşmacı ayrımı, özet ve yorum üretir. Her firma için farklı tanımlayın.
                  </p>
                </div>
              </>
            )}

            {formData.provider === "gemini" && (
              <>
                <div>
                  <Label className={labelClass}>Gemini API Key *</Label>
                  <Input
                    type="password"
                    value={formData.gemini_api_key}
                    onChange={(e) => setFormData({ ...formData, gemini_api_key: e.target.value })}
                    placeholder={formData.has_gemini_api_key ? "Mevcut anahtar korunur — değiştirmek için yeni key girin" : "AIza..."}
                    className={inputClass}
                  />
                </div>
                <div>
                  <Label className={labelClass}>Gemini Servis URL (opsiyonel)</Label>
                  <Input
                    value={formData.gemini_service_url}
                    onChange={(e) => setFormData({ ...formData, gemini_service_url: e.target.value })}
                    placeholder="https://gemini.etelekom.net"
                    className={`${inputClass} font-mono text-sm`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>Canlı servis: https://gemini.etelekom.net — boş bırakılırsa cc2 doğrudan Gemini API kullanır.</p>
                </div>
                <div>
                  <Label className={labelClass}>Model</Label>
                  <Input
                    value={formData.gemini_model}
                    onChange={(e) => setFormData({ ...formData, gemini_model: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </>
            )}

            {formData.provider === "cozucu" && (
              <>
                <div>
                  <Label className={labelClass}>Cozucu API URL *</Label>
                  <Input
                    value={formData.cozucu_api_url}
                    onChange={(e) => setFormData({ ...formData, cozucu_api_url: e.target.value })}
                    placeholder="http://3.127.243.35:8200/transcribe-uri"
                    className={`${inputClass} font-mono text-sm`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                    POST transcribe-uri — s3_uri ve language gönderilir. Worker her dakika kuyruğu işler.
                  </p>
                </div>
                <div>
                  <Label className={labelClass}>Timeout (saniye)</Label>
                  <Input
                    type="number"
                    min={60}
                    max={900}
                    value={formData.cozucu_timeout_seconds}
                    onChange={(e) => setFormData({ ...formData, cozucu_timeout_seconds: e.target.value })}
                    className={inputClass}
                  />
                  <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                    Uzun kayıtlar için 300+ önerilir (ör. 162 sn latency).
                  </p>
                </div>
                <div>
                  <Label className={labelClass}>Cozucu API Token (opsiyonel / eski API)</Label>
                  <Input
                    type="password"
                    value={formData.cozucu_api_token}
                    onChange={(e) => setFormData({ ...formData, cozucu_api_token: e.target.value })}
                    placeholder={formData.has_cozucu_api_token ? "Mevcut token korunur" : "Yeni API için gerekmez"}
                    className={inputClass}
                  />
                </div>
              </>
            )}

            {formData.provider === "yeni_analiz" && (
              <>
                <div>
                  <Label className={labelClass}>YeniAnaliz API Key *</Label>
                  <Input
                    type="password"
                    value={formData.yeni_analiz_api_key}
                    onChange={(e) => setFormData({ ...formData, yeni_analiz_api_key: e.target.value })}
                    placeholder={formData.has_yeni_analiz_api_key ? "Mevcut anahtar korunur" : "ga_..."}
                    className={`${inputClass} font-mono text-sm`}
                  />
                </div>
                <div>
                  <Label className={labelClass}>YeniAnaliz API URL *</Label>
                  <Input
                    value={formData.yeni_analiz_api_url}
                    onChange={(e) => setFormData({ ...formData, yeni_analiz_api_url: e.target.value })}
                    placeholder="https://analysis.etelekom.net"
                    className={`${inputClass} font-mono text-sm`}
                  />
                  <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                    POST /v1/calls/CALL_ID/transcribe ile analiz başlatılır, /report ile sonuç sorgulanır.
                  </p>
                </div>
                <div>
                  <Label className={labelClass}>Timeout (saniye)</Label>
                  <Input
                    type="number"
                    min={60}
                    max={900}
                    value={formData.yeni_analiz_timeout_seconds}
                    onChange={(e) => setFormData({ ...formData, yeni_analiz_timeout_seconds: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </>
            )}

            {(formData.provider === "gemini" || formData.provider === "groq_gemini" || formData.provider === "cozucu" || formData.provider === "yeni_analiz") && (
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testing || loading || saving}
                  className={isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : ""}
                >
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  {testing ? "Test ediliyor..." : "Bağlantıyı Test Et"}
                </Button>
                {testResult && (
                  <div className={`rounded-lg border p-3 text-xs space-y-2 ${testResult.success
                    ? (isDark ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-800")
                    : (isDark ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-red-200 bg-red-50 text-red-800")
                  }`}>
                    <p className="font-medium">{testResult.message || testResult.error || (testResult.success ? "Başarılı" : "Başarısız")}</p>
                    {Array.isArray(testResult.checks) && testResult.checks.map((check) => (
                      <div key={check.name} className="flex items-start gap-2">
                        {check.success
                          ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          : <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
                        <span>{check.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={handleSave} disabled={saving || loading || testing} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CompanyCrmSettingsModal = ({ isOpen, onClose, company, isDark, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState(DEFAULT_LEAD_STATUSES);
  const [sources, setSources] = useState(DEFAULT_LEAD_SOURCES);

  useEffect(() => {
    if (!isOpen || !company?.id) return;
    setLoading(true);
    axios.get(`${API}/companies/${company.id}/crm-settings`)
      .then((res) => {
        setStatuses(res.data.lead_statuses?.length ? res.data.lead_statuses : DEFAULT_LEAD_STATUSES);
        setSources(res.data.lead_sources?.length ? res.data.lead_sources : DEFAULT_LEAD_SOURCES);
      })
      .catch(() => toast.error("CRM ayarları yüklenemedi"))
      .finally(() => setLoading(false));
  }, [isOpen, company?.id]);

  const updateStatus = (index, patch) => {
    setStatuses((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const addStatus = () => {
    setStatuses((prev) => [...prev, { value: "", label: "", tone: "violet" }]);
  };

  const removeStatus = (index) => {
    setStatuses((prev) => {
      if (prev.length <= 1) return prev;
      const item = prev[index];
      if (item?.value === "yeni") return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeSource = (index) => {
    setSources((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSave = async () => {
    if (!company?.id) return;
    const cleanedStatuses = statuses.filter((s) => s.label?.trim());
    const cleanedSources = sources.filter((s) => s.label?.trim());
    if (cleanedStatuses.length === 0 || cleanedSources.length === 0) {
      toast.error("En az bir lead durumu ve kaynak gerekli");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/companies/${company.id}/crm-settings`, {
        lead_statuses: cleanedStatuses,
        lead_sources: cleanedSources,
      });
      const savedStatuses = res.data.lead_statuses || cleanedStatuses;
      const savedSources = res.data.lead_sources || cleanedSources;
      setStatuses(savedStatuses);
      setSources(savedSources);
      onSaved?.(savedStatuses, savedSources, company.id);
      toast.success("CRM ayarları kaydedildi");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";
  const labelClass = isDark ? "text-slate-300" : "text-gray-700";
  const cardClass = isDark ? "rounded-lg border border-slate-800 bg-slate-950/40 p-3" : "rounded-lg border border-gray-200 bg-gray-50/80 p-3";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={`max-w-xl max-h-[90vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>
            Müşteri CRM Ayarları · {company?.ad}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-violet-500" /></div>
        ) : (
          <div className="space-y-4 py-2">
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
              Lead durumları ve kaynak listeleri müşteri kartında kullanılır.
            </p>
            <p className={`text-xs font-semibold uppercase tracking-wider ${labelClass}`}>Lead Durumları</p>
            <div className="space-y-2">
              {statuses.map((item, index) => (
                <div key={`${item.value || "new"}-${index}`} className={`${cardClass} grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2 items-end`}>
                  <div>
                    <Label className={`text-xs ${labelClass}`}>Durum adı</Label>
                    <Input
                      value={item.label || ""}
                      onChange={(e) => updateStatus(index, { label: e.target.value })}
                      placeholder="Örn: Reçete Bekliyor"
                      className={`mt-1 ${inputClass}`}
                      disabled={item.value === "yeni"}
                    />
                  </div>
                  <div>
                    <Label className={`text-xs ${labelClass}`}>Renk</Label>
                    <Select value={item.tone || "violet"} onValueChange={(v) => updateStatus(index, { tone: v })}>
                      <SelectTrigger className={`mt-1 ${inputClass}`}><SelectValue /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        {LEAD_TONE_OPTIONS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStatus(index)}
                    disabled={item.value === "yeni" || statuses.length <= 1}
                    className={isDark ? "text-rose-400 hover:text-rose-300" : "text-rose-600"}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" onClick={addStatus} className={isDark ? "border-slate-700" : ""}>
              <Plus className="w-4 h-4 mr-2" />Durum Ekle
            </Button>

            <p className={`text-xs font-semibold uppercase tracking-wider pt-2 ${labelClass}`}>Lead Kaynakları</p>
            <div className="space-y-2">
              {sources.map((item, index) => (
                <div key={`src-${item.value || "new"}-${index}`} className={`${cardClass} grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end`}>
                  <div>
                    <Label className={`text-xs ${labelClass}`}>Kaynak adı</Label>
                    <Input
                      value={item.label || ""}
                      onChange={(e) => setSources((prev) => prev.map((row, i) => (i === index ? { ...row, label: e.target.value } : row)))}
                      placeholder="Örn: Whatsapp"
                      className={`mt-1 ${inputClass}`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSource(index)}
                    disabled={sources.length <= 1}
                    className={isDark ? "text-rose-400 hover:text-rose-300" : "text-rose-600"}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" onClick={() => setSources((prev) => [...prev, { value: "", label: "" }])} className={isDark ? "border-slate-700" : ""}>
              <Plus className="w-4 h-4 mr-2" />Kaynak Ekle
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={handleSave} disabled={saving || loading} className="bg-violet-600 hover:bg-violet-700 text-white">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TAB_IDS = ["cdr", "search", "transcripts", "customer", "crm", "agents", "reports", "special-agent-report", "special-agent-report-2", "team-leader-performance", "tl-ciro-records", "mr-excel-records", "lines", "devices", "todos", "calendar", "users", "panel-roles", "companies", "finance", "otp-settings", "recording-download-settings", "recording-errors", "webhook-logs", "sesk-panel", "work-schedules", "mr-assistant-mapping"];

const isRecordingDownloadSettingsUnlocked = (searchParams) => (
  String(searchParams?.get("ses") || "").trim().toLowerCase() === "yes"
);

const canAccessRecordingDownloadSettings = (searchParams, isSuperAdmin) => (
  isSuperAdmin && isRecordingDownloadSettingsUnlocked(searchParams)
);

// Main Dashboard
const Dashboard = ({ isDark, onToggleTheme }) => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const [searchParams] = useSearchParams();
  const customerPhoneParam = searchParams.get("phone");
  const [totpSettingsOpen, setTotpSettingsOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [cdrStagingModalOpen, setCdrStagingModalOpen] = useState(false);
  const cdrStaging = useCdrStagingBasket();
  const [headerPinned, setHeaderPinned] = useState(() => {
    const saved = localStorage.getItem("panel_header_pinned");
    return saved !== "false";
  });

  useEffect(() => {
    localStorage.setItem("panel_header_pinned", headerPinned ? "true" : "false");
  }, [headerPinned]);
  
  // CDR State
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [cdrTotalCount, setCdrTotalCount] = useState(0);
  
  // Stats Date Filter
  const [statsDateRange, setStatsDateRange] = useState("today");
  const [statsStartDate, setStatsStartDate] = useState(getTodayStr());
  const [statsEndDate, setStatsEndDate] = useState(getTodayStr());
  const [dataRange, setDataRange] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Filters - default resolved in fetchOptions (today, or yesterday if today empty)
  const [startDate, setStartDate] = useState(getTodayStr);
  const [endDate, setEndDate] = useState(getTodayStr);
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [cdrPhoneFilterInput, setCdrPhoneFilterInput] = useState("");
  const [cdrPhoneFilter, setCdrPhoneFilter] = useState("");
  const [cdrCompanyFilter, setCdrCompanyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [yonFilter, setYonFilter] = useState("all"); // all, 1 (gelen), 2 (giden)
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const cdrListSnapshotRef = useRef(null);
  const [cdrFollowUpIds, setCdrFollowUpIds] = useState(null);
  const [cdrFollowUpLabel, setCdrFollowUpLabel] = useState("");
  
  // Reset all filters to default
  const resetFilters = () => {
    const today = getTodayStr();
    setCdrFollowUpIds(null);
    setCdrFollowUpLabel("");
    cdrListSnapshotRef.current = null;
    setStatsDateRange("today");
    setStatsStartDate(today);
    setStatsEndDate(today);
    setStartDate(today);
    setEndDate(today);
    setStartTime("00:00");
    setEndTime("23:59");
    setCdrPhoneFilterInput("");
    setCdrPhoneFilter("");
    setCdrCompanyFilter("all");
    setStatusFilter("all");
    setYonFilter("all");
    setSelectedAgent(null);
    setPage(1);
  };
  
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [panelRoles, setPanelRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsSearch, setTeamsSearch] = useState("");
  const [usersSearch, setUsersSearch] = useState("");
  const [adminLoginLinkModal, setAdminLoginLinkModal] = useState({ open: false, url: "", user: null, expiresIn: 0 });
  const [adminLoginLinkLoadingId, setAdminLoginLinkLoadingId] = useState(null);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyTranscriptSettingsOpen, setCompanyTranscriptSettingsOpen] = useState(false);
  const [transcriptSettingsCompany, setTranscriptSettingsCompany] = useState(null);
  const [companyCrmSettingsOpen, setCompanyCrmSettingsOpen] = useState(false);
  const [crmSettingsCompany, setCrmSettingsCompany] = useState(null);
  const [leadStatuses, setLeadStatuses] = useState(DEFAULT_LEAD_STATUSES);
  const [leadSources, setLeadSources] = useState(DEFAULT_LEAD_SOURCES);

  // To-Do list
  const [todos, setTodos] = useState([]);
  const [todosLoading, setTodosLoading] = useState(false);
  const [todosCompanyFilter, setTodosCompanyFilter] = useState("all");
  const [todoCreateModalOpen, setTodoCreateModalOpen] = useState(false);
  const [todoDetailModalOpen, setTodoDetailModalOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [todoForm, setTodoForm] = useState({ baslik: "", detay: "", files: [] });
  const [todoSaving, setTodoSaving] = useState(false);
  const [todoDragOver, setTodoDragOver] = useState(false);
  const [todoDetailDragOver, setTodoDetailDragOver] = useState(false);
  const [draggingTodoId, setDraggingTodoId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const todoFileInputRef = useRef(null);
  const todoDetailFileInputRef = useRef(null);
  const [todoEditForm, setTodoEditForm] = useState({ baslik: "", proses: "", sonuc: "", status: "yeni" });

  // GSM Search State
  const [gsmSearchNumber, setGsmSearchNumber] = useState("");
  const [gsmSearchStartDate, setGsmSearchStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [gsmSearchEndDate, setGsmSearchEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [gsmSearchResults, setGsmSearchResults] = useState([]);
  const [gsmSearchLoading, setGsmSearchLoading] = useState(false);
  const [gsmSearchCount, setGsmSearchCount] = useState(0);
  const [gsmSearchPage, setGsmSearchPage] = useState(1);
  const [gsmSearchLimit, setGsmSearchLimit] = useState(20);
  const [gsmExporting, setGsmExporting] = useState(false);

  // Column visibility - per user in localStorage
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns);
  
  // Export columns - separate from table view
  const [exportColumns, setExportColumns] = useState(() => {
    const saved = localStorage.getItem("cdr_export_columns");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const validIds = new Set(ALL_COLUMNS.map((c) => c.id));
          const filtered = parsed.filter((id) => validIds.has(id));
          if (filtered.length > 0) return filtered;
        }
      } catch { /* use default */ }
    }
    return ALL_COLUMNS.filter(c => c.default && c.id !== "recording").map(c => c.id);
  });
  
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Modals
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState({ url: "", record: null });

  const openAudioPlayer = useCallback((url, record) => {
    setSelectedAudio({ url: url || "", record: record || null });
    setAudioModalOpen(true);
  }, []);

  const closeAudioPlayer = useCallback(() => {
    setAudioModalOpen(false);
    setSelectedAudio({ url: "", record: null });
  }, []);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userAccessDetailUser, setUserAccessDetailUser] = useState(null);
  
  // Device Agent Assignment
  const [preSelectedDeviceForAgent, setPreSelectedDeviceForAgent] = useState(null);
  const [deviceAgentMap, setDeviceAgentMap] = useState({}); // device_id -> agent info
  const [changeAgentModalOpen, setChangeAgentModalOpen] = useState(false);
  const [selectedDeviceForChange, setSelectedDeviceForChange] = useState(null);
  const [agentDevicesModalOpen, setAgentDevicesModalOpen] = useState(false);
  const [selectedAgentForDevices, setSelectedAgentForDevices] = useState(null);
  
  // Token Modal
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [selectedTokenAgent, setSelectedTokenAgent] = useState(null);
  const [agentPublicExportOpen, setAgentPublicExportOpen] = useState(false);
  const [agentPublicExportAgent, setAgentPublicExportAgent] = useState(null);
  
  // Agent Search
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const [agentsView, setAgentsView] = useState("list");
  const [agentsCompanyId, setAgentsCompanyId] = useState("all");
  const [agentsStatusFilter, setAgentsStatusFilter] = useState("all");
  const [agentsHireDateFilter, setAgentsHireDateFilter] = useState("all");
  const [agentTableColumns, setAgentTableColumns] = useState(() => loadAgentTableColumns());
  const [agentColumnPickerOpen, setAgentColumnPickerOpen] = useState(false);
  const [agentInlineSavingId, setAgentInlineSavingId] = useState(null);
  const [agentDateDrafts, setAgentDateDrafts] = useState({});
  const [hireImportOpen, setHireImportOpen] = useState(false);
  const [hireParsed, setHireParsed] = useState(null);
  const [hireFileName, setHireFileName] = useState("");
  const [hireParsing, setHireParsing] = useState(false);
  const [hireImporting, setHireImporting] = useState(false);
  const [hireImportResult, setHireImportResult] = useState(null);
  const hireImportInputRef = useRef(null);
  const [agentsTeamFilter, setAgentsTeamFilter] = useState("all");
  const [agentsLeaderFilter, setAgentsLeaderFilter] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(getTodayStr);
  const [attendanceFilter, setAttendanceFilter] = useState("all");
  const [attendanceCompanyId, setAttendanceCompanyId] = useState("all");
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [attendanceSort, setAttendanceSort] = useState({ key: "agent_name", dir: "asc" });
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  // CRM State
  const [crmCustomers, setCrmCustomers] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmPage, setCrmPage] = useState(1);
  const [crmTotal, setCrmTotal] = useState(0);
  const [crmSearchInput, setCrmSearchInput] = useState("");
  const [crmSearchTerm, setCrmSearchTerm] = useState("");
  const [crmLeadFilter, setCrmLeadFilter] = useState("all");
  const [crmCompanyFilter, setCrmCompanyFilter] = useState("all");

  // Transcript State
  const [selectedTranscriptRecord, setSelectedTranscriptRecord] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const [transcriptPage, setTranscriptPage] = useState(1);
  const [transcriptTotal, setTranscriptTotal] = useState(0);
  const [transcriptFilter, setTranscriptFilter] = useState("all"); // all, has, none
  const [transcriptSearchInput, setTranscriptSearchInput] = useState("");
  const [transcriptSearchTerm, setTranscriptSearchTerm] = useState("");
  const [transcriptPolling, setTranscriptPolling] = useState(false);
  const [transcriptDeleteOpen, setTranscriptDeleteOpen] = useState(false);
  const [transcriptToDelete, setTranscriptToDelete] = useState(null);
  const [transcriptDeleting, setTranscriptDeleting] = useState(false);
  const [transcriptRecreateOpen, setTranscriptRecreateOpen] = useState(false);
  const [transcriptRecreateRecord, setTranscriptRecreateRecord] = useState(null);
  const [transcriptRecreateOptions, setTranscriptRecreateOptions] = useState({});
  const [transcriptSearchQuery, setTranscriptSearchQuery] = useState("");
  const [customerCrmOpen, setCustomerCrmOpen] = useState(false);
  const [customerCrmPhone, setCustomerCrmPhone] = useState("");
  const [customerCrmRecord, setCustomerCrmRecord] = useState(null);
  const [customerProfileCache, setCustomerProfileCache] = useState({});
  const [customerProfileLoadingKeys, setCustomerProfileLoadingKeys] = useState({});
  const customerProfileCacheRef = useRef({});
  const customerPrefetchPendingRef = useRef(new Set());

  // Agent Analytics State
  const [analyticsAgent, setAnalyticsAgent] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDateRange, setAnalyticsDateRange] = useState("today"); // today, yesterday, this_week, this_month, custom
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState("");
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState("");

  // Reports State
  const [reportsData, setReportsData] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsDatePreset, setReportsDatePreset] = useState("today");
  const [reportsStartDate, setReportsStartDate] = useState(getTodayStr);
  const [reportsEndDate, setReportsEndDate] = useState(getTodayStr);
  const [reportsSelectedAgents, setReportsSelectedAgents] = useState([]);
  const [reportsYon, setReportsYon] = useState("all"); // all, 1=Gelen, 2=Giden
  const [reportsCompanyId, setReportsCompanyId] = useState("all");
  const [reportsTeamFilter, setReportsTeamFilter] = useState([]);
  const [reportsLeaderFilter, setReportsLeaderFilter] = useState([]);
  const [reportsAgentSearch, setReportsAgentSearch] = useState("");
  const [reportsAgentDropdownOpen, setReportsAgentDropdownOpen] = useState(false);

  // Mesai Report State
  const [mesaiReportData, setMesaiReportData] = useState(null);
  const [mesaiReportLoading, setMesaiReportLoading] = useState(false);
  const [mesaiStartDate, setMesaiStartDate] = useState(getThisMonthStart);
  const [mesaiEndDate, setMesaiEndDate] = useState(getTodayStr);
  const [mesaiDatePreset, setMesaiDatePreset] = useState("thisMonth");
  const [mesaiCompanyId, setMesaiCompanyId] = useState("all");
  const [mesaiSearch, setMesaiSearch] = useState("");
  const [mesaiReportSort, setMesaiReportSort] = useState({ key: "late_count", dir: "desc" });
  const [mesaiLateModalAgent, setMesaiLateModalAgent] = useState(null);
  const [mesaiEarlyModalAgent, setMesaiEarlyModalAgent] = useState(null);

  // Mola Report State
  const [molaReportData, setMolaReportData] = useState(null);
  const [molaReportLoading, setMolaReportLoading] = useState(false);
  const [molaStartDate, setMolaStartDate] = useState(getTodayStr);
  const [molaEndDate, setMolaEndDate] = useState(getTodayStr);
  const [molaDatePreset, setMolaDatePreset] = useState("today");
  const [molaCompanyId, setMolaCompanyId] = useState("all");
  const [molaSearch, setMolaSearch] = useState("");
  const [molaReportSort, setMolaReportSort] = useState({ key: "break_count", dir: "desc" });
  const [molaBreakModalAgent, setMolaBreakModalAgent] = useState(null);

  // Reports Daily Detail Modal State
  const [reportDetailAgent, setReportDetailAgent] = useState(null);
  const [reportDetailData, setReportDetailData] = useState(null);
  const [reportDetailLoading, setReportDetailLoading] = useState(false);
  const [reportDetailSelectedDate, setReportDetailSelectedDate] = useState(null);
  const [reportDetailDayRecords, setReportDetailDayRecords] = useState([]);
  const [reportDetailDayRecordsLoading, setReportDetailDayRecordsLoading] = useState(false);
  const [reportDetailUniqueOnly, setReportDetailUniqueOnly] = useState(false);
  const [reportDetailSort, setReportDetailSort] = useState({ key: "call_date", dir: "desc" });
  const [reportsSort, setReportsSort] = useState({ key: "agent_name", dir: "asc" });
  const reportDetailDayRecordsRef = useRef(null);
  const [callAnalysisOpen, setCallAnalysisOpen] = useState(false);
  const [callAnalysisLoading, setCallAnalysisLoading] = useState(false);
  const [callAnalysisData, setCallAnalysisData] = useState(null);

  const [unfollowedMissedOpen, setUnfollowedMissedOpen] = useState(false);
  const [unfollowedMissedLoading, setUnfollowedMissedLoading] = useState(false);
  const [unfollowedMissedData, setUnfollowedMissedData] = useState(null);

  // Break Analysis Modal State
  const [breakAnalysisAgent, setBreakAnalysisAgent] = useState(null);
  const [breakAnalysisData, setBreakAnalysisData] = useState(null);
  const [breakAnalysisLoading, setBreakAnalysisLoading] = useState(false);
  const [breakAnalysisExpandedDay, setBreakAnalysisExpandedDay] = useState(null);
  const [breakAnalysisUnfollowedDay, setBreakAnalysisUnfollowedDay] = useState(null);
  const [breakAnalysisPeriod, setBreakAnalysisPeriod] = useState(null);
  const [breakAnalysisView, setBreakAnalysisView] = useState(null);

  const agentAnalyticsModalExpand = usePanelModalExpand();
  const mesaiLateModalExpand = usePanelModalExpand(true);
  const mesaiEarlyModalExpand = usePanelModalExpand(true);
  const molaBreakModalExpand = usePanelModalExpand(true);
  const reportDetailModalExpand = usePanelModalExpand(true);
  const breakAnalysisModalExpand = usePanelModalExpand(true);

  // Devices State
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesSearch, setDevicesSearch] = useState("");
  const [devicesPage, setDevicesPage] = useState(1);
  const [devicesCompanyId, setDevicesCompanyId] = useState("all");
  const [devicesTotalPages, setDevicesTotalPages] = useState(1);
  const [devicesTotal, setDevicesTotal] = useState(0);
  const [deviceFailedStatsMap, setDeviceFailedStatsMap] = useState({});
  const [editingDeviceGsm, setEditingDeviceGsm] = useState({}); // {deviceId: gsmValue}
  const [deviceCleanupOpen, setDeviceCleanupOpen] = useState(false);
  const [deviceCleanupMode, setDeviceCleanupMode] = useState("device");
  const [deviceCleanupDevice, setDeviceCleanupDevice] = useState(null);

  const handleDeviceFailedStatsMapChange = useCallback((map) => {
    setDeviceFailedStatsMap(map || {});
  }, []);
  
  // Device Call Logs Modal
  const [callLogsModalOpen, setCallLogsModalOpen] = useState(false);
  const [selectedDeviceForLogs, setSelectedDeviceForLogs] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [callLogsPage, setCallLogsPage] = useState(1);
  const [callLogsLimit, setCallLogsLimit] = useState(50);
  const [callLogsTotalPages, setCallLogsTotalPages] = useState(1);
  const [callLogsTotal, setCallLogsTotal] = useState(0);
  const [callLogsSummary, setCallLogsSummary] = useState({ total_calls: 0, incoming: 0, outgoing: 0, missed: 0, total_duration: 0 });
  const [callLogsStartDate, setCallLogsStartDate] = useState("");
  const [callLogsEndDate, setCallLogsEndDate] = useState("");
  const [callLogsStartTime, setCallLogsStartTime] = useState("00:00");
  const [callLogsEndTime, setCallLogsEndTime] = useState("23:59");
  const [callLogsDateRange, setCallLogsDateRange] = useState("all");
  const [callLogsSearch, setCallLogsSearch] = useState("");
  const [callLogsExcludeMissed, setCallLogsExcludeMissed] = useState(false);
  const [callLogsCallTypeFilter, setCallLogsCallTypeFilter] = useState("all");
  const [callLogsModalTab, setCallLogsModalTab] = useState("calls");

  // Agent-Device State
  const [agentDeviceCounts, setAgentDeviceCounts] = useState({});
  const [allDevicesList, setAllDevicesList] = useState([]);

  // Permissions
  const canSeeRecordings = user?.ses_kaydi_goster === 1;
  const canManageAgents = user?.agent_yonetimi === 1;
  const canExport = user?.excel_indirme === 1;
  const canSearch = user?.arama_yetkisi === 1 || user?.is_admin === 1;
  const canViewAnalytics = user?.agent_analiz_yetkisi === 1 || user?.is_admin === 1;
  const canUseSoftphone = user?.softphone_yetkisi === 1;
  const isAdmin = user?.is_admin === 1;
  const isSuperAdmin = user?.is_super_admin === 1;
  const recordingDownloadSettingsVisible = canAccessRecordingDownloadSettings(searchParams, isSuperAdmin);
  const canManageUsers = isAdmin || user?.kullanici_yonetimi === 1;
  const canManageCompanySettings = isSuperAdmin || isAdmin || user?.firma_yonetimi === 1;
  const isCompanyAdmin = (isAdmin && !isSuperAdmin && !!user?.company_id) || (user?.firma_yonetimi === 1 && !!user?.company_id);
  const canAccessTodos = isCompanyAdmin || isSuperAdmin;
  const canManageTodos = isCompanyAdmin || isSuperAdmin;
  const canManageDeviceCompany = isSuperAdmin || isAdmin || canManageAgents;
  const canManageDeviceCleanup = canManageDeviceCompany || user?.firma_yonetimi === 1;

  const hasMenu = useCallback((key) => hasPanelMenuPermission(user, key), [user]);

  const allowedReportViews = useMemo(
    () => (
      hasPanelMenuPermission(user, "menu_reports")
        ? REPORT_VIEWS.filter((v) => hasPanelMenuPermission(user, getReportViewPermissionKey(v.id)))
        : []
    ),
    [user]
  );
  const allowedAgentsViews = useMemo(
    () => (
      hasPanelMenuPermission(user, "menu_agents")
        ? AGENTS_VIEWS.filter((v) => hasPanelMenuPermission(user, getAgentsViewPermissionKey(v.id)))
        : []
    ),
    [user]
  );
  const allowedLineViews = useMemo(
    () => LINE_VIEWS.filter((v) => hasPanelMenuPermission(user, getLinesViewPermissionKey(v.id))),
    [user]
  );
  const allowedCrmViews = useMemo(
    () => (
      hasPanelMenuPermission(user, "menu_crm")
        ? CRM_VIEWS.filter((v) => hasPanelMenuPermission(user, getCrmViewPermissionKey(v.id)))
        : []
    ),
    [user]
  );

  const resolveAllowedTab = useCallback((tab) => {
    if (!tab || !TAB_IDS.includes(tab)) return TAB_IDS.find((t) => {
      if (t === "companies" && !isSuperAdmin) return false;
      if (t === "finance" && !isSuperAdmin && !isAdmin) return false;
      if (t === "users" && !isAdmin) return false;
      if (t === "panel-roles" && !isAdmin) return false;
      if (t === "calendar" && !isAdmin) return false;
      if (t === "otp-settings" && !isSuperAdmin) return false;
      if (t === "recording-download-settings" && !canAccessRecordingDownloadSettings(searchParams, isSuperAdmin)) return false;
      if (t === "recording-errors" && !isSuperAdmin) return false;
      if (t === "webhook-logs" && !canManageCompanySettings) return false;
      if (t === "sesk-panel" && !isSuperAdmin) return false;
      if (t === "todos" && (!canAccessTodos || !hasPanelMenuPermission(user, "menu_todos"))) return false;
      if (t === "work-schedules" && ((!isAdmin && !canManageAgents) || !hasPanelMenuPermission(user, "menu_work_schedules"))) return false;
      if (t === "search" && (!canSearch || !hasPanelMenuPermission(user, "menu_search"))) return false;
      if (t === "transcripts" && (!canSeeRecordings || !hasPanelMenuPermission(user, "menu_transcripts"))) return false;
      if (t === "cdr" && !hasPanelMenuPermission(user, "menu_cdr")) return false;
      if (t === "crm" && allowedCrmViews.length === 0) return false;
      if (t === "reports" && allowedReportViews.length === 0) return false;
      if ((t === "special-agent-report" || t === "special-agent-report-2") && !hasPanelMenuPermission(user, "menu_reports_special_agent")) return false;
      if (t === "team-leader-performance" && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return false;
      if (t === "mr-excel-records" && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return false;
      if (t === "tl-ciro-records" && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return false;
      if (t === "mr-assistant-mapping" && !isAdmin && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return false;
      if (t === "lines" && allowedLineViews.length === 0) return false;
      if (t === "agents" && allowedAgentsViews.length === 0) return false;
      if (t === "devices" && !hasPanelMenuPermission(user, "menu_devices")) return false;
      return true;
    }) || "cdr";
    if (tab === "companies" && !isSuperAdmin) return resolveAllowedTab(null);
    if (tab === "finance" && !isSuperAdmin && !isAdmin) return resolveAllowedTab(null);
    if (tab === "todos" && (!canAccessTodos || !hasPanelMenuPermission(user, "menu_todos"))) return resolveAllowedTab(null);
    if (tab === "users" && !isAdmin) return resolveAllowedTab(null);
    if (tab === "panel-roles" && !isAdmin) return resolveAllowedTab(null);
    if (tab === "calendar" && !isAdmin) return resolveAllowedTab(null);
    if (tab === "otp-settings" && !isSuperAdmin) return resolveAllowedTab(null);
    if (tab === "recording-download-settings" && !canAccessRecordingDownloadSettings(searchParams, isSuperAdmin)) return resolveAllowedTab(null);
    if (tab === "recording-errors" && !isSuperAdmin) return resolveAllowedTab(null);
    if (tab === "webhook-logs" && !canManageCompanySettings) return resolveAllowedTab(null);
    if (tab === "sesk-panel" && !isSuperAdmin) return resolveAllowedTab(null);
    if (tab === "work-schedules" && ((!isAdmin && !canManageAgents) || !hasPanelMenuPermission(user, "menu_work_schedules"))) return resolveAllowedTab(null);
    if (tab === "search" && (!canSearch || !hasPanelMenuPermission(user, "menu_search"))) return resolveAllowedTab(null);
    if (tab === "transcripts" && (!canSeeRecordings || !hasPanelMenuPermission(user, "menu_transcripts"))) return resolveAllowedTab(null);
    if (tab === "cdr" && !hasPanelMenuPermission(user, "menu_cdr")) return resolveAllowedTab(null);
    if (tab === "crm" && allowedCrmViews.length === 0) return resolveAllowedTab(null);
    if (tab === "reports" && allowedReportViews.length === 0) return resolveAllowedTab(null);
    if ((tab === "special-agent-report" || tab === "special-agent-report-2") && !hasPanelMenuPermission(user, "menu_reports_special_agent")) return resolveAllowedTab(null);
    if (tab === "team-leader-performance" && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return resolveAllowedTab(null);
    if (tab === "mr-excel-records" && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return resolveAllowedTab(null);
    if (tab === "tl-ciro-records" && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return resolveAllowedTab(null);
    if (tab === "mr-assistant-mapping" && !isAdmin && !hasPanelMenuPermission(user, "menu_reports_tl_performance")) return resolveAllowedTab(null);
    if (tab === "lines" && allowedLineViews.length === 0) return resolveAllowedTab(null);
    if (tab === "agents" && allowedAgentsViews.length === 0) return resolveAllowedTab(null);
    if (tab === "devices" && !hasPanelMenuPermission(user, "menu_devices")) return resolveAllowedTab(null);
    return tab;
  }, [isAdmin, isSuperAdmin, canSearch, canSeeRecordings, canAccessTodos, canManageAgents, canManageCompanySettings, user, allowedCrmViews.length, allowedReportViews.length, allowedLineViews.length, allowedAgentsViews.length, searchParams]);

  const reportsAgents = useMemo(() => {
    const list = asAgentList(agents);
    if (!isSuperAdmin || reportsCompanyId === "all") return list;
    return list.filter((a) => String(a.company_id) === String(reportsCompanyId));
  }, [agents, isSuperAdmin, reportsCompanyId]);

  const reportScopeOptions = useMemo(
    () => buildAgentScopeOptions(reportsAgents, teams),
    [reportsAgents, teams]
  );

  const reportsScopedAgents = useMemo(
    () => filterAgentsByTeamLeader(reportsAgents, reportsTeamFilter, reportsLeaderFilter),
    [reportsAgents, reportsTeamFilter, reportsLeaderFilter]
  );

  const reportScopeTeamOptions = useMemo(() => {
    const leaderIds = normalizeScopeFilter(reportsLeaderFilter);
    if (leaderIds.length === 0) return reportScopeOptions.teams;
    return reportScopeOptions.teams.filter((t) => leaderIds.includes(String(t.leaderId)));
  }, [reportScopeOptions.teams, reportsLeaderFilter]);

  const appendReportScopeParams = useCallback((params) => {
    const teamIds = normalizeScopeFilter(reportsTeamFilter);
    const leaderIds = normalizeScopeFilter(reportsLeaderFilter);
    if (teamIds.length > 0) params.append("team_id", teamIds.join(","));
    if (leaderIds.length > 0) params.append("leader_user_id", leaderIds.join(","));
  }, [reportsTeamFilter, reportsLeaderFilter]);

  const showReportScopeFilters = reportScopeOptions.teams.length > 0 || reportScopeOptions.leaders.length > 0;

  const filteredTeams = useMemo(() => {
    if (!teamsSearch.trim()) return teams;
    const term = teamsSearch.toLowerCase().trim();
    return teams.filter((t) =>
      (t.ad && t.ad.toLowerCase().includes(term)) ||
      (t.lider_name && t.lider_name.toLowerCase().includes(term)) ||
      (t.agents || []).some((a) => a.ad_soyad && a.ad_soyad.toLowerCase().includes(term))
    );
  }, [teams, teamsSearch]);

  const teamTableStats = useMemo(() => [
    {
      label: "Toplam Takım",
      value: teams.length,
      colorClass: isDark ? "text-indigo-300" : "text-indigo-700",
    },
    {
      label: "Atanmış Agent",
      value: teams.reduce((sum, t) => sum + (t.agent_count || 0), 0),
      colorClass: isDark ? "text-violet-300" : "text-violet-700",
    },
    {
      label: "Lider Atanan",
      value: teams.filter((t) => t.lider_user_id).length,
      colorClass: isDark ? "text-emerald-300" : "text-emerald-700",
    },
    {
      label: "Boş Takım",
      value: teams.filter((t) => !(t.agent_count > 0)).length,
      colorClass: isDark ? "text-amber-300" : "text-amber-700",
    },
  ], [teams, isDark]);

  const teamTablePagination = usePanelTablePagination(filteredTeams, { pageSize: 10, resetDeps: [teamsSearch] });

  const filteredUsers = useMemo(() => {
    if (!usersSearch.trim()) return users;
    const term = usersSearch.toLowerCase().trim();
    const termDigits = term.replace(/\D/g, "");
    return users.filter((u) => {
      const phones = getUserPhonesForDisplay(u);
      return (
        (u.ad_soyad && u.ad_soyad.toLowerCase().includes(term)) ||
        (u.kullanici_adi && u.kullanici_adi.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term)) ||
        (u.company_name && u.company_name.toLowerCase().includes(term)) ||
        (u.firma_adi && u.firma_adi.toLowerCase().includes(term)) ||
        (u.gorev && gorevLabel(u.gorev).toLowerCase().includes(term)) ||
        getUserMenuPermissionSummary(u).toLowerCase().includes(term) ||
        getUserCorePermissionBadges(u).some((b) => b.label.toLowerCase().includes(term)) ||
        phones.some((p) => p.toLowerCase().includes(term) || (termDigits && p.replace(/\D/g, "").includes(termDigits)))
      );
    });
  }, [users, usersSearch]);

  const userTableStats = useMemo(() => [
    {
      label: "Toplam Kullanıcı",
      value: users.length,
      colorClass: isDark ? "text-purple-300" : "text-purple-700",
    },
    {
      label: "Aktif",
      value: users.filter((u) => u.status === 1).length,
      colorClass: isDark ? "text-emerald-300" : "text-emerald-700",
    },
    {
      label: "OTP Aktif",
      value: users.filter((u) => u.login_otp_enabled === 1).length,
      colorClass: isDark ? "text-blue-300" : "text-blue-700",
    },
    {
      label: "Admin",
      value: users.filter((u) => u.is_admin === 1).length,
      colorClass: isDark ? "text-violet-300" : "text-violet-700",
    },
  ], [users, isDark]);

  const userTablePagination = usePanelTablePagination(filteredUsers, { pageSize: 15, resetDeps: [usersSearch] });

  const userTableColSpan = isSuperAdmin ? 8 : 7;

  useEffect(() => {
    setReportsSelectedAgents((prev) => prev.filter((id) => reportsScopedAgents.some((a) => a.id === id)));
  }, [reportsTeamFilter, reportsLeaderFilter, reportsScopedAgents]);

  useEffect(() => {
    const teamIds = normalizeScopeFilter(reportsTeamFilter);
    if (teamIds.length === 0) return;
    const valid = teamIds.filter((id) => reportScopeTeamOptions.some((t) => String(t.id) === String(id)));
    if (valid.length !== teamIds.length) setReportsTeamFilter(valid);
  }, [reportsLeaderFilter, reportScopeTeamOptions, reportsTeamFilter]);

  const isTabAllowed = useCallback((tab) => {
    if (!TAB_IDS.includes(tab)) return false;
    return resolveAllowedTab(tab) === tab;
  }, [resolveAllowedTab]);

  const activeTab = resolveAllowedTab(tabParam);
  const navView = searchParams.get("view");
  const reportsView = activeTab === "reports" ? (navView || "agents") : "agents";
  const linesView = activeTab === "lines" ? (navView || "settings") : "settings";
  const crmView = activeTab === "crm" ? (navView || "customers") : "customers";
  const companiesView = activeTab === "companies" ? (navView || "list") : "list";
  const financeView = activeTab === "finance" ? (navView || "dashboard") : "dashboard";
  const calendarView = activeTab === "calendar" ? (navView || "medicrm") : "medicrm";
  const activeNavView = useMemo(() => {
    if (activeTab === "reports") return reportsView;
    if (activeTab === "lines") return linesView;
    if (activeTab === "crm") return crmView;
    if (activeTab === "agents") return agentsView;
    if (activeTab === "companies") return companiesView;
    if (activeTab === "finance") return financeView;
    if (activeTab === "calendar") return calendarView;
    return "default";
  }, [activeTab, reportsView, linesView, crmView, agentsView, companiesView, financeView, calendarView]);
  const currentRouteKey = useMemo(
    () => buildTabRouteKey(activeTab, activeNavView),
    [activeTab, activeNavView]
  );

  const tabFetchCacheRef = useRef(new Map());
  const [tabReclickNonce, setTabReclickNonce] = useState(0);
  const panelLoader = useMemo(() => createPanelLoader(), []);

  useEffect(() => {
    panelLoader.setTheme(isDark);
  }, [isDark, panelLoader]);

  const showPanelLoader = useCallback(() => {
    panelLoader.show();
  }, [panelLoader]);

  const hidePanelLoader = useCallback(() => {
    panelLoader.hide();
  }, [panelLoader]);

  useEffect(() => () => panelLoader.dispose(), [panelLoader]);

  const invalidateTabRoute = useCallback((tab, view = "default") => {
    tabFetchCacheRef.current.delete(buildTabRouteKey(tab, view));
  }, []);

  const runTabFetch = useCallback(async (routeKey, signature, fetchFn, { force = false } = {}) => {
    if (!force && tabFetchCacheRef.current.get(routeKey) === signature) {
      hidePanelLoader();
      return false;
    }
    showPanelLoader();
    try {
      await fetchFn();
      tabFetchCacheRef.current.set(routeKey, signature);
    } catch (error) {
      console.error("Tab fetch error:", error);
    } finally {
      hidePanelLoader();
    }
    return true;
  }, [showPanelLoader, hidePanelLoader]);

  const navEntries = useMemo(
    () => buildNavEntries({
      canSearch, canSeeRecordings, canAccessTodos, isAdmin, isSuperAdmin, canManageAgents, canManageCompanySettings, hasMenu,
      showRecordingDownloadSettings: recordingDownloadSettingsVisible,
    }),
    [canSearch, canSeeRecordings, canAccessTodos, isAdmin, isSuperAdmin, canManageAgents, canManageCompanySettings, hasMenu, recordingDownloadSettingsVisible]
  );

  const isNavItemActive = useCallback((item) => {
    if (!item || item.kind === "separator") return false;
    if (item.kind === "submenu") {
      return item.items.some((sub) => isNavItemActive(sub));
    }
    if (item.kind === "link") {
      return activeTab === item.tab;
    }
    if (activeTab !== item.tab) return false;
    if (item.tab === "reports") return reportsView === (item.view || "agents");
    if (item.tab === "lines") return linesView === (item.view || "settings");
    if (item.tab === "crm") return crmView === (item.view || "customers");
    if (item.tab === "agents") return agentsView === (item.view || "list");
    if (item.tab === "companies") return companiesView === (item.view || "list");
    if (item.tab === "finance") return financeView === (item.view || "dashboard");
    if (item.tab === "calendar") return calendarView === (item.view || "medicrm");
    return !item.view;
  }, [activeTab, reportsView, linesView, crmView, agentsView, companiesView, financeView, calendarView]);

  const isEntryActive = useCallback((entry) => {
    if (entry.kind === "link") {
      if (entry.view) {
        if (entry.tab === "finance") return activeTab === "finance" && financeView === entry.view;
        if (entry.tab === "calendar") return activeTab === "calendar" && calendarView === entry.view;
        return activeTab === entry.tab && navView === entry.view;
      }
      return activeTab === entry.tab;
    }
    if (entry.id === "reports") {
      return activeTab === "reports" || activeTab === "special-agent-report" || activeTab === "special-agent-report-2" || activeTab === "team-leader-performance" || activeTab === "tl-ciro-records" || activeTab === "mr-excel-records" || entry.items.some((item) => isNavItemActive(item));
    }
    if (entry.id === "crm") return activeTab === "crm";
    if (entry.id === "companies") return activeTab === "companies";
    if (entry.id === "finance") return activeTab === "finance";
    if (entry.id === "calendar") return activeTab === "calendar";
    return entry.items.some((item) => isNavItemActive(item));
  }, [activeTab, financeView, calendarView, isNavItemActive, navView]);

  const navigateToTab = useCallback((tab, view = null) => {
    if (!isTabAllowed(tab)) return;

    if (tab === "transcripts" && activeTab === "transcripts" && selectedTranscriptRecord) {
      setSelectedTranscriptRecord(null);
      return;
    }

    if (activeTab === "transcripts" && tab !== "transcripts" && selectedTranscriptRecord) {
      setSelectedTranscriptRecord(null);
    }

    if (tab === "agents" && view) {
      setAgentsView(view);
    }

    const resolvedView = view
      ?? (tab === "reports" ? (allowedReportViews[0]?.id || "agents")
        : tab === "lines" ? (allowedLineViews[0]?.id || "settings")
        : tab === "crm" ? (allowedCrmViews[0]?.id || "customers")
        : tab === "agents" ? (allowedAgentsViews[0]?.id || agentsView)
        : tab === "companies" ? (navView || "list")
        : tab === "finance" ? (navView || "dashboard")
        : tab === "calendar" ? (navView || "medicrm")
        : "default");
    const nextRouteKey = buildTabRouteKey(tab, resolvedView);
    const currentKey = buildTabRouteKey(activeTab, activeNavView);
    if (nextRouteKey === currentKey) {
      invalidateTabRoute(tab, resolvedView);
      setTabReclickNonce((n) => n + 1);
      if (!isStaticPanelRoute(nextRouteKey)) {
        showPanelLoader();
      }
      return;
    }
    if (nextRouteKey !== currentKey && !isStaticPanelRoute(nextRouteKey)) {
      showPanelLoader();
    }

    const params = new URLSearchParams();
    if (view && (tab === "reports" || tab === "lines" || tab === "crm" || tab === "agents" || tab === "companies" || tab === "finance" || tab === "calendar")) {
      params.set("view", view);
    }
    if (tab === "crm" && view === "customer" && customerPhoneParam) {
      params.set("phone", customerPhoneParam);
    }
    if (tab === "customer" && customerPhoneParam) {
      params.set("phone", customerPhoneParam);
    }
    if (searchParams.get("ses") === "yes") {
      params.set("ses", "yes");
    }
    const qs = params.toString();
    navigate(qs ? `/${tab}?${qs}` : `/${tab}`);
  }, [navigate, isTabAllowed, activeTab, activeNavView, agentsView, selectedTranscriptRecord, customerPhoneParam, showPanelLoader, invalidateTabRoute, allowedReportViews, allowedLineViews, allowedCrmViews, allowedAgentsViews, navView, searchParams]);

  const navigateToCustomerDetail = useCallback((phone) => {
    if (!phone) return;
    setCustomerCrmOpen(false);
    navigate(`/crm?view=customer&phone=${encodeURIComponent(phone)}`);
  }, [navigate]);

  useEffect(() => {
    if (!tabParam) return;
    const allowedTab = resolveAllowedTab(tabParam);
    if (allowedTab !== tabParam) {
      navigate(`/${allowedTab}`, { replace: true });
      return;
    }
    if (tabParam === "reports" && (reportsView === "special-agent" || navView === "special-agent")) {
      if (hasPanelMenuPermission(user, "menu_reports_special_agent")) {
        navigate("/special-agent-report", { replace: true });
        return;
      }
    }
    if (tabParam === "reports" && allowedReportViews.length > 0 && !allowedReportViews.some((v) => v.id === reportsView)) {
      navigate(`/reports?view=${allowedReportViews[0].id}`, { replace: true });
      return;
    }
    if (tabParam === "lines" && allowedLineViews.length > 0 && !allowedLineViews.some((v) => v.id === linesView)) {
      navigate(`/lines?view=${allowedLineViews[0].id}`, { replace: true });
      return;
    }
    if (tabParam === "crm" && crmView !== "customer" && allowedCrmViews.length > 0 && !allowedCrmViews.some((v) => v.id === crmView)) {
      navigate(`/crm?view=${allowedCrmViews[0].id}`, { replace: true });
      return;
    }
    if (tabParam === "customer" && customerPhoneParam) {
      navigate(`/crm?view=customer&phone=${encodeURIComponent(customerPhoneParam)}`, { replace: true });
      return;
    }
    if (tabParam === "customer" && !customerPhoneParam) {
      navigate("/crm?view=customers", { replace: true });
      return;
    }
    if (tabParam === "crm" && crmView === "customer" && !customerPhoneParam) {
      navigate("/crm?view=customers", { replace: true });
    }
    if (tabParam === "companies" && navView && !COMPANIES_VIEWS.some((v) => v.id === navView)) {
      navigate("/companies?view=list", { replace: true });
    }
    if (tabParam === "finance" && navView && !FINANCE_VIEWS.some((v) => v.id === navView)) {
      navigate("/finance?view=dashboard", { replace: true });
    }
    if (tabParam === "calendar" && navView && !CALENDAR_VIEWS.some((v) => v.id === navView)) {
      navigate("/calendar?view=medicrm", { replace: true });
    }
    if (tabParam === "calendar" && !navView) {
      navigate("/calendar?view=medicrm", { replace: true });
    }
  }, [tabParam, resolveAllowedTab, navigate, customerPhoneParam, crmView, reportsView, linesView, navView, allowedReportViews, allowedLineViews, allowedCrmViews, user]);

  useEffect(() => {
    if (activeTab !== "agents" || allowedAgentsViews.length === 0) return;
    if (navView && allowedAgentsViews.some((v) => v.id === navView)) {
      setAgentsView(navView);
      return;
    }
    if (!allowedAgentsViews.some((v) => v.id === agentsView)) {
      setAgentsView(allowedAgentsViews[0].id);
    }
  }, [activeTab, navView, agentsView, allowedAgentsViews]);

  // Filter agents based on user permissions
  const visibleAgents = React.useMemo(() => {
    const list = asAgentList(agents);
    if (isAdmin) return list;
    const resolved = user?.resolved_visible_agent_ids;
    if (Array.isArray(resolved)) {
      if (resolved.length === 0) return [];
      return list.filter((a) => resolved.includes(a.id));
    }
    if (!user?.gorebilecegi_agentlar) return list;
    const allowedIds = user.gorebilecegi_agentlar.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    if (allowedIds.length === 0) return list;
    return list.filter(a => allowedIds.includes(a.id));
  }, [agents, user, isAdmin]);

  const companyFilteredAgents = React.useMemo(() => {
    if (!isSuperAdmin || agentsCompanyId === "all") return visibleAgents;
    return visibleAgents.filter((a) => String(a.company_id) === String(agentsCompanyId));
  }, [visibleAgents, isSuperAdmin, agentsCompanyId]);

  const agentsForHireImport = companyFilteredAgents;

  const agentsHireImportCompanyLabel = useMemo(() => {
    if (isSuperAdmin) {
      if (agentsCompanyId === "all") return null;
      return companies.find((c) => String(c.id) === String(agentsCompanyId))?.ad || null;
    }
    const cid = user?.company_id;
    return companies.find((c) => Number(c.id) === Number(cid))?.ad || null;
  }, [isSuperAdmin, agentsCompanyId, companies, user?.company_id]);

  const hireImportNeedsCompanyPick = isSuperAdmin && agentsCompanyId === "all";

  const agentsScopeOptions = useMemo(
    () => buildAgentScopeOptions(companyFilteredAgents, teams),
    [companyFilteredAgents, teams]
  );

  const agentsScopedAgents = useMemo(
    () => filterAgentsByTeamLeader(companyFilteredAgents, agentsTeamFilter, agentsLeaderFilter),
    [companyFilteredAgents, agentsTeamFilter, agentsLeaderFilter]
  );

  const agentsScopeTeamOptions = useMemo(() => {
    const leaderIds = normalizeScopeFilter(agentsLeaderFilter);
    if (leaderIds.length === 0) return agentsScopeOptions.teams;
    return agentsScopeOptions.teams.filter((t) => leaderIds.includes(String(t.leaderId)));
  }, [agentsScopeOptions.teams, agentsLeaderFilter]);

  const showAgentsScopeFilters = agentsScopeOptions.teams.length > 0 || agentsScopeOptions.leaders.length > 0;

  const appendAgentsScopeParams = useCallback((params) => {
    if (agentsTeamFilter !== "all") params.append("team_id", agentsTeamFilter);
    const leaderIds = normalizeScopeFilter(agentsLeaderFilter);
    if (leaderIds.length > 0) params.append("leader_user_id", leaderIds.join(","));
  }, [agentsTeamFilter, agentsLeaderFilter]);

  useEffect(() => {
    if (agentsTeamFilter !== "all" && !agentsScopeTeamOptions.some((t) => String(t.id) === String(agentsTeamFilter))) {
      setAgentsTeamFilter("all");
    }
  }, [agentsLeaderFilter, agentsScopeTeamOptions, agentsTeamFilter]);

  // Check if user has agent restrictions (cannot create new agents)
  const hasAgentRestrictions = !isAdmin && !!(user?.gorev || (user?.gorebilecegi_agentlar && user.gorebilecegi_agentlar.trim() !== ""));

  const agentsStatusFiltered = React.useMemo(() => {
    if (agentsStatusFilter === "all") return agentsScopedAgents;
    const status = Number(agentsStatusFilter);
    return agentsScopedAgents.filter((a) => Number(a.status) === status);
  }, [agentsScopedAgents, agentsStatusFilter]);

  const agentsEmploymentFiltered = React.useMemo(() => {
    if (agentsHireDateFilter !== "missing_hire") return agentsStatusFiltered;
    return agentsStatusFiltered.filter((a) => !formatAgentEmploymentDate(a.ise_giris_tarihi));
  }, [agentsStatusFiltered, agentsHireDateFilter]);

  const agentColVisible = React.useCallback((key) => {
    if (key === "company" && !isSuperAdmin) return false;
    return agentTableColumns[key] !== false;
  }, [agentTableColumns, isSuperAdmin]);

  const agentTableVisibleColCount = React.useMemo(() => {
    let n = 1;
    AGENT_TABLE_COLUMN_DEFS.forEach((c) => {
      if (c.superAdminOnly && !isSuperAdmin) return;
      if (agentColVisible(c.key)) n += 1;
    });
    return n + 1;
  }, [agentColVisible, isSuperAdmin]);

  const toggleAgentTableColumn = React.useCallback((key, visible) => {
    setAgentTableColumns((prev) => {
      const next = { ...prev, [key]: visible };
      saveAgentTableColumns(next);
      return next;
    });
  }, []);

  // Filtered agents based on search
  const filteredAgents = React.useMemo(() => {
    if (!agentSearchTerm.trim()) return agentsEmploymentFiltered;
    const term = agentSearchTerm.toLowerCase();
    return agentsEmploymentFiltered.filter(a =>
      a.ad_soyad?.toLowerCase().includes(term) ||
      a.kullanici_adi?.toLowerCase().includes(term) ||
      a.gsm?.toLowerCase().includes(term) ||
      a.company_name?.toLowerCase().includes(term) ||
      a.team_name?.toLowerCase().includes(term)
    );
  }, [agentsEmploymentFiltered, agentSearchTerm]);

  const agentTableStats = React.useMemo(() => {
    const list = asAgentList(filteredAgents);
    const active = list.filter((a) => a.status === 1).length;
    const withDevice = list.filter((a) => (agentDeviceCounts[a.id] || 0) > 0).length;
    return [
      { label: "Toplam", value: list.length, colorClass: isDark ? "text-slate-100" : "text-gray-900" },
      { label: "Aktif", value: active, colorClass: isDark ? "text-emerald-400" : "text-emerald-600" },
      { label: "Cihazlı", value: withDevice, colorClass: isDark ? "text-pink-400" : "text-pink-600" },
      { label: "Pasif", value: list.length - active, colorClass: isDark ? "text-slate-400" : "text-gray-500" },
    ];
  }, [filteredAgents, agentDeviceCounts, isDark]);

  const agentTablePagination = usePanelTablePagination(asAgentList(filteredAgents), {
    pageSize: 15,
    resetDeps: [agentSearchTerm, agentsCompanyId, agentsStatusFilter, agentsHireDateFilter, agentsTeamFilter, agentsLeaderFilter],
  });

  const agentDevicesByAgent = React.useMemo(() => {
    const deviceLookup = new Map();
    [...allDevicesList, ...devices].forEach((d) => {
      if (d?.id != null) deviceLookup.set(String(d.id), d);
    });
    const map = {};
    Object.entries(deviceAgentMap).forEach(([deviceDbId, agentInfo]) => {
      const agentId = String(agentInfo.agent_id);
      if (!map[agentId]) map[agentId] = [];
      const deviceRecord = deviceLookup.get(String(deviceDbId));
      map[agentId].push(deviceRecord ? { ...deviceRecord } : {
        id: deviceDbId,
        device_id: deviceRecord?.device_id,
        device_name: deviceRecord?.device_name,
        gsm: deviceRecord?.gsm,
      });
    });
    return map;
  }, [deviceAgentMap, allDevicesList, devices]);

  const enrichedDeviceAgentMap = React.useMemo(() => {
    const map = {};
    Object.entries(deviceAgentMap).forEach(([deviceId, info]) => {
      const agent = agents.find((a) => a.id === info.agent_id);
      map[deviceId] = {
        ...info,
        company_name: info.company_name || agent?.company_name || null,
      };
    });
    return map;
  }, [deviceAgentMap, agents]);

  const skipColumnSaveRef = useRef(true);

  // Load column preferences when user is available
  useEffect(() => {
    if (!user?.id) return;
    skipColumnSaveRef.current = true;
    setVisibleColumns(loadVisibleColumnsForUser(user.id));
  }, [user?.id]);

  // Save column preferences to localStorage (per user)
  useEffect(() => {
    if (!user?.id) return;
    if (skipColumnSaveRef.current) {
      skipColumnSaveRef.current = false;
      return;
    }
    localStorage.setItem(`cdr_visible_columns_u${user.id}`, JSON.stringify(visibleColumns));
  }, [visibleColumns, user?.id]);

  useEffect(() => {
    localStorage.setItem("cdr_export_columns", JSON.stringify(exportColumns));
  }, [exportColumns]);

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Toggle export column
  const toggleExportColumn = (columnId) => {
    setExportColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Check if column is visible
  const isColumnVisible = (columnId) => {
    if (columnId === "recording" && !canSeeRecordings) return false;
    return visibleColumns.includes(columnId);
  };

  // Handle stats date range change
  const handleStatsDateChange = (range) => {
    // First update the range type
    setStatsDateRange(range);
    
    // Reset time to full day
    setStartTime("00:00");
    setEndTime("23:59");
    
    // Then update dates based on selection (update CDR filters)
    switch (range) {
      case "today":
        setStartDate(getTodayStr());
        setEndDate(getTodayStr());
        break;
      case "yesterday":
        setStartDate(getYesterdayStr());
        setEndDate(getYesterdayStr());
        break;
      case "last7":
        setStartDate(getLast7DaysStr());
        setEndDate(getTodayStr());
        break;
      case "thisMonth":
        setStartDate(getThisMonthStart());
        setEndDate(getTodayStr());
        break;
      case "lastMonth":
        const { start, end } = getLastMonthRange();
        setStartDate(start);
        setEndDate(end);
        break;
      case "custom":
        setStatsStartDate(startDate);
        setStatsEndDate(endDate);
        break;
      default:
        break;
    }
    setPage(1);
  };

  const applyCustomStatsRange = () => {
    if (!statsStartDate || !statsEndDate) {
      toast.warning("Başlangıç ve bitiş tarihi seçin");
      return;
    }
    if (statsStartDate > statsEndDate) {
      toast.warning("Başlangıç tarihi bitişten sonra olamaz");
      return;
    }
    setStartDate(statsStartDate);
    setEndDate(statsEndDate);
    setPage(1);
  };

  // Separate effect for agents loading
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await axios.get(`${API}/agents`, { timeout: 90000 });
        setAgents(asAgentList(response.data));
      } catch (err) {
        console.error('Agents load error:', err);
        toast.warning('Asistan listesi yüklenemedi. Sayfayı yenileyin veya bağlantıyı kontrol edin.');
      }
    };
    loadAgents();
  }, []);

  useEffect(() => {
    if (!user) return;
    if (isSuperAdmin) {
      setLeadStatuses(DEFAULT_LEAD_STATUSES);
      return;
    }
    axios.get(`${API}/crm/lead-statuses`, { timeout: 30000 })
      .then((res) => {
        setLeadStatuses(res.data.lead_statuses?.length ? res.data.lead_statuses : DEFAULT_LEAD_STATUSES);
        setLeadSources(res.data.lead_sources?.length ? res.data.lead_sources : DEFAULT_LEAD_SOURCES);
      })
      .catch(() => {
        setLeadStatuses(DEFAULT_LEAD_STATUSES);
        setLeadSources(DEFAULT_LEAD_SOURCES);
      });
  }, [user, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    setReportsSelectedAgents([]);
    setReportsData(null);
  }, [reportsCompanyId, isSuperAdmin]);

  useEffect(() => {
    const applyCdrDay = (targetDate, datePreset) => {
      setStartDate(targetDate);
      setEndDate(targetDate);
      setStatsStartDate(targetDate);
      setStatsEndDate(targetDate);
      setStatsDateRange(datePreset);
      setStartTime("00:00");
      setEndTime("23:59");
    };

    const resolveInitialCdrDates = async () => {
      const today = getTodayStr();
      applyCdrDay(today, "today");
      setInitialLoading(false);

      axios.get(`${API}/data-range`, { timeout: 90000 })
        .then((rangeRes) => setDataRange(rangeRes.data))
        .catch((err) => console.warn("data-range load skipped:", err?.message || err));

      try {
        const countParams = new URLSearchParams();
        countParams.append("start_date", `${today} 00:00:00`);
        countParams.append("end_date", `${today} 23:59:59`);
        const countRes = await axios.get(`${API}/cdr/count?${countParams}`, { timeout: 90000 });
        const todayCount = countRes.data?.count ?? 0;
        if (todayCount === 0) {
          applyCdrDay(getYesterdayStr(), "yesterday");
        }
      } catch (error) {
        console.error("resolveInitialCdrDates count error:", error);
      }
    };
    resolveInitialCdrDates();
  }, []);
  
  // Fetch agent device counts
  const fetchAgentDeviceCounts = async () => {
    try {
      const res = await axios.get(`${API}/agent-devices`);
      setAgentDeviceCounts(res.data.data || {});
    } catch (error) {
      console.error("Agent device counts error:", error);
    }
  };
  
  // Fetch all devices for selection
  const fetchAllDevicesList = async () => {
    try {
      const res = await axios.get(`${API}/devices?limit=1000`);
      setAllDevicesList(res.data.data || []);
    } catch (error) {
      console.error("All devices error:", error);
    }
  };
  
  // Fetch device -> agent mapping
  const fetchDeviceAgentMap = async () => {
    try {
      const res = await axios.get(`${API}/device-agent-map`);
      setDeviceAgentMap(res.data.data || {});
    } catch (error) {
      console.error("Device agent map error:", error);
    }
  };
  
  // Initial load of device data
  useEffect(() => {
    fetchAgentDeviceCounts();
    fetchAllDevicesList();
    fetchDeviceAgentMap();
  }, []);

  useEffect(() => {
    fetchCompanies().catch(console.error);
    if (isAdmin || canManageAgents) {
      axios.get(`${API}/users`).then(res => setUsers(res.data || [])).catch(console.error);
      axios.get(`${API}/teams`).then(res => setTeams(res.data || [])).catch(console.error);
    }
    if (isAdmin) {
      fetchPanelRoles();
    }
  }, [isAdmin, canManageAgents]);

  const cdrFilteredAgents = React.useMemo(() => {
    if (!isSuperAdmin || cdrCompanyFilter === "all") return visibleAgents;
    return visibleAgents.filter((a) => String(a.company_id) === String(cdrCompanyFilter));
  }, [visibleAgents, isSuperAdmin, cdrCompanyFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = cdrPhoneFilterInput.trim();
      if (next !== cdrPhoneFilter) {
        setCdrPhoneFilter(next);
        setPage(1);
      }
    }, 500);
    return () => window.clearTimeout(timer);
  }, [cdrPhoneFilterInput, cdrPhoneFilter]);

  const buildCdrFilterParams = useCallback((withPagination = false) => {
    const params = new URLSearchParams();
    if (withPagination) {
      params.set("page", page.toString());
      params.set("limit", limit.toString());
    }
    if (cdrFollowUpIds) {
      params.append("cdr_ids", cdrFollowUpIds);
    } else {
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (cdrPhoneFilter.trim()) params.append("numara", normalizeTurkishPhone(cdrPhoneFilter));
      if (statusFilter !== "all") params.append("durum", statusFilter);
      if (yonFilter !== "all") params.append("yon", yonFilter);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      if (isSuperAdmin && cdrCompanyFilter && cdrCompanyFilter !== "all") {
        params.append("company_id", cdrCompanyFilter);
      }
    }
    return params;
  }, [page, limit, startDate, endDate, startTime, endTime, cdrPhoneFilter, statusFilter, yonFilter, selectedAgent, cdrFollowUpIds, isSuperAdmin, cdrCompanyFilter]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildCdrFilterParams(true);
      const requests = [axios.get(`${API}/cdr?${params}`, { timeout: 60000 })];
      if (!cdrFollowUpIds) {
        requests.push(axios.get(`${API}/cdr/count?${buildCdrFilterParams(false)}`, { timeout: 30000 }));
      }

      const [recordsRes, countRes] = await Promise.all(requests);
      const rows = Array.isArray(recordsRes.data) ? recordsRes.data : (recordsRes.data?.records || []);
      setRecords(rows);
      if (cdrFollowUpIds) {
        setCdrTotalCount(rows.length);
      } else {
        setCdrTotalCount(countRes?.data?.count ?? 0);
      }
    } catch (error) {
      console.error("CDR fetch error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Bağlantı zaman aşımı - lütfen tekrar deneyin");
      } else {
        toast.error("Kayıtlar yüklenirken hata");
      }
    } finally {
      setLoading(false);
    }
  }, [buildCdrFilterParams, cdrFollowUpIds]);

  const handleCdrFollowUpClick = useCallback((record) => {
    const followUpId = record.follow_up_cdr_id;
    if (!followUpId) return;

    if (!cdrFollowUpIds) {
      cdrListSnapshotRef.current = {
        page,
        startDate,
        endDate,
        startTime,
        endTime,
        cdrPhoneFilter,
        cdrCompanyFilter,
        statusFilter,
        yonFilter,
        selectedAgent,
      };
    }

    const answered = record.follow_up_status === "answered";
    const time = record.follow_up_time || "";
    const phone = record.arayan_numara || record.aranan_numara || "";
    const label = answered
      ? (time ? `${phone} — ${time} geri arandı ve görüşüldü` : `${phone} — geri arama (görüşüldü)`)
      : (time ? `${phone} — ${time} geri arama denemesi (cevapsız)` : `${phone} — geri arama denemesi (cevapsız)`);

    setCdrFollowUpLabel(label);
    setCdrFollowUpIds([record.id, followUpId].filter(Boolean).join(","));
    setPage(1);
  }, [
    cdrFollowUpIds, page, startDate, endDate, startTime, endTime,
    cdrPhoneFilter, cdrCompanyFilter, statusFilter, yonFilter, selectedAgent,
  ]);

  const cancelCdrFollowUpView = useCallback(() => {
    const snap = cdrListSnapshotRef.current;
    if (snap) {
      setPage(snap.page);
      setStartDate(snap.startDate);
      setEndDate(snap.endDate);
      setStartTime(snap.startTime);
      setEndTime(snap.endTime);
      setCdrPhoneFilterInput(snap.cdrPhoneFilter || "");
      setCdrPhoneFilter(snap.cdrPhoneFilter || "");
      setCdrCompanyFilter(snap.cdrCompanyFilter || "all");
      setStatusFilter(snap.statusFilter);
      setYonFilter(snap.yonFilter);
      setSelectedAgent(snap.selectedAgent);
    }
    setCdrFollowUpIds(null);
    setCdrFollowUpLabel("");
    cdrListSnapshotRef.current = null;
  }, []);

  const openCdrRecordById = useCallback((cdrId, label) => {
    const id = Number(cdrId);
    if (!id) return;

    if (!cdrFollowUpIds) {
      cdrListSnapshotRef.current = {
        page,
        startDate,
        endDate,
        startTime,
        endTime,
        cdrPhoneFilter,
        cdrCompanyFilter,
        statusFilter,
        yonFilter,
        selectedAgent,
      };
    }

    setCdrFollowUpLabel(label || `Kayıt #${id}`);
    setCdrFollowUpIds(String(id));
    setPage(1);
    navigateToTab("cdr");
  }, [
    cdrFollowUpIds, page, startDate, endDate, startTime, endTime,
    cdrPhoneFilter, cdrCompanyFilter, statusFilter, yonFilter, selectedAgent, navigateToTab,
  ]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = new URLSearchParams();
      // Use CDR filter dates and times for stats too
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      // Add phone number filters with Turkish number normalization
      if (cdrPhoneFilter.trim()) params.append("numara", normalizeTurkishPhone(cdrPhoneFilter));
      if (isSuperAdmin && cdrCompanyFilter && cdrCompanyFilter !== "all") {
        params.append("company_id", cdrCompanyFilter);
      }
      const response = await axios.get(`${API}/stats?${params}`, { timeout: 60000 });
      setStats(response.data);
    } catch (error) {
      console.error("Stats fetch error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("İstatistik yüklemesi zaman aşımı");
      }
    } finally {
      setStatsLoading(false);
    }
  }, [startDate, endDate, startTime, endTime, selectedAgent, cdrPhoneFilter, isSuperAdmin, cdrCompanyFilter]);

  const handleExport = async () => {
    setExporting(true);
    setExportModalOpen(false);
    setExportMenuOpen(false);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (cdrPhoneFilter.trim()) params.append("numara", normalizeTurkishPhone(cdrPhoneFilter));
      if (statusFilter !== "all") params.append("durum", statusFilter);
      if (yonFilter !== "all") params.append("yon", yonFilter);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      if (isSuperAdmin && cdrCompanyFilter && cdrCompanyFilter !== "all") {
        params.append("company_id", cdrCompanyFilter);
      }
      
      const response = await axios.get(`${API}/cdr/export?${params}`, { timeout: 60000 });
      const data = response.data.data;
      
      if (data.length === 0) { toast.warning("Export edilecek kayıt yok"); return; }
      
      // Build export data based on selected columns
      const exportData = data.map(r => {
        const row = {};
        if (exportColumns.includes("tarih")) row["Tarih"] = r.tarih;
        if (exportColumns.includes("yon")) row["Yön"] = r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-";
        if (exportColumns.includes("agent_name")) row["Agent"] = r.agent_name || "";
        if (exportColumns.includes("company_name")) row["Firma"] = r.company_name || "";
        if (exportColumns.includes("arayan_numara")) row["Arayan"] = r.arayan_numara;
        if (exportColumns.includes("aranan_numara")) row["Aranan"] = r.aranan_numara;
        if (exportColumns.includes("konusma_suresi")) row["Konuşma (sn)"] = r.konusma_suresi;
        if (exportColumns.includes("did")) row["Departman"] = r.did;
        if (exportColumns.includes("durum")) row["Durum"] = r.durum_aciklama === "cevaplanan" ? "Cevaplandı" : r.durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız";
        if (exportColumns.includes("transkript")) row["Transkript"] = r.transkript === 1 ? "Var" : "Yok";
        if (exportColumns.includes("recording") && canSeeRecordings) row["Ses Kaydı"] = r.recording || "";
        return row;
      });
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CDR");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf]), `CDR_${startDate}_${endDate}.xlsx`);
      toast.success(`${data.length} kayıt export edildi`);
    } catch (error) {
      toast.error("Export hatası");
    } finally {
      setExporting(false);
    }
  };

  // Export CDR records to PDF
  const handleExportPdf = async () => {
    setExporting(true);
    setExportMenuOpen(false);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", `${startDate} ${startTime}:00`);
      if (endDate) params.append("end_date", `${endDate} ${endTime}:59`);
      if (cdrPhoneFilter.trim()) params.append("numara", normalizeTurkishPhone(cdrPhoneFilter));
      if (statusFilter !== "all") params.append("durum", statusFilter);
      if (yonFilter !== "all") params.append("yon", yonFilter);
      if (selectedAgent) params.append("agent_id", selectedAgent.id);
      if (isSuperAdmin && cdrCompanyFilter && cdrCompanyFilter !== "all") {
        params.append("company_id", cdrCompanyFilter);
      }
      
      const response = await axios.get(`${API}/cdr/export?${params}`, { timeout: 60000 });
      const data = response.data.data;
      
      if (data.length === 0) { 
        toast.warning("Export edilecek kayıt yok"); 
        setExporting(false);
        return; 
      }
      
      // Build table rows
      const tableRows = data.slice(0, 500).map(r => `
        <tr>
          <td>${r.tarih || '-'}</td>
          <td>${r.yon === 1 ? 'Gelen' : r.yon === 2 ? 'Giden' : '-'}</td>
          <td>${r.arayan_numara || '-'}</td>
          <td>${r.aranan_numara || '-'}</td>
          <td>${formatDuration(r.konusma_suresi)}</td>
          <td>${r.durum_aciklama === "cevaplanan" ? "Cevaplandı" : r.durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız"}</td>
        </tr>
      `).join('');

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>E-CDR Çağrı Kayıtları</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 11px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; }
            .header h1 { color: #3b82f6; margin: 0; font-size: 20px; }
            .header p { color: #666; margin: 5px 0 0; font-size: 12px; }
            .info { margin-bottom: 15px; font-size: 11px; color: #666; }
            table { width: 100%; border-collapse: collapse; }
            th { background: #3b82f6; color: white; padding: 8px 5px; text-align: left; font-size: 10px; }
            td { padding: 6px 5px; border-bottom: 1px solid #e2e8f0; font-size: 10px; }
            tr:nth-child(even) { background: #f8fafc; }
            .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 10px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>E-CDR Çağrı Kayıtları</h1>
            <p>${user?.firma_adi || 'E-CDR'}</p>
          </div>
          <div class="info">
            <strong>Tarih:</strong> ${startDate} ${startTime} - ${endDate} ${endTime} | 
            <strong>Toplam:</strong> ${data.length} kayıt ${data.length > 500 ? '(ilk 500 gösteriliyor)' : ''} |
            ${selectedAgent ? `<strong>Agent:</strong> ${selectedAgent.ad_soyad}` : ''}
          </div>
          <table>
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Yön</th>
                <th>Arayan</th>
                <th>Aranan</th>
                <th>Süre</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            E-CDR Çağrı Kayıt Yönetim Sistemi | Rapor: ${new Date().toLocaleString('tr-TR')}
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        toast.success("PDF yazdırma penceresi açıldı");
      }, 250);
    } catch (error) {
      toast.error("Export hatası");
    } finally {
      setExporting(false);
    }
  };

  // Export stats to Excel
  const handleExportStatsExcel = () => {
    if (!stats) {
      toast.warning("Export edilecek istatistik yok");
      return;
    }

    const dateRangeLabels = {
      today: "Bugün",
      yesterday: "Dün",
      last7: "Son 7 Gün",
      thisMonth: "Bu Ay",
      lastMonth: "Geçen Ay",
      custom: "Özel Tarih"
    };

    // Prepare stats data
    const statsData = [
      { "Bilgi": "Rapor Tarihi", "Değer": new Date().toLocaleString('tr-TR') },
      { "Bilgi": "Tarih Aralığı", "Değer": dateRangeLabels[statsDateRange] || statsDateRange },
      { "Bilgi": "Başlangıç", "Değer": `${startDate} ${startTime}` },
      { "Bilgi": "Bitiş", "Değer": `${endDate} ${endTime}` },
      ...(selectedAgent ? [{ "Bilgi": "Agent", "Değer": selectedAgent.ad_soyad }] : []),
      { "Bilgi": "", "Değer": "" },
      { "Bilgi": "Toplam Çağrı", "Değer": stats.total_calls || 0 },
      { "Bilgi": "Cevaplanan", "Değer": stats.answered_calls || 0 },
      { "Bilgi": "Cevaplanan Oranı (%)", "Değer": stats.total_calls ? ((stats.answered_calls / stats.total_calls) * 100).toFixed(1) : 0 },
      { "Bilgi": "Cevapsız", "Değer": stats.missed_calls || 0 },
      { "Bilgi": "Meşgul", "Değer": stats.busy_calls || 0 },
      { "Bilgi": "Toplam Konuşma (sn)", "Değer": stats.total_talk_time || 0 },
      { "Bilgi": "Ortalama Konuşma (sn)", "Değer": (stats.avg_talk_time || 0).toFixed(1) }
    ];

    const ws = XLSX.utils.json_to_sheet(statsData);
    
    // Set column widths
    ws['!cols'] = [{ wch: 25 }, { wch: 30 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "İstatistikler");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    
    const fileName = selectedAgent 
      ? `Istatistikler_${selectedAgent.ad_soyad}_${startDate}_${endDate}.xlsx`
      : `Istatistikler_${startDate}_${endDate}.xlsx`;
    
    saveAs(new Blob([buf]), fileName);
    toast.success("İstatistikler Excel olarak indirildi");
  };

  // Export stats to PDF
  const handleExportStatsPdf = () => {
    if (!stats) {
      toast.warning("Export edilecek istatistik yok");
      return;
    }

    const dateRangeLabels = {
      today: "Bugün",
      yesterday: "Dün",
      last7: "Son 7 Gün",
      thisMonth: "Bu Ay",
      lastMonth: "Geçen Ay",
      custom: "Özel Tarih"
    };

    // Create HTML content for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>E-CDR İstatistik Raporu</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; }
          .header h1 { color: #3b82f6; margin: 0; font-size: 24px; }
          .header p { color: #666; margin: 5px 0 0; }
          .info-section { margin-bottom: 30px; background: #f8fafc; padding: 15px; border-radius: 8px; }
          .info-section h3 { margin: 0 0 10px; color: #3b82f6; font-size: 14px; }
          .info-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #e2e8f0; }
          .info-row:last-child { border-bottom: none; }
          .stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
          .stat-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; text-align: center; }
          .stat-card.green { border-left: 4px solid #10b981; }
          .stat-card.amber { border-left: 4px solid #f59e0b; }
          .stat-card.red { border-left: 4px solid #ef4444; }
          .stat-card.blue { border-left: 4px solid #3b82f6; }
          .stat-card.cyan { border-left: 4px solid #06b6d4; }
          .stat-value { font-size: 28px; font-weight: bold; color: #1e293b; }
          .stat-label { font-size: 12px; color: #64748b; margin-top: 5px; }
          .footer { margin-top: 30px; text-align: center; color: #94a3b8; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>E-CDR İstatistik Raporu</h1>
          <p>${user?.firma_adi || 'E-CDR'} - Çağrı Merkezi İstatistikleri</p>
        </div>
        
        <div class="info-section">
          <h3>Rapor Bilgileri</h3>
          <div class="info-row"><span>Rapor Tarihi:</span><span>${new Date().toLocaleString('tr-TR')}</span></div>
          <div class="info-row"><span>Tarih Aralığı:</span><span>${dateRangeLabels[statsDateRange] || statsDateRange}</span></div>
          <div class="info-row"><span>Başlangıç:</span><span>${startDate} ${startTime}</span></div>
          <div class="info-row"><span>Bitiş:</span><span>${endDate} ${endTime}</span></div>
          ${selectedAgent ? `<div class="info-row"><span>Agent:</span><span>${selectedAgent.ad_soyad}</span></div>` : ''}
        </div>
        
        <div class="stats-grid">
          <div class="stat-card blue">
            <div class="stat-value">${(stats.total_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Toplam Çağrı</div>
          </div>
          <div class="stat-card green">
            <div class="stat-value">${(stats.answered_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Cevaplanan (${stats.total_calls ? ((stats.answered_calls / stats.total_calls) * 100).toFixed(1) : 0}%)</div>
          </div>
          <div class="stat-card amber">
            <div class="stat-value">${(stats.missed_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Cevapsız</div>
          </div>
          <div class="stat-card red">
            <div class="stat-value">${(stats.busy_calls || 0).toLocaleString('tr-TR')}</div>
            <div class="stat-label">Meşgul</div>
          </div>
          <div class="stat-card cyan">
            <div class="stat-value">${formatDuration(stats.total_talk_time || 0)}</div>
            <div class="stat-label">Toplam Konuşma</div>
          </div>
          <div class="stat-card cyan">
            <div class="stat-value">${formatDuration(Math.round(stats.avg_talk_time || 0))}</div>
            <div class="stat-label">Ortalama Konuşma</div>
          </div>
        </div>
        
        <div class="footer">
          E-CDR Çağrı Kayıt Yönetim Sistemi | ${new Date().getFullYear()}
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      toast.success("PDF yazdırma penceresi açıldı");
    }, 250);
  };

  // GSM Search Functions
  const fetchGsmSearch = useCallback(async () => {
    if (!gsmSearchNumber || gsmSearchNumber.length < 3) {
      toast.error("En az 3 karakter girin");
      return;
    }
    
    setGsmSearchLoading(true);
    try {
      const params = new URLSearchParams({ 
        gsm_numara: gsmSearchNumber,
        page: gsmSearchPage.toString(), 
        limit: gsmSearchLimit.toString() 
      });
      if (gsmSearchStartDate) params.append("start_date", gsmSearchStartDate);
      if (gsmSearchEndDate) params.append("end_date", gsmSearchEndDate + " 23:59:59");
      
      // Make both requests in parallel
      const resultsPromise = axios.get(`${API}/gsm-search?${params}`, { timeout: 30000 });
      const countPromise = axios.get(`${API}/gsm-search/count?${params}`, { timeout: 30000 });
      
      const resultsRes = await resultsPromise;
      const results = resultsRes.data || [];
      
      setGsmSearchResults(results);
      setGsmSearchLoading(false);
      
      if (results.length > 0) {
        setGsmSearchCount(results.length);
        toast.success(`${results.length}+ sonuç bulundu`);
        
        try {
          const countRes = await countPromise;
          setGsmSearchCount(countRes.data?.count || results.length);
        } catch (countError) {
          // Count fetch failed, using results length
        }
      } else {
        setGsmSearchCount(0);
        toast.info("Sonuç bulunamadı");
      }
    } catch (error) {
      console.error("GSM search error:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Arama zaman aşımı - lütfen tekrar deneyin");
      } else if (error.response?.status === 401) {
        toast.error("Oturum süresi doldu, lütfen tekrar giriş yapın");
      } else {
        toast.error("Arama sırasında hata oluştu");
      }
      setGsmSearchLoading(false);
    }
  }, [gsmSearchNumber, gsmSearchStartDate, gsmSearchEndDate, gsmSearchPage, gsmSearchLimit]);

  // Fetch Transcripts
  const fetchTranscripts = useCallback(async () => {
    if (!canSeeRecordings) return;
    
    setTranscriptsLoading(true);
    try {
      try {
        await axios.get(`${API}/transcripts/sync`, { timeout: 15000 });
      } catch (syncErr) {
        console.warn("Transcript sync skipped:", syncErr);
      }

      const params = new URLSearchParams({ page: transcriptPage.toString(), limit: "20" });
      if (transcriptFilter === "has") params.append("has_transcript", "true");
      if (transcriptFilter === "none") params.append("has_transcript", "false");
      if (transcriptSearchTerm.trim()) params.append("search", transcriptSearchTerm.trim());
      
      const res = await axios.get(`${API}/transcripts?${params}`, { timeout: 30000 });
      const rows = res.data.data || [];
      setTranscripts(rows);
      setTranscriptTotal(res.data.total || 0);
    } catch (error) {
      console.error("Transcripts fetch error:", error);
      toast.error("Transkriptler yüklenemedi");
    } finally {
      setTranscriptsLoading(false);
    }
  }, [transcriptPage, transcriptFilter, transcriptSearchTerm, canSeeRecordings]);

  const applyTranscriptSearch = () => {
    setSelectedTranscriptRecord(null);
    setTranscriptSearchTerm(transcriptSearchInput.trim());
    setTranscriptPage(1);
  };

  const clearTranscriptSearch = () => {
    setTranscriptSearchInput("");
    setTranscriptSearchTerm("");
    setTranscriptPage(1);
  };

  const isTranscriptInProgress = (record) => {
    if (!record) return false;
    const status = (record.status || "").toLowerCase();
    return ["queued", "processing", "pending"].includes(status);
  };

  const isTranscriptCompleted = (record) => {
    if (!record) return false;
    if (record.transkript === 1) return true;
    const status = (record.status || "").toLowerCase();
    return status === "completed" || !!record.transcript_text;
  };

  const getTranscriptStatusLabel = (status) => {
    const s = (status || "none").toLowerCase();
    if (s === "completed") return "Tamamlandı";
    if (s === "processing") return "İşleniyor";
    if (s === "failed") return "Hata oluştu";
    if (s === "queued" || s === "pending") return "Kuyrukta";
    if (s === "none" || s === "") return "Henüz başlatılmadı";
    return "Bekliyor";
  };

  const pollTranscriptStatus = useCallback(async (cdrId, updateSelected = true) => {
    if (!cdrId) return null;
    try {
      setTranscriptPolling(true);
      const res = await axios.get(`${API}/transcripts/${cdrId}/poll`, { timeout: 15000 });
      if (updateSelected) {
        setSelectedTranscriptRecord(res.data);
      }
      return res.data;
    } catch (error) {
      console.error("Transcript poll error:", error);
      return null;
    } finally {
      setTranscriptPolling(false);
    }
  }, []);

  // Create/Start Transcript
  const handleStartTranscript = async (record, options = {}) => {
    const { force = false } = options;
    const toastId = toast.loading("Transkript kuyruğa alınıyor...");
    try {
      const res = await axios.post(`${API}/transcripts`, {
        cdr_id: record.id || record.cdr_id,
        recording_url: record.recording || record.recording_url,
        s3_uri: record.s3_uri,
        language: "tr",
        force,
      }, { timeout: 30000 });
      const data = res.data.transcript || res.data;
      setSelectedTranscriptRecord(data);
      fetchTranscripts();
      if (data.status === "completed") {
        toast.success("Transkript tamamlandı", { id: toastId });
      } else if (data.status === "failed") {
        toast.error(data.error_message || "Transkript başarısız", { id: toastId });
      } else if (res.data.already_exists) {
        if (data.status === "completed") {
          toast.warning("Bu görüşme için transkript daha önce oluşturulmuş. Tekrar oluşturmak için Yeniden İşle kullanın.", { id: toastId });
        } else if (isTranscriptInProgress(data)) {
          toast.info("Transkript arka planda işleniyor", { id: toastId });
          pollTranscriptStatus(data.cdr_id || data.id, true);
        } else {
          toast.info("Bu kayıt için transkript işlemi zaten mevcut", { id: toastId });
        }
      } else {
        toast.success("Transkript kuyruğa alındı — arka planda işlenecek", { id: toastId });
        if (isTranscriptInProgress(data)) {
          pollTranscriptStatus(data.cdr_id || data.id, true);
        }
      }
      return res.data;
    } catch (error) {
      const msg = error.response?.data?.detail || "Transkript başlatılamadı";
      toast.error(msg, { id: toastId });
      return null;
    }
  };

  const requestStartTranscript = (record, options = {}) => {
    const { force = false } = options;
    if (force || isTranscriptCompleted(record)) {
      if (isTranscriptCompleted(record) || (record.status || "").toLowerCase() === "completed") {
        setTranscriptRecreateRecord(record);
        setTranscriptRecreateOptions(options);
        setTranscriptRecreateOpen(true);
        return;
      }
    }
    handleStartTranscript(record, options);
  };

  const handleConfirmRecreateTranscript = async () => {
    const record = transcriptRecreateRecord;
    if (!record) return;
    setTranscriptRecreateOpen(false);
    await handleStartTranscript(record, { ...transcriptRecreateOptions, force: true });
    setTranscriptRecreateRecord(null);
    setTranscriptRecreateOptions({});
  };

  const handleCopyTranscript = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Transkript kopyalandı");
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const handleViewTranscript = async (cdrId, options = {}) => {
    const { autoStart = false, searchQuery } = options;
    const term = searchQuery !== undefined ? (searchQuery || "").trim() : transcriptSearchTerm.trim();
    setTranscriptSearchQuery(term);
    try {
      const res = await axios.get(`${API}/transcripts/${cdrId}`);
      const data = res.data;
      setSelectedTranscriptRecord(data);
      const status = (data.status || "").toLowerCase();
      if (isTranscriptInProgress(data)) {
        pollTranscriptStatus(cdrId, true);
      } else if (autoStart && (data.status === "none" || !data.status) && (data.recording || data.recording_url || data.s3_uri)) {
        await handleStartTranscript(data);
      }
    } catch (error) {
      toast.error("Transkript detayı yüklenemedi");
    }
  };

  const handleLeadStatusesSaved = useCallback((statuses, sources, companyId) => {
    if (!isSuperAdmin && String(companyId) === String(user?.company_id)) {
      if (statuses?.length) setLeadStatuses(statuses);
      if (sources?.length) setLeadSources(sources);
    }
  }, [isSuperAdmin, user?.company_id]);

  const openCompanyCrmSettings = (company) => {
    if (!company?.id) return;
    setCrmSettingsCompany(company);
    setCompanyCrmSettingsOpen(true);
  };

  const cacheCustomerProfile = useCallback((phone, profile) => {
    const key = normalizeTurkishPhone(phone);
    if (!key || !profile) return;
    setCustomerProfileCache((prev) => ({ ...prev, [key]: profile }));
  }, []);

  useEffect(() => {
    customerProfileCacheRef.current = customerProfileCache;
  }, [customerProfileCache]);

  const fetchCustomerProfileOnDemand = useCallback(async (phone) => {
    const key = normalizeTurkishPhone(phone);
    if (!key || !phone) return;
    if (customerProfileCacheRef.current[key] || customerPrefetchPendingRef.current.has(key)) return;

    customerPrefetchPendingRef.current.add(key);
    setCustomerProfileLoadingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await axios.get(`${API}/customers`, { params: { phone }, timeout: 15000 });
      const profile = res.data?.profile;
      setCustomerProfileCache((prev) => (
        prev[key] ? prev : { ...prev, [key]: profile || { ad_soyad: "", lead_durumu: "yeni" } }
      ));
    } catch {
      setCustomerProfileCache((prev) => (
        prev[key] ? prev : { ...prev, [key]: { ad_soyad: "", lead_durumu: "yeni", _prefetch_failed: true } }
      ));
    } finally {
      customerPrefetchPendingRef.current.delete(key);
      setCustomerProfileLoadingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, []);

  const openCustomerCrm = (record) => {
    const phone = getCustomerPhoneFromRecord(record, agents);
    if (!phone) {
      toast.warning("Bu kayıt için müşteri numarası belirlenemedi");
      return;
    }
    setCustomerCrmPhone(phone);
    setCustomerCrmRecord(record);
    setCustomerCrmOpen(true);
  };

  const handleCustomerCrmViewTranscript = async (cdrId) => {
    setCustomerCrmOpen(false);
    await handleViewTranscript(cdrId);
    navigateToTab("transcripts");
  };

  const openTranscriptDeleteModal = (record) => {
    setTranscriptToDelete(record);
    setTranscriptDeleteOpen(true);
  };

  const handleConfirmDeleteTranscript = async () => {
    const cdrId = transcriptToDelete?.cdr_id || transcriptToDelete?.id;
    if (!cdrId) return;
    setTranscriptDeleting(true);
    try {
      await axios.delete(`${API}/transcripts/${cdrId}`);
      toast.success("Transkript kaydı silindi");
      if ((selectedTranscriptRecord?.cdr_id || selectedTranscriptRecord?.id) === cdrId) {
        setSelectedTranscriptRecord(null);
      }
      setTranscriptDeleteOpen(false);
      setTranscriptToDelete(null);
      fetchTranscripts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transkript silinemedi");
    } finally {
      setTranscriptDeleting(false);
    }
  };

  // Agent Analytics Functions
  const getAnalyticsDateRange = useCallback(() => {
    const today = new Date();
    let start, end;
    
    switch (analyticsDateRange) {
      case "today":
        start = end = getTodayStr();
        break;
      case "yesterday":
        start = end = getYesterdayStr();
        break;
      case "this_week": {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = formatLocalDate(weekStart);
        end = getTodayStr();
        break;
      }
      case "this_month":
        start = getThisMonthStart();
        end = getTodayStr();
        break;
      case "last_month":
        ({ start, end } = getLastMonthRange());
        break;
      case "custom":
        start = analyticsCustomStart || getTodayStr();
        end = analyticsCustomEnd || getTodayStr();
        break;
      default:
        start = end = getTodayStr();
    }
    return { start, end };
  }, [analyticsDateRange, analyticsCustomStart, analyticsCustomEnd]);

  const fetchAgentAnalytics = useCallback(async () => {
    if (!analyticsAgent) return;
    
    setAnalyticsLoading(true);
    try {
      const { start, end } = getAnalyticsDateRange();
      const res = await axios.get(`${API}/agents/${analyticsAgent.id}/analytics?start_date=${start}&end_date=${end}`, { timeout: 60000 });
      setAnalyticsData(res.data);
    } catch (error) {
      console.error("Analytics fetch error:", error);
      toast.error("Analiz verileri yüklenemedi");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsAgent, getAnalyticsDateRange]);

  useEffect(() => {
    if (analyticsAgent) {
      fetchAgentAnalytics();
    }
  }, [analyticsAgent, analyticsDateRange, analyticsCustomStart, analyticsCustomEnd, fetchAgentAnalytics]);

  const openAgentAnalytics = (agent) => {
    setAnalyticsAgent(agent);
    setAnalyticsData(null);
  };

  const closeAgentAnalytics = () => {
    setAnalyticsAgent(null);
    setAnalyticsData(null);
    agentAnalyticsModalExpand.resetExpanded();
  };

  const closeMesaiLateModal = () => {
    setMesaiLateModalAgent(null);
    mesaiLateModalExpand.resetExpanded();
  };

  const closeMesaiEarlyModal = () => {
    setMesaiEarlyModalAgent(null);
    mesaiEarlyModalExpand.resetExpanded();
  };

  const closeMolaBreakModal = () => {
    setMolaBreakModalAgent(null);
    molaBreakModalExpand.resetExpanded();
  };

  // Reports Functions
  const fetchReports = useCallback(async (dateOverride) => {
    const startDate = dateOverride?.start ?? reportsStartDate;
    const endDate = dateOverride?.end ?? reportsEndDate;
    const agentCount = reportsSelectedAgents.length;
    setReportsLoading(true);
    if (agentCount > 15) {
      toast.info(`${agentCount} agent için rapor hazırlanıyor, lütfen bekleyin...`, { duration: 5000 });
    }
    try {
      const params = new URLSearchParams();
      params.append("start_date", startDate);
      params.append("end_date", endDate);
      if (reportsSelectedAgents.length > 0) {
        params.append("agent_ids", reportsSelectedAgents.join(","));
      } else {
        params.append("agent_ids", "all");
      }
      if (reportsYon !== "all") {
        params.append("yon", reportsYon);
      }
      if (isSuperAdmin && reportsCompanyId && reportsCompanyId !== "all") {
        params.append("company_id", reportsCompanyId);
      }
      appendReportScopeParams(params);

      const res = await axios.get(`${API}/agent-report?${params}`, { timeout: REPORT_API_TIMEOUT });
      setReportsData(res.data);
    } catch (error) {
      console.error("Reports fetch error:", error);
      if (error.code === "ECONNABORTED") {
        toast.error("Rapor zaman aşımına uğradı. Daha kısa tarih aralığı veya daha az agent deneyin.");
      } else {
        toast.error(error.response?.data?.detail || "Rapor verileri yüklenemedi");
      }
    } finally {
      setReportsLoading(false);
    }
  }, [reportsStartDate, reportsEndDate, reportsSelectedAgents, reportsYon, reportsCompanyId, isSuperAdmin, appendReportScopeParams]);

  const handleReportsQuickRange = (preset) => {
    let start;
    let end;
    if (preset === "today") {
      start = getTodayStr();
      end = getTodayStr();
    } else if (preset === "yesterday") {
      start = getYesterdayStr();
      end = getYesterdayStr();
    } else if (preset === "thisMonth") {
      start = getThisMonthStart();
      end = getTodayStr();
    } else if (preset === "lastMonth") {
      ({ start, end } = getLastMonthRange());
    } else {
      return;
    }
    setReportsDatePreset(preset);
    setReportsStartDate(start);
    setReportsEndDate(end);
    if (reportsSelectedAgents.length > 0) {
      fetchReports({ start, end });
    }
  };

  const handleReportsSelectAllAgents = () => {
    if (reportsSelectedAgents.length === reportsScopedAgents.length && reportsScopedAgents.length > 0) {
      setReportsSelectedAgents([]);
    } else {
      setReportsSelectedAgents(reportsScopedAgents.map(a => a.id));
    }
  };

  const handleReportsSelectScopedAgents = () => {
    setReportsSelectedAgents(reportsScopedAgents.map((a) => a.id));
  };

  const handleReportsToggleAgent = (agentId) => {
    const isAdding = !reportsSelectedAgents.includes(agentId);
    setReportsSelectedAgents((prev) =>
      prev.includes(agentId)
        ? prev.filter((id) => id !== agentId)
        : [...prev, agentId]
    );
    if (isAdding) {
      setReportsAgentSearch("");
    }
  };

  const formatTalkTime = (seconds) => {
    if (!seconds || seconds === 0) return "0 dk";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours} saat ${minutes} dk`;
    }
    return `${minutes} dk`;
  };

  const handleExportReportsPdf = () => {
    if (!reportsData || !reportsData.data || reportsData.data.length === 0) {
      toast.warning("Export edilecek veri yok");
      return;
    }

    const tableRows = reportsData.data.map(r => `
      <tr>
        <td>${r.agent_name}</td>
        <td>${formatReportDate(r.period)}</td>
        <td>${r.total_calls} (${r.unique_total_calls ?? 0} tekil)</td>
        <td>${r.answered_calls} (${r.unique_answered_calls ?? 0} tekil)</td>
        <td>${r.missed_calls} (${r.unique_missed_calls ?? 0} tekil)</td>
        <td>${formatTalkTime(r.total_talk_time)}</td>
        <td>${r.short_calls ?? 0}</td>
        <td>${r.unfollowed_missed ?? 0}</td>
        <td>${formatTalkTime(r.total_break_seconds || 0)}</td>
        <td>${r.working_days > 0 ? formatTalkTime(r.avg_daily_break_seconds ?? Math.round((r.total_break_seconds || 0) / r.working_days)) : "-"}</td>
        <td>${r.working_days} gün</td>
        <td>${formatTalkTime(r.avg_daily_talk_time)}</td>
      </tr>
    `).join('');

    const totals = reportsData.totals;
    const footerRow = `
      <tr style="font-weight: bold; background: #e2e8f0;">
        <td colspan="2">TOPLAM</td>
        <td>${totals.total_calls} (${totals.unique_total_calls ?? 0} tekil)</td>
        <td>${totals.answered_calls} (${totals.unique_answered_calls ?? 0} tekil)</td>
        <td>${totals.missed_calls} (${totals.unique_missed_calls ?? 0} tekil)</td>
        <td>${formatTalkTime(totals.total_talk_time)}</td>
        <td>${totals.short_calls ?? 0}</td>
        <td>${totals.unfollowed_missed ?? 0}</td>
        <td>${formatTalkTime(totals.total_break_seconds || 0)}</td>
        <td>${totals.total_working_days > 0 ? formatTalkTime(totals.avg_daily_break_seconds ?? Math.round((totals.total_break_seconds || 0) / totals.total_working_days)) : "-"}</td>
        <td>${totals.total_working_days} gün</td>
        <td>${formatTalkTime(totals.avg_daily_talk_time)}</td>
      </tr>
    `;

    const companyLabel = isSuperAdmin && reportsCompanyId !== "all"
      ? (companies.find((c) => String(c.id) === String(reportsCompanyId))?.ad || "Seçili Firma")
      : (user?.firma_adi || "E-CDR");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>E-CDR Agent Görüşme Raporu</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 11px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; }
          .header h1 { color: #3b82f6; margin: 0; font-size: 18px; }
          .header p { color: #666; margin: 5px 0 0; font-size: 12px; }
          .info { margin-bottom: 15px; font-size: 11px; color: #666; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #3b82f6; color: white; padding: 8px 5px; text-align: left; font-size: 10px; }
          td { padding: 6px 5px; border-bottom: 1px solid #e2e8f0; font-size: 10px; }
          tr:nth-child(even) { background: #f8fafc; }
          .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 10px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Agent Görüşme Raporu</h1>
          <p>${companyLabel}</p>
        </div>
        <div class="info">
          <strong>Tarih:</strong> ${formatReportDate(reportsStartDate)} - ${formatReportDate(reportsEndDate)} | 
          <strong>Yön:</strong> ${reportsYon === '1' ? 'Gelen' : reportsYon === '2' ? 'Giden' : 'Tümü'} |
          ${isSuperAdmin && reportsCompanyId !== "all" ? `<strong>Firma:</strong> ${companyLabel} | ` : ""}
          <strong>Agent Sayısı:</strong> ${reportsData.data.length}
        </div>
        <table>
          <thead>
            <tr>
              <th>Agent İsmi</th>
              <th>Tarih Aralığı</th>
              <th>Toplam Çağrı</th>
              <th>Cevaplanan</th>
              <th>Cevapsız</th>
              <th>Toplam Konuşma</th>
              <th>&lt;1 dk Görüşme</th>
              <th>Takipsiz Cvsz</th>
              <th>Mola Süresi</th>
              <th>Ort. Mola/Gün</th>
              <th>Çalışma Günü</th>
              <th>Ort/Konuşma</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
            ${footerRow}
          </tbody>
        </table>
        <div class="footer">
          E-CDR Çağrı Kayıt Yönetim Sistemi | Rapor: ${new Date().toLocaleString('tr-TR')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      toast.success("PDF yazdırma penceresi açıldı");
    }, 250);
  };

  const handleExportReportsExcel = () => {
    if (!reportsData || !reportsData.data || reportsData.data.length === 0) {
      toast.warning("Export edilecek veri yok");
      return;
    }

    const excelData = reportsData.data.map(r => ({
      "Agent İsmi": r.agent_name,
      "Tarih Aralığı": formatReportDate(r.period),
      "Toplam Çağrı": r.total_calls,
      "Toplam Tekil": r.unique_total_calls ?? 0,
      "Cevaplanan": r.answered_calls,
      "Cevaplanan Tekil": r.unique_answered_calls ?? 0,
      "Cevapsız": r.missed_calls,
      "Cevapsız Tekil": r.unique_missed_calls ?? 0,
      "Toplam Konuşma (sn)": r.total_talk_time,
      "Toplam Konuşma": formatTalkTime(r.total_talk_time),
      "<1 dk Görüşme": r.short_calls ?? 0,
      "Takipsiz Cevapsız": r.unfollowed_missed ?? 0,
      "Mola Süresi (sn)": r.total_break_seconds ?? 0,
      "Mola Süresi": formatTalkTime(r.total_break_seconds || 0),
      "Ort. Mola/Gün (sn)": r.avg_daily_break_seconds ?? (r.working_days > 0 ? Math.round((r.total_break_seconds || 0) / r.working_days) : 0),
      "Ort. Mola/Gün": r.working_days > 0 ? formatTalkTime(r.avg_daily_break_seconds ?? Math.round((r.total_break_seconds || 0) / r.working_days)) : "-",
      "Çalışma Günü": r.working_days,
      "Ort/Konuşma (sn)": r.avg_daily_talk_time,
      "Ort/Konuşma": formatTalkTime(r.avg_daily_talk_time)
    }));

    // Add totals row
    const totals = reportsData.totals;
    excelData.push({
      "Agent İsmi": "TOPLAM",
      "Tarih Aralığı": "",
      "Toplam Çağrı": totals.total_calls,
      "Toplam Tekil": totals.unique_total_calls ?? 0,
      "Cevaplanan": totals.answered_calls,
      "Cevaplanan Tekil": totals.unique_answered_calls ?? 0,
      "Cevapsız": totals.missed_calls,
      "Cevapsız Tekil": totals.unique_missed_calls ?? 0,
      "Toplam Konuşma (sn)": totals.total_talk_time,
      "Toplam Konuşma": formatTalkTime(totals.total_talk_time),
      "<1 dk Görüşme": totals.short_calls ?? 0,
      "Takipsiz Cevapsız": totals.unfollowed_missed ?? 0,
      "Mola Süresi (sn)": totals.total_break_seconds ?? 0,
      "Mola Süresi": formatTalkTime(totals.total_break_seconds || 0),
      "Ort. Mola/Gün (sn)": totals.avg_daily_break_seconds ?? (totals.total_working_days > 0 ? Math.round((totals.total_break_seconds || 0) / totals.total_working_days) : 0),
      "Ort. Mola/Gün": totals.total_working_days > 0 ? formatTalkTime(totals.avg_daily_break_seconds ?? Math.round((totals.total_break_seconds || 0) / totals.total_working_days)) : "-",
      "Çalışma Günü": totals.total_working_days,
      "Ort/Konuşma (sn)": totals.avg_daily_talk_time,
      "Ort/Konuşma": formatTalkTime(totals.avg_daily_talk_time)
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
      { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 15 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Agent Raporu");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `Agent_Gorusme_Raporu_${reportsStartDate}_${reportsEndDate}.xlsx`);
    toast.success("Excel dosyası indirildi");
  };

  const getReportDetailExportRows = () => {
    if (!reportDetailData?.daily_breakdown?.length) return [];
    return sortReportRows(reportDetailData.daily_breakdown, reportDetailSort);
  };

  const formatReportDetailWorkingRange = (mins) => {
    if (!mins || mins <= 0) return "-";
    return `${Math.floor(mins / 60)}sa ${mins % 60}dk`;
  };

  const mapReportDetailDayExportRow = (day) => ({
    "Tarih": formatReportDate(day.call_date),
    "Toplam Çağrı": day.total_calls,
    "Toplam Tekil": day.unique_total_calls ?? 0,
    "Cevaplanan": day.answered_calls,
    "Cevaplanan Tekil": day.unique_answered_calls ?? 0,
    "Cevapsız": day.missed_calls ?? (day.total_calls - day.answered_calls),
    "Cevapsız Tekil": day.unique_missed_calls ?? 0,
    "Konuşma": formatTalkTime(day.total_talk_time || 0),
    "Konuşma (sn)": day.total_talk_time || 0,
    "<1 dk Görüşme": day.short_calls ?? 0,
    "Takipsiz Cevapsız": day.unfollowed_missed ?? 0,
    "Mola Süresi": formatTalkTime(day.break_seconds || 0),
    "Mola (sn)": day.break_seconds || 0,
    "Çalışma Günü": day.is_working_day ? "Çalıştı" : "<1 saat",
    "İlk Çağrı": day.first_call || "-",
    "Son Çağrı": day.last_call || "-",
    "Aralık": formatReportDetailWorkingRange(day.working_minutes),
  });

  const handleExportReportDetailExcel = () => {
    const rows = getReportDetailExportRows();
    if (!rows.length) {
      toast.warning("Export edilecek veri yok");
      return;
    }

    const agentName = reportDetailAgent?.agent_name || "Agent";
    const safeName = agentName.replace(/[^\w\s-ğüşıöçĞÜŞİÖÇ]/g, "").trim().replace(/\s+/g, "_") || "Agent";
    const excelData = rows.map(mapReportDetailDayExportRow);
    const summary = reportDetailData?.summary;
    if (summary) {
      excelData.push({
        "Tarih": "ÖZET",
        "Toplam Çağrı": summary.total_calls ?? 0,
        "Toplam Tekil": summary.unique_total_calls ?? 0,
        "Cevaplanan": summary.answered_calls ?? 0,
        "Cevaplanan Tekil": summary.unique_answered_calls ?? 0,
        "Cevapsız": summary.missed_calls ?? 0,
        "Cevapsız Tekil": summary.unique_missed_calls ?? 0,
        "Konuşma": formatTalkTime((summary.total_talk_minutes || 0) * 60),
        "Konuşma (sn)": (summary.total_talk_minutes || 0) * 60,
        "<1 dk Görüşme": "",
        "Takipsiz Cevapsız": "",
        "Mola Süresi": "",
        "Mola (sn)": "",
        "Çalışma Günü": `${summary.working_days ?? 0} gün`,
        "İlk Çağrı": "-",
        "Son Çağrı": "-",
        "Aralık": "-",
      });
    }

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gunluk Detay");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `${safeName}_Gunluk_Detay_${reportsStartDate}_${reportsEndDate}.xlsx`);
    toast.success("Günlük detay Excel dosyası indirildi");
  };

  const handleExportReportDetailPdf = () => {
    const rows = getReportDetailExportRows();
    if (!rows.length) {
      toast.warning("Export edilecek veri yok");
      return;
    }

    const agentName = reportDetailAgent?.agent_name || "Agent";
    const yonLabel = reportsYon === "1" ? "Gelen" : reportsYon === "2" ? "Giden" : "Tümü";
    const workingDays = reportDetailData?.summary?.working_days;

    const tableRows = rows.map((day) => `
      <tr>
        <td>${formatReportDate(day.call_date)}</td>
        <td>${day.total_calls} (${day.unique_total_calls ?? 0})</td>
        <td>${day.answered_calls} (${day.unique_answered_calls ?? 0})</td>
        <td>${day.missed_calls ?? (day.total_calls - day.answered_calls)} (${day.unique_missed_calls ?? 0})</td>
        <td>${formatTalkTime(day.total_talk_time || 0)}</td>
        <td>${day.short_calls ?? 0}</td>
        <td>${day.unfollowed_missed ?? 0}</td>
        <td>${formatTalkTime(day.break_seconds || 0)}</td>
        <td>${day.is_working_day ? "Çalıştı" : "&lt;1 saat"}</td>
        <td>${day.first_call || "-"}</td>
        <td>${day.last_call || "-"}</td>
        <td>${formatReportDetailWorkingRange(day.working_minutes)}</td>
      </tr>
    `).join("");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${agentName} - Günlük Detay</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 11px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #14b8a6; padding-bottom: 15px; }
          .header h1 { color: #0f766e; margin: 0; font-size: 18px; }
          .header p { color: #666; margin: 5px 0 0; font-size: 12px; }
          .info { margin-bottom: 15px; font-size: 11px; color: #666; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #14b8a6; color: white; padding: 8px 5px; text-align: left; font-size: 10px; }
          td { padding: 6px 5px; border-bottom: 1px solid #e2e8f0; font-size: 10px; }
          tr:nth-child(even) { background: #f0fdfa; }
          .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 10px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${agentName} — Günlük Detay</h1>
          <p>Agent Görüşme Raporu</p>
        </div>
        <div class="info">
          <strong>Tarih:</strong> ${formatReportDate(reportsStartDate)} - ${formatReportDate(reportsEndDate)} |
          <strong>Yön:</strong> ${yonLabel}
          ${workingDays != null ? " | <strong>Çalışma günü:</strong> " + workingDays : ""}
        </div>
        <table>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Toplam</th>
              <th>Cevaplanan</th>
              <th>Cevapsız</th>
              <th>Konuşma</th>
              <th>&lt;1 dk</th>
              <th>Takipsiz</th>
              <th>Mola</th>
              <th>Gün</th>
              <th>İlk</th>
              <th>Son</th>
              <th>Aralık</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
        <div class="footer">
          E-CDR Çağrı Kayıt Yönetim Sistemi | Rapor: ${new Date().toLocaleString("tr-TR")}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      toast.success("PDF yazdırma penceresi açıldı");
    }, 250);
  };

  // Fetch daily detail for a specific agent in reports
  const fetchReportDailyDetail = async (agentRow) => {
    setReportDetailAgent(agentRow);
    setReportDetailLoading(true);
    setReportDetailData(null);
    setReportDetailSelectedDate(null);
    setReportDetailDayRecords([]);
    setReportDetailUniqueOnly(false);
    
    try {
      const agent = agents.find(a => a.id === agentRow.agent_id);
      if (!agent) {
        toast.error("Agent bulunamadı");
        return;
      }
      
      const params = new URLSearchParams();
      params.append("start_date", reportsStartDate);
      params.append("end_date", reportsEndDate);
      if (reportsYon !== "all") {
        params.append("yon", reportsYon);
      }
      const res = await axios.get(`${API}/agents/${agentRow.agent_id}/analytics?${params}`, { timeout: 60000 });
      setReportDetailData(res.data);
    } catch (error) {
      console.error("Report detail fetch error:", error);
      toast.error("Günlük detay yüklenemedi");
    } finally {
      setReportDetailLoading(false);
    }
  };

  const fetchReportDayRecords = async (date, uniqueOnly = reportDetailUniqueOnly) => {
    if (!reportDetailAgent?.agent_id || !date) return;

    setReportDetailDayRecordsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("date", date);
      if (reportsYon !== "all") {
        params.append("yon", reportsYon);
      }
      if (uniqueOnly) {
        params.append("unique_only", "1");
      }
      const res = await axios.get(`${API}/agents/${reportDetailAgent.agent_id}/day-records?${params}`, { timeout: 60000 });
      setReportDetailDayRecords(res.data.records || []);
    } catch (error) {
      console.error("Report day records fetch error:", error);
      toast.error("Günlük kayıtlar yüklenemedi");
      setReportDetailDayRecords([]);
    } finally {
      setReportDetailDayRecordsLoading(false);
    }
  };

  const handleReportDayClick = (day) => {
    if (reportDetailSelectedDate === day.call_date) {
      setReportDetailSelectedDate(null);
      setReportDetailDayRecords([]);
      return;
    }
    setReportDetailSelectedDate(day.call_date);
    fetchReportDayRecords(day.call_date, reportDetailUniqueOnly);
  };

  useEffect(() => {
    if (!reportDetailSelectedDate) return;
    const frame = requestAnimationFrame(() => {
      reportDetailDayRecordsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [reportDetailSelectedDate]);

  const handleReportUniqueOnlyChange = (checked) => {
    setReportDetailUniqueOnly(checked);
    if (reportDetailSelectedDate) {
      fetchReportDayRecords(reportDetailSelectedDate, checked);
    }
  };

  const closeReportDetail = () => {
    setReportDetailAgent(null);
    setReportDetailData(null);
    setReportDetailSelectedDate(null);
    setReportDetailDayRecords([]);
    setReportDetailUniqueOnly(false);
    setReportDetailSort({ key: "call_date", dir: "desc" });
    setCallAnalysisOpen(false);
    setCallAnalysisData(null);
    reportDetailModalExpand.resetExpanded();
  };

  const openCallAnalysis = async (date) => {
    if (!reportDetailAgent?.agent_id || !date) return;
    setCallAnalysisOpen(true);
    setCallAnalysisLoading(true);
    setCallAnalysisData(null);
    try {
      const params = new URLSearchParams({ date });
      if (reportsYon !== "all") params.append("yon", reportsYon);
      const res = await axios.get(`${API}/agents/${reportDetailAgent.agent_id}/day-call-analysis?${params}`, { timeout: 60000 });
      setCallAnalysisData(res.data);
    } catch (error) {
      console.error("Call analysis fetch error:", error);
      toast.error(error.response?.data?.detail || "Çağrı analizi yüklenemedi");
      setCallAnalysisOpen(false);
    } finally {
      setCallAnalysisLoading(false);
    }
  };

  const closeCallAnalysis = () => {
    setCallAnalysisOpen(false);
    setCallAnalysisData(null);
  };

  const openUnfollowedMissedModal = async ({ agentId, agentName, startDate, endDate }) => {
    if (!agentId || Number.isNaN(agentId)) return;
    setUnfollowedMissedOpen(true);
    setUnfollowedMissedLoading(true);
    setUnfollowedMissedData(null);
    try {
      const params = new URLSearchParams();
      if (startDate === endDate) {
        params.append("date", startDate);
      } else {
        params.append("start_date", startDate);
        params.append("end_date", endDate);
      }
      if (reportsYon !== "all") {
        params.append("yon", reportsYon);
      }
      const res = await axios.get(`${API}/agents/${agentId}/unfollowed-missed?${params}`, { timeout: 60000 });
      setUnfollowedMissedData(res.data);
    } catch (error) {
      console.error("Unfollowed missed fetch error:", error);
      toast.error(error.response?.data?.detail || "Takipsiz cevapsız listesi yüklenemedi");
      setUnfollowedMissedOpen(false);
    } finally {
      setUnfollowedMissedLoading(false);
    }
  };

  const closeUnfollowedMissedModal = () => {
    setUnfollowedMissedOpen(false);
    setUnfollowedMissedData(null);
  };

  const fetchBreakAnalysis = async (agentRow, options = {}) => {
    const startDate = options.startDate ?? reportsStartDate;
    const endDate = options.endDate ?? reportsEndDate;
    const yon = options.yon ?? reportsYon;
    const view = options.view === "special" ? "special" : "standard";

    setBreakAnalysisView(view);
    setBreakAnalysisAgent(agentRow);
    setBreakAnalysisLoading(true);
    setBreakAnalysisData(null);
    setBreakAnalysisExpandedDay(null);
    setBreakAnalysisUnfollowedDay(null);
    setBreakAnalysisPeriod({ start: startDate, end: endDate });

    try {
      const params = new URLSearchParams();
      params.append("start_date", startDate);
      params.append("end_date", endDate);
      if (yon !== "all") {
        params.append("yon", yon);
      }
      const res = await axios.get(`${API}/agents/${agentRow.agent_id}/break-analysis?${params}`, { timeout: BREAK_ANALYSIS_TIMEOUT });
      setBreakAnalysisData(res.data);
      const firstActiveDay = res.data.daily_breakdown?.find((d) => d.has_activity);
      setBreakAnalysisExpandedDay(firstActiveDay?.date || null);
    } catch (error) {
      console.error("Break analysis fetch error:", error);
      if (error.code === "ECONNABORTED") {
        toast.error("Mola analizi zaman aşımına uğradı. Daha kısa tarih aralığı deneyin.");
      } else {
        toast.error(error.response?.data?.detail || "Mola analizi yüklenemedi");
      }
    } finally {
      setBreakAnalysisLoading(false);
    }
  };

  const closeBreakAnalysis = () => {
    setBreakAnalysisAgent(null);
    setBreakAnalysisData(null);
    setBreakAnalysisExpandedDay(null);
    setBreakAnalysisUnfollowedDay(null);
    breakAnalysisModalExpand.resetExpanded();
    setBreakAnalysisPeriod(null);
    setBreakAnalysisView(null);
  };

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", devicesPage.toString());
      params.append("limit", "20");
      if (devicesSearch) {
        params.append("search", devicesSearch);
      }
      if (isSuperAdmin && devicesCompanyId && devicesCompanyId !== "all") {
        params.append("company_id", devicesCompanyId);
      }
      
      const res = await axios.get(`${API}/devices?${params}`, { timeout: 30000 });
      setDevices(res.data.data || []);
      setDevicesTotalPages(res.data.total_pages || 1);
      setDevicesTotal(res.data.total || 0);
    } catch (error) {
      console.error("Devices fetch error:", error);
      toast.error("Cihazlar yüklenemedi");
    }
    setDevicesLoading(false);
  }, [devicesPage, devicesSearch, devicesCompanyId, isSuperAdmin]);

  useEffect(() => {
    if (activeTab !== "devices") return;
    const routeKey = buildTabRouteKey("devices", "default");
    const signature = [devicesPage, devicesSearch, devicesCompanyId, tabReclickNonce].join("|");
    runTabFetch(routeKey, signature, fetchDevices);
  }, [activeTab, devicesPage, devicesSearch, devicesCompanyId, tabReclickNonce, fetchDevices, runTabFetch]);

  const handleDevicesSearch = (e) => {
    e.preventDefault();
    setDevicesPage(1);
    fetchDevices();
  };

  // Cihaz GSM güncelle
  const handleUpdateDeviceGsm = async (deviceId, gsm) => {
    const normalized = (gsm ?? "").trim();
    const payload = normalized === "" ? "-" : normalized;
    try {
      await axios.put(`${API}/devices/${deviceId}`, { gsm: payload });
      toast.success(normalized === "" ? "GSM temizlendi" : "GSM güncellendi");
      setEditingDeviceGsm(prev => {
        const newState = { ...prev };
        delete newState[deviceId];
        return newState;
      });
      invalidateTabRoute("devices");
      fetchDevices();
    } catch (error) {
      console.error("Update device GSM error:", error);
      toast.error("GSM güncellenemedi");
    }
  };

  const handleUpdateDeviceCompany = async (deviceId, companyId) => {
    try {
      await axios.put(`${API}/devices/${deviceId}`, {
        company_id: companyId === "none" ? null : parseInt(companyId, 10),
      });
      toast.success("Firma ataması güncellendi");
      invalidateTabRoute("devices");
      fetchDevices();
      fetchAllDevicesList();
    } catch (error) {
      console.error("Update device company error:", error);
      toast.error(error.response?.data?.detail || "Firma ataması güncellenemedi");
    }
  };

  // Cihaz sil
  const resolvedDevicesCleanupCompanyId = useMemo(() => {
    if (isSuperAdmin) {
      if (!devicesCompanyId || devicesCompanyId === "all" || devicesCompanyId === "none") return null;
      return parseInt(devicesCompanyId, 10);
    }
    return user?.company_id ? parseInt(user.company_id, 10) : null;
  }, [isSuperAdmin, devicesCompanyId, user?.company_id]);

  const resolvedDevicesCleanupCompanyName = useMemo(() => {
    if (!resolvedDevicesCleanupCompanyId) return "";
    const c = companies.find((x) => Number(x.id) === Number(resolvedDevicesCleanupCompanyId));
    return c?.ad || "";
  }, [companies, resolvedDevicesCleanupCompanyId]);

  const openDeviceCleanupModal = (device) => {
    setDeviceCleanupMode("device");
    setDeviceCleanupDevice(device);
    setDeviceCleanupOpen(true);
  };

  const openCompanyDeviceCleanupModal = () => {
    if (!resolvedDevicesCleanupCompanyId) {
      toast.warning(isSuperAdmin ? "Firma toplu temizlik için listeden bir firma seçin" : "Firma bilgisi bulunamadı");
      return;
    }
    setDeviceCleanupMode("company");
    setDeviceCleanupDevice(null);
    setDeviceCleanupOpen(true);
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm("Bu cihazı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/devices/${deviceId}`);
      toast.success("Cihaz silindi");
      invalidateTabRoute("devices");
      fetchDevices();
      fetchDeviceAgentMap();
    } catch (error) {
      console.error("Delete device error:", error);
      toast.error("Cihaz silinemedi");
    }
  };

  // Cihaz çağrı kayıtlarını getir
  const fetchCallLogs = async (deviceId, page = 1, startDate = null, endDate = null, startTime = null, endTime = null, searchOverride, limitOverride, excludeMissedOverride, callTypeFilterOverride) => {
    setCallLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("device_id", deviceId);
      params.append("page", page.toString());
      const pageLimit = limitOverride !== undefined ? limitOverride : callLogsLimit;
      params.append("limit", String(pageLimit));
      
      const sDate = startDate !== undefined && startDate !== null ? startDate : callLogsStartDate;
      const eDate = endDate !== undefined && endDate !== null ? endDate : callLogsEndDate;
      const sTime = startTime || callLogsStartTime;
      const eTime = endTime || callLogsEndTime;
      const search = searchOverride !== undefined ? searchOverride : callLogsSearch;
      const excludeMissed = excludeMissedOverride !== undefined ? excludeMissedOverride : callLogsExcludeMissed;
      const callTypeFilter = callTypeFilterOverride !== undefined ? callTypeFilterOverride : callLogsCallTypeFilter;
      
      if (sDate) params.append("start_date", `${sDate} ${sTime}:00`);
      if (eDate) params.append("end_date", `${eDate} ${eTime}:59`);
      if (search?.trim()) params.append("search", search.trim());
      if (excludeMissed) params.append("exclude_missed", "1");
      if (callTypeFilter && callTypeFilter !== "all") params.append("call_type", callTypeFilter);
      
      const res = await axios.get(`${API}/call-logs?${params}`);
      setCallLogs(res.data.data || []);
      setCallLogsTotalPages(res.data.total_pages || 1);
      setCallLogsTotal(res.data.total || 0);
      setCallLogsPage(page);
      setCallLogsSummary(res.data.summary || { total_calls: 0, incoming: 0, outgoing: 0, missed: 0, total_duration: 0, unique_total: 0, unique_incoming: 0, unique_outgoing: 0, unique_missed: 0 });
    } catch (error) {
      console.error("Call logs error:", error);
      toast.error("Çağrı kayıtları yüklenemedi");
    }
    setCallLogsLoading(false);
  };

  const handleCallLogsDateRangeChange = (range) => {
    setCallLogsDateRange(range);
    let newStartDate = "";
    let newEndDate = "";
    
    if (range === "all") {
      newStartDate = "";
      newEndDate = "";
    } else if (range === "today") {
      newStartDate = getTodayStr();
      newEndDate = getTodayStr();
    } else if (range === "yesterday") {
      newStartDate = getYesterdayStr();
      newEndDate = getYesterdayStr();
    } else if (range === "7days") {
      newStartDate = getLast7DaysStr();
      newEndDate = getTodayStr();
    } else if (range === "custom") {
      newStartDate = getTodayStr();
      newEndDate = getTodayStr();
    }
    
    setCallLogsStartDate(newStartDate);
    setCallLogsEndDate(newEndDate);
    setCallLogsStartTime("00:00");
    setCallLogsEndTime("23:59");
    
    if (selectedDeviceForLogs && range !== "custom") {
      fetchCallLogs(selectedDeviceForLogs.id, 1, newStartDate, newEndDate, "00:00", "23:59", callLogsSearch);
    }
  };

  const handleCallLogsFilter = () => {
    if (selectedDeviceForLogs) {
      fetchCallLogs(selectedDeviceForLogs.id, 1, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime, callLogsSearch);
    }
  };

  const handleClearCallLogsSearch = () => {
    setCallLogsSearch("");
    if (selectedDeviceForLogs) {
      fetchCallLogs(selectedDeviceForLogs.id, 1, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime, "");
    }
  };

  const handleCallLogsPageSizeChange = (newLimit) => {
    setCallLogsLimit(newLimit);
    if (selectedDeviceForLogs) {
      fetchCallLogs(selectedDeviceForLogs.id, 1, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime, callLogsSearch, newLimit);
    }
  };

  const handleCallLogsExcludeMissedChange = (checked) => {
    setCallLogsExcludeMissed(checked);
    if (selectedDeviceForLogs) {
      fetchCallLogs(selectedDeviceForLogs.id, 1, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime, callLogsSearch, undefined, checked);
    }
  };

  const handleCallLogsCallTypeFilterChange = (filter) => {
    setCallLogsCallTypeFilter(filter);
    if (selectedDeviceForLogs) {
      fetchCallLogs(selectedDeviceForLogs.id, 1, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime, callLogsSearch, undefined, undefined, filter);
    }
  };

  // Cihaz çağrı kayıtları modalını aç
  const resolveDeviceRecord = useCallback(async (deviceId) => {
    const numericId = parseInt(deviceId, 10);
    if (!numericId) return null;

    const fromCache = allDevicesList.find((d) => d.id === numericId)
      ?? devices.find((d) => d.id === numericId);
    if (fromCache) return fromCache;

    try {
      const res = await axios.get(`${API}/devices/${numericId}`);
      return res.data;
    } catch (error) {
      console.error("Resolve device error:", error);
      return null;
    }
  }, [allDevicesList, devices]);

  const openCallLogsModal = (device, options = {}) => {
    const initialTab = options.initialTab === "failed" ? "failed" : "calls";
    setSelectedDeviceForLogs(device);
    setCallLogsModalOpen(true);
    setCallLogsModalTab(initialTab);
    setCallLogsPage(1);
    setCallLogsDateRange("all");
    setCallLogsStartDate("");
    setCallLogsEndDate("");
    setCallLogsStartTime("00:00");
    setCallLogsEndTime("23:59");
    setCallLogsSearch("");
    setCallLogsExcludeMissed(false);
    setCallLogsCallTypeFilter("all");
    if (initialTab === "calls") {
      fetchCallLogs(device.id, 1, "", "", "00:00", "23:59", "", undefined, false, "all");
    }
  };

  const openAgentDevicesModal = (agent) => {
    setSelectedAgentForDevices(agent);
    setAgentDevicesModalOpen(true);
  };

  const handleAgentDeviceViewCallLogs = (device) => {
    setAgentDevicesModalOpen(false);
    openCallLogsModal(device);
  };

  const openCallLogsModalByDeviceId = async (deviceId, options = {}) => {
    const device = await resolveDeviceRecord(deviceId);
    if (!device) {
      toast.error("Cihaz bilgisi bulunamadı");
      return;
    }
    openCallLogsModal(device, options);
  };

  // Cihaza agent ata veya değiştir
  const handleChangeDeviceAgent = async (deviceId, newAgentId) => {
    try {
      if (newAgentId === null) {
        // Remove device from all agents
        // First get which agent has this device
        const currentAgent = deviceAgentMap[deviceId];
        if (currentAgent) {
          // Get current agent's devices and remove this one
          const res = await axios.get(`${API}/agent-devices?agent_id=${currentAgent.agent_id}`);
          const currentDevices = (res.data.data || []).map(d => d.device_id).filter(id => id !== deviceId);
          await axios.post(`${API}/agent-devices/${currentAgent.agent_id}`, { device_ids: currentDevices });
        }
      } else {
        // Add device to new agent
        // First remove from old agent if exists
        const currentAgent = deviceAgentMap[deviceId];
        if (currentAgent && currentAgent.agent_id !== newAgentId) {
          const oldRes = await axios.get(`${API}/agent-devices?agent_id=${currentAgent.agent_id}`);
          const oldDevices = (oldRes.data.data || []).map(d => d.device_id).filter(id => id !== deviceId);
          await axios.post(`${API}/agent-devices/${currentAgent.agent_id}`, { device_ids: oldDevices });
        }
        
        // Add to new agent
        const newRes = await axios.get(`${API}/agent-devices?agent_id=${newAgentId}`);
        const newDevices = (newRes.data.data || []).map(d => d.device_id);
        if (!newDevices.includes(deviceId)) {
          newDevices.push(deviceId);
        }
        await axios.post(`${API}/agent-devices/${newAgentId}`, { device_ids: newDevices });
      }
      
      // Refresh data
      fetchDeviceAgentMap();
      fetchAgentDeviceCounts();
    } catch (error) {
      console.error("Change device agent error:", error);
      throw error;
    }
  };

  // Cihazdan yeni agent oluştur
  const handleCreateAgentFromDevice = (device) => {
    setPreSelectedDeviceForAgent(device);
    setEditingAgent(null);
    setAgentModalOpen(true);
  };

  useEffect(() => {
    if (activeTab !== "transcripts" || !canSeeRecordings) return;
    const routeKey = buildTabRouteKey("transcripts", "default");
    const signature = [transcriptPage, transcriptFilter, transcriptSearchTerm, tabReclickNonce].join("|");
    runTabFetch(routeKey, signature, fetchTranscripts);
  }, [activeTab, canSeeRecordings, transcriptPage, transcriptFilter, transcriptSearchTerm, tabReclickNonce, fetchTranscripts, runTabFetch]);

  useEffect(() => {
    const cdrId = selectedTranscriptRecord?.cdr_id || selectedTranscriptRecord?.id;
    if (activeTab !== "transcripts" || !cdrId || !isTranscriptInProgress(selectedTranscriptRecord)) {
      return undefined;
    }

    const maxAttempts = 120;
    let attempts = 0;

    const interval = setInterval(() => {
      attempts += 1;
      if (attempts > maxAttempts) {
        clearInterval(interval);
        toast.info("Transkript uzun süredir tamamlanmadı. Worker veya API ayarlarını kontrol edin.");
        return;
      }
      pollTranscriptStatus(cdrId, true).then((data) => {
        if (data && ["completed", "failed"].includes((data.status || "").toLowerCase())) {
          fetchTranscripts();
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [activeTab, selectedTranscriptRecord, pollTranscriptStatus, fetchTranscripts]);

  const handleGsmExport = async () => {
    if (!gsmSearchNumber || gsmSearchNumber.length < 3) {
      toast.error("Önce arama yapın");
      return;
    }
    
    setGsmExporting(true);
    try {
      const params = new URLSearchParams({ gsm_numara: gsmSearchNumber });
      if (gsmSearchStartDate) params.append("start_date", gsmSearchStartDate);
      if (gsmSearchEndDate) params.append("end_date", gsmSearchEndDate + " 23:59:59");
      
      const response = await axios.get(`${API}/gsm-search/export?${params}`, { timeout: 60000 });
      const data = response.data.data;
      
      if (data.length === 0) { 
        toast.warning("Export edilecek kayıt yok"); 
        return; 
      }
      
      // Build export data with new fields: agent, sistem, device_id
      const exportData = data.map(r => ({
        "Tarih": r.tarih,
        "Yön": r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-",
        "Agent": r.agent_name || "-",
        "Arayan": r.arayan_numara,
        "Aranan": r.aranan_numara,
        "Konuşma (sn)": r.konusma_suresi,
        "Çalma (sn)": r.calma_suresi,
        "Durum": r.durum_aciklama === "cevaplanan" ? "Cevaplandı" : r.durum_aciklama === "mesgul" ? "Meşgul" : "Cevapsız",
        "Sistem": r.sistem === 1 ? "GSM" : "Sabit Hat",
        "Cihaz ID": r.device_id || "-",
        ...(canSeeRecordings && { "Ses Kaydı": r.recording || "" })
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "GSM Arama");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf]), `GSM_Arama_${gsmSearchNumber}_${gsmSearchStartDate}_${gsmSearchEndDate}.xlsx`);
      toast.success(`${data.length} kayıt export edildi`);
    } catch (error) {
      toast.error("Export hatası");
    } finally {
      setGsmExporting(false);
    }
  };

  const fetchAgents = async () => {
    const res = await axios.get(`${API}/agents`);
    setAgents(asAgentList(res.data));
  };

  const fetchAttendance = useCallback(async () => {
    setAttendanceLoading(true);
    try {
      const params = new URLSearchParams({ date: attendanceDate, status: "all" });
      if (isSuperAdmin && attendanceCompanyId && attendanceCompanyId !== "all") {
        params.append("company_id", attendanceCompanyId);
      }
      appendAgentsScopeParams(params);
      const res = await axios.get(`${API}/agent-attendance?${params}`, { timeout: REPORT_API_TIMEOUT });
      setAttendanceData(res.data);
    } catch (error) {
      console.error("Attendance fetch error:", error);
      toast.error(error.response?.data?.detail || "Mesai takibi yüklenemedi");
      setAttendanceData(null);
    } finally {
      setAttendanceLoading(false);
    }
  }, [attendanceDate, attendanceCompanyId, isSuperAdmin, appendAgentsScopeParams]);

  const attendanceSummary = React.useMemo(() => {
    const s = attendanceData?.summary;
    if (s) return s;
    const agents = attendanceData?.agents || [];
    return {
      total: agents.length,
      on_time: agents.filter((a) => a.status === "on_time").length,
      late: agents.filter((a) => a.status === "late").length,
      absent: agents.filter((a) => a.status === "absent").length,
      off_day: agents.filter((a) => a.status === "off_day").length,
    };
  }, [attendanceData]);

  const filteredAttendanceRows = React.useMemo(() => {
    let rows = attendanceData?.agents || [];
    if (attendanceFilter !== "all") {
      rows = rows.filter((r) => r.status === attendanceFilter);
    }
    if (attendanceSearch.trim()) {
      const term = attendanceSearch.toLowerCase().trim();
      rows = rows.filter(
        (r) =>
          r.agent_name?.toLowerCase().includes(term) ||
          r.company_name?.toLowerCase().includes(term)
      );
    }
    return rows;
  }, [attendanceData, attendanceFilter, attendanceSearch]);

  const sortedAttendanceRows = useMemo(
    () => sortReportRows(filteredAttendanceRows, attendanceSort),
    [filteredAttendanceRows, attendanceSort]
  );

  const formatLateDuration = (seconds) => {
    if (!seconds || seconds <= 0) return "-";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} dk geç`;
    const hours = Math.floor(minutes / 60);
    const rem = minutes % 60;
    return rem > 0 ? `${hours} sa ${rem} dk geç` : `${hours} sa geç`;
  };

  const formatEarlyLeaveDuration = (seconds) => {
    if (!seconds || seconds <= 0) return "-";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} dk`;
    const hours = Math.floor(minutes / 60);
    const rem = minutes % 60;
    return rem > 0 ? `${hours} sa ${rem} dk` : `${hours} sa`;
  };

  useEffect(() => {
    if (activeTab !== "agents" || agentsView !== "attendance") return;
    const routeKey = buildTabRouteKey("agents", "attendance");
    const signature = [attendanceDate, attendanceCompanyId, attendanceFilter, agentsTeamFilter, agentsLeaderFilter, tabReclickNonce].join("|");
    runTabFetch(routeKey, signature, fetchAttendance);
  }, [activeTab, agentsView, attendanceDate, attendanceCompanyId, attendanceFilter, agentsTeamFilter, agentsLeaderFilter, tabReclickNonce, fetchAttendance, runTabFetch]);

  const fetchCrmCustomers = useCallback(async () => {
    setCrmLoading(true);
    try {
      const params = {
        list: 1,
        page: crmPage,
        limit: 20,
      };
      if (crmSearchTerm) params.search = crmSearchTerm;
      if (crmLeadFilter !== "all") params.lead_durumu = crmLeadFilter;
      if (isSuperAdmin && crmCompanyFilter !== "all") params.company_id = crmCompanyFilter;

      const res = await axios.get(`${API}/customers`, { params, timeout: 30000 });
      setCrmCustomers(res.data.items || []);
      setCrmTotal(res.data.total || 0);
      if (res.data.lead_statuses?.length) setLeadStatuses(res.data.lead_statuses);
      if (res.data.lead_sources?.length) setLeadSources(res.data.lead_sources);
    } catch (error) {
      console.error("CRM customers fetch error:", error);
      toast.error(error.response?.data?.detail || "Müşteri listesi yüklenemedi");
      setCrmCustomers([]);
      setCrmTotal(0);
    } finally {
      setCrmLoading(false);
    }
  }, [crmPage, crmSearchTerm, crmLeadFilter, crmCompanyFilter, isSuperAdmin]);

  const applyCrmSearch = () => {
    setCrmSearchTerm(crmSearchInput.trim());
    setCrmPage(1);
  };

  const clearCrmSearch = () => {
    setCrmSearchInput("");
    setCrmSearchTerm("");
    setCrmPage(1);
  };

  useEffect(() => {
    if (activeTab !== "crm" || crmView !== "customers") return;
    const routeKey = buildTabRouteKey("crm", "customers");
    const signature = [crmPage, crmSearchTerm, crmLeadFilter, crmCompanyFilter, tabReclickNonce].join("|");
    runTabFetch(routeKey, signature, fetchCrmCustomers);
  }, [activeTab, crmView, crmPage, crmSearchTerm, crmLeadFilter, crmCompanyFilter, tabReclickNonce, fetchCrmCustomers, runTabFetch]);

  const fetchMesaiReport = useCallback(async (dateOverride) => {
    const startDate = dateOverride?.start ?? mesaiStartDate;
    const endDate = dateOverride?.end ?? mesaiEndDate;
    setMesaiReportLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (isSuperAdmin && mesaiCompanyId && mesaiCompanyId !== "all") {
        params.append("company_id", mesaiCompanyId);
      }
      appendReportScopeParams(params);
      const res = await axios.get(`${API}/agent-attendance?${params}`, { timeout: REPORT_API_TIMEOUT });
      setMesaiReportData(res.data);
    } catch (error) {
      console.error("Mesai report error:", error);
      toast.error(error.response?.data?.detail || "Mesai raporu yüklenemedi");
      setMesaiReportData(null);
    } finally {
      setMesaiReportLoading(false);
    }
  }, [mesaiStartDate, mesaiEndDate, mesaiCompanyId, isSuperAdmin, appendReportScopeParams]);

  const filteredMesaiReportRows = React.useMemo(() => {
    let rows = mesaiReportData?.agents || [];
    if (mesaiSearch.trim()) {
      const term = mesaiSearch.toLowerCase().trim();
      rows = rows.filter(
        (r) =>
          r.agent_name?.toLowerCase().includes(term) ||
          r.company_name?.toLowerCase().includes(term)
      );
    }
    return rows;
  }, [mesaiReportData, mesaiSearch]);

  const sortedMesaiReportRows = useMemo(
    () => sortReportRows(filteredMesaiReportRows, mesaiReportSort),
    [filteredMesaiReportRows, mesaiReportSort]
  );

  const mesaiReportRowTotals = useMemo(() => {
    if (!sortedMesaiReportRows.length) return null;
    return sortedMesaiReportRows.reduce(
      (acc, row) => ({
        scheduled_days: acc.scheduled_days + (row.scheduled_days || 0),
        on_time_count: acc.on_time_count + (row.on_time_count || 0),
        late_count: acc.late_count + (row.late_count || 0),
        early_leave_count: acc.early_leave_count + (row.early_leave_count || 0),
        total_early_leave_seconds: acc.total_early_leave_seconds + (row.total_early_leave_seconds || 0),
        absent_count: acc.absent_count + (row.absent_count || 0),
        off_day_count: acc.off_day_count + (row.off_day_count || 0),
      }),
      { scheduled_days: 0, on_time_count: 0, late_count: 0, early_leave_count: 0, total_early_leave_seconds: 0, absent_count: 0, off_day_count: 0 }
    );
  }, [sortedMesaiReportRows]);

  const handleMesaiQuickRange = (preset) => {
    let start;
    let end;
    if (preset === "today") {
      start = getTodayStr();
      end = getTodayStr();
    } else if (preset === "yesterday") {
      start = getYesterdayStr();
      end = getYesterdayStr();
    } else if (preset === "thisMonth") {
      start = getThisMonthStart();
      end = getTodayStr();
    } else if (preset === "lastMonth") {
      ({ start, end } = getLastMonthRange());
    } else {
      return;
    }
    setMesaiDatePreset(preset);
    setMesaiStartDate(start);
    setMesaiEndDate(end);
    fetchMesaiReport({ start, end });
  };

  useEffect(() => {
    if (activeTab !== "reports" || reportsView !== "mesai") return;
    const routeKey = buildTabRouteKey("reports", "mesai");
    const signature = [mesaiStartDate, mesaiEndDate, mesaiCompanyId, normalizeScopeFilter(reportsTeamFilter).join(","), normalizeScopeFilter(reportsLeaderFilter).join(","), tabReclickNonce].join("|");
    runTabFetch(routeKey, signature, () => fetchMesaiReport());
  }, [activeTab, reportsView, mesaiStartDate, mesaiEndDate, mesaiCompanyId, reportsTeamFilter, reportsLeaderFilter, tabReclickNonce, fetchMesaiReport, runTabFetch]);

  const fetchMolaReport = useCallback(async (dateOverride) => {
    const startDate = dateOverride?.start ?? molaStartDate;
    const endDate = dateOverride?.end ?? molaEndDate;
    setMolaReportLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (isSuperAdmin && molaCompanyId && molaCompanyId !== "all") {
        params.append("company_id", molaCompanyId);
      }
      appendReportScopeParams(params);
      const res = await axios.get(`${API}/agent-break-report?${params}`, { timeout: REPORT_API_TIMEOUT });
      setMolaReportData(res.data);
    } catch (error) {
      console.error("Mola report error:", error);
      toast.error(error.response?.data?.detail || "Mola raporu yüklenemedi");
      setMolaReportData(null);
    } finally {
      setMolaReportLoading(false);
    }
  }, [molaStartDate, molaEndDate, molaCompanyId, isSuperAdmin, appendReportScopeParams]);

  const filteredMolaReportRows = React.useMemo(() => {
    let rows = molaReportData?.agents || [];
    if (molaSearch.trim()) {
      const term = molaSearch.toLowerCase().trim();
      rows = rows.filter((r) =>
        r.agent_name?.toLowerCase().includes(term) ||
        r.company_name?.toLowerCase().includes(term)
      );
    }
    return rows;
  }, [molaReportData, molaSearch]);

  const sortedMolaReportRows = useMemo(
    () => sortReportRows(filteredMolaReportRows, molaReportSort),
    [filteredMolaReportRows, molaReportSort]
  );

  const handleMolaQuickRange = (preset) => {
    let start;
    let end;
    if (preset === "today") {
      start = getTodayStr();
      end = getTodayStr();
    } else if (preset === "yesterday") {
      start = getYesterdayStr();
      end = getYesterdayStr();
    } else if (preset === "thisMonth") {
      start = getThisMonthStart();
      end = getTodayStr();
    } else if (preset === "lastMonth") {
      ({ start, end } = getLastMonthRange());
    } else {
      return;
    }
    setMolaDatePreset(preset);
    setMolaStartDate(start);
    setMolaEndDate(end);
    fetchMolaReport({ start, end });
  };

  const openMolaBreakAnalysis = (row) => {
    fetchBreakAnalysis(row, {
      startDate: molaReportData?.start_date || molaStartDate,
      endDate: molaReportData?.end_date || molaEndDate,
      yon: "all",
      view: "standard",
    });
  };

  useEffect(() => {
    if (activeTab !== "reports" || reportsView !== "mola") return;
    const routeKey = buildTabRouteKey("reports", "mola");
    const signature = [molaStartDate, molaEndDate, molaCompanyId, normalizeScopeFilter(reportsTeamFilter).join(","), normalizeScopeFilter(reportsLeaderFilter).join(","), tabReclickNonce].join("|");
    runTabFetch(routeKey, signature, () => fetchMolaReport());
  }, [activeTab, reportsView, molaStartDate, molaEndDate, molaCompanyId, reportsTeamFilter, reportsLeaderFilter, tabReclickNonce, fetchMolaReport, runTabFetch]);

  const closeHireImportModal = () => {
    if (hireImporting) return;
    setHireImportOpen(false);
    setHireParsed(null);
    setHireImportResult(null);
  };

  const requestHireExcelImport = useCallback(() => {
    if (hireImportNeedsCompanyPick) {
      toast.warning("İşe giriş Excel için önce listeden bir firma seçin («Tüm Firmalar» ile import yapılamaz).");
      return;
    }
    if (!isSuperAdmin && !user?.company_id) {
      toast.warning("Firma bilginiz tanımlı değil; import yapılamaz.");
      return;
    }
    hireImportInputRef.current?.click();
  }, [hireImportNeedsCompanyPick, isSuperAdmin, user?.company_id]);

  const onHireExcelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (hireImportNeedsCompanyPick) {
      toast.warning("İşe giriş Excel için önce bir firma seçin.");
      return;
    }
    setHireFileName(file.name);
    setHireImportResult(null);
    setHireParsed(null);
    setHireImportOpen(true);
    setHireParsing(true);
    try {
      const buf = await file.arrayBuffer();
      await new Promise((r) => setTimeout(r, 0));
      const parsed = parseAgentHireWorkbook(buf);
      setHireParsed(parsed);
      if (parsed.error) toast.warning(parsed.error);
      else if (!parsed.sheets?.length) toast.warning("Sheet bulunamadı");
    } catch (err) {
      toast.error(err?.message || "Excel okunamadı");
      setHireParsed({ error: err?.message, sheets: [] });
    } finally {
      setHireParsing(false);
    }
  };

  const confirmHireImport = async (payload) => {
    let companyId = null;
    if (isSuperAdmin && agentsCompanyId !== "all") companyId = Number(agentsCompanyId);
    else if (!isSuperAdmin && user?.company_id) companyId = Number(user.company_id);
    if (isSuperAdmin && (!companyId || !Number.isFinite(companyId))) {
      toast.warning("Kaydetmek için Agent Yönetimi'nde firma filtresi seçin.");
      return;
    }
    setHireImporting(true);
    try {
      const res = await axios.post(`${API}/agent-hire-date-import`, {
        company_id: companyId || undefined,
        ...payload,
      });
      setHireImportResult(res.data);
      const n = res.data?.updated_count ?? 0;
      if (n > 0) toast.success(`${n} işe giriş tarihi güncellendi`);
      else if ((res.data?.skipped_details?.length ?? 0) > 0) {
        const same = (res.data.skipped_details || []).filter((s) => s.reason === "Tarih zaten aynı").length;
        toast.warning(
          same === res.data.skipped_details.length
            ? `${res.data.skipped} satır atlandı: panel tarihi Excel ile aynı (serial tarih düzeltmesi sonrası yeniden deneyin).`
            : `${n} güncellendi — ${res.data?.skipped ?? 0} satır atlandı (detay modalda)`,
        );
      } else if ((res.data?.errors?.length ?? 0) > 0) {
        toast.warning(`${n} güncellendi — bazı satırlar hata verdi (modalda)`);
      } else toast.info("Kaydedilecek satır gönderilmedi veya değişiklik yok.");
      fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import başarısız");
    } finally {
      setHireImporting(false);
    }
  };

  const agentInlineDateKey = (agentId, field) => `${agentId}:${field}`;

  const getAgentInlineDateValue = (agentRow, field) => {
    const key = agentInlineDateKey(agentRow.id, field);
    if (Object.prototype.hasOwnProperty.call(agentDateDrafts, key)) {
      return agentDateDrafts[key];
    }
    return formatAgentEmploymentDate(agentRow[field]) || "";
  };

  const handleAgentInlineDateChange = (agentRow, field, value) => {
    if (value !== "" && !isAgentEmploymentDateInputAllowed(value)) return;
    setAgentDateDrafts((prev) => ({ ...prev, [agentInlineDateKey(agentRow.id, field)]: value }));
  };

  const commitAgentInlineDateField = async (agentRow, field, rawValue) => {
    const key = agentInlineDateKey(agentRow.id, field);
    setAgentDateDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    const trimmed = rawValue === "" ? "" : String(rawValue).slice(0, 10);
    if (trimmed !== "" && !isValidAgentEmploymentDateInput(trimmed)) {
      toast.error("Geçerli bir tarih seçin (takvimden veya yyyy-aa-gg, yıl 1900–2100)");
      return;
    }
    const prev = formatAgentEmploymentDate(agentRow[field]) || "";
    if (trimmed === prev) return;
    await patchAgentInlineField(agentRow, field, trimmed);
  };

  const patchAgentInlineField = async (agentRow, field, value) => {
    if (!canManageAgents) return;
    const normalized = value === "" ? null : value;
    const hire = formatAgentEmploymentDate(agentRow.ise_giris_tarihi);
    const exit = field === "isten_cikis_tarihi"
      ? normalized
      : formatAgentEmploymentDate(agentRow.isten_cikis_tarihi);
    const hireCheck = field === "ise_giris_tarihi" ? normalized : hire;
    if (hireCheck && exit && exit < hireCheck) {
      toast.error("İşten çıkış tarihi, işe girişten önce olamaz");
      return;
    }
    setAgentInlineSavingId(agentRow.id);
    try {
      const payload = { [field]: normalized };
      await axios.put(`${API}/agents/${agentRow.id}`, payload);
      await fetchAgents();
      toast.success("Kaydedildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setAgentInlineSavingId(null);
    }
  };

  const patchAgentTeamAssignment = async (agentRow, teamIdValue, { leaderHint } = {}) => {
    if (!canManageAgents) return;
    const teamId = teamIdValue === AGENT_INLINE_TEAM_NONE || teamIdValue == null || teamIdValue === ""
      ? null
      : Number(teamIdValue);
    if (teamId !== null && !Number.isFinite(teamId)) return;
    setAgentInlineSavingId(agentRow.id);
    try {
      await axios.put(`${API}/agents/${agentRow.id}`, { team_id: teamId });
      await fetchAgents();
      if (leaderHint?.ambiguous && leaderHint?.teamName) {
        toast.success(`Takım: ${leaderHint.teamName} (${leaderHint.teamCount} takımdan biri)`);
      } else {
        toast.success(teamId ? "Takım ataması güncellendi" : "Agent takımsız bırakıldı");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Takım güncellenemedi");
    } finally {
      setAgentInlineSavingId(null);
    }
  };

  const patchAgentTeamLeader = async (agentRow, leaderValue) => {
    if (!canManageAgents) return;
    const resolved = resolveTeamIdForLeaderChange(agentRow, leaderValue, teams);
    if (resolved.teamId === undefined) {
      toast.error("Seçilen lider için takım tanımlı değil (Takım Yönetimi)");
      return;
    }
    const nextTeamKey = resolved.teamId == null ? AGENT_INLINE_TEAM_NONE : String(resolved.teamId);
    const currentKey = agentRow.team_id ? String(agentRow.team_id) : AGENT_INLINE_TEAM_NONE;
    if (nextTeamKey === currentKey) return;
    await patchAgentTeamAssignment(agentRow, nextTeamKey, {
      leaderHint: resolved.ambiguous ? resolved : null,
    });
  };

  const handleSaveAgent = async (formData, agentId) => {
    try {
      const deviceIds = formData.device_ids || [];
      const applyScheduleToAll = !!formData.apply_schedule_to_all;
      const teamLeaderChange = formData.team_leader_change || null;
      const agentData = { ...formData };
      delete agentData.device_ids;
      delete agentData.apply_schedule_to_all;
      delete agentData.team_leader_change;
      
      let savedAgentId = agentId;
      
      if (agentId) {
        if (!agentData.sifre) delete agentData.sifre;
        delete agentData.kullanici_adi;
        await axios.put(`${API}/agents/${agentId}`, agentData);
        if (teamLeaderChange?.change_date
          && (teamLeaderChange.leader_user_id || (teamLeaderChange.leader_name || "").trim())) {
          await axios.post(`${API}/agent-team-leader-history`, {
            agent_id: agentId,
            leader_user_id: teamLeaderChange.leader_user_id || undefined,
            leader_name: (teamLeaderChange.leader_name || "").trim() || undefined,
            change_date: teamLeaderChange.change_date,
          });
          toast.success("Agent güncellendi · önceki takım lideri arşive eklendi");
        } else {
          toast.success("Agent güncellendi");
        }
      } else {
        const response = await axios.post(`${API}/agents`, agentData);
        savedAgentId = response.data?.id;
        toast.success("Agent oluşturuldu");
      }

      if (applyScheduleToAll && agentData.work_schedule) {
        const bulkRes = await axios.put(`${API}/agent-work-schedules`, {
          work_schedule: agentData.work_schedule,
          apply_all: true,
        });
        toast.success(`${bulkRes.data?.updated || 0} agent mesai planı güncellendi`);
      }
      
      // Save device associations if we have an agent ID
      if (savedAgentId && deviceIds.length >= 0) {
        try {
          await axios.post(`${API}/agent-devices/${savedAgentId}`, { device_ids: deviceIds });
        } catch (deviceError) {
          console.error("Device association error:", deviceError);
          // Don't throw - agent was still saved successfully
        }
      }
      
      // Refresh data
      fetchAgents();
      fetchAgentDeviceCounts();
      fetchDeviceAgentMap();
      
      // Return the agent id for modal
      return { id: savedAgentId };
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hata");
      throw error;
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!isSuperAdmin) return;
    if (!window.confirm("Agent silinecek, emin misiniz?")) return;
    try {
      await axios.delete(`${API}/agents/${agentId}`);
      toast.success("Agent silindi");
      fetchAgents();
      if (selectedAgent?.id === agentId) setSelectedAgent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silinemedi");
    }
  };

  const fetchUsers = async () => {
    const res = await axios.get(`${API}/users`);
    setUsers(res.data || []);
  };

  const fetchPanelRoles = async () => {
    try {
      const res = await axios.get(`${API}/panel-roles`);
      setPanelRoles(res.data?.items || []);
    } catch (error) {
      console.error("Panel roles load error:", error);
    }
  };

  const fetchTeams = async () => {
    setTeamsLoading(true);
    try {
      const res = await axios.get(`${API}/teams`);
      setTeams(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Takımlar yüklenemedi");
    } finally {
      setTeamsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "agents" || agentsView !== "list" || !canManageAgents) return;
    fetchTeams();
  }, [activeTab, agentsView, canManageAgents]);

  const handleSaveTeam = async (formData, teamId) => {
    try {
      if (teamId) {
        await axios.put(`${API}/teams?id=${teamId}`, formData);
        toast.success("Takım güncellendi");
      } else {
        await axios.post(`${API}/teams`, formData);
        toast.success("Takım oluşturuldu");
      }
      fetchTeams();
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hata");
      throw error;
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm("Takım silinecek, emin misiniz?")) return;
    try {
      await axios.delete(`${API}/teams?id=${teamId}`);
      toast.success("Takım silindi");
      fetchTeams();
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silinemedi");
    }
  };

  const fetchCompanies = async () => {
    const res = await axios.get(`${API}/companies`);
    setCompanies(res.data || []);
  };

  const fetchTodos = useCallback(async () => {
    setTodosLoading(true);
    try {
      const params = new URLSearchParams();
      if (isSuperAdmin && todosCompanyFilter && todosCompanyFilter !== "all") {
        params.append("company_id", todosCompanyFilter);
      }
      const res = await axios.get(`${API}/todos?${params}`, { timeout: 30000 });
      setTodos(res.data?.data || []);
    } catch (error) {
      console.error("Todos fetch error:", error);
      toast.error(error.response?.data?.detail || "To-Do listesi yüklenemedi");
    } finally {
      setTodosLoading(false);
    }
  }, [isSuperAdmin, todosCompanyFilter]);

  useEffect(() => {
    if (activeTab !== "todos" || !canAccessTodos) return;
    const routeKey = buildTabRouteKey("todos", "default");
    const signature = [todosCompanyFilter, tabReclickNonce].join("|");
    runTabFetch(routeKey, signature, fetchTodos);
  }, [activeTab, canAccessTodos, todosCompanyFilter, tabReclickNonce, fetchTodos, runTabFetch]);

  useEffect(() => {
    if (!todoDetailModalOpen) return;
    setSelectedTodo((current) => {
      if (!current?.id) return current;
      const fresh = todos.find((t) => t.id === current.id);
      return fresh || current;
    });
  }, [todos, todoDetailModalOpen]);

  const uploadTodoFiles = async (todoId, files) => {
    const errors = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        const fileName = file.name || `dosya-${Date.now()}.png`;
        formData.append("file", file, fileName);
        await axios.post(`${API}/todos?action=upload&todo_id=${todoId}`, formData, {
          timeout: 120000,
        });
      } catch (error) {
        const name = file.name || "Dosya";
        errors.push(`${name}: ${error.response?.data?.detail || "yüklenemedi"}`);
      }
    }
    if (errors.length) {
      throw new Error(errors.join(" · "));
    }
  };

  const addTodoFormFiles = (fileList) => {
    if (!fileList?.length) return;
    setTodoForm((prev) => ({ ...prev, files: [...prev.files, ...Array.from(fileList)] }));
  };

  const handleCreateTodo = async (e) => {
    e.preventDefault();
    if (!todoForm.baslik.trim()) {
      toast.error("Başlık alanı zorunludur");
      return;
    }
    if (isSuperAdmin && (!todosCompanyFilter || todosCompanyFilter === "all")) {
      toast.error("Lütfen üstten bir firma seçin");
      return;
    }
    setTodoSaving(true);
    try {
      const payload = {
        baslik: todoForm.baslik.trim(),
        proses: todoForm.detay.trim() || todoForm.baslik.trim(),
        status: "yeni",
      };
      if (isSuperAdmin) {
        payload.company_id = Number(todosCompanyFilter);
      }
      const res = await axios.post(`${API}/todos`, payload);
      const todoId = res.data?.id;
      const fileCount = todoForm.files?.length || 0;
      if (todoId && fileCount) {
        try {
          await uploadTodoFiles(todoId, todoForm.files);
          toast.success(`To-Do eklendi (${fileCount} dosya)`);
        } catch (uploadError) {
          toast.success("To-Do eklendi");
          toast.error(uploadError.message || "Dosyalar yüklenemedi");
        }
      } else {
        toast.success("To-Do eklendi");
      }
      setTodoForm({ baslik: "", detay: "", files: [] });
      setTodoCreateModalOpen(false);
      fetchTodos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "To-Do eklenemedi");
    } finally {
      setTodoSaving(false);
    }
  };

  const openTodoCreateModal = () => {
    setTodoForm({ baslik: "", detay: "", files: [] });
    setTodoCreateModalOpen(true);
  };

  const handleTodoNotePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const named = new File([file], `ekran-goruntusu-${Date.now()}.png`, { type: file.type });
          addTodoFormFiles([named]);
          toast.success("Ekran görüntüsü eklendi");
        }
        break;
      }
    }
  };

  const handleTodoDrop = (e) => {
    e.preventDefault();
    setTodoDragOver(false);
    addTodoFormFiles(e.dataTransfer.files);
  };

  const openTodoDetail = (todo) => {
    const fresh = todos.find((t) => t.id === todo.id) || todo;
    setSelectedTodo(fresh);
    setTodoEditForm({
      baslik: fresh.baslik || "",
      proses: fresh.proses || "",
      sonuc: fresh.sonuc || "",
      status: fresh.status || "yeni",
    });
    setTodoDetailModalOpen(true);
  };

  const refreshSelectedTodo = async (todoId) => {
    const params = new URLSearchParams();
    if (isSuperAdmin && todosCompanyFilter && todosCompanyFilter !== "all") {
      params.append("company_id", todosCompanyFilter);
    }
    const res = await axios.get(`${API}/todos?${params}`, { timeout: 30000 });
    const updated = res.data?.data || [];
    setTodos(updated);
    const fresh = updated.find((t) => t.id === todoId);
    if (fresh) setSelectedTodo(fresh);
    return fresh;
  };

  const handleSaveTodoDetail = async () => {
    if (!selectedTodo) return;
    if (!todoEditForm.proses.trim()) {
      toast.error("Proses alanı zorunludur");
      return;
    }
    setTodoSaving(true);
    try {
      await axios.put(`${API}/todos?id=${selectedTodo.id}`, {
        baslik: todoEditForm.baslik.trim(),
        proses: todoEditForm.proses.trim(),
        sonuc: todoEditForm.sonuc.trim(),
        status: todoEditForm.status,
      });
      toast.success("To-Do güncellendi");
      setTodoDetailModalOpen(false);
      setSelectedTodo(null);
      fetchTodos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Güncellenemedi");
    } finally {
      setTodoSaving(false);
    }
  };

  const handleDeleteTodo = async (todoId) => {
    if (!window.confirm("Bu To-Do kaydı silinecek. Emin misiniz?")) return;
    try {
      await axios.delete(`${API}/todos?id=${todoId}`);
      toast.success("To-Do silindi");
      fetchTodos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silinemedi");
    }
  };

  const handleDeleteTodoFile = async (fileId) => {
    if (!window.confirm("Dosya silinecek. Emin misiniz?")) return;
    try {
      await axios.delete(`${API}/todos?file_id=${fileId}`);
      toast.success("Dosya silindi");
      if (selectedTodo) await refreshSelectedTodo(selectedTodo.id);
      else fetchTodos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Dosya silinemedi");
    }
  };

  const handleAddFilesToTodo = async (todoId, fileList) => {
    if (!fileList?.length) return;
    try {
      await uploadTodoFiles(todoId, Array.from(fileList));
      toast.success("Dosya(lar) eklendi");
      await refreshSelectedTodo(todoId);
    } catch (error) {
      toast.error(error.message || error.response?.data?.detail || "Dosya yüklenemedi");
    }
  };

  const handleTodoDetailDrop = (e) => {
    e.preventDefault();
    setTodoDetailDragOver(false);
    if (selectedTodo && canManageTodos) {
      handleAddFilesToTodo(selectedTodo.id, e.dataTransfer.files);
    }
  };

  const formatFileSize = (bytes) => {
    const size = Number(bytes) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTodoShortDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const formatTodoDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const getTodoCardDescription = (todo) => {
    const text = todo.status === "tamamlandi" && todo.sonuc ? todo.sonuc : todo.proses;
    if (!text) return "";
    const line = text.split("\n")[0];
    return line.length > 120 ? `${line.slice(0, 117)}...` : line;
  };

  const handleMoveTodoStatus = async (todoId, newStatus) => {
    if (!canManageTodos) return;
    const todo = todos.find((t) => t.id === todoId);
    if (!todo || todo.status === newStatus) return;

    const prevStatus = todo.status;
    setTodos((prev) => prev.map((t) => (t.id === todoId ? { ...t, status: newStatus } : t)));

    try {
      await axios.put(`${API}/todos?id=${todoId}`, { status: newStatus });
      const colLabel = TODO_STATUSES.find((s) => s.id === newStatus)?.label || newStatus;
      toast.success(`${colLabel} sütununa taşındı`);
    } catch (error) {
      setTodos((prev) => prev.map((t) => (t.id === todoId ? { ...t, status: prevStatus } : t)));
      toast.error(error.response?.data?.detail || "Taşınamadı");
    }
  };

  const handleTodoCardDragStart = (e, todo) => {
    if (!canManageTodos) return;
    setDraggingTodoId(todo.id);
    e.dataTransfer.setData("text/plain", String(todo.id));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleTodoColumnDragOver = (e, columnId) => {
    if (!canManageTodos || !draggingTodoId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnId);
  };

  const handleTodoColumnDrop = (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDraggingTodoId(null);
    if (!canManageTodos) return;
    const todoId = Number(e.dataTransfer.getData("text/plain"));
    if (todoId) handleMoveTodoStatus(todoId, columnId);
  };

  const handleTodoDragEnd = () => {
    setDraggingTodoId(null);
    setDragOverColumn(null);
  };

  const downloadTodoFile = (file) => {
    axios.get(`${API}/todos?action=download&file_id=${file.id}`, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.original_name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Dosya indirilemedi"));
  };

  const todosByStatus = useMemo(() => {
    const grouped = { yeni: [], islemde: [], tamamlandi: [] };
    todos.forEach((todo) => {
      const status = todo.status || "yeni";
      if (grouped[status]) grouped[status].push(todo);
      else grouped.yeni.push(todo);
    });
    return grouped;
  }, [todos]);

  const handleSaveUser = async (formData, userId) => {
    try {
      if (userId) {
        const updateData = { ...formData };
        if (!updateData.sifre) delete updateData.sifre;
        if (!updateData.kullanici_adi) delete updateData.kullanici_adi;

        const isSelf = userId === user?.id;
        if (isSelf && !canManageUsers) {
          const profileData = {
            ad_soyad: updateData.ad_soyad,
            gsm: updateData.gsm,
            login_otp_enabled: updateData.login_otp_enabled,
            login_otp_sms: updateData.login_otp_sms,
            login_otp_whatsapp: updateData.login_otp_whatsapp,
          };
          if (updateData.sifre) profileData.sifre = updateData.sifre;
          const res = await axios.put(`${API}/auth/me`, profileData);
          if (res.data?.user) refreshUser();
          toast.success("Profil güncellendi");
        } else {
          await axios.put(`${API}/users/${userId}`, updateData);
          toast.success("Kullanıcı güncellendi");
          if (isSelf) refreshUser();
          fetchUsers();
        }
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success("Kullanıcı oluşturuldu");
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hata");
      throw error;
    }
  };

  const openCurrentUserEdit = useCallback(async () => {
    if (!user?.id) return;

    if (canManageUsers) {
      let target = users.find((u) => u.id === user.id);
      if (!target) {
        try {
          const res = await axios.get(`${API}/users`);
          const list = res.data || [];
          setUsers(list);
          target = list.find((u) => u.id === user.id);
        } catch (error) {
          toast.error(error.response?.data?.detail || "Kullanıcı bilgisi yüklenemedi");
          return;
        }
      }
      if (target) {
        setEditingUser(target);
        setUserModalOpen(true);
        return;
      }
    }

    setEditingUser({ ...user });
    setUserModalOpen(true);
  }, [user, users, canManageUsers]);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Kullanıcıyı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Kullanıcı silindi");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silinemedi");
    }
  };

  const handleCreateAdminLoginLink = async (targetUser) => {
    if (!isSuperAdmin || !targetUser?.id) return;
    setAdminLoginLinkLoadingId(targetUser.id);
    try {
      const res = await axios.post(`${API}/auth/admin-login-link`, {
        user_id: targetUser.id,
        panel_base_url: window.location.origin,
        ttl_seconds: 900,
      });
      const data = res.data || {};
      if (!data.ok || !data.url) {
        toast.error(data.message || "Giriş linki oluşturulamadı");
        return;
      }
      setAdminLoginLinkModal({
        open: true,
        url: data.url,
        user: data.target_user || targetUser,
        expiresIn: data.expires_in || 900,
      });
      toast.success("Tek kullanımlık giriş linki oluşturuldu");
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Giriş linki oluşturulamadı");
    } finally {
      setAdminLoginLinkLoadingId(null);
    }
  };

  const copyAdminLoginLink = async () => {
    if (!adminLoginLinkModal.url) return;
    try {
      await navigator.clipboard.writeText(adminLoginLinkModal.url);
      toast.success("Link kopyalandı");
    } catch {
      toast.error("Kopyalanamadı — linki elle seçin");
    }
  };

  // Fetch data when filters change (after initial load)
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    if (!initialLoading) {
      setIsReady(true);
    }
  }, [initialLoading]);
  
  useEffect(() => {
    if (!isReady || activeTab !== "cdr") return;
    const routeKey = buildTabRouteKey("cdr", "default");
    const signature = [
      page, limit, startDate, endDate, startTime, endTime,
      cdrPhoneFilter, cdrCompanyFilter, statusFilter, yonFilter, selectedAgent?.id ?? "",
      cdrFollowUpIds ?? "",
      tabReclickNonce,
    ].join("|");
    runTabFetch(routeKey, signature, async () => {
      await Promise.all([fetchRecords(), fetchStats()]);
    });
  }, [
    isReady, activeTab, page, limit, startDate, endDate, startTime, endTime,
    cdrPhoneFilter, cdrCompanyFilter, statusFilter, yonFilter, selectedAgent, cdrFollowUpIds,
    tabReclickNonce, fetchRecords, fetchStats, runTabFetch,
  ]);

  const cdrFollowUpIdSet = useMemo(() => {
    if (!cdrFollowUpIds) return null;
    return new Set(cdrFollowUpIds.split(",").map((id) => Number(id)).filter(Boolean));
  }, [cdrFollowUpIds]);

  useEffect(() => {
    if (isStaticPanelRoute(currentRouteKey)) {
      hidePanelLoader();
    }
  }, [currentRouteKey, hidePanelLoader]);
  
  const refreshCrmCustomers = useCallback(() => {
    const routeKey = buildTabRouteKey("crm", "customers");
    const signature = [crmPage, crmSearchTerm, crmLeadFilter, crmCompanyFilter].join("|");
    runTabFetch(routeKey, signature, fetchCrmCustomers, { force: true });
  }, [crmPage, crmSearchTerm, crmLeadFilter, crmCompanyFilter, fetchCrmCustomers, runTabFetch]);

  const refreshAttendance = useCallback(() => {
    const routeKey = buildTabRouteKey("agents", "attendance");
    const signature = [attendanceDate, attendanceCompanyId, attendanceFilter, agentsTeamFilter, agentsLeaderFilter].join("|");
    runTabFetch(routeKey, signature, fetchAttendance, { force: true });
  }, [attendanceDate, attendanceCompanyId, attendanceFilter, agentsTeamFilter, agentsLeaderFilter, fetchAttendance, runTabFetch]);

  // Re-fetch GSM search when page changes (only if there's already a search)
  useEffect(() => {
    if (gsmSearchNumber && gsmSearchNumber.length >= 3 && gsmSearchResults.length > 0) {
      fetchGsmSearch();
    }
  }, [gsmSearchPage, gsmSearchLimit, fetchGsmSearch, gsmSearchNumber, gsmSearchResults.length]);

  const cdrTotalPages = Math.max(1, Math.ceil((cdrTotalCount || 0) / limit));
  const showCdrPagination = cdrFollowUpIds ? records.length > 0 : (cdrTotalCount > 0 || page > 1 || loading);
  const cardClass = isDark ? "bg-slate-950/50 border-slate-800" : "bg-white border-gray-200 shadow-sm";
  const inputClass = isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300";
  const labelClass = isDark ? "text-slate-400" : "text-gray-600";
  const textClass = isDark ? "text-slate-100" : "text-gray-900";

  return (
    <TooltipProvider delayDuration={150}>
    <div className={`min-h-screen ${isDark ? "bg-[#020617]" : "bg-gray-50"}`}>
      <CallRecordingPlayerModal
        key={`audio-${selectedAudio.record?.id ?? selectedAudio.record?.cdr_id ?? "none"}-${selectedAudio.url || ""}`}
        isOpen={audioModalOpen}
        onClose={closeAudioPlayer}
        audioUrl={selectedAudio.url}
        recordInfo={selectedAudio.record}
        isDark={isDark}
        apiBase={API}
        onOpenTranscript={async (record, options = {}) => {
          const cdrId = record.id ?? record.cdr_id;
          const hasTranscript = typeof options.hasTranscript === "boolean"
            ? options.hasTranscript
            : (record.transkript === 1 || !!record.transcript_text);
          await handleViewTranscript(cdrId, { autoStart: !hasTranscript });
          navigateToTab("transcripts");
        }}
      />
      <AgentHireDateImportModal
        open={hireImportOpen}
        onClose={closeHireImportModal}
        fileName={hireFileName}
        parsed={hireParsed}
        agents={agentsForHireImport}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        importing={hireImporting}
        parsing={hireParsing}
        onConfirm={confirmHireImport}
        importResult={hireImportResult}
        companyLabel={agentsHireImportCompanyLabel}
      />
      <AgentModal 
        isOpen={agentModalOpen} 
        onClose={() => { setAgentModalOpen(false); setEditingAgent(null); setPreSelectedDeviceForAgent(null); }} 
        agent={editingAgent} 
        onSave={handleSaveAgent} 
        isDark={isDark}
        allDevices={allDevicesList}
        preSelectedDevice={preSelectedDeviceForAgent}
        existingAgentsCount={agents.length}
        companies={companies}
        teams={teams}
        isSuperAdmin={isSuperAdmin}
        userCompanyId={user?.company_id}
        onDevicesUpdate={async () => {
          try {
            const res = await axios.get(`${API}/agent-devices`);
            setAgentDeviceCounts(res.data.data || {});
            fetchDeviceAgentMap();
          } catch (e) {
            console.error("Error updating devices:", e);
          }
        }}
      />
      <ChangeAgentModal
        isOpen={changeAgentModalOpen}
        onClose={() => { setChangeAgentModalOpen(false); setSelectedDeviceForChange(null); }}
        device={selectedDeviceForChange}
        agents={visibleAgents}
        agentDevicesByAgent={agentDevicesByAgent}
        currentAgentId={selectedDeviceForChange ? deviceAgentMap[selectedDeviceForChange.id]?.agent_id : null}
        isDark={isDark}
        onSave={handleChangeDeviceAgent}
        onCreateNew={handleCreateAgentFromDevice}
      />
      <DeviceCleanupModal
        open={deviceCleanupOpen}
        onClose={() => { setDeviceCleanupOpen(false); setDeviceCleanupDevice(null); }}
        API={API}
        isDark={isDark}
        mode={deviceCleanupMode}
        device={deviceCleanupDevice}
        companyId={deviceCleanupMode === "company" ? resolvedDevicesCleanupCompanyId : null}
        companyName={resolvedDevicesCleanupCompanyName}
        onSaved={() => {
          invalidateTabRoute("devices");
          fetchDevices();
        }}
      />
      <AgentDevicesModal
        isOpen={agentDevicesModalOpen}
        onClose={() => { setAgentDevicesModalOpen(false); setSelectedAgentForDevices(null); }}
        agent={selectedAgentForDevices}
        devices={selectedAgentForDevices ? (agentDevicesByAgent[String(selectedAgentForDevices.id)] || []) : []}
        isDark={isDark}
        onViewCallLogs={handleAgentDeviceViewCallLogs}
      />
      <DeviceCallLogsModal
        isOpen={callLogsModalOpen}
        onClose={() => { setCallLogsModalOpen(false); setSelectedDeviceForLogs(null); setCallLogs([]); setCallLogsModalTab("calls"); setCallLogsSummary({ total_calls: 0, incoming: 0, outgoing: 0, missed: 0, total_duration: 0, unique_total: 0, unique_incoming: 0, unique_outgoing: 0, unique_missed: 0 }); setCallLogsDateRange("all"); setCallLogsSearch(""); setCallLogsExcludeMissed(false); setCallLogsCallTypeFilter("all"); }}
        device={selectedDeviceForLogs}
        callLogs={callLogs}
        loading={callLogsLoading}
        page={callLogsPage}
        totalPages={callLogsTotalPages}
        total={callLogsTotal}
        pageSize={callLogsLimit}
        summary={callLogsSummary}
        onPageChange={(newPage) => fetchCallLogs(selectedDeviceForLogs?.id, newPage, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime, callLogsSearch)}
        onPageSizeChange={handleCallLogsPageSizeChange}
        isDark={isDark}
        startDate={callLogsStartDate}
        endDate={callLogsEndDate}
        startTime={callLogsStartTime}
        endTime={callLogsEndTime}
        onStartDateChange={setCallLogsStartDate}
        onEndDateChange={setCallLogsEndDate}
        onStartTimeChange={setCallLogsStartTime}
        onEndTimeChange={setCallLogsEndTime}
        onFilter={handleCallLogsFilter}
        onClearSearch={handleClearCallLogsSearch}
        searchTerm={callLogsSearch}
        onSearchChange={setCallLogsSearch}
        dateRange={callLogsDateRange}
        onDateRangeChange={handleCallLogsDateRangeChange}
        onPlayRecording={openAudioPlayer}
        canSeeRecordings={canSeeRecordings}
        excludeMissed={callLogsExcludeMissed}
        onExcludeMissedChange={handleCallLogsExcludeMissedChange}
        callTypeFilter={callLogsCallTypeFilter}
        onCallTypeFilterChange={handleCallLogsCallTypeFilterChange}
        activeTab={callLogsModalTab}
        onTabChange={(tab) => {
          setCallLogsModalTab(tab);
          if (tab === "calls" && selectedDeviceForLogs && callLogs.length === 0 && !callLogsLoading) {
            fetchCallLogs(selectedDeviceForLogs.id, 1, callLogsStartDate, callLogsEndDate, callLogsStartTime, callLogsEndTime, callLogsSearch);
          }
        }}
        apiBase={API}
        failedRecordsCount={selectedDeviceForLogs ? (deviceFailedStatsMap[String(selectedDeviceForLogs.id)]?.terk_adet || 0) : 0}
        inputClass={inputClass}
      />
      <TeamModal
        isOpen={teamModalOpen}
        onClose={() => { setTeamModalOpen(false); setEditingTeam(null); }}
        teamToEdit={editingTeam}
        onSave={handleSaveTeam}
        isDark={isDark}
        agents={agents}
        panelUsers={users}
        companies={companies}
        isSuperAdmin={isSuperAdmin}
        userCompanyId={user?.company_id}
      />
      <UserModal isOpen={userModalOpen} onClose={() => { setUserModalOpen(false); setEditingUser(null); }} userToEdit={editingUser} onSave={handleSaveUser} isDark={isDark} agents={agents} teams={teams} panelUsers={users} panelRoles={panelRoles} companies={companies} isSuperAdmin={isSuperAdmin} userCompanyId={user?.company_id} currentUserId={user?.id} canManageUsers={canManageUsers} />
      <UserAccessDetailModal
        open={!!userAccessDetailUser}
        onClose={() => setUserAccessDetailUser(null)}
        user={userAccessDetailUser}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        panelRoles={panelRoles}
        teams={teams}
        agents={agents}
        panelUsers={users}
        gorevLabelFn={gorevLabel}
      />

      <Dialog open={adminLoginLinkModal.open} onOpenChange={(open) => !open && setAdminLoginLinkModal({ open: false, url: "", user: null, expiresIn: 0 })}>
        <DialogContent className={`max-w-lg ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-cyan-500" />
              Giriş linki
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className={`text-sm ${labelClass}`}>
              <strong className={textClass}>{adminLoginLinkModal.user?.ad_soyad || adminLoginLinkModal.user?.kullanici_adi}</strong>
              {" "}hesabına, o kullanıcının kendi yetkileriyle giriş yapılır. Süper admin yetkisi verilmez.
            </p>
            <p className={`text-xs ${labelClass}`}>
              Link <strong>{Math.round((adminLoginLinkModal.expiresIn || 900) / 60)} dakika</strong> geçerlidir ve yalnızca <strong>bir kez</strong> kullanılabilir.
            </p>
            <Input
              readOnly
              value={adminLoginLinkModal.url}
              className={`font-mono text-xs ${inputClass}`}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setAdminLoginLinkModal({ open: false, url: "", user: null, expiresIn: 0 })}>
              Kapat
            </Button>
            <Button type="button" onClick={copyAdminLoginLink}>
              <Copy className="w-4 h-4 mr-1" /> Kopyala
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserTotpSettings
        open={totpSettingsOpen}
        onClose={() => setTotpSettingsOpen(false)}
        apiBase={API}
        isDark={isDark}
        onStatusChange={() => refreshUser()}
      />

      <CdrStagingBubble
        count={cdrStaging.count}
        isDark={isDark}
        onOpen={() => setCdrStagingModalOpen(true)}
        onClear={() => {
          cdrStaging.clear();
          toast.success("CDR sepeti temizlendi");
        }}
      />

      <CdrStagingViewModal
        open={cdrStagingModalOpen}
        onClose={() => setCdrStagingModalOpen(false)}
        items={cdrStaging.items}
        isDark={isDark}
        labelClass={labelClass}
        textClass={textClass}
        cardClass={cardClass}
        onRemove={cdrStaging.remove}
        onClear={() => {
          cdrStaging.clear();
          toast.success("CDR sepeti temizlendi");
        }}
        onPlay={openAudioPlayer}
      />

      <LogoutConfirmModal
        isDark={isDark}
        open={logoutModalOpen}
        onCancel={() => setLogoutModalOpen(false)}
        onLogout={async (scope) => {
          setLogoutModalOpen(false);
          await logout({ scope });
        }}
      />

      <CompanyTranscriptSettingsModal
        isOpen={companyTranscriptSettingsOpen}
        onClose={() => { setCompanyTranscriptSettingsOpen(false); setTranscriptSettingsCompany(null); }}
        company={transcriptSettingsCompany}
        isDark={isDark}
      />
      <CompanyCrmSettingsModal
        isOpen={companyCrmSettingsOpen}
        onClose={() => { setCompanyCrmSettingsOpen(false); setCrmSettingsCompany(null); }}
        company={crmSettingsCompany}
        isDark={isDark}
        onSaved={handleLeadStatusesSaved}
      />
      <CustomerCrmModal
        isOpen={customerCrmOpen}
        onClose={() => { setCustomerCrmOpen(false); setCustomerCrmPhone(""); setCustomerCrmRecord(null); }}
        phone={customerCrmPhone}
        isDark={isDark}
        leadStatuses={leadStatuses}
        leadSources={leadSources}
        onLeadStatusesChange={setLeadStatuses}
        onLeadSourcesChange={setLeadSources}
        onProfileChange={cacheCustomerProfile}
        onOpenDetails={navigateToCustomerDetail}
      />
      <AlertDialog open={transcriptDeleteOpen} onOpenChange={(open) => { if (!open) { setTranscriptDeleteOpen(false); setTranscriptToDelete(null); } }}>
        <AlertDialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? "text-white" : ""}>Transkript kaydını sil</AlertDialogTitle>
            <AlertDialogDescription className={isDark ? "text-slate-400" : ""}>
              Bu transkript kaydı kalıcı olarak silinecek. CDR kaydı silinmez; yalnızca transkript metni ve işlem bilgisi kaldırılır.
              {transcriptToDelete && (
                <span className="block mt-3 font-mono text-xs">
                  {transcriptToDelete.tarih} · {transcriptToDelete.arayan_numara} → {transcriptToDelete.aranan_numara}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transcriptDeleting} className={isDark ? "border-slate-700 text-slate-300" : ""}>İptal</AlertDialogCancel>
            <AlertDialogAction
              disabled={transcriptDeleting}
              onClick={(e) => { e.preventDefault(); handleConfirmDeleteTranscript(); }}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {transcriptDeleting ? "Siliniyor..." : "Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={transcriptRecreateOpen} onOpenChange={(open) => { if (!open) { setTranscriptRecreateOpen(false); setTranscriptRecreateRecord(null); setTranscriptRecreateOptions({}); } }}>
        <AlertDialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? "text-white" : ""}>Transkript zaten mevcut</AlertDialogTitle>
            <AlertDialogDescription className={isDark ? "text-slate-400" : ""}>
              Bu görüşme için transkript daha önce oluşturulmuş. Tekrar oluşturmak mevcut transkripti siler ve yeniden işler.
              {transcriptRecreateRecord && (
                <span className="block mt-3 font-mono text-xs">
                  {transcriptRecreateRecord.tarih} · {transcriptRecreateRecord.arayan_numara || transcriptRecreateRecord.arayan} → {transcriptRecreateRecord.aranan_numara || transcriptRecreateRecord.aranan}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? "border-slate-700 text-slate-300" : ""}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmRecreateTranscript(); }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Yine de oluştur
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Top shell — logo + menü + araçlar tek satır (uzman site-header) */}
      <div
        id="app-top-shell"
        className={`site-header ${headerPinned ? "site-header--fixed" : "site-header--scroll"} ${isDark ? "site-header--dark" : "site-header--light"} ${
          isDark ? "bg-[#020617]/98 border-white/5 backdrop-blur-md" : "bg-white/98 border-gray-200 backdrop-blur-md"
        } border-b shadow-sm`}
      >
        <div className="app-header-bar mx-auto px-4 sm:px-5 lg:px-6 flex items-center gap-1.5 sm:gap-2 min-w-0">
          {/* Logo */}
          <div className="app-header-brand">
          <button
            type="button"
            onClick={() => navigateToTab("cdr")}
            title="Ana sayfa"
            className={`group flex items-center gap-2 rounded-lg px-1.5 py-1 shrink-0 transition-all outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
              isDark ? "hover:bg-slate-800/60" : "hover:bg-gray-100/80"
            }`}
          >
            <div className={`relative w-8 h-8 rounded-lg flex items-center justify-center shadow-sm transition-transform duration-200 group-hover:scale-[1.03] ${
              isDark
                ? "bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600"
                : "bg-gradient-to-br from-blue-500 to-blue-600"
            }`}>
              <Phone className="w-3.5 h-3.5 text-white" />
            </div>
            <span className={`hidden xl:inline text-sm font-bold tracking-tight whitespace-nowrap ${
              isDark ? "text-slate-100 group-hover:text-blue-300" : "text-gray-900 group-hover:text-blue-600"
            }`}>
              {user?.firma_adi || "eTelekom CDR"}
            </span>
          </button>
          </div>

          {/* Ana menü — yatay, scroll */}
          <AppMainNav
            entries={navEntries}
            isNavItemActive={isNavItemActive}
            isEntryActive={isEntryActive}
            onNavigate={navigateToTab}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
          />

          {/* Sağ araçlar */}
          <div className={`app-header-tools border-l ${isDark ? "border-slate-800" : "border-gray-200"}`}>
            {canUseSoftphone && (
              <div className={`app-header-softphone-slot border-r ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                <HeaderSoftphoneButton isDark={isDark} labelClass={labelClass} />
              </div>
            )}
            <div className="app-header-tools-cluster">
            <HeaderSettingsMenu
              isDark={isDark}
              labelClass={labelClass}
              headerPinned={headerPinned}
              onHeaderPinnedChange={setHeaderPinned}
              onToggleTheme={onToggleTheme}
              totpEnabled={!!user?.totp_enabled}
              onOpenTotpSettings={() => setTotpSettingsOpen(true)}
              currentUserName={user?.ad_soyad || user?.kullanici_adi}
              currentUserSubtitle={user?.kullanici_adi ? `@${user.kullanici_adi} · Profili düzenle` : "Profili düzenle"}
              onOpenProfile={openCurrentUserEdit}
            />
            {selectedAgent && (
              <div className={`hidden xl:flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${isDark ? "bg-blue-600/20 text-blue-400" : "bg-blue-100 text-blue-700"}`}>
                <User className="w-3 h-3" /><span className="font-medium truncate max-w-24">{selectedAgent.ad_soyad}</span>
                <button type="button" onClick={() => setSelectedAgent(null)} className="ml-0.5"><X className="w-3 h-3" /></button>
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={() => setLogoutModalOpen(true)} title="Çıkış" className={`h-8 w-8 ${isDark ? "text-slate-400 hover:text-red-400" : "text-gray-600 hover:text-red-600"}`}>
              <LogOut className="w-3.5 h-3.5" />
            </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main — SPA içerik alanı (#panel-main) */}
      <main
        id="panel-main"
        className={`panel-main ${headerPinned ? "panel-main--offset pb-3 sm:pb-5" : "py-3 sm:py-5"}`}
      >
        <Tabs value={activeTab} onValueChange={(tab) => navigateToTab(tab)} className="space-y-4 sm:space-y-6">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 space-y-4 sm:space-y-6">

          {/* CDR Tab */}
          <TabsContent value="cdr" className="space-y-4 sm:space-y-6">
            {/* Stats with Date Filter */}
            <Card className={cardClass}>
              <CardContent className="p-3 sm:p-4">
                <div className="space-y-3 mb-3 sm:mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="flex items-center gap-1.5 shrink-0">
                      <TrendingUp className="w-4 h-4 text-blue-500 shrink-0" />
                      <h3 className={`text-sm sm:text-base font-semibold whitespace-nowrap ${textClass}`}>
                        İstatistikler
                      </h3>
                    </div>
                    {dataRange && dataRange.max_date && (
                      <p
                        className={`hidden sm:block text-[10px] sm:text-xs shrink-0 whitespace-nowrap ${labelClass}`}
                        title={`Veri aralığı: ${dataRange.min_date} – ${dataRange.max_date}`}
                      >
                        Veri aralığı: {dataRange.min_date} – {dataRange.max_date}
                      </p>
                    )}
                    <div className="stats-date-pills flex-1 min-w-0">
                      <div className="stats-date-pills-track">
                        {[
                          { id: "today", label: "Bugün" },
                          { id: "yesterday", label: "Dün" },
                          { id: "last7", label: "7 Gün" },
                          { id: "thisMonth", label: "Bu Ay" },
                          { id: "lastMonth", label: "Geçen Ay" },
                          { id: "custom", label: "Özel" },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => handleStatsDateChange(opt.id)}
                            className={`stats-date-pill ${
                              statsDateRange === opt.id
                                ? (isDark ? "bg-blue-600 text-white shadow-sm" : "bg-blue-500 text-white shadow-sm")
                                : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200")
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                      {statsLoading && <div className="spinner w-4 h-4" />}
                      {canExport && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExportStatsPdf}
                            disabled={!stats || statsLoading}
                            className={`h-8 w-8 p-0 ${isDark ? "border-slate-700 text-red-400 hover:bg-slate-800 hover:text-red-300" : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                            title="PDF olarak indir"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExportStatsExcel}
                            disabled={!stats || statsLoading}
                            className={`h-8 w-8 p-0 ${isDark ? "border-slate-700 text-emerald-400 hover:bg-slate-800 hover:text-emerald-300" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"}`}
                            title="Excel olarak indir"
                          >
                            <FileSpreadsheet className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {dataRange && dataRange.max_date && (
                    <p className={`sm:hidden text-[10px] leading-snug ${labelClass}`}>
                      Veri aralığı: {dataRange.min_date} – {dataRange.max_date}
                    </p>
                  )}

                  {statsDateRange === "custom" && (
                    <div className={`flex flex-wrap items-center gap-1.5 rounded-lg p-2.5 ${isDark ? "bg-slate-900/60 border border-slate-800" : "bg-gray-50 border border-gray-100"}`}>
                      <div className="relative">
                        <Input
                          type="date"
                          value={statsStartDate}
                          onChange={(e) => setStatsStartDate(e.target.value)}
                          className={`w-[7.5rem] h-8 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                      <div className="relative">
                        <Input
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className={`w-[5.5rem] h-8 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                      <span className={`text-xs ${labelClass}`}>–</span>
                      <div className="relative">
                        <Input
                          type="date"
                          value={statsEndDate}
                          onChange={(e) => setStatsEndDate(e.target.value)}
                          className={`w-[7.5rem] h-8 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                      <div className="relative">
                        <Input
                          type="time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className={`w-[5.5rem] h-8 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                        />
                        <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={applyCustomStatsRange}
                        disabled={statsLoading}
                        className={`h-8 text-xs ${isDark ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                      >
                        {statsLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5 mr-1.5" />}
                        Uygula
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4">
                  <StatCard icon={Phone} title="Toplam Çağrı" value={stats?.total_calls?.toLocaleString('tr-TR') || "0"} secondary={formatUniqueStat(stats?.unique_total_calls)} color="blue" isDark={isDark} />
                  <StatCard icon={TrendingUp} title="Cevaplanan" value={stats?.answered_calls?.toLocaleString('tr-TR') || "0"} subtitle={stats?.total_calls ? `%${((stats.answered_calls / stats.total_calls) * 100).toFixed(1)}` : ""} secondary={formatUniqueStat(stats?.unique_answered_calls)} color="green" isDark={isDark} />
                  <StatCard icon={PhoneMissed} title="Cevapsız" value={stats?.missed_calls?.toLocaleString('tr-TR') || "0"} secondary={formatUniqueStat(stats?.unique_missed_calls)} color="amber" isDark={isDark} />
                  <StatCard icon={PhoneOff} title="Meşgul" value={stats?.busy_calls?.toLocaleString('tr-TR') || "0"} secondary={formatUniqueStat(stats?.unique_busy_calls)} color="red" isDark={isDark} />
                  <StatCard icon={Clock} title="Toplam Konuşma" value={formatStatDuration(stats?.total_talk_time || 0)} subtitle={`Ort: ${formatStatDuration(Math.round(stats?.avg_talk_time || 0))}`} color="cyan" isDark={isDark} compactValue />
                </div>
              </CardContent>
            </Card>

            {/* Filters */}
            <Card className={cardClass}>
              <CardContent className="p-2 sm:p-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-2 items-end xl:flex xl:flex-nowrap xl:gap-2">
                  <div className="min-w-0 xl:flex-none xl:w-[8.25rem]">
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Başlangıç</label>
                    <div className="relative">
                      <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div className="min-w-0 xl:flex-none xl:w-[5.25rem]">
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Saat</label>
                    <div className="relative">
                      <Input type="time" value={startTime} onChange={(e) => { setStartTime(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div className="min-w-0 xl:flex-none xl:w-[8.25rem]">
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Bitiş</label>
                    <div className="relative">
                      <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Calendar className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div className="min-w-0 xl:flex-none xl:w-[5.25rem]">
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Saat</label>
                    <div className="relative">
                      <Input type="time" value={endTime} onChange={(e) => { setEndTime(e.target.value); setPage(1); }} className={`h-7 text-xs pl-7 ${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                      <Clock className={`w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-blue-500"}`} />
                    </div>
                  </div>
                  <div className="min-w-0 xl:flex-none xl:w-[4.75rem]">
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Yön</label>
                    <Select value={yonFilter} onValueChange={(v) => { setYonFilter(v); setPage(1); }}>
                      <SelectTrigger className={`h-7 text-xs px-1.5 min-w-0 ${inputClass}`}><SelectValue /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="1">Gelen</SelectItem>
                        <SelectItem value="2">Giden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {isSuperAdmin && (
                    <div className="min-w-0 xl:flex-none xl:w-[6.5rem]">
                      <label className={`block text-xs mb-0.5 truncate ${labelClass}`} title="Firma">
                        <Building2 className="w-3 h-3 inline mr-0.5 opacity-70" />
                        Firma
                      </label>
                      <Select
                        value={cdrCompanyFilter}
                        onValueChange={(v) => {
                          setCdrCompanyFilter(v);
                          setPage(1);
                          if (selectedAgent && v !== "all" && String(selectedAgent.company_id) !== v) {
                            setSelectedAgent(null);
                          }
                        }}
                      >
                        <SelectTrigger className={`h-7 text-xs px-1.5 min-w-0 [&>span]:truncate ${inputClass}`}>
                          <SelectValue placeholder="Tümü" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="all">Tümü</SelectItem>
                          {companies.filter((c) => c.status === 1).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="min-w-0 xl:flex-[1.05] xl:basis-0 xl:min-w-[6.5rem]">
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Agent</label>
                    <SearchableAgentSelect
                      agents={cdrFilteredAgents}
                      value={selectedAgent}
                      onChange={(agent) => { setSelectedAgent(agent); setPage(1); }}
                      isDark={isDark}
                      inputClass={inputClass}
                      labelClass={labelClass}
                      requireGsm
                      showCompany={isSuperAdmin && cdrCompanyFilter === "all"}
                    />
                  </div>
                  <div className="min-w-0 col-span-2 sm:col-span-1 xl:col-span-1 xl:flex-[1.35] xl:basis-0 xl:min-w-[8.5rem] xl:max-w-[10.5rem]">
                    <label className={`block text-xs mb-0.5 truncate ${labelClass}`}>
                      <Phone className="w-3 h-3 inline mr-0.5 opacity-70" />
                      Numara
                    </label>
                    <div className="relative">
                      <Search className={`w-3 h-3 absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-violet-400" : "text-violet-500"}`} />
                      <Input
                        placeholder="05xx…"
                        value={cdrPhoneFilterInput}
                        onChange={(e) => setCdrPhoneFilterInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const next = cdrPhoneFilterInput.trim();
                            setCdrPhoneFilter(next);
                            setPage(1);
                          }
                        }}
                        className={`h-7 text-xs pl-6 pr-6 min-w-0 w-full ${inputClass} font-mono ${
                          (cdrPhoneFilterInput.trim() || cdrPhoneFilter.trim())
                            ? (isDark ? "border-violet-500/40 bg-violet-500/5" : "border-violet-300 bg-violet-50/40")
                            : ""
                        }`}
                      />
                      {(cdrPhoneFilterInput || cdrPhoneFilter) && (
                        <button
                          type="button"
                          onClick={() => {
                            setCdrPhoneFilterInput("");
                            setCdrPhoneFilter("");
                            setPage(1);
                          }}
                          className={`absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-black/10 ${isDark ? "text-slate-400 hover:text-slate-200" : "text-gray-400 hover:text-gray-700"}`}
                          title="Temizle"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 xl:flex-none xl:w-[6.75rem]">
                    <label className={`block text-xs mb-0.5 ${labelClass}`}>Durum</label>
                    <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                      <SelectTrigger className={`h-7 text-xs px-1.5 min-w-0 ${inputClass}`}><SelectValue /></SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="1">Cevaplanan</SelectItem>
                        <SelectItem value="2">Cevapsız</SelectItem>
                        <SelectItem value="3">Meşgul</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-1 min-w-0 col-span-2 sm:col-span-3 md:col-span-4 lg:col-span-5 xl:flex-none xl:shrink-0 xl:justify-end">
                    {canManageCompanySettings && user?.company_id && !isSuperAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCompanyCrmSettings({ id: user.company_id, ad: user.firma_adi || "Firma" })}
                          className={`h-7 px-2 ${isDark ? "border-violet-500/40 text-violet-400 hover:bg-violet-500/10" : "text-violet-600 border-violet-200 hover:bg-violet-50"}`}
                          title="Lead Durumları"
                        >
                          <Contact className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setTranscriptSettingsCompany({ id: user.company_id, ad: user.firma_adi || "Firma" }); setCompanyTranscriptSettingsOpen(true); }}
                          className={`h-7 px-2 ${isDark ? "border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10" : "text-cyan-600 border-cyan-200 hover:bg-cyan-50"}`}
                          title="Transkript Ayarları"
                        >
                          <FileText className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    <Button variant="secondary" size="sm" onClick={() => { setPage(1); fetchRecords(); fetchStats(); }} className={`h-7 px-2 ${isDark ? "bg-slate-800 hover:bg-slate-700 text-white" : ""}`} title="Yenile"><RefreshCw className="w-3.5 h-3.5" /></Button>
                    <Button variant="outline" size="sm" onClick={resetFilters} className={`h-7 px-2 ${isDark ? "border-slate-700 text-amber-400 hover:bg-slate-800" : "text-amber-600 hover:bg-amber-50"}`} title="Filtreleri Sıfırla"><FilterX className="w-3.5 h-3.5" /></Button>
                    {canExport && (
                      <div className="relative">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setExportMenuOpen(!exportMenuOpen)}
                          disabled={exporting} 
                          className={`h-8 px-2 ${isDark ? "border-slate-700 text-emerald-400 hover:bg-slate-800" : "text-emerald-600 hover:bg-emerald-50"}`}
                          title="Export"
                        >
                          {exporting ? <div className="spinner w-4 h-4" /> : <Download className="w-4 h-4" />}
                        </Button>
                        {exportMenuOpen && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
                            <div className={`absolute right-0 mt-1 z-50 rounded-lg border shadow-lg py-1 min-w-[140px] ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"}`}>
                              <button 
                                onClick={() => setExportModalOpen(true)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? "hover:bg-slate-800 text-emerald-400" : "hover:bg-gray-50 text-emerald-600"}`}
                              >
                                <FileSpreadsheet className="w-4 h-4" /> Excel Export
                              </button>
                              <button 
                                onClick={handleExportPdf}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? "hover:bg-slate-800 text-red-400" : "hover:bg-gray-50 text-red-600"}`}
                              >
                                <FileText className="w-4 h-4" /> PDF Export
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Export Modal */}
            <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
              <DialogContent className={`max-w-md ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
                <DialogHeader>
                  <DialogTitle className={isDark ? "text-white" : "text-gray-900"}>
                    <Download className="w-5 h-5 inline mr-2 text-blue-500" />Excel Export Ayarları
                  </DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className={`text-sm mb-4 ${labelClass}`}>Export&apos;a dahil edilecek kolonları seçin:</p>
                  <div className="space-y-3">
                    {ALL_COLUMNS.map(col => {
                      if (col.requiresPermission && !canSeeRecordings) return null;
                      return (
                        <div key={col.id} className="flex items-center gap-3">
                          <Checkbox
                            checked={exportColumns.includes(col.id)}
                            onCheckedChange={() => toggleExportColumn(col.id)}
                            id={`export-${col.id}`}
                          />
                          <label htmlFor={`export-${col.id}`} className={`text-sm cursor-pointer ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                            {col.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setExportModalOpen(false)}>İptal</Button>
                  <Button onClick={handleExport} disabled={exporting || exportColumns.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {exporting ? "Export ediliyor..." : "Excel İndir"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Table */}
            {cdrFollowUpIds && (
              <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border mb-3 ${
                isDark ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-emerald-50 border-emerald-200 text-emerald-900"
              }`}>
                <div className="flex items-center gap-2 text-sm">
                  <PhoneOutgoing className="w-4 h-4 shrink-0" />
                  <span>
                    <span className="font-medium">Geri arama kaydı gösteriliyor:</span>{" "}
                    {cdrFollowUpLabel}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelCdrFollowUpView}
                  className={isDark ? "border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10" : "border-emerald-300 text-emerald-800 hover:bg-emerald-100"}
                >
                  <X className="w-4 h-4 mr-1" />Listeye dön
                </Button>
              </div>
            )}
            <Card className={cardClass}>
              <CardHeader className="p-[1.1rem] space-y-0 flex flex-row items-center justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className={`text-base font-semibold flex flex-wrap items-center gap-1.5 ${textClass}`}>
                    Çağrı Kayıtları
                    {!loading && cdrTotalCount > 0 && (
                      <>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full tabular-nums ${
                          isDark ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/25" : "bg-indigo-50 text-indigo-700 border border-indigo-200"
                        }`}>
                          {cdrTotalCount.toLocaleString("tr-TR")} kayıt
                        </span>
                        {!cdrFollowUpIds && cdrTotalPages > 1 && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full tabular-nums ${
                            isDark ? "bg-slate-800 text-slate-300 border border-slate-700" : "bg-slate-100 text-slate-700 border border-slate-200"
                          }`}>
                            Sayfa {page} / {cdrTotalPages}
                          </span>
                        )}
                      </>
                    )}
                    {loading && (
                      <span className={`text-xs font-normal ${labelClass}`}>yükleniyor…</span>
                    )}
                  </CardTitle>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Column Visibility Selector */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className={isDark ? "border-slate-700 text-slate-300" : ""}>
                        <Columns3 className="w-4 h-4 mr-2" />Kolonlar
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className={`w-56 ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`} align="end">
                      <div className="space-y-2">
                        <p className={`text-sm font-medium mb-3 ${isDark ? "text-slate-300" : "text-gray-700"}`}>Görünür Kolonlar</p>
                        {ALL_COLUMNS.map(col => {
                          if (col.requiresPermission && !canSeeRecordings) return null;
                          return (
                            <div key={col.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={visibleColumns.includes(col.id)}
                                onCheckedChange={() => toggleColumn(col.id)}
                                id={`col-${col.id}`}
                              />
                              <label htmlFor={`col-${col.id}`} className={`text-sm cursor-pointer ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                                {col.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Select value={limit.toString()} onValueChange={(v) => { setLimit(parseInt(v, 10)); setPage(1); }}>
                    <SelectTrigger className={`w-20 ${inputClass}`}><SelectValue /></SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className={`${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"}`}>
                        {isColumnVisible("tarih") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Tarih</TableHead>}
                        {isColumnVisible("yon") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Yön</TableHead>}
                        {isColumnVisible("agent_name") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Agent</TableHead>}
                        {isColumnVisible("company_name") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Firma</TableHead>}
                        {isColumnVisible("arayan_numara") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Arayan</TableHead>}
                        {isColumnVisible("aranan_numara") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Aranan</TableHead>}
                        {isColumnVisible("konusma_suresi") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Süre</TableHead>}
                        {isColumnVisible("durum") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Durum</TableHead>}
                        {isColumnVisible("sistem") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Sistem</TableHead>}
                        {isColumnVisible("device_id") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Cihaz ID</TableHead>}
                        {isColumnVisible("did") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Departman</TableHead>}
                        {isColumnVisible("transkript") && canSeeRecordings && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>Transkript</TableHead>}
                        {isColumnVisible("recording") && <TableHead className={`font-semibold ${isDark ? "text-slate-300" : "text-gray-700"}`}>İşlemler</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow><TableCell colSpan={visibleColumns.length + 1} className="text-center py-16"><div className="flex flex-col items-center gap-3"><div className="spinner w-8 h-8" /><span className={labelClass}>Yükleniyor...</span></div></TableCell></TableRow>
                      ) : records.length === 0 ? (
                        <TableRow><TableCell colSpan={visibleColumns.length + 1} className={`text-center py-16 ${labelClass}`}><Database className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Kayıt bulunamadı</p></TableCell></TableRow>
                      ) : (
                        records.map((r, i) => {
                          const customerPhone = getCustomerPhoneFromRecord(r, agents);
                          const customerKey = customerPhone ? normalizeTurkishPhone(customerPhone) : "";
                          const cachedCustomer = customerKey ? customerProfileCache[customerKey] : null;
                          const isFollowUpRow = cdrFollowUpIdSet?.has(Number(r.id));
                          return (
                          <TableRow key={r.id || i} className={`transition-colors ${isDark ? "border-slate-800/50 hover:bg-slate-800/40" : "border-gray-100 hover:bg-blue-50/50"} ${i % 2 === 0 ? (isDark ? "bg-slate-900/30" : "bg-white") : (isDark ? "bg-slate-800/20" : "bg-gray-50/50")} ${isFollowUpRow ? (isDark ? "ring-1 ring-inset ring-emerald-500/40 bg-emerald-500/5" : "ring-1 ring-inset ring-emerald-300 bg-emerald-50/60") : ""}`}>
                            {isColumnVisible("tarih") && (
                              <TableCell className="whitespace-nowrap">
                                <CdrDateTimeCell value={r.tarih} isDark={isDark} />
                              </TableCell>
                            )}
                            {isColumnVisible("yon") && (
                              <TableCell>
                                {String(r.durum) !== '1' ? (
                                  // Cevapsız - kırmızı renk ve cevapsız ikon
                                  <Badge className={`${isDark ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-red-100 text-red-700 border border-red-200"}`}>
                                    <PhoneMissed className="w-3 h-3 mr-1" />
                                    {r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-"}
                                  </Badge>
                                ) : r.yon === 1 ? (
                                  <Badge className={`${isDark ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border border-emerald-200"}`}>
                                    <PhoneIncoming className="w-3 h-3 mr-1" />Gelen
                                  </Badge>
                                ) : r.yon === 2 ? (
                                  <Badge className={`${isDark ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-blue-100 text-blue-700 border border-blue-200"}`}>
                                    <PhoneOutgoing className="w-3 h-3 mr-1" />Giden
                                  </Badge>
                                ) : <span className={labelClass}>-</span>}
                              </TableCell>
                            )}
                            {isColumnVisible("agent_name") && (
                              <TableCell className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                {r.agent_name ? (
                                  <AgentNameBadge
                                    agentName={r.agent_name}
                                    agentId={r.agentId}
                                    agents={agents}
                                    isDark={isDark}
                                    onClick={() => {
                                      const agent = agents.find(a => String(a.id) === String(r.agentId) || a.ad_soyad === r.agent_name);
                                      if (agent) {
                                        setSelectedAgent(agent);
                                        toast.success(`${r.agent_name} filtrelendi`);
                                      }
                                    }}
                                  />
                                ) : <span className={labelClass}>-</span>}
                              </TableCell>
                            )}
                            {isColumnVisible("company_name") && (
                              <TableCell className={`text-sm max-w-[140px] truncate ${isDark ? "text-slate-400" : "text-gray-600"}`} title={r.company_name || undefined}>
                                {r.company_name || "-"}
                              </TableCell>
                            )}
                            {isColumnVisible("arayan_numara") && (
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                {phonesMatch(r.arayan_numara, customerPhone) ? (
                                  <CustomerBadge
                                    phone={r.arayan_numara}
                                    displayName={cachedCustomer?.ad_soyad}
                                    leadStatus={cachedCustomer?.lead_durumu}
                                    leadStatuses={leadStatuses}
                                    isDark={isDark}
                                    onClick={() => openCustomerCrm(r)}
                                    onHoverRequest={fetchCustomerProfileOnDemand}
                                    loading={!!customerProfileLoadingKeys[customerKey]}
                                  />
                                ) : (
                                  <AgentPhoneChip phone={r.arayan_numara} isDark={isDark} />
                                )}
                              </TableCell>
                            )}
                            {isColumnVisible("aranan_numara") && (
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                {phonesMatch(r.aranan_numara, customerPhone) ? (
                                  <CustomerBadge
                                    phone={r.aranan_numara}
                                    displayName={cachedCustomer?.ad_soyad}
                                    leadStatus={cachedCustomer?.lead_durumu}
                                    leadStatuses={leadStatuses}
                                    isDark={isDark}
                                    onClick={() => openCustomerCrm(r)}
                                    onHoverRequest={fetchCustomerProfileOnDemand}
                                    loading={!!customerProfileLoadingKeys[customerKey]}
                                  />
                                ) : (
                                  <AgentPhoneChip phone={r.aranan_numara} isDark={isDark} />
                                )}
                              </TableCell>
                            )}
                            {isColumnVisible("konusma_suresi") && (
                              <TableCell>
                                <span className={`inline-flex items-center px-2 py-1 rounded font-mono text-sm ${r.konusma_suresi > 0 ? (isDark ? "bg-purple-500/20 text-purple-400" : "bg-purple-100 text-purple-700") : (isDark ? "text-slate-500" : "text-gray-400")}`}>
                                  <Clock className="w-3 h-3 mr-1" />{formatDuration(r.konusma_suresi)}
                                </span>
                              </TableCell>
                            )}
                            {isColumnVisible("durum") && (
                              <TableCell>
                                <div className="inline-flex items-center gap-1">
                                  <StatusBadge durum={r.durum} durum_aciklama={r.durum_aciklama} isDark={isDark} />
                                  <CdrFollowUpIndicator record={r} isDark={isDark} onFollowUpClick={handleCdrFollowUpClick} />
                                </div>
                              </TableCell>
                            )}
                            {isColumnVisible("sistem") && (
                              <TableCell>
                                {r.sistem === 1 ? (
                                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${isDark ? "bg-cyan-500/20 text-cyan-400" : "bg-cyan-100 text-cyan-700"}`} title="GSM">
                                    <Smartphone className="w-4 h-4" />
                                  </span>
                                ) : (
                                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"}`} title="Sabit Hat">
                                    <Phone className="w-4 h-4" />
                                  </span>
                                )}
                              </TableCell>
                            )}
                            {isColumnVisible("device_id") && (
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                                {r.device_id ? (
                                  <span 
                                    className={`px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all ${isDark ? "bg-slate-800 text-cyan-400" : "bg-gray-100 text-cyan-700"}`}
                                    onClick={() => openCallLogsModalByDeviceId(r.device_id)}
                                    title="Çağrı kayıtlarını görüntüle"
                                  >
                                    <Smartphone className="w-3 h-3 inline mr-1" />{r.device_id}
                                  </span>
                                ) : <span className={labelClass}>-</span>}
                              </TableCell>
                            )}
                            {isColumnVisible("did") && <TableCell className={`font-mono text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{r.did || "-"}</TableCell>}
                            {isColumnVisible("transkript") && canSeeRecordings && (
                              <TableCell>
                                {r.recording ? (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={async () => { 
                                      await handleViewTranscript(r.id, { autoStart: r.transkript !== 1 }); 
                                      navigateToTab("transcripts"); 
                                    }}
                                    className={`p-1 h-auto ${
                                      r.transkript === 1
                                        ? (isDark ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50")
                                        : (isDark ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" : "text-rose-600 hover:text-rose-700 hover:bg-rose-50")
                                    }`}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    <span className="text-xs font-medium">{r.transkript === 1 ? "Görüntüle" : "Oluştur"}</span>
                                  </Button>
                                ) : (
                                  <span className={`inline-flex items-center text-xs px-2 py-1 rounded ${isDark ? "bg-slate-800 text-slate-500" : "bg-gray-100 text-gray-400"}`}>
                                    <FileText className="w-3 h-3 mr-1" />Yok
                                  </span>
                                )}
                              </TableCell>
                            )}
                            {isColumnVisible("recording") && (
                              <TableCell>
                                <div className="flex items-center gap-0.5">
                                  {r.recording && (
                                    <CdrStagingAddButton
                                      record={r}
                                      onAdd={cdrStaging.add}
                                      isDark={isDark}
                                    />
                                  )}
                                  <RecordingTableActions
                                    record={r}
                                    isDark={isDark}
                                    labelClass={labelClass}
                                    onPlay={openAudioPlayer}
                                    variant="icons"
                                  />
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
                {showCdrPagination && (
                  <CdrTablePagination
                    isDark={isDark}
                    page={page}
                    pageSize={limit}
                    total={cdrTotalCount}
                    totalPages={cdrTotalPages}
                    loading={loading}
                    onPageChange={setPage}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GSM Search Tab */}
          {canSearch && (
          <TabsContent value="search" className="space-y-4 sm:space-y-6">
            {/* Search Filters */}
            <Card className={cardClass}>
              <CardContent className="p-3 sm:p-4">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className={`w-5 h-5 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                    <h3 className={`text-sm sm:text-base font-semibold ${textClass}`}>GSM Numarası ile Arama</h3>
                  </div>
                  <p className={`text-xs ${labelClass}`}>
                    Arayan veya aranan numara içinde arama yapar. Sonuçlarda gelen/giden yönü gösterilir.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                    <div className="lg:col-span-2">
                      <label className={`block text-xs sm:text-sm mb-1 sm:mb-2 ${labelClass}`}>
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />GSM Numarası
                      </label>
                      <Input
                        placeholder="05xx veya 850..."
                        value={gsmSearchNumber}
                        onChange={(e) => setGsmSearchNumber(e.target.value)}
                        className={`h-9 sm:h-10 text-sm font-mono ${inputClass}`}
                        onKeyDown={(e) => { if (e.key === 'Enter') fetchGsmSearch(); }}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs sm:text-sm mb-1 sm:mb-2 ${labelClass}`}>
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />Başlangıç
                      </label>
                      <Input
                        type="date"
                        value={gsmSearchStartDate}
                        onChange={(e) => setGsmSearchStartDate(e.target.value)}
                        className={`h-9 sm:h-10 text-xs sm:text-sm ${inputClass}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs sm:text-sm mb-1 sm:mb-2 ${labelClass}`}>
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />Bitiş
                      </label>
                      <Input
                        type="date"
                        value={gsmSearchEndDate}
                        onChange={(e) => setGsmSearchEndDate(e.target.value)}
                        className={`h-9 sm:h-10 text-xs sm:text-sm ${inputClass}`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={fetchGsmSearch}
                        disabled={gsmSearchLoading || !gsmSearchNumber || gsmSearchNumber.length < 3}
                        className="flex-1 h-9 sm:h-10 bg-cyan-600 hover:bg-cyan-700 text-white"
                      >
                        {gsmSearchLoading ? <div className="spinner w-4 h-4" /> : <><Search className="w-4 h-4 mr-1" />Ara</>}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setGsmSearchNumber("");
                          const today = new Date().toISOString().split("T")[0];
                          setGsmSearchStartDate(today);
                          setGsmSearchEndDate(today);
                          setGsmSearchResults([]);
                          setGsmSearchCount(0);
                          setGsmSearchPage(1);
                        }}
                        className={`h-9 sm:h-10 ${isDark ? "border-slate-700 text-amber-400 hover:bg-slate-800" : "text-amber-600 hover:bg-amber-50"}`}
                        title="Sıfırla"
                      >
                        <FilterX className="w-4 h-4" />
                      </Button>
                      {canExport && gsmSearchResults.length > 0 && (
                        <Button
                          variant="outline"
                          onClick={handleGsmExport}
                          disabled={gsmExporting}
                          className={`h-9 sm:h-10 ${isDark ? "border-slate-700 text-slate-300" : ""}`}
                        >
                          {gsmExporting ? <div className="spinner w-4 h-4" /> : <Download className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Search Results */}
            <Card className={cardClass}>
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className={`text-lg font-semibold ${textClass}`}>
                  <Search className="w-5 h-5 inline mr-2 text-cyan-400" />
                  Arama Sonuçları
                  <span className={`text-sm font-normal ml-2 ${labelClass}`}>({gsmSearchCount.toLocaleString('tr-TR')})</span>
                </CardTitle>
                <Select value={gsmSearchLimit.toString()} onValueChange={(v) => { setGsmSearchLimit(parseInt(v)); setGsmSearchPage(1); }}>
                  <SelectTrigger className={`w-20 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className={isDark ? "border-slate-800" : "border-gray-200"}>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Yön</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Agent</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Arayan</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Aranan</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Süre</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Sistem</TableHead>
                        <TableHead className={isDark ? "text-slate-400" : ""}>Cihaz ID</TableHead>
                        {canSeeRecordings && <TableHead className={isDark ? "text-slate-400" : ""}>Kayıt</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gsmSearchLoading ? (
                        <TableRow><TableCell colSpan={10} className="text-center py-12"><div className="spinner w-6 h-6 mx-auto" /></TableCell></TableRow>
                      ) : gsmSearchResults.length === 0 ? (
                        <TableRow><TableCell colSpan={10} className={`text-center py-12 ${labelClass}`}>
                          {gsmSearchNumber ? "Sonuç bulunamadı" : "Arama yapmak için GSM numarası girin"}
                        </TableCell></TableRow>
                      ) : (
                        gsmSearchResults.map((r, i) => (
                          <TableRow key={r.id || i} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                            <TableCell className={`font-mono whitespace-nowrap text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{r.tarih}</TableCell>
                            <TableCell>
                              {String(r.durum) !== '1' ? (
                                // Cevapsız - kırmızı renk ve cevapsız ikon
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700"}`}>
                                  <PhoneMissed className="w-3 h-3" />
                                  {r.yon === 1 ? "Gelen" : r.yon === 2 ? "Giden" : "-"}
                                </span>
                              ) : r.yon === 1 ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700"}`}>
                                  <PhoneIncoming className="w-3 h-3" />Gelen
                                </span>
                              ) : r.yon === 2 ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700"}`}>
                                  <PhoneOutgoing className="w-3 h-3" />Giden
                                </span>
                              ) : (
                                <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>-</span>
                              )}
                            </TableCell>
                            <TableCell className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                              {r.agent_name ? (
                                <AgentNameBadge
                                  agentName={r.agent_name}
                                  agentId={r.agentId}
                                  agents={agents}
                                  isDark={isDark}
                                  onClick={() => {
                                    const agent = agents.find(a => String(a.id) === String(r.agentId) || a.ad_soyad === r.agent_name);
                                    if (agent) {
                                      setSelectedAgent(agent);
                                      navigateToTab("cdr");
                                      toast.success(`${r.agent_name} filtrelendi - Çağrılar sayfasına yönlendirildi`);
                                    }
                                  }}
                                />
                              ) : <span className={labelClass}>-</span>}
                            </TableCell>
                            <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{r.arayan_numara}</TableCell>
                            <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{r.aranan_numara}</TableCell>
                            <TableCell className={`font-mono text-xs sm:text-sm ${isDark ? "text-slate-300" : ""}`}>{formatDuration(r.konusma_suresi)}</TableCell>
                            <TableCell><StatusBadge durum={r.durum} durum_aciklama={r.durum_aciklama} isDark={isDark} /></TableCell>
                            <TableCell>
                              {r.sistem === 1 ? (
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${isDark ? "bg-cyan-500/20 text-cyan-400" : "bg-cyan-100 text-cyan-700"}`} title="GSM">
                                  <Smartphone className="w-3.5 h-3.5" />
                                </span>
                              ) : (
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"}`} title="Sabit Hat">
                                  <Phone className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </TableCell>
                            <TableCell className={`font-mono text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                              {r.device_id ? (
                                <span 
                                  className={`px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all ${isDark ? "bg-slate-800 text-cyan-400" : "bg-gray-100 text-cyan-700"}`}
                                  onClick={() => openCallLogsModalByDeviceId(r.device_id)}
                                  title="Çağrı kayıtlarını görüntüle"
                                >
                                  <Smartphone className="w-3 h-3 inline mr-1" />{r.device_id}
                                </span>
                              ) : <span className={labelClass}>-</span>}
                            </TableCell>
                            {canSeeRecordings && (
                              <TableCell>
                                <RecordingTableActions
                                  record={r}
                                  isDark={isDark}
                                  labelClass={labelClass}
                                  onPlay={openAudioPlayer}
                                  variant="compact"
                                />
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {Math.ceil(gsmSearchCount / gsmSearchLimit) > 1 && (
                  <div className={`flex items-center justify-between px-4 py-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                    <div className={`text-sm ${labelClass}`}>Sayfa {gsmSearchPage} / {Math.ceil(gsmSearchCount / gsmSearchLimit)}</div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(1); fetchGsmSearch(); }} disabled={gsmSearchPage === 1}>İlk</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(p => Math.max(1, p - 1)); }} disabled={gsmSearchPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className={`px-3 py-1 rounded ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{gsmSearchPage}</span>
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(p => Math.min(Math.ceil(gsmSearchCount / gsmSearchLimit), p + 1)); }} disabled={gsmSearchPage === Math.ceil(gsmSearchCount / gsmSearchLimit)}><ChevronRight className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => { setGsmSearchPage(Math.ceil(gsmSearchCount / gsmSearchLimit)); }} disabled={gsmSearchPage === Math.ceil(gsmSearchCount / gsmSearchLimit)}>Son</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Transcripts Tab */}
          {canSeeRecordings && (
            <TabsContent value="transcripts" className="space-y-6">
              {/* Transcript Detail View */}
              {selectedTranscriptRecord ? (
                <TranscriptDetailView
                  record={selectedTranscriptRecord}
                  agents={agents}
                  leadStatuses={leadStatuses}
                  customerProfileCache={customerProfileCache}
                  customerProfileLoadingKeys={customerProfileLoadingKeys}
                  onCustomerClick={() => openCustomerCrm(selectedTranscriptRecord)}
                  onCustomerHover={fetchCustomerProfileOnDemand}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  inputClass={inputClass}
                  searchQuery={transcriptSearchQuery}
                  onSearchChange={setTranscriptSearchQuery}
                  onCopy={handleCopyTranscript}
                  onStart={() => requestStartTranscript(selectedTranscriptRecord, { force: selectedTranscriptRecord.status === "failed" })}
                  onRetry={() => requestStartTranscript(selectedTranscriptRecord, { force: true })}
                  onPoll={() => pollTranscriptStatus(selectedTranscriptRecord.cdr_id || selectedTranscriptRecord.id)}
                  transcriptPolling={transcriptPolling}
                  isTranscriptInProgress={isTranscriptInProgress}
                  getTranscriptStatusLabel={getTranscriptStatusLabel}
                  onClose={() => setSelectedTranscriptRecord(null)}
                  onDelete={() => openTranscriptDeleteModal(selectedTranscriptRecord)}
                />
              ) : (
                /* Transcripts List */
                <Card className={cardClass}>
                  <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <CardTitle className={`text-lg font-semibold ${textClass}`}>
                        <FileText className="w-5 h-5 inline mr-2 text-emerald-500" />
                        Ses Transkriptleri
                        <span className={`text-sm font-normal ml-2 ${labelClass}`}>({transcriptTotal})</span>
                      </CardTitle>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                        <div className="flex items-center gap-2 flex-1 min-w-[240px] max-w-xl">
                          <div className="relative flex-1">
                            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${labelClass}`} />
                            <Input
                              value={transcriptSearchInput}
                              onChange={(e) => setTranscriptSearchInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") applyTranscriptSearch(); }}
                              placeholder="Kelime, numara veya özet ara..."
                              className={`pl-9 h-9 ${inputClass}`}
                            />
                          </div>
                          <Button size="sm" onClick={applyTranscriptSearch} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
                            Bul
                          </Button>
                          {transcriptSearchTerm && (
                            <Button size="sm" variant="ghost" onClick={clearTranscriptSearch} className={`h-9 shrink-0 ${isDark ? "text-slate-400" : ""}`}>
                              Temizle
                            </Button>
                          )}
                        </div>
                        <Select value={transcriptFilter} onValueChange={(v) => { setTranscriptFilter(v); setTranscriptPage(1); }}>
                          <SelectTrigger className={`w-40 ${inputClass}`}><SelectValue /></SelectTrigger>
                          <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                            <SelectItem value="all">Tümü</SelectItem>
                            <SelectItem value="has">Transkripti Var</SelectItem>
                            <SelectItem value="none">Transkripti Yok</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchTranscripts()}
                          disabled={transcriptsLoading}
                          className={`h-9 shrink-0 ${isDark ? "border-slate-700" : ""}`}
                          title="Listeyi yenile"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${transcriptsLoading ? "animate-spin" : ""}`} />
                          Yenile
                        </Button>
                      </div>
                    </div>
                    {transcriptSearchTerm && (
                      <p className={`text-sm mt-3 ${labelClass}`}>
                        <span className="font-medium">"{transcriptSearchTerm}"</span> için {transcriptTotal} sonuç — satıra tıklayarak detaya girebilirsiniz
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className={isDark ? "border-slate-800" : ""}>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Arayan</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Aranan</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Süre</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                            {transcriptSearchTerm && <TableHead className={isDark ? "text-slate-400" : ""}>Eşleşme</TableHead>}
                            <TableHead className={isDark ? "text-slate-400" : ""}>Transkript</TableHead>
                            <TableHead className={isDark ? "text-slate-400" : ""}>İşlemler</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transcriptsLoading ? (
                            <TableRow><TableCell colSpan={transcriptSearchTerm ? 8 : 7} className="text-center py-12"><div className="spinner w-6 h-6 mx-auto" /></TableCell></TableRow>
                          ) : transcripts.length === 0 ? (
                            <TableRow><TableCell colSpan={transcriptSearchTerm ? 8 : 7} className={`text-center py-12 ${labelClass}`}>
                              {transcriptSearchTerm ? `"${transcriptSearchTerm}" için sonuç bulunamadı` : "Transkript bulunamadı"}
                            </TableCell></TableRow>
                          ) : transcripts.map(t => {
                            const customerPhone = getCustomerPhoneFromRecord(t, agents);
                            const customerKey = customerPhone ? normalizeTurkishPhone(customerPhone) : "";
                            const cachedCustomer = customerKey ? customerProfileCache[customerKey] : null;
                            return (
                            <TableRow
                              key={t.id}
                              className={`cursor-pointer ${isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}`}
                              onClick={() => handleViewTranscript(t.cdr_id, { searchQuery: transcriptSearchTerm.trim() || undefined })}
                            >
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : ""}`}>{t.tarih}</TableCell>
                              <TableCell className="text-sm">
                                <CallPartyPhone
                                  record={t}
                                  phone={t.arayan_numara}
                                  agents={agents}
                                  isDark={isDark}
                                  leadStatuses={leadStatuses}
                                  customerProfile={cachedCustomer}
                                  customerProfileLoading={!!customerProfileLoadingKeys[customerKey]}
                                  onCustomerClick={() => openCustomerCrm(t)}
                                  onHoverRequest={fetchCustomerProfileOnDemand}
                                />
                              </TableCell>
                              <TableCell className="text-sm">
                                <CallPartyPhone
                                  record={t}
                                  phone={t.aranan_numara}
                                  agents={agents}
                                  isDark={isDark}
                                  leadStatuses={leadStatuses}
                                  customerProfile={cachedCustomer}
                                  customerProfileLoading={!!customerProfileLoadingKeys[customerKey]}
                                  onCustomerClick={() => openCustomerCrm(t)}
                                  onHoverRequest={fetchCustomerProfileOnDemand}
                                />
                              </TableCell>
                              <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : ""}`}>{formatDuration(t.konusma_suresi)}</TableCell>
                              <TableCell>
                                <Badge className={
                                  t.status === "completed" ? (isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700") :
                                  t.status === "processing" ? (isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700") :
                                  t.status === "failed" ? (isDark ? "bg-rose-500/20 text-rose-400" : "bg-rose-100 text-rose-700") :
                                  (isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700")
                                }>
                                  {getTranscriptStatusLabel(t.status)}
                                </Badge>
                              </TableCell>
                              {transcriptSearchTerm && (
                                <TableCell className={`text-xs max-w-[260px] ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                                  {getTranscriptSearchSnippet(t, transcriptSearchTerm) ? (
                                    <span className="line-clamp-2">
                                      <HighlightText text={getTranscriptSearchSnippet(t, transcriptSearchTerm)} query={transcriptSearchTerm} isDark={isDark} />
                                    </span>
                                  ) : (
                                    <span className={labelClass}>—</span>
                                  )}
                                </TableCell>
                              )}
                              <TableCell>
                                {t.transcript_text ? (
                                  <Badge className={isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"}>
                                    <CheckSquare className="w-3 h-3 mr-1" />Var
                                  </Badge>
                                ) : (
                                  <Badge className={isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-600"}>
                                    <Square className="w-3 h-3 mr-1" />Yok
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleViewTranscript(t.cdr_id, { searchQuery: transcriptSearchTerm.trim() || undefined })}
                                    className={isDark ? "text-emerald-400" : "text-emerald-600"}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {t.recording_url && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => openAudioPlayer(t.recording_url, t)}
                                      className={isDark ? "text-cyan-400" : "text-blue-600"}
                                    >
                                      <Volume2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openTranscriptDeleteModal(t)}
                                    className={isDark ? "text-rose-400 hover:text-rose-300" : "text-rose-600 hover:text-rose-700"}
                                    title="Sil"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    {Math.ceil(transcriptTotal / 20) > 1 && (
                      <div className={`flex items-center justify-between px-4 py-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                        <div className={`text-sm ${labelClass}`}>Sayfa {transcriptPage} / {Math.ceil(transcriptTotal / 20)}</div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(1)} disabled={transcriptPage === 1}>İlk</Button>
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(p => Math.max(1, p - 1))} disabled={transcriptPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                          <span className={`px-3 py-1 rounded ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{transcriptPage}</span>
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(p => Math.min(Math.ceil(transcriptTotal / 20), p + 1))} disabled={transcriptPage === Math.ceil(transcriptTotal / 20)}><ChevronRight className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setTranscriptPage(Math.ceil(transcriptTotal / 20))} disabled={transcriptPage === Math.ceil(transcriptTotal / 20)}>Son</Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          <TabsContent value="crm" className="space-y-6">
            {crmView === "customer" && customerPhoneParam ? (
              <CustomerDetailView
                phone={customerPhoneParam}
                isDark={isDark}
                leadStatuses={leadStatuses}
                leadSources={leadSources}
                onLeadStatusesChange={setLeadStatuses}
                onLeadSourcesChange={setLeadSources}
                onProfileChange={cacheCustomerProfile}
                onViewTranscript={handleCustomerCrmViewTranscript}
                onBack={() => navigate("/crm?view=customers")}
                canSeeRecordings={canSeeRecordings}
              />
            ) : (
              <>
                <SubNavPills
                  items={allowedCrmViews}
                  activeId={crmView}
                  onChange={(view) => navigateToTab("crm", view)}
                  isDark={isDark}
                  textClass={textClass}
                />

                {crmView === "customers" && (
                <CrmCustomersPage
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  inputClass={inputClass}
                  leadStatuses={leadStatuses}
                  companies={companies}
                  isSuperAdmin={isSuperAdmin}
                  crmSearchInput={crmSearchInput}
                  setCrmSearchInput={setCrmSearchInput}
                  crmSearchTerm={crmSearchTerm}
                  applyCrmSearch={applyCrmSearch}
                  clearCrmSearch={clearCrmSearch}
                  crmLeadFilter={crmLeadFilter}
                  setCrmLeadFilter={setCrmLeadFilter}
                  crmCompanyFilter={crmCompanyFilter}
                  setCrmCompanyFilter={setCrmCompanyFilter}
                  crmPage={crmPage}
                  setCrmPage={setCrmPage}
                  crmTotal={crmTotal}
                  crmLoading={crmLoading}
                  crmCustomers={crmCustomers}
                  refreshCrmCustomers={refreshCrmCustomers}
                  onCustomerClick={navigateToCustomerDetail}
                />
                )}
                {crmView === "data-transfer" && (
                  <CrmDataTransferPage
                    API={API}
                    isDark={isDark}
                    textClass={textClass}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    companies={companies}
                    isSuperAdmin={isSuperAdmin}
                    userCompanyId={user?.company_id}
                    visibleAgents={visibleAgents}
                  />
                )}
              </>
            )}
          </TabsContent>

          {/* Agents Tab */}
          <TabsContent value="agents" className="space-y-4">
            <SubNavPills
              items={allowedAgentsViews}
              activeId={agentsView}
              onChange={(view) => navigateToTab("agents", view)}
              isDark={isDark}
              textClass={textClass}
            />

            {showAgentsScopeFilters && agentsView !== "list" && (
              <ReportScopeFilters
                teamFilter={agentsTeamFilter}
                leaderFilter={agentsLeaderFilter}
                onTeamChange={setAgentsTeamFilter}
                onLeaderChange={setAgentsLeaderFilter}
                teamOptions={agentsScopeTeamOptions}
                leaderOptions={agentsScopeOptions.leaders}
                leaderMultiple
                isDark={isDark}
                labelClass={labelClass}
                inputClass={inputClass}
              />
            )}

            {!isAdmin && user?.gorev && (
              <p className={`text-xs px-1 ${labelClass}`}>
                Görünürlük: <span className={isDark ? "text-indigo-300" : "text-indigo-700"}>{gorevLabel(user.gorev)}</span>
                {" · "}{agentsScopedAgents.length} agent
              </p>
            )}

            {agentsView === "list" && (
            <PanelTableCard isDark={isDark}>
              <PanelTableHeader
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                accent="blue"
                icon={Users}
                title="Agent Yönetimi"
                description="Agent hesapları, cihaz atamaları ve erişim yetkileri"
                stackToolbar
                toolbar={(
                  <div className="flex flex-col gap-3 w-full">
                    <div
                      className={`flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2.5 ${
                        isDark ? "border-slate-700/80 bg-slate-900/40" : "border-gray-200/90 bg-white/70"
                      }`}
                    >
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide shrink-0 mr-1 ${labelClass}`}>
                        <Filter className="w-3.5 h-3.5 opacity-70" />
                        Filtreler
                      </span>
                      {isSuperAdmin && (
                        <Select value={agentsCompanyId} onValueChange={setAgentsCompanyId}>
                          <SelectTrigger
                            className={`h-9 w-full sm:w-40 text-sm ${inputClass} ${
                              hireImportNeedsCompanyPick ? (isDark ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-amber-400 ring-1 ring-amber-200") : ""
                            }`}
                          >
                            <SelectValue placeholder="Firma" />
                          </SelectTrigger>
                          <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                            <SelectItem value="all">Tüm Firmalar</SelectItem>
                            {companies.filter((c) => c.status === 1).map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Select value={agentsStatusFilter} onValueChange={setAgentsStatusFilter}>
                        <SelectTrigger className={`h-9 w-full sm:w-36 text-sm ${inputClass}`}>
                          <SelectValue placeholder="Durum" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="all">Tüm durumlar</SelectItem>
                          <SelectItem value="1">Aktif</SelectItem>
                          <SelectItem value="0">Pasif</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={agentsHireDateFilter} onValueChange={setAgentsHireDateFilter}>
                        <SelectTrigger className={`h-9 w-full sm:w-44 text-sm ${inputClass}`}>
                          <SelectValue placeholder="İşe giriş" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="all">Tüm agentlar</SelectItem>
                          <SelectItem value="missing_hire">İşe giriş tarihi eksik</SelectItem>
                        </SelectContent>
                      </Select>
                      {agentsScopeOptions.leaders.length > 0 && (
                        <div className="w-full sm:w-52 min-w-[11rem]">
                          <ReportScopeMultiSelect
                            label="Takım lideri"
                            options={agentsScopeOptions.leaders}
                            values={agentsLeaderFilter}
                            onChange={setAgentsLeaderFilter}
                            placeholder="Tüm liderler"
                            isDark={isDark}
                            inputClass={`h-9 text-sm ${inputClass}`}
                            labelClass={labelClass}
                            getOptionLabel={(l) => l.name}
                          />
                        </div>
                      )}
                      {agentsScopeTeamOptions.length > 0 && (
                        <Select value={agentsTeamFilter} onValueChange={setAgentsTeamFilter}>
                          <SelectTrigger className={`h-9 w-full sm:w-52 text-sm ${inputClass}`}>
                            <SelectValue placeholder="Takım" />
                          </SelectTrigger>
                          <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                            <SelectItem value="all">Tüm takımlar</SelectItem>
                            {agentsScopeTeamOptions.map((t) => (
                              <SelectItem key={t.id} value={String(t.id)}>
                                {t.name}{t.leaderName ? ` · ${t.leaderName}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Popover open={agentColumnPickerOpen} onOpenChange={setAgentColumnPickerOpen}>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" size="sm" className={`h-9 ${isDark ? "border-slate-700" : ""}`}>
                            <Columns3 className="w-4 h-4 mr-1" />
                            Kolonlar
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className={`w-56 p-3 ${isDark ? "bg-slate-900 border-slate-800" : ""}`} align="end">
                          <p className={`text-xs font-medium mb-2 ${labelClass}`}>Tabloda göster</p>
                          <ul className="space-y-2 max-h-64 overflow-y-auto">
                            {AGENT_TABLE_COLUMN_DEFS.filter((c) => !c.superAdminOnly || isSuperAdmin).map((col) => (
                              <li key={col.key}>
                                <label className={`flex items-center gap-2 text-sm cursor-pointer ${textClass}`}>
                                  <Checkbox
                                    checked={agentColVisible(col.key)}
                                    onCheckedChange={(v) => toggleAgentTableColumn(col.key, v === true)}
                                  />
                                  {col.label}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                      <div className="relative flex-1 min-w-0 max-w-md">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                        <Input
                          placeholder="Agent ara (ad, kullanıcı adı, takım…)"
                          value={agentSearchTerm}
                          onChange={(e) => setAgentSearchTerm(e.target.value)}
                          className={`pl-10 h-9 w-full ${inputClass}`}
                        />
                      </div>
                      {canManageAgents && !hasAgentRestrictions && (
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          <input
                            ref={hireImportInputRef}
                            type="file"
                            accept=".xlsx,.xls,.xlsm"
                            className="sr-only"
                            onChange={onHireExcelFile}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            title={hireImportNeedsCompanyPick ? "Önce firma seçin" : "Toplu işe giriş tarihi yükle"}
                            className={`h-9 ${
                              hireImportNeedsCompanyPick
                                ? isDark ? "border-amber-600/50 text-amber-200" : "border-amber-300 text-amber-900 bg-amber-50/80"
                                : isDark ? "border-slate-700" : ""
                            }`}
                            disabled={hireParsing}
                            onClick={requestHireExcelImport}
                          >
                            {hireParsing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                            İşe giriş Excel
                          </Button>
                          <Button
                            onClick={() => { setEditingAgent(null); setAgentModalOpen(true); }}
                            className="h-9 bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-600/20"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Yeni Agent
                          </Button>
                        </div>
                      )}
                    </div>
                    {hireImportNeedsCompanyPick && canManageAgents && !hasAgentRestrictions && (
                      <p className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-amber-300/90" : "text-amber-800"}`}>
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        Excel import için «Tüm Firmalar» yerine tek firma (ör. SPN) seçin.
                      </p>
                    )}
                  </div>
                )}
              />
              <PanelTableStats isDark={isDark} items={agentTableStats} />
              <PanelTableFrame
                isDark={isDark}
                footer={filteredAgents.length > 0 ? (
                  <PanelTablePagination
                    isDark={isDark}
                    page={agentTablePagination.page}
                    pageSize={agentTablePagination.pageSize}
                    total={agentTablePagination.total}
                    totalPages={agentTablePagination.totalPages}
                    rangeStart={agentTablePagination.rangeStart}
                    rangeEnd={agentTablePagination.rangeEnd}
                    onPageChange={agentTablePagination.setPage}
                    onPageSizeChange={agentTablePagination.setPageSize}
                    pageSizeOptions={[15, 25, 50, 100]}
                  />
                ) : null}
              >
                <TableHeader>
                  <PanelTableHeadRow isDark={isDark}>
                    <PanelTableHead isDark={isDark}>Ad Soyad</PanelTableHead>
                    {agentColVisible("kullanici_adi") && <PanelTableHead isDark={isDark}>Kullanıcı Adı</PanelTableHead>}
                    {agentColVisible("company") && <PanelTableHead isDark={isDark}>Firma</PanelTableHead>}
                    {agentColVisible("gsm") && <PanelTableHead isDark={isDark}>GSM</PanelTableHead>}
                    {agentColVisible("team") && <PanelTableHead isDark={isDark}>Takım</PanelTableHead>}
                    {agentColVisible("team_leader") && <PanelTableHead isDark={isDark}>Takım lideri</PanelTableHead>}
                    {agentColVisible("ise_giris_tarihi") && <PanelTableHead isDark={isDark}>İşe giriş</PanelTableHead>}
                    {agentColVisible("isten_cikis_tarihi") && <PanelTableHead isDark={isDark}>İşten çıkış</PanelTableHead>}
                    {agentColVisible("yeni_dataya_gecis_tarihi") && <PanelTableHead isDark={isDark}>Yeni dataya geçiş</PanelTableHead>}
                    {agentColVisible("devices") && <PanelTableHead isDark={isDark} align="center">Cihaz</PanelTableHead>}
                    {agentColVisible("token") && <PanelTableHead isDark={isDark}>Token</PanelTableHead>}
                    {agentColVisible("status") && <PanelTableHead isDark={isDark} align="center">Durum</PanelTableHead>}
                    {agentColVisible("yetki") && <PanelTableHead isDark={isDark}>Yetki</PanelTableHead>}
                    <PanelTableHead isDark={isDark} align="right">İşlemler</PanelTableHead>
                  </PanelTableHeadRow>
                </TableHeader>
                <PanelTableBody>
                  {filteredAgents.length === 0 ? (
                    <PanelTableEmpty
                      colSpan={agentTableVisibleColCount}
                      isDark={isDark}
                      icon={Users}
                      title={agentSearchTerm || agentsCompanyId !== "all" || agentsStatusFilter !== "all" || agentsHireDateFilter !== "all" || agentsTeamFilter !== "all" || normalizeScopeFilter(agentsLeaderFilter).length > 0 ? "Arama sonucu bulunamadı" : "Agent bulunamadı"}
                      description={agentSearchTerm || agentsCompanyId !== "all" || agentsStatusFilter !== "all" || agentsHireDateFilter !== "all" || agentsTeamFilter !== "all" || normalizeScopeFilter(agentsLeaderFilter).length > 0 ? "Farklı bir arama veya filtre deneyin." : "Yeni agent ekleyerek başlayın."}
                    />
                  ) : agentTablePagination.paginatedItems.map((a) => (
                    <PanelTableRow key={a.id} isDark={isDark}>
                      <PanelTableCell isDark={isDark} className="font-semibold">{a.ad_soyad}</PanelTableCell>
                      {agentColVisible("kullanici_adi") && (
                        <PanelTableCell isDark={isDark} mono className={isDark ? "text-slate-400" : "text-gray-600"}>{a.kullanici_adi}</PanelTableCell>
                      )}
                      {agentColVisible("company") && (
                        <PanelTableCell isDark={isDark} className={isDark ? "text-slate-300" : "text-gray-600"}>{a.company_name || "-"}</PanelTableCell>
                      )}
                      {agentColVisible("gsm") && (
                        <PanelTableCell isDark={isDark} mono className={isDark ? "text-slate-300" : ""}>{a.gsm || "-"}</PanelTableCell>
                      )}
                      {agentColVisible("team") && (
                        <PanelTableCell isDark={isDark} className="min-w-[10.5rem] max-w-[14rem]">
                          {canManageAgents ? (
                            <Select
                              value={a.team_id ? String(a.team_id) : AGENT_INLINE_TEAM_NONE}
                              disabled={agentInlineSavingId === a.id}
                              onValueChange={(v) => patchAgentTeamAssignment(a, v)}
                            >
                              <SelectTrigger className={`h-8 text-xs ${inputClass}`}>
                                <SelectValue placeholder="Takım seç" />
                              </SelectTrigger>
                              <SelectContent className={isDark ? "bg-slate-900 border-slate-800 max-h-72" : "max-h-72"}>
                                <SelectItem value={AGENT_INLINE_TEAM_NONE}>Takımsız</SelectItem>
                                {teamsForAgentCompany(a, teams).map((t) => (
                                  <SelectItem key={t.id} value={String(t.id)}>
                                    {t.ad}{t.lider_name ? ` · ${t.lider_name}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            a.team_name ? (
                              <Badge variant="outline" className={isDark ? "border-indigo-500/30 text-indigo-300" : "border-indigo-200 text-indigo-700"}>
                                {a.team_name}
                              </Badge>
                            ) : "—"
                          )}
                        </PanelTableCell>
                      )}
                      {agentColVisible("team_leader") && (
                        <PanelTableCell isDark={isDark} className="min-w-[10.5rem] max-w-[14rem]">
                          {canManageAgents ? (
                            <Select
                              value={a.team_leader_user_id ? String(a.team_leader_user_id) : AGENT_INLINE_TEAM_NONE}
                              disabled={agentInlineSavingId === a.id}
                              onValueChange={(v) => patchAgentTeamLeader(a, v)}
                            >
                              <SelectTrigger
                                className={`h-8 text-xs ${inputClass}`}
                                title={a.team_name ? `Mevcut takım: ${a.team_name}` : "Lider seçince uygun takıma atanır"}
                              >
                                <SelectValue placeholder="Takım lideri" />
                              </SelectTrigger>
                              <SelectContent className={isDark ? "bg-slate-900 border-slate-800 max-h-72" : "max-h-72"}>
                                <SelectItem value={AGENT_INLINE_TEAM_NONE}>Lider yok / takımsız</SelectItem>
                                {leaderOptionsFromTeams(teamsForAgentCompany(a, teams)).map((l) => (
                                  <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={isDark ? "text-slate-300" : "text-gray-600"}>{a.team_leader_name || "—"}</span>
                          )}
                        </PanelTableCell>
                      )}
                      {agentColVisible("ise_giris_tarihi") && (
                        <PanelTableCell isDark={isDark} className="min-w-[9.5rem]">
                          {canManageAgents ? (
                            <Input
                              type="date"
                              className={`h-8 text-xs ${inputClass} ${!formatAgentEmploymentDate(a.ise_giris_tarihi) ? (isDark ? "border-amber-500/50" : "border-amber-400") : ""}`}
                              value={getAgentInlineDateValue(a, "ise_giris_tarihi")}
                              disabled={agentInlineSavingId === a.id}
                              onChange={(e) => handleAgentInlineDateChange(a, "ise_giris_tarihi", e.target.value)}
                              onBlur={(e) => commitAgentInlineDateField(a, "ise_giris_tarihi", e.target.value)}
                            />
                          ) : (
                            formatAgentEmploymentDate(a.ise_giris_tarihi) || "—"
                          )}
                        </PanelTableCell>
                      )}
                      {agentColVisible("isten_cikis_tarihi") && (
                        <PanelTableCell isDark={isDark} className="min-w-[9.5rem]">
                          {canManageAgents ? (
                            <Input
                              type="date"
                              className={`h-8 text-xs ${inputClass} ${formatAgentEmploymentDate(a.isten_cikis_tarihi) ? (isDark ? "border-rose-500/40" : "border-rose-300") : ""}`}
                              value={getAgentInlineDateValue(a, "isten_cikis_tarihi")}
                              disabled={agentInlineSavingId === a.id}
                              title="Boş bırakılırsa aktif personel"
                              onChange={(e) => handleAgentInlineDateChange(a, "isten_cikis_tarihi", e.target.value)}
                              onBlur={(e) => commitAgentInlineDateField(a, "isten_cikis_tarihi", e.target.value)}
                            />
                          ) : (
                            formatAgentEmploymentDate(a.isten_cikis_tarihi) || "—"
                          )}
                        </PanelTableCell>
                      )}
                      {agentColVisible("yeni_dataya_gecis_tarihi") && (
                        <PanelTableCell isDark={isDark} className="min-w-[9.5rem]">
                          {canManageAgents ? (
                            <Input
                              type="date"
                              className={`h-8 text-xs ${inputClass}`}
                              value={getAgentInlineDateValue(a, "yeni_dataya_gecis_tarihi")}
                              disabled={agentInlineSavingId === a.id}
                              onChange={(e) => handleAgentInlineDateChange(a, "yeni_dataya_gecis_tarihi", e.target.value)}
                              onBlur={(e) => commitAgentInlineDateField(a, "yeni_dataya_gecis_tarihi", e.target.value)}
                            />
                          ) : (
                            formatAgentEmploymentDate(a.yeni_dataya_gecis_tarihi) || "—"
                          )}
                        </PanelTableCell>
                      )}
                      {agentColVisible("devices") && (
                        <PanelTableCell isDark={isDark} align="center">
                          <button
                            type="button"
                            onClick={() => openAgentDevicesModal(a)}
                            disabled={!(agentDeviceCounts[a.id] > 0)}
                            title={agentDeviceCounts[a.id] > 0 ? "Cihaz ayrıntılarını gör" : "Atanmış cihaz yok"}
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-all ${
                              agentDeviceCounts[a.id] > 0
                                ? (isDark ? "bg-pink-500/15 text-pink-400 border-pink-500/30 hover:bg-pink-500/25 cursor-pointer" : "bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100 cursor-pointer")
                                : (isDark ? "bg-slate-800 text-slate-500 border-slate-700 cursor-default opacity-70" : "bg-gray-50 text-gray-400 border-gray-200 cursor-default opacity-70")
                            }`}
                          >
                            <Smartphone className="w-3 h-3 mr-1" />
                            {agentDeviceCounts[a.id] || 0}
                          </button>
                        </PanelTableCell>
                      )}
                      {agentColVisible("token") && (
                        <PanelTableCell isDark={isDark}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedTokenAgent(a); setTokenModalOpen(true); }}
                            className={`h-8 px-2 ${isDark ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"}`}
                          >
                            <Key className="w-4 h-4 mr-1" />
                            <span className="text-xs">Göster</span>
                          </Button>
                        </PanelTableCell>
                      )}
                      {agentColVisible("status") && (
                        <PanelTableCell isDark={isDark} align="center">
                          <Badge className={a.status === 1
                            ? (isDark ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200")
                            : (isDark ? "bg-slate-700/50 text-slate-400 border-slate-600" : "bg-gray-100 text-gray-500 border-gray-200")
                          }>
                            {a.status === 1 ? "Aktif" : "Pasif"}
                          </Badge>
                        </PanelTableCell>
                      )}
                      {agentColVisible("yetki") && (
                        <PanelTableCell isDark={isDark}>
                          <Badge className={isDark ? "bg-blue-500/15 text-blue-400 border-blue-500/30" : "bg-blue-50 text-blue-700 border-blue-200"}>
                            <Shield className="w-3 h-3 mr-1 inline" />
                            {a.yetki_durumu === 3 ? "Admin" : a.yetki_durumu === 2 ? "Yönetici" : "Kullanıcı"}
                          </Badge>
                        </PanelTableCell>
                      )}
                      <PanelTableCell isDark={isDark} align="right" className="!text-right">
                        <PanelTableActions>
                          {isSuperAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setAgentPublicExportAgent(a); setAgentPublicExportOpen(true); }}
                              className={`h-8 w-8 ${isDark ? "text-violet-400 hover:bg-violet-500/10" : "text-violet-600 hover:bg-violet-50"}`}
                              title="AI JSON Export URL"
                            >
                              <Bot className="w-4 h-4" />
                            </Button>
                          )}
                          {canViewAnalytics && (
                            <Button variant="ghost" size="icon" onClick={() => openAgentAnalytics(a)} className={`h-8 w-8 ${isDark ? "text-emerald-400 hover:bg-emerald-500/10" : "text-emerald-600 hover:bg-emerald-50"}`} title="Analiz">
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => { setSelectedAgent(a); navigateToTab("cdr"); }} className={`h-8 w-8 ${isDark ? "text-cyan-400 hover:bg-cyan-500/10" : "text-blue-600 hover:bg-blue-50"}`} title="CDR">
                            <Phone className="w-4 h-4" />
                          </Button>
                          {canManageAgents && (
                            <Button variant="ghost" size="icon" onClick={() => { setEditingAgent(a); setAgentModalOpen(true); }} className={`h-8 w-8 ${isDark ? "text-slate-400 hover:bg-slate-800" : "text-gray-600 hover:bg-gray-100"}`} title="Düzenle">
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {isSuperAdmin && (
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteAgent(a.id)} className={`h-8 w-8 ${isDark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50"}`} title="Sil">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </PanelTableActions>
                      </PanelTableCell>
                    </PanelTableRow>
                  ))}
                </PanelTableBody>
              </PanelTableFrame>
            </PanelTableCard>
            )}

            {agentsView === "attendance" && (
            <div className="space-y-4">
              <Card className={cardClass}>
                <CardHeader className="pb-3">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <CardTitle className={`text-lg font-semibold ${textClass}`}>
                        <Clock className="w-5 h-5 inline mr-2 text-cyan-500" />
                        Mesai Takibi
                      </CardTitle>
                      <p className={`text-xs mt-1 ${labelClass}`}>
                        {attendanceData?.day_name ? `${attendanceData.day_name} · ` : ""}
                        İlk giden görüşme saati mesai başlangıcı ile karşılaştırılır (60 sn tolerans)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refreshAttendance}
                      disabled={attendanceLoading}
                      className={isDark ? "border-slate-700" : ""}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${attendanceLoading ? "animate-spin" : ""}`} />
                      Yenile
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className={`p-3 sm:p-4 rounded-xl ${isDark ? "bg-slate-800/50 border border-slate-700/50" : "bg-gray-50 border border-gray-100"}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {[
                        { id: "today", label: "Bugün" },
                        { id: "yesterday", label: "Dün" },
                      ].map((opt) => {
                        const active =
                          (opt.id === "today" && attendanceDate === getTodayStr()) ||
                          (opt.id === "yesterday" && attendanceDate === getYesterdayStr());
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setAttendanceDate(opt.id === "today" ? getTodayStr() : getYesterdayStr())}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                              active
                                ? isDark ? "bg-cyan-600 text-white" : "bg-cyan-500 text-white"
                                : isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className={`grid grid-cols-1 sm:grid-cols-2 ${isSuperAdmin ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3`}>
                      <div>
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Tarih</label>
                        <Input
                          type="date"
                          value={attendanceDate}
                          onChange={(e) => setAttendanceDate(e.target.value)}
                          className={`h-9 text-sm ${inputClass}`}
                        />
                      </div>
                      {isSuperAdmin && (
                        <div>
                          <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Firma</label>
                          <Select value={attendanceCompanyId} onValueChange={setAttendanceCompanyId}>
                            <SelectTrigger className={`h-9 text-sm ${inputClass}`}>
                              <SelectValue placeholder="Firma seçin" />
                            </SelectTrigger>
                            <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                              <SelectItem value="all">Tüm Firmalar</SelectItem>
                              {companies.filter((c) => c.status === 1).map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className={isSuperAdmin ? "" : "sm:col-span-1 lg:col-span-2"}>
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Agent Ara</label>
                        <div className="relative">
                          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                          <Input
                            placeholder="Agent veya firma ara..."
                            value={attendanceSearch}
                            onChange={(e) => setAttendanceSearch(e.target.value)}
                            className={`pl-10 h-9 text-sm ${inputClass}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                      { id: "all", label: "Toplam", count: attendanceSummary.total, tone: isDark ? "border-slate-700 bg-slate-800/40" : "border-gray-200 bg-white", activeTone: isDark ? "ring-cyan-500/50" : "ring-cyan-400" },
                      { id: "on_time", label: "Zamanında", count: attendanceSummary.on_time, tone: isDark ? "border-emerald-500/30 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50", activeTone: isDark ? "ring-emerald-500/50" : "ring-emerald-400" },
                      { id: "late", label: "Geç Giriş", count: attendanceSummary.late, tone: isDark ? "border-rose-500/30 bg-rose-500/10" : "border-rose-200 bg-rose-50", activeTone: isDark ? "ring-rose-500/50" : "ring-rose-400" },
                      { id: "absent", label: "Görüşme Yok", count: attendanceSummary.absent, tone: isDark ? "border-amber-500/30 bg-amber-500/10" : "border-amber-200 bg-amber-50", activeTone: isDark ? "ring-amber-500/50" : "ring-amber-400" },
                      { id: "off_day", label: "Mesai Dışı", count: attendanceSummary.off_day, tone: isDark ? "border-slate-600 bg-slate-800/30" : "border-gray-200 bg-gray-50", activeTone: isDark ? "ring-slate-500/50" : "ring-gray-400" },
                    ].map((card) => (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => setAttendanceFilter(card.id)}
                        className={`rounded-xl border p-3 text-left transition-all hover:scale-[1.02] ${card.tone} ${
                          attendanceFilter === card.id ? `ring-2 ${card.activeTone}` : ""
                        }`}
                      >
                        <p className={`text-[10px] sm:text-xs font-medium uppercase tracking-wide ${labelClass}`}>{card.label}</p>
                        <p className={`text-xl sm:text-2xl font-bold mt-1 ${textClass}`}>{card.count ?? 0}</p>
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {ATTENDANCE_FILTERS.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setAttendanceFilter(f.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                          attendanceFilter === f.id
                            ? f.id === "on_time"
                              ? isDark ? "bg-emerald-600/30 border-emerald-500/50 text-emerald-300" : "bg-emerald-100 border-emerald-300 text-emerald-800"
                              : f.id === "late"
                              ? isDark ? "bg-rose-600/30 border-rose-500/50 text-rose-300" : "bg-rose-100 border-rose-300 text-rose-800"
                              : f.id === "absent"
                              ? isDark ? "bg-amber-600/30 border-amber-500/50 text-amber-300" : "bg-amber-100 border-amber-300 text-amber-800"
                              : isDark ? "bg-blue-600/30 border-blue-500/50 text-blue-300" : "bg-blue-100 border-blue-300 text-blue-800"
                            : isDark ? "border-slate-700 text-slate-400 hover:bg-slate-800" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className={cardClass}>
                <CardContent className="p-0">
                  {attendanceLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                    </div>
                  ) : sortedAttendanceRows.length === 0 ? (
                    <div className={`text-center py-16 ${labelClass}`}>
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{attendanceSearch.trim() || attendanceFilter !== "all" ? "Filtreye uygun kayıt bulunamadı" : "Mesai kaydı bulunamadı"}</p>
                    </div>
                  ) : (
                    <>
                      <div className="hidden md:block overflow-x-auto p-4 pt-0">
                        <div className={`overflow-x-auto rounded-xl border ${isDark ? "border-slate-700/80" : "border-cyan-100"}`}>
                          <Table className="text-xs sm:text-sm">
                            <TableHeader>
                              <TableRow className={isDark ? "border-slate-700 bg-slate-800/80" : "bg-cyan-50/80 border-cyan-100"}>
                                <ReportSortableHead label="Agent" column="agent_name" sort={attendanceSort} onSort={(key) => setAttendanceSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                                {isSuperAdmin && (
                                  <ReportSortableHead label="Firma" column="company_name" sort={attendanceSort} onSort={(key) => setAttendanceSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                                )}
                                <ReportSortableHead label="Mesai Başlangıç" column="scheduled_start" sort={attendanceSort} onSort={(key) => setAttendanceSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                                <ReportSortableHead label="İlk Giden Görüşme" column="first_call_time" sort={attendanceSort} onSort={(key) => setAttendanceSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                                <ReportSortableHead label="Erken Çıkma" column="early_leave_seconds" sort={attendanceSort} onSort={(key) => setAttendanceSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} title="Son görüşme bitişi ile mesai bitişi arası (mola analizi ile aynı)" />
                                <ReportSortableHead label="Fark" column="late_seconds" sort={attendanceSort} onSort={(key) => setAttendanceSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                                <ReportSortableHead label="Durum" column="status" sort={attendanceSort} onSort={(key) => setAttendanceSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {sortedAttendanceRows.map((row, idx) => (
                                <TableRow
                                  key={row.agent_id}
                                  className={`border-l-4 ${getAttendanceRowClass(row.status, isDark)} ${isDark ? "border-slate-800/50 hover:bg-slate-800/40" : "hover:bg-cyan-50/60"} ${idx % 2 === 1 ? (isDark ? "bg-slate-900/15" : "bg-gray-50/50") : ""}`}
                                >
                                  <TableCell className={`font-medium whitespace-nowrap ${textClass}`}>
                                    <div className="flex items-center gap-2">
                                      {row.status === "on_time" && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                                      {row.status === "late" && <XCircle className="w-4 h-4 text-rose-500 shrink-0" />}
                                      {row.agent_name}
                                    </div>
                                  </TableCell>
                                  {isSuperAdmin && (
                                    <TableCell className={`whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-600"}`}>{row.company_name || "-"}</TableCell>
                                  )}
                                  <TableCell className={`font-mono text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                    {row.is_scheduled_work_day ? row.scheduled_start : "-"}
                                  </TableCell>
                                  <TableCell className={`font-mono text-sm ${row.status === "late" ? (isDark ? "text-rose-400 font-semibold" : "text-rose-600 font-semibold") : row.status === "on_time" ? (isDark ? "text-emerald-400 font-semibold" : "text-emerald-600 font-semibold") : isDark ? "text-slate-400" : "text-gray-500"}`}>
                                    {row.first_call_time || "-"}
                                  </TableCell>
                                  <TableCell className={`font-mono text-sm ${(row.early_leave_seconds || 0) > 0 ? (isDark ? "text-orange-300 font-semibold" : "text-orange-700 font-semibold") : labelClass}`}>
                                    {(row.early_leave_seconds || 0) > 0 ? (
                                      <span title={row.last_call_time ? `Son görüşme: ${row.last_call_time}` : undefined}>
                                        {row.early_leave_label || formatEarlyLeaveDuration(row.early_leave_seconds)}
                                      </span>
                                    ) : "-"}
                                  </TableCell>
                                  <TableCell className={`text-sm ${row.status === "late" ? (isDark ? "text-rose-400 font-medium" : "text-rose-600 font-medium") : labelClass}`}>
                                    {row.status === "late" ? formatLateDuration(row.late_seconds) : row.status === "on_time" ? "Zamanında" : "-"}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={`border ${getAttendanceBadgeClass(row.status, isDark)}`}>
                                      {row.status_label}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      <div className="md:hidden divide-y divide-slate-800/50">
                        {sortedAttendanceRows.map((row) => (
                          <div key={row.agent_id} className={`p-4 border-l-4 ${getAttendanceRowClass(row.status, isDark)}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div>
                                <p className={`font-medium ${textClass}`}>{row.agent_name}</p>
                                {isSuperAdmin && row.company_name && (
                                  <p className={`text-xs mt-0.5 ${labelClass}`}>{row.company_name}</p>
                                )}
                              </div>
                              <Badge className={`border shrink-0 ${getAttendanceBadgeClass(row.status, isDark)}`}>
                                {row.status_label}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className={labelClass}>Mesai: </span>
                                <span className={`font-mono ${textClass}`}>{row.is_scheduled_work_day ? row.scheduled_start : "-"}</span>
                              </div>
                              <div>
                                <span className={labelClass}>İlk görüşme: </span>
                                <span className={`font-mono ${row.status === "late" ? "text-rose-500" : row.status === "on_time" ? "text-emerald-500" : textClass}`}>
                                  {row.first_call_time || "-"}
                                </span>
                              </div>
                              {(row.early_leave_seconds || 0) > 0 && (
                                <div>
                                  <span className={labelClass}>Erken çıkış: </span>
                                  <span className={`font-mono font-medium ${isDark ? "text-orange-300" : "text-orange-700"}`}>
                                    {row.early_leave_label || formatEarlyLeaveDuration(row.early_leave_seconds)}
                                  </span>
                                </div>
                              )}
                              {row.status === "late" && (
                                <div className="col-span-2 text-rose-500 font-medium">
                                  {formatLateDuration(row.late_seconds)}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className={`px-4 py-3 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                        <p className={`text-sm ${labelClass}`}>
                          {sortedAttendanceRows.length} kayıt gösteriliyor
                          {(attendanceFilter !== "all" || attendanceSearch.trim()) && attendanceData?.agents?.length
                            ? ` (${attendanceData.agents.length} toplam)`
                            : ""}
                          {" · "}Kolon başlıklarına tıklayarak sıralayın
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>

          {/* Token Modal */}
          <AgentPublicExportModal
            open={agentPublicExportOpen}
            onClose={() => { setAgentPublicExportOpen(false); setAgentPublicExportAgent(null); }}
            agent={agentPublicExportAgent}
            isDark={isDark}
            apiBase={API}
          />
          {tokenModalOpen && selectedTokenAgent && (
            <PanelModal open onClose={() => setTokenModalOpen(false)}>
              <PanelModalContent
                className={`w-full max-w-lg rounded-xl shadow-2xl ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between p-4 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                  <h3 className={`text-lg font-semibold ${textClass}`}>
                    <Key className="w-5 h-5 inline mr-2 text-amber-500" />
                    Agent Token - {selectedTokenAgent.ad_soyad}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setTokenModalOpen(false)}><X className="w-5 h-5" /></Button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelClass}`}>Token</label>
                    <div className={`relative p-4 rounded-lg font-mono text-sm break-all ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                      {selectedTokenAgent.token || "Token bulunamadı"}
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`absolute top-2 right-2 ${isDark ? "text-slate-400 hover:text-white" : ""}`}
                        onClick={() => {
                          navigator.clipboard.writeText(selectedTokenAgent.token || "");
                          toast.success("Token kopyalandı!");
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {canManageAgents && (
                    <Button
                      variant="outline"
                      className={`w-full ${isDark ? "border-slate-700" : ""}`}
                      onClick={async () => {
                        if (window.confirm("Token yenilensin mi? Eski token geçersiz olacak.")) {
                          try {
                            const res = await axios.post(`${API}/agents/regenerate-token`, { agent_id: selectedTokenAgent.id });
                            toast.success("Token yenilendi!");
                            setSelectedTokenAgent({ ...selectedTokenAgent, token: res.data.token });
                            fetchAgents();
                          } catch (error) {
                            toast.error("Token yenilenemedi");
                          }
                        }
                      }}
                    >
                      <RotateCw className="w-4 h-4 mr-2" />Token Yenile
                    </Button>
                  )}
                </div>
              </PanelModalContent>
            </PanelModal>
          )}

          {/* Agent Analytics Modal */}
          {analyticsAgent && (
            <PanelModal open align="start" onClose={closeAgentAnalytics}>
              <PanelModalContent
                className={`w-full my-2 sm:my-4 rounded-xl shadow-2xl flex flex-col ${panelModalSizeClasses(agentAnalyticsModalExpand.expanded, "max")} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white"}`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Sticky Header */}
                <div className={`sticky top-0 z-10 flex items-center justify-between gap-2 p-4 border-b rounded-t-xl ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
                  <h3 className={`text-lg font-semibold min-w-0 truncate ${textClass}`}>
                    <BarChart3 className="w-5 h-5 inline mr-2 text-emerald-500" />
                    Agent Performans Analizi - {analyticsAgent.ad_soyad}
                  </h3>
                  <PanelModalHeaderActions
                    expanded={agentAnalyticsModalExpand.expanded}
                    onToggle={agentAnalyticsModalExpand.toggleExpanded}
                    onClose={closeAgentAnalytics}
                    isDark={isDark}
                  />
                </div>
                
                {/* Date Range Selector - Also Sticky */}
                <div className={`sticky top-[60px] z-10 p-4 border-b ${isDark ? "border-slate-800 bg-slate-800/95" : "border-gray-200 bg-gray-50"}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <Label className={`text-sm font-medium ${labelClass}`}>Tarih Aralığı:</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: "today", label: "Bugün" },
                        { value: "yesterday", label: "Dün" },
                        { value: "this_week", label: "Bu Hafta" },
                        { value: "this_month", label: "Bu Ay" },
                        { value: "last_month", label: "Geçen Ay" },
                        { value: "custom", label: "Özel" },
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          variant={analyticsDateRange === opt.value ? "default" : "outline"}
                          size="sm"
                          className={analyticsDateRange === opt.value 
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                            : isDark ? "border-slate-700 text-slate-300" : ""}
                          onClick={() => setAnalyticsDateRange(opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                    {analyticsDateRange === "custom" && (
                      <div className="flex items-center gap-2 ml-4">
                        <Input
                          type="date"
                          value={analyticsCustomStart}
                          onChange={(e) => setAnalyticsCustomStart(e.target.value)}
                          className={`w-40 ${inputClass}`}
                        />
                        <span className={labelClass}>-</span>
                        <Input
                          type="date"
                          value={analyticsCustomEnd}
                          onChange={(e) => setAnalyticsCustomEnd(e.target.value)}
                          className={`w-40 ${inputClass}`}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Analytics Content - Scrollable */}
                <div className="p-6 overflow-y-auto flex-1">
                  {analyticsLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                        <p className={labelClass}>Analiz verileri yükleniyor...</p>
                      </div>
                    </div>
                  ) : analyticsData ? (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-blue-50 border border-blue-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-blue-600"}`}>Toplam Çağrı</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-blue-400" : "text-blue-700"}`}>{analyticsData.summary.total_calls}</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-emerald-50 border border-emerald-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-emerald-600"}`}>Cevaplanan</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>{analyticsData.summary.answered_calls}</div>
                          <div className={`text-xs ${isDark ? "text-slate-500" : "text-emerald-500"}`}>%{analyticsData.summary.answer_rate} başarı</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-purple-50 border border-purple-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-purple-600"}`}>Toplam Konuşma</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-purple-400" : "text-purple-700"}`}>{analyticsData.summary.total_talk_minutes} dk</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-amber-50 border border-amber-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-amber-600"}`}>Ort. Konuşma</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{analyticsData.summary.avg_talk_seconds} sn</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-cyan-50 border border-cyan-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-cyan-600"}`}>Giden Çağrı</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-cyan-400" : "text-cyan-700"}`}>{analyticsData.summary.outgoing_calls}</div>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800" : "bg-rose-50 border border-rose-100"}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? "text-slate-400" : "text-rose-600"}`}>Gelen Çağrı</div>
                          <div className={`text-2xl font-bold ${isDark ? "text-rose-400" : "text-rose-700"}`}>{analyticsData.summary.incoming_calls}</div>
                        </div>
                      </div>

                      {/* Work Hours Card */}
                      <div className={`p-6 rounded-xl ${isDark ? "bg-gradient-to-br from-emerald-900/30 to-cyan-900/30 border border-emerald-800/50" : "bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200"}`}>
                        <h4 className={`text-sm font-semibold mb-4 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                          <Clock className="w-4 h-4 inline mr-2" />Çalışma Saatleri
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>İlk Çağrı (İşe Başlama)</div>
                            <div className={`text-lg font-mono font-bold ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                              {analyticsData.summary.first_call ? analyticsData.summary.first_call.split(' ')[1] : "-"}
                            </div>
                            <div className={`text-xs ${labelClass}`}>
                              {analyticsData.summary.first_call ? analyticsData.summary.first_call.split(' ')[0] : ""}
                            </div>
                          </div>
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>Son Çağrı (İş Bitirme)</div>
                            <div className={`text-lg font-mono font-bold ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>
                              {analyticsData.summary.last_call ? analyticsData.summary.last_call.split(' ')[1] : "-"}
                            </div>
                            <div className={`text-xs ${labelClass}`}>
                              {analyticsData.summary.last_call ? analyticsData.summary.last_call.split(' ')[0] : ""}
                            </div>
                          </div>
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>Toplam Çalışma Süresi</div>
                            <div className={`text-lg font-bold ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                              {Math.floor(analyticsData.summary.working_minutes / 60)} saat {analyticsData.summary.working_minutes % 60} dk
                            </div>
                          </div>
                          <div>
                            <div className={`text-xs mb-1 ${labelClass}`}>Aktif Konuşma Oranı</div>
                            <div className={`text-lg font-bold ${isDark ? "text-purple-400" : "text-purple-600"}`}>
                              {analyticsData.summary.working_minutes > 0 
                                ? Math.round((analyticsData.summary.total_talk_minutes / analyticsData.summary.working_minutes) * 100) 
                                : 0}%
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Hourly Distribution */}
                      <div className={`p-6 rounded-xl ${isDark ? "bg-slate-800" : "bg-gray-50 border border-gray-200"}`}>
                        <h4 className={`text-sm font-semibold mb-4 ${textClass}`}>
                          <BarChart3 className="w-4 h-4 inline mr-2" />Saatlik Çağrı Dağılımı
                        </h4>
                        {analyticsData.hourly_distribution && analyticsData.hourly_distribution.length > 0 ? (
                          <div>
                            <div className="flex items-end gap-1" style={{ height: '120px' }}>
                              {Array.from({ length: 24 }, (_, hour) => {
                                const hourData = analyticsData.hourly_distribution.find(h => parseInt(h.hour) === hour);
                                const count = hourData ? parseInt(hourData.call_count) || 0 : 0;
                                const maxCount = Math.max(...analyticsData.hourly_distribution.map(h => parseInt(h.call_count) || 0), 1);
                                const heightPx = count > 0 ? Math.max((count / maxCount) * 100, 8) : 3;
                                return (
                                  <div 
                                    key={hour} 
                                    className={`flex-1 rounded-t cursor-pointer transition-all hover:opacity-80 ${count > 0 ? (isDark ? "bg-emerald-500" : "bg-emerald-400") : (isDark ? "bg-slate-700" : "bg-gray-200")}`}
                                    style={{ height: `${heightPx}px` }}
                                    title={`${hour}:00 - ${count} çağrı`}
                                  />
                                );
                              })}
                            </div>
                            <div className="flex gap-1 mt-1">
                              {Array.from({ length: 24 }, (_, hour) => (
                                <div key={hour} className="flex-1 text-center">
                                  <span className={`text-[8px] ${labelClass}`}>{hour}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className={`text-center py-8 ${labelClass}`}>
                            <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p>Bu dönemde saatlik çağrı verisi bulunamadı</p>
                          </div>
                        )}
                      </div>

                      {/* Daily Breakdown Table */}
                      <div className={`rounded-xl overflow-hidden ${isDark ? "bg-slate-800" : "bg-white border border-gray-200"}`}>
                        <h4 className={`text-sm font-semibold p-4 border-b ${isDark ? "border-slate-700 text-slate-200" : "border-gray-200 text-gray-800"}`}>
                          <Calendar className="w-4 h-4 inline mr-2" />Günlük Detay
                        </h4>
                        {analyticsData.daily_breakdown && analyticsData.daily_breakdown.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className={isDark ? "border-slate-700" : ""}>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Toplam</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Cevaplanan</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Cevapsız</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Günlük Konuşma (dk)</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>İlk Çağrı</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Son Çağrı</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Çalışma</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {analyticsData.daily_breakdown.map((day, idx) => (
                                  <TableRow key={idx} className={isDark ? "border-slate-700/50" : ""}>
                                    <TableCell className={`font-mono ${isDark ? "text-slate-200" : ""}`}>{formatReportDate(day.call_date)}</TableCell>
                                    <TableCell className={`font-bold ${isDark ? "text-blue-400" : "text-blue-600"}`}>{day.total_calls}</TableCell>
                                    <TableCell className={isDark ? "text-emerald-400" : "text-emerald-600"}>{day.answered_calls}</TableCell>
                                    <TableCell className={isDark ? "text-amber-400" : "text-amber-600"}>{day.missed_calls || (day.total_calls - day.answered_calls)}</TableCell>
                                    <TableCell className={isDark ? "text-purple-400" : "text-purple-600"}>{Math.round((day.total_talk_time || 0) / 60)}</TableCell>
                                    <TableCell className={`font-mono text-sm ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{day.first_call || "-"}</TableCell>
                                    <TableCell className={`font-mono text-sm ${isDark ? "text-amber-400" : "text-amber-600"}`}>{day.last_call || "-"}</TableCell>
                                    <TableCell className={isDark ? "text-slate-300" : ""}>
                                      {day.working_minutes > 0 
                                        ? `${Math.floor(day.working_minutes / 60)}sa ${day.working_minutes % 60}dk`
                                        : "-"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className={`text-center py-8 ${labelClass}`}>
                            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            <p>Bu dönemde günlük veri bulunamadı</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={`text-center py-20 ${labelClass}`}>
                      <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Analiz verisi bulunamadı</p>
                    </div>
                  )}
                </div>
              </PanelModalContent>
            </PanelModal>
          )}

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <SubNavPills
              items={allowedReportViews}
              activeId={reportsView}
              onChange={(view) => navigateToTab("reports", view)}
              isDark={isDark}
              textClass={textClass}
            />

            {showReportScopeFilters && !isSpreadsheetStyleReportView(reportsView) && (
              <ReportScopeFilters
                multiple
                teamFilter={reportsTeamFilter}
                leaderFilter={reportsLeaderFilter}
                onTeamChange={setReportsTeamFilter}
                onLeaderChange={setReportsLeaderFilter}
                teamOptions={reportScopeTeamOptions}
                leaderOptions={reportScopeOptions.leaders}
                isDark={isDark}
                labelClass={labelClass}
                inputClass={inputClass}
                extraActions={reportsView === "agents" && (normalizeScopeFilter(reportsTeamFilter).length > 0 || normalizeScopeFilter(reportsLeaderFilter).length > 0) ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleReportsSelectScopedAgents}
                    className={`h-8 text-xs shrink-0 ${isDark ? "border-indigo-500/40 text-indigo-300" : "border-indigo-200 text-indigo-700"}`}
                  >
                    Filtreyi seç ({reportsScopedAgents.length})
                  </Button>
                ) : null}
              />
            )}

            {!isAdmin && user?.gorev && !isSpreadsheetStyleReportView(reportsView) && (
              <p className={`text-xs px-1 ${labelClass}`}>
                Görünürlük: <span className={isDark ? "text-indigo-300" : "text-indigo-700"}>{gorevLabel(user.gorev)}</span>
                {" · "}{reportsScopedAgents.length} agent
              </p>
            )}

            {reportsView === "agents" && (
            <Card className={cardClass}>
              <CardHeader className="pb-4">
                <CardTitle className={`text-lg font-semibold ${textClass}`}>
                  <BarChart3 className="w-5 h-5 inline mr-2 text-teal-500" />Agent Görüşme Raporu
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filters - Mobile Responsive */}
                <div className={`p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
                    {[
                      { id: "today", label: "Bugün" },
                      { id: "yesterday", label: "Dün" },
                      { id: "thisMonth", label: "Bu Ay" },
                      { id: "lastMonth", label: "Geçen Ay" },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleReportsQuickRange(opt.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                          reportsDatePreset === opt.id
                            ? (isDark ? "bg-teal-600 text-white" : "bg-teal-500 text-white")
                            : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200")
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                    {reportsDatePreset === "custom" && (
                      <span className={`text-[10px] sm:text-xs ${labelClass}`}>Özel tarih aralığı</span>
                    )}
                  </div>
                  <div className={`grid grid-cols-2 ${isSuperAdmin ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 items-end`}>
                    <div className="min-w-0">
                      <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Başlangıç</label>
                      <Input
                        type="date"
                        value={reportsStartDate}
                        onChange={(e) => {
                          setReportsStartDate(e.target.value);
                          setReportsDatePreset("custom");
                        }}
                        className={`h-8 w-full text-xs ${inputClass}`}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Bitiş</label>
                      <Input
                        type="date"
                        value={reportsEndDate}
                        onChange={(e) => {
                          setReportsEndDate(e.target.value);
                          setReportsDatePreset("custom");
                        }}
                        className={`h-8 w-full text-xs ${inputClass}`}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Yön</label>
                      <Select value={reportsYon} onValueChange={setReportsYon}>
                        <SelectTrigger className={`h-8 w-full text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="all">Tümü</SelectItem>
                          <SelectItem value="1">Gelen</SelectItem>
                          <SelectItem value="2">Giden</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {isSuperAdmin && (
                      <div className="min-w-0">
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Firma</label>
                        <Select value={reportsCompanyId} onValueChange={setReportsCompanyId}>
                          <SelectTrigger className={`h-8 w-full text-xs ${inputClass}`}>
                            <SelectValue placeholder="Firma seçin" />
                          </SelectTrigger>
                          <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                            <SelectItem value="all">Tüm Firmalar</SelectItem>
                            {companies.filter((c) => c.status === 1).map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="min-w-0 col-span-2 lg:col-span-1">
                      <Button
                        onClick={() => fetchReports()}
                        disabled={reportsLoading || reportsSelectedAgents.length === 0}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white h-8 text-xs"
                      >
                        {reportsLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
                        Rapor Al
                      </Button>
                    </div>
                  </div>
                  
                  {/* Agent Selection - Searchable Multi-Select Dropdown */}
                  <div className="mt-3 sm:mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-[10px] sm:text-xs font-medium ${labelClass}`}>
                        Agent Seçimi <span className={`${reportsSelectedAgents.length > 0 ? (isDark ? "text-teal-400" : "text-teal-600") : (isDark ? "text-red-400" : "text-red-500")}`}>({reportsSelectedAgents.length}/{reportsScopedAgents.length})</span>
                      </label>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleReportsSelectAllAgents}
                          className={`h-6 text-[10px] sm:text-xs px-2 ${isDark ? "text-teal-400 hover:text-teal-300" : "text-teal-600 hover:text-teal-700"}`}
                        >
                          {reportsSelectedAgents.length === reportsScopedAgents.length && reportsScopedAgents.length > 0 ? "Temizle" : "Tümünü Seç"}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Dropdown Container */}
                    <div className="relative">
                      {/* Selected Tags & Search Input */}
                      <div 
                        className={`min-h-[42px] p-2 rounded-lg border cursor-text flex flex-wrap gap-1.5 items-center ${
                          isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300"
                        } ${reportsAgentDropdownOpen ? (isDark ? "ring-2 ring-teal-500" : "ring-2 ring-teal-400") : ""}`}
                        onClick={() => setReportsAgentDropdownOpen(true)}
                      >
                        {/* Selected Tags */}
                        {reportsSelectedAgents.slice(0, 5).map(agentId => {
                          const agent = reportsScopedAgents.find(a => a.id === agentId) || agents.find(a => a.id === agentId);
                          return agent ? (
                            <span 
                              key={agentId}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] sm:text-xs ${
                                isDark ? "bg-teal-600/30 text-teal-300 border border-teal-500/50" : "bg-teal-100 text-teal-700 border border-teal-300"
                              }`}
                            >
                              {agent.ad_soyad}
                              <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleReportsToggleAgent(agentId); }}
                                className="hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ) : null;
                        })}
                        {reportsSelectedAgents.length > 5 && (
                          <span className={`text-[10px] sm:text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            +{reportsSelectedAgents.length - 5} daha
                          </span>
                        )}
                        
                        {/* Search Input */}
                        <input
                          type="text"
                          placeholder={reportsSelectedAgents.length === 0 ? "Agent ara ve seç..." : "Ara..."}
                          value={reportsAgentSearch}
                          onChange={(e) => setReportsAgentSearch(e.target.value)}
                          onFocus={() => setReportsAgentDropdownOpen(true)}
                          className={`flex-1 min-w-[100px] outline-none text-xs sm:text-sm bg-transparent ${
                            isDark ? "text-slate-200 placeholder-slate-500" : "text-gray-800 placeholder-gray-400"
                          }`}
                        />
                      </div>
                      
                      {/* Dropdown List */}
                      {reportsAgentDropdownOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => { setReportsAgentDropdownOpen(false); setReportsAgentSearch(""); }} />
                          <div className={`absolute z-50 w-full mt-1 rounded-lg border shadow-lg max-h-48 overflow-y-auto ${
                            isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"
                          }`}>
                            {reportsScopedAgents
                              .filter(a => a.ad_soyad.toLowerCase().includes(reportsAgentSearch.toLowerCase()))
                              .sort((a, b) => a.ad_soyad.localeCompare(b.ad_soyad, 'tr'))
                              .map(agent => (
                                <div
                                  key={agent.id}
                                  onClick={() => handleReportsToggleAgent(agent.id)}
                                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs sm:text-sm ${
                                    reportsSelectedAgents.includes(agent.id)
                                      ? isDark ? "bg-teal-600/20 text-teal-300" : "bg-teal-50 text-teal-700"
                                      : isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-gray-50 text-gray-700"
                                  }`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                    reportsSelectedAgents.includes(agent.id)
                                      ? "bg-teal-600 border-teal-600"
                                      : isDark ? "border-slate-600" : "border-gray-300"
                                  }`}>
                                    {reportsSelectedAgents.includes(agent.id) && (
                                      <CheckSquare className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  {agent.ad_soyad}
                                  {isSuperAdmin && reportsCompanyId === "all" && agent.company_name && (
                                    <span className={`ml-1 text-[10px] ${labelClass}`}>({agent.company_name})</span>
                                  )}
                                </div>
                              ))}
                            {reportsScopedAgents.filter(a => a.ad_soyad.toLowerCase().includes(reportsAgentSearch.toLowerCase())).length === 0 && (
                              <div className={`px-3 py-4 text-center text-xs ${labelClass}`}>
                                Agent bulunamadı
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {reportsSelectedAgents.length === 0 && reportsScopedAgents.length > 0 && (
                      <p className={`text-[10px] sm:text-xs mt-1 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                        ⚠️ En az bir agent seçmelisiniz
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Export Buttons */}
                {reportsData && reportsData.data && reportsData.data.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3 sm:mb-4">
                    <p className={`text-[10px] sm:text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                      Çalışma günü: günlük toplam konuşma süresi ≥ 1 saat olan günler sayılır.
                      <span className="hidden sm:inline"> · </span>
                      <span className="block sm:inline"><strong>&lt;1 dk</strong>: görüşülen (cevaplanan) kısa çağrılar · <strong>Takipsiz</strong>: tek denemeli cevapsız (tıklayınca liste) · <strong>Mola</strong>: mesai içi ≥5 dk aralar + mesai sonu ≥5 dk boşluk toplamı</span>
                    </p>
                    <div className="flex gap-2 sm:ml-auto">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleExportReportsPdf}
                      className={`h-8 text-xs sm:text-sm ${isDark ? "border-slate-700 text-red-400 hover:bg-slate-800 hover:text-red-300" : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleExportReportsExcel}
                      className={`${isDark ? "border-slate-700 text-emerald-400 hover:bg-slate-800 hover:text-emerald-300" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"}`}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                    </div>
                  </div>
                )}
                
                {/* Results Table */}
                {reportsLoading ? (
                  <div className="flex items-center justify-center py-12 sm:py-20">
                    <div className="spinner w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                ) : reportsData && reportsData.data && reportsData.data.length > 0 ? (
                  <div className={`overflow-x-auto -mx-3 sm:mx-0 rounded-xl border ${isDark ? "border-slate-700/80" : "border-gray-200"}`}>
                    <Table className="text-xs sm:text-sm">
                      <TableHeader>
                        <TableRow className={isDark ? "border-slate-700 bg-slate-800/80" : "bg-teal-50/80 border-teal-100"}>
                          <ReportSortableHead label="Agent" column="agent_name" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                          <ReportSortableHead label="Tarih" column="period" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`hidden sm:table-cell ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                          <ReportSortableHead label="Toplam" column="total_calls" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                          <ReportSortableHead label="Cvp" column="answered_calls" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                          <ReportSortableHead label="Cvsz" column="missed_calls" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                          <ReportSortableHead label="Konuşma" column="total_talk_time" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                          <ReportSortableHead label="<1 dk" column="short_calls" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Görüşülen (cevaplanan) çağrılar — konuşma süresi 1 dakikadan kısa" />
                          <ReportSortableHead label="Takipsiz" column="unfollowed_missed" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Aynı gün tekrar temas olmayan tek denemeli cevapsız numaralar" />
                          <ReportSortableHead label="Mola" column="total_break_seconds" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Seçilen dönemde mesai içi ≥5 dk aralar + mesai sonu ≥5 dk boşluk toplamı" />
                          <ReportSortableHead label="Ort Mola" column="avg_daily_break_seconds" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Toplam mola süresinin çalışma günü sayısına bölünmesi" />
                          <ReportSortableHead label="Ort Mola (60+)" column="avg_high_talk_break_seconds" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Günlük konuşması 60 dk ve üzeri olan günlerin mola sürelerinin ortalaması (Mola Analizi ile aynı mantık)" />
                          <ReportSortableHead label="Gün" column="working_days" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                          <ReportSortableHead label="Ort/Konuşma" column="avg_daily_talk_time" sort={reportsSort} onSort={(key) => setReportsSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} title="Toplam konuşma süresinin çalışma günü sayısına bölünmesi" />
                          <TableHead className={`text-center whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Analiz</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortReportRows(reportsData.data, reportsSort).map((row, idx) => (
                          <TableRow 
                            key={row.agent_id ?? idx} 
                            className={`${isDark ? "border-slate-800/50 hover:bg-slate-800/50" : "hover:bg-teal-50/70"} ${idx % 2 === 1 ? (isDark ? "bg-slate-900/20" : "bg-gray-50/60") : ""}`}
                          >
                            <TableCell
                              className={`font-medium whitespace-nowrap cursor-pointer ${isDark ? "text-slate-200" : ""}`}
                              onClick={() => fetchReportDailyDetail(row)}
                            >
                              <span className="flex items-center gap-1">
                                {row.agent_name}
                                <Eye className="w-3 h-3 opacity-50" />
                              </span>
                            </TableCell>
                            <TableCell
                              className={`hidden sm:table-cell text-[10px] sm:text-xs cursor-pointer ${labelClass}`}
                              onClick={() => fetchReportDailyDetail(row)}
                            >
                              {formatReportDate(row.period)}
                            </TableCell>
                            <ReportStatCell value={row.total_calls} unique={row.unique_total_calls} colorClass={isDark ? "text-blue-400" : "text-blue-600"} isDark={isDark} />
                            <ReportStatCell value={row.answered_calls} unique={row.unique_answered_calls} colorClass={isDark ? "text-emerald-400" : "text-emerald-600"} isDark={isDark} />
                            <ReportStatCell value={row.missed_calls} unique={row.unique_missed_calls} colorClass={isDark ? "text-amber-400" : "text-amber-600"} isDark={isDark} />
                            <TableCell className={`whitespace-nowrap ${isDark ? "text-purple-400" : "text-purple-600"}`}>{formatTalkTime(row.total_talk_time)}</TableCell>
                            <TableCell className="text-center">
                              <ReportMetricBadge value={row.short_calls} tone="slate" isDark={isDark} title="Görüşülen çağrılar — konuşma 1–59 sn" />
                            </TableCell>
                            <TableCell className="text-center">
                              <ReportMetricBadge
                                value={row.unfollowed_missed}
                                tone="rose"
                                isDark={isDark}
                                title="Tek deneme cevapsız; aynı gün tekrar arama veya müşteri geri dönüşü yok"
                                onClick={() => openUnfollowedMissedModal({
                                  agentId: row.agent_id,
                                  startDate: reportsStartDate,
                                  endDate: reportsEndDate,
                                })}
                              />
                            </TableCell>
                            <TableCell className={`whitespace-nowrap ${isDark ? "text-amber-400" : "text-amber-700"}`}>{formatTalkTime(row.total_break_seconds || 0)}</TableCell>
                            <TableCell className={`whitespace-nowrap ${isDark ? "text-orange-400" : "text-orange-700"}`}>
                              {row.working_days > 0 ? formatTalkTime(row.avg_daily_break_seconds ?? Math.round((row.total_break_seconds || 0) / row.working_days)) : "-"}
                            </TableCell>
                            <TableCell className={`whitespace-nowrap ${isDark ? "text-rose-400" : "text-rose-700"}`} title={row.high_talk_days > 0 ? `${row.high_talk_days} aktif gün` : undefined}>
                              {row.high_talk_days > 0 ? formatTalkTime(row.avg_high_talk_break_seconds ?? 0) : "-"}
                            </TableCell>
                            <TableCell className={`text-center ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{row.working_days}</TableCell>
                            <TableCell className={`whitespace-nowrap ${isDark ? "text-teal-400" : "text-teal-600"}`}>{formatTalkTime(row.avg_daily_talk_time)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center justify-center gap-1 min-w-[5.5rem]">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); fetchBreakAnalysis(row, { view: "standard" }); }}
                                  className={`h-7 w-full text-[10px] sm:text-xs px-2 ${isDark ? "border-amber-700/50 text-amber-400 hover:bg-amber-500/10" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}
                                >
                                  <Clock className="w-3 h-3 mr-1" />
                                  Mola Analiz
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); fetchBreakAnalysis(row, { view: "special" }); }}
                                  className={`h-7 w-full text-[10px] sm:text-xs px-2 ${isDark ? "border-violet-700/50 text-violet-400 hover:bg-violet-500/10" : "border-violet-300 text-violet-700 hover:bg-violet-50"}`}
                                >
                                  <BarChart3 className="w-3 h-3 mr-1" />
                                  Özel Analiz
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Footer Totals */}
                        <TableRow className={`font-bold ${isDark ? "bg-slate-800 border-slate-700" : "bg-gray-100"}`}>
                          <TableCell className={textClass}>TOPLAM</TableCell>
                          <TableCell className="hidden sm:table-cell"></TableCell>
                          <ReportStatCell value={reportsData.totals.total_calls} unique={reportsData.totals.unique_total_calls} colorClass={isDark ? "text-blue-400" : "text-blue-600"} isDark={isDark} />
                          <ReportStatCell value={reportsData.totals.answered_calls} unique={reportsData.totals.unique_answered_calls} colorClass={isDark ? "text-emerald-400" : "text-emerald-600"} isDark={isDark} />
                          <ReportStatCell value={reportsData.totals.missed_calls} unique={reportsData.totals.unique_missed_calls} colorClass={isDark ? "text-amber-400" : "text-amber-600"} isDark={isDark} />
                          <TableCell className={`whitespace-nowrap ${isDark ? "text-purple-400" : "text-purple-600"}`}>{formatTalkTime(reportsData.totals.total_talk_time)}</TableCell>
                          <TableCell className="text-center">
                            <ReportMetricBadge value={reportsData.totals.short_calls} tone="slate" isDark={isDark} />
                          </TableCell>
                          <TableCell className="text-center">
                            <ReportMetricBadge value={reportsData.totals.unfollowed_missed} tone="rose" isDark={isDark} />
                          </TableCell>
                          <TableCell className={`whitespace-nowrap ${isDark ? "text-amber-400" : "text-amber-700"}`}>{formatTalkTime(reportsData.totals.total_break_seconds || 0)}</TableCell>
                          <TableCell className={`whitespace-nowrap ${isDark ? "text-orange-400" : "text-orange-700"}`}>
                            {reportsData.totals.total_working_days > 0
                              ? formatTalkTime(reportsData.totals.avg_daily_break_seconds ?? Math.round((reportsData.totals.total_break_seconds || 0) / reportsData.totals.total_working_days))
                              : "-"}
                          </TableCell>
                          <TableCell className={`whitespace-nowrap ${isDark ? "text-rose-400" : "text-rose-700"}`} title={reportsData.totals.high_talk_days > 0 ? `${reportsData.totals.high_talk_days} aktif gün` : undefined}>
                            {reportsData.totals.high_talk_days > 0
                              ? formatTalkTime(reportsData.totals.avg_high_talk_break_seconds ?? 0)
                              : "-"}
                          </TableCell>
                          <TableCell className={`text-center ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{reportsData.totals.total_working_days}</TableCell>
                          <TableCell className={`whitespace-nowrap ${isDark ? "text-teal-400" : "text-teal-600"}`}>{formatTalkTime(reportsData.totals.avg_daily_talk_time)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ) : reportsData ? (
                  <div className={`text-center py-12 sm:py-20 ${labelClass}`}>
                    <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 opacity-30" />
                    <p className="text-sm">Seçilen kriterlere uygun veri bulunamadı</p>
                  </div>
                ) : (
                  <div className={`text-center py-12 sm:py-20 ${labelClass}`}>
                    <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 opacity-30" />
                    <p className="text-sm">Agent seçip &quot;Rapor Al&quot; butonuna tıklayın</p>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {reportsView === "mesai" && (
              <Card className={cardClass}>
                <CardHeader className="pb-4">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <CardTitle className={`text-lg font-semibold ${textClass}`}>
                        <Clock className="w-5 h-5 inline mr-2 text-cyan-500" />
                        Mesai Raporları
                      </CardTitle>
                      <p className={`text-xs mt-1 ${labelClass}`}>
                        Seçilen tarih aralığında agent başına geç kalma adedi ve erken çıkış süresi (60 sn tolerans · Ort. Mesai: günün ilk → son giden arama saati ortalaması)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchMesaiReport()}
                      disabled={mesaiReportLoading}
                      className={isDark ? "border-slate-700" : ""}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${mesaiReportLoading ? "animate-spin" : ""}`} />
                      Yenile
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className={`p-3 sm:p-4 rounded-xl ${isDark ? "bg-slate-800/50 border border-slate-700/50" : "bg-gray-50 border border-gray-100"}`}>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
                      {[
                        { id: "today", label: "Bugün" },
                        { id: "yesterday", label: "Dün" },
                        { id: "thisMonth", label: "Bu Ay" },
                        { id: "lastMonth", label: "Geçen Ay" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handleMesaiQuickRange(opt.id)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                            mesaiDatePreset === opt.id
                              ? isDark ? "bg-cyan-600 text-white" : "bg-cyan-500 text-white"
                              : isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                      {mesaiDatePreset === "custom" && (
                        <span className={`text-[10px] sm:text-xs ${labelClass}`}>Özel tarih aralığı</span>
                      )}
                    </div>
                    <div className={`grid grid-cols-2 ${isSuperAdmin ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 items-end`}>
                      <div className="min-w-0">
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Başlangıç</label>
                        <Input
                          type="date"
                          value={mesaiStartDate}
                          onChange={(e) => {
                            setMesaiStartDate(e.target.value);
                            setMesaiDatePreset("custom");
                          }}
                          className={`h-9 text-sm ${inputClass}`}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Bitiş</label>
                        <Input
                          type="date"
                          value={mesaiEndDate}
                          onChange={(e) => {
                            setMesaiEndDate(e.target.value);
                            setMesaiDatePreset("custom");
                          }}
                          className={`h-9 text-sm ${inputClass}`}
                        />
                      </div>
                      {isSuperAdmin && (
                        <div className="min-w-0">
                          <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Firma</label>
                          <Select value={mesaiCompanyId} onValueChange={setMesaiCompanyId}>
                            <SelectTrigger className={`h-9 text-sm ${inputClass}`}>
                              <SelectValue placeholder="Firma seçin" />
                            </SelectTrigger>
                            <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                              <SelectItem value="all">Tüm Firmalar</SelectItem>
                              {companies.filter((c) => c.status === 1).map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="min-w-0 col-span-2 lg:col-span-1">
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Agent Ara</label>
                        <div className="relative">
                          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                          <Input
                            placeholder="Agent ara..."
                            value={mesaiSearch}
                            onChange={(e) => setMesaiSearch(e.target.value)}
                            className={`pl-10 h-9 text-sm ${inputClass}`}
                          />
                        </div>
                      </div>
                      <div className="min-w-0 col-span-2 lg:col-span-1">
                        <Button
                          onClick={() => fetchMesaiReport()}
                          disabled={mesaiReportLoading}
                          className="w-full h-9 bg-cyan-600 hover:bg-cyan-700 text-white"
                        >
                          {mesaiReportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                          Rapor Al
                        </Button>
                      </div>
                    </div>
                  </div>

                  {mesaiReportData?.summary && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {[
                        { label: "Mesai Günü", value: mesaiReportData.summary.scheduled_days, tone: isDark ? "border-slate-700 bg-slate-800/40" : "border-gray-200 bg-white" },
                        { label: "Zamanında", value: mesaiReportData.summary.on_time_count, tone: isDark ? "border-emerald-500/30 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50" },
                        { label: "Geç Kalma", value: mesaiReportData.summary.late_count, tone: isDark ? "border-rose-500/30 bg-rose-500/10" : "border-rose-200 bg-rose-50" },
                        { label: "Erken Çıkma", value: formatEarlyLeaveDuration(mesaiReportData.summary.total_early_leave_seconds || 0), tone: isDark ? "border-orange-500/30 bg-orange-500/10" : "border-orange-200 bg-orange-50" },
                        { label: "Görüşme Yok", value: mesaiReportData.summary.absent_count, tone: isDark ? "border-amber-500/30 bg-amber-500/10" : "border-amber-200 bg-amber-50" },
                      ].map((card) => (
                        <div key={card.label} className={`rounded-xl border p-3 ${card.tone}`}>
                          <p className={`text-[10px] sm:text-xs font-medium uppercase tracking-wide ${labelClass}`}>{card.label}</p>
                          <p className={`text-xl sm:text-2xl font-bold mt-1 ${textClass}`}>{card.value ?? 0}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {mesaiReportData && (
                    <p className={`text-xs ${labelClass}`}>
                      {mesaiReportData.start_date} – {mesaiReportData.end_date}
                      {mesaiReportData.day_count ? ` · ${mesaiReportData.day_count} gün` : ""}
                    </p>
                  )}

                  <div className="overflow-x-auto -mx-1">
                    {mesaiReportLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                      </div>
                    ) : sortedMesaiReportRows.length === 0 ? (
                      <div className={`text-center py-16 ${labelClass}`}>
                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>{mesaiSearch.trim() ? "Arama sonucu bulunamadı" : "Tarih aralığı seçip Rapor Al'a tıklayın"}</p>
                      </div>
                    ) : (
                      <div className={`overflow-x-auto rounded-xl border ${isDark ? "border-slate-700/80" : "border-cyan-100"}`}>
                        <Table className="text-xs sm:text-sm">
                          <TableHeader>
                            <TableRow className={isDark ? "border-slate-700 bg-slate-800/80" : "bg-cyan-50/80 border-cyan-100"}>
                              <ReportSortableHead label="Agent" column="agent_name" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                              {isSuperAdmin && (
                                <ReportSortableHead label="Firma" column="company_name" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                              )}
                              <ReportSortableHead label="Mesai Günü" column="scheduled_days" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                              <ReportSortableHead label="Zamanında" column="on_time_count" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                              <ReportSortableHead label="Geç Kalma" column="late_count" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                              <ReportSortableHead label="Erken Çıkma" column="total_early_leave_seconds" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={`text-center min-w-[100px] ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Mola analizindeki erken çıkış süresi toplamı (son görüşme – mesai bitişi)" />
                              <ReportSortableHead label="Ort. Mesai" column="avg_start_time" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={`text-center min-w-[120px] ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Çalışılan günlerde ilk giden arama → son giden arama saati ortalaması" />
                              <ReportSortableHead label="Görüşme Yok" column="absent_count" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                              <ReportSortableHead label="Mesai Dışı" column="off_day_count" sort={mesaiReportSort} onSort={(key) => setMesaiReportSort((s) => toggleReportSort(s, key))} className={`hidden lg:table-cell text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedMesaiReportRows.map((row, idx) => (
                              <TableRow
                                key={row.agent_id}
                                className={`${isDark ? "border-slate-800/50 hover:bg-slate-800/40" : "hover:bg-cyan-50/60"} ${
                                  row.late_count > 0
                                    ? isDark ? "bg-rose-500/5" : "bg-rose-50/50"
                                    : row.on_time_count > 0 && row.late_count === 0
                                    ? isDark ? "bg-emerald-500/5" : "bg-emerald-50/30"
                                    : ""
                                } ${idx % 2 === 1 ? (isDark ? "bg-slate-900/15" : "bg-gray-50/50") : ""}`}
                              >
                                <TableCell className={`font-medium whitespace-nowrap ${textClass}`}>{row.agent_name}</TableCell>
                                {isSuperAdmin && (
                                  <TableCell className={`whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-600"}`}>{row.company_name || "-"}</TableCell>
                                )}
                                <TableCell className={`text-center font-mono ${isDark ? "text-slate-300" : "text-gray-700"}`}>{row.scheduled_days}</TableCell>
                                <TableCell className={`text-center font-semibold ${row.on_time_count > 0 ? (isDark ? "text-emerald-400" : "text-emerald-600") : labelClass}`}>
                                  {row.on_time_count}
                                </TableCell>
                                <TableCell className="text-center">
                                  {row.late_count > 0 ? (
                                    <button
                                      type="button"
                                      onClick={() => setMesaiLateModalAgent(row)}
                                      title="Geç kalınan günleri görüntüle"
                                      className="inline-flex rounded-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60"
                                    >
                                      <Badge className={`cursor-pointer ${isDark ? "bg-rose-500/20 text-rose-300 border border-rose-500/30 text-base px-3 py-0.5 hover:bg-rose-500/30" : "bg-rose-100 text-rose-700 border border-rose-200 text-base px-3 py-0.5 hover:bg-rose-200"}`}>
                                        {row.late_count}
                                      </Badge>
                                    </button>
                                  ) : (
                                    <span className={labelClass}>0</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {(row.total_early_leave_seconds || 0) > 0 ? (
                                    <button
                                      type="button"
                                      onClick={() => setMesaiEarlyModalAgent(row)}
                                      title="Erken çıkış günlerini görüntüle"
                                      className="inline-flex flex-col items-center rounded-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
                                    >
                                      <span className={`font-semibold font-mono ${isDark ? "text-orange-300" : "text-orange-700"}`}>
                                        {formatEarlyLeaveDuration(row.total_early_leave_seconds)}
                                      </span>
                                      {(row.early_leave_count || 0) > 0 && (
                                        <span className={`text-[10px] ${labelClass}`}>{row.early_leave_count} gün</span>
                                      )}
                                    </button>
                                  ) : (
                                    <span className={labelClass}>-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center align-middle">
                                  <MesaiScheduleCell row={row} isDark={isDark} labelClass={labelClass} />
                                </TableCell>
                                <TableCell className={`text-center font-medium ${row.absent_count > 0 ? (isDark ? "text-amber-400" : "text-amber-600") : labelClass}`}>
                                  {row.absent_count}
                                </TableCell>
                                <TableCell className={`hidden lg:table-cell text-center font-mono ${isDark ? "text-slate-400" : "text-gray-500"}`}>{row.off_day_count}</TableCell>
                              </TableRow>
                            ))}
                            {mesaiReportRowTotals && (
                              <TableRow className={`font-bold ${isDark ? "bg-slate-800 border-slate-700" : "bg-gray-100"}`}>
                                <TableCell className={textClass}>TOPLAM</TableCell>
                                {isSuperAdmin && <TableCell />}
                                <TableCell className={`text-center font-mono ${isDark ? "text-slate-200" : "text-gray-800"}`}>{mesaiReportRowTotals.scheduled_days}</TableCell>
                                <TableCell className={`text-center ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{mesaiReportRowTotals.on_time_count}</TableCell>
                                <TableCell className={`text-center ${isDark ? "text-rose-400" : "text-rose-600"}`}>{mesaiReportRowTotals.late_count}</TableCell>
                                <TableCell className={`text-center font-mono ${isDark ? "text-orange-300" : "text-orange-700"}`}>
                                  {formatEarlyLeaveDuration(mesaiReportRowTotals.total_early_leave_seconds)}
                                </TableCell>
                                <TableCell />
                                <TableCell className={`text-center ${isDark ? "text-amber-400" : "text-amber-600"}`}>{mesaiReportRowTotals.absent_count}</TableCell>
                                <TableCell className={`hidden lg:table-cell text-center ${isDark ? "text-slate-400" : "text-gray-600"}`}>{mesaiReportRowTotals.off_day_count}</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {sortedMesaiReportRows.length > 0 && (
                    <p className={`text-sm ${labelClass}`}>
                      {sortedMesaiReportRows.length} agent · Kolon başlıklarına tıklayarak sıralayın · Geç kalma / erken çıkış hücrelerine tıklayarak detayları görüntüleyin
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {reportsView === "mola" && (
              <Card className={cardClass}>
                <CardHeader className="pb-4">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <CardTitle className={`text-lg font-semibold ${textClass}`}>
                        <PauseCircle className="w-5 h-5 inline mr-2 text-amber-500" />
                        Mola Raporları
                      </CardTitle>
                      <p className={`text-xs mt-1 ${labelClass}`}>
                        Seçilen tarih aralığında agent başına mola adedi ve süreleri (≥5 dk aralar + mesai sonu ≥5 dk · mesai içi)
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchMolaReport()}
                      disabled={molaReportLoading}
                      className={isDark ? "border-slate-700" : ""}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${molaReportLoading ? "animate-spin" : ""}`} />
                      Yenile
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`p-3 sm:p-4 rounded-lg mb-4 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
                      {[
                        { id: "today", label: "Bugün" },
                        { id: "yesterday", label: "Dün" },
                        { id: "thisMonth", label: "Bu Ay" },
                        { id: "lastMonth", label: "Geçen Ay" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handleMolaQuickRange(opt.id)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
                            molaDatePreset === opt.id
                              ? (isDark ? "bg-amber-600 text-white" : "bg-amber-500 text-white")
                              : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200")
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                      {molaDatePreset === "custom" && (
                        <span className={`text-[10px] sm:text-xs ${labelClass}`}>Özel tarih aralığı</span>
                      )}
                    </div>
                    <div className={`grid grid-cols-2 ${isSuperAdmin ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 items-end`}>
                      <div className="min-w-0">
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Başlangıç</label>
                        <Input
                          type="date"
                          value={molaStartDate}
                          onChange={(e) => {
                            setMolaStartDate(e.target.value);
                            setMolaDatePreset("custom");
                          }}
                          className={`h-8 w-full text-xs ${inputClass}`}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Bitiş</label>
                        <Input
                          type="date"
                          value={molaEndDate}
                          onChange={(e) => {
                            setMolaEndDate(e.target.value);
                            setMolaDatePreset("custom");
                          }}
                          className={`h-8 w-full text-xs ${inputClass}`}
                        />
                      </div>
                      {isSuperAdmin && (
                        <div className="min-w-0">
                          <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Firma</label>
                          <Select value={molaCompanyId} onValueChange={setMolaCompanyId}>
                            <SelectTrigger className={`h-8 w-full text-xs ${inputClass}`}>
                              <SelectValue placeholder="Firma seçin" />
                            </SelectTrigger>
                            <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                              <SelectItem value="all">Tüm Firmalar</SelectItem>
                              {companies.filter((c) => c.status === 1).map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className={`min-w-0 ${isSuperAdmin ? "" : "col-span-2 lg:col-span-1"}`}>
                        <label className={`text-xs font-medium ${labelClass} mb-1 block`}>Agent Ara</label>
                        <div className="relative">
                          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${labelClass}`} />
                          <Input
                            placeholder="Agent veya firma ara..."
                            value={molaSearch}
                            onChange={(e) => setMolaSearch(e.target.value)}
                            className={`h-8 pl-8 text-xs ${inputClass}`}
                          />
                        </div>
                      </div>
                      <div className="min-w-0 col-span-2 lg:col-span-1">
                        <Button
                          onClick={() => fetchMolaReport()}
                          disabled={molaReportLoading}
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                        >
                          {molaReportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                          Rapor Al
                        </Button>
                      </div>
                    </div>
                  </div>

                  {molaReportData?.summary && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                      {[
                        { label: "Aktif Agent", value: molaReportData.agents?.length || 0, tone: isDark ? "border-slate-700 bg-slate-800/40" : "border-gray-200 bg-white" },
                        { label: "Toplam Mola", value: molaReportData.summary.total_break_count, tone: isDark ? "border-amber-500/30 bg-amber-500/10" : "border-amber-200 bg-amber-50" },
                        { label: "Toplam Süre", value: formatTalkTime(molaReportData.summary.total_break_seconds || 0), tone: isDark ? "border-purple-500/30 bg-purple-500/10" : "border-purple-200 bg-purple-50" },
                        { label: "Ort. Mola", value: formatTalkTime(molaReportData.summary.avg_break_seconds || 0), tone: isDark ? "border-cyan-500/30 bg-cyan-500/10" : "border-cyan-200 bg-cyan-50" },
                      ].map((card) => (
                        <div key={card.label} className={`rounded-lg border px-3 py-2.5 ${card.tone}`}>
                          <p className={`text-[10px] sm:text-xs ${labelClass}`}>{card.label}</p>
                          <p className={`text-lg sm:text-xl font-bold font-mono ${textClass}`}>{card.value}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {molaReportData && (
                    <p className={`text-xs mb-3 ${labelClass}`}>
                      {molaReportData.start_date} – {molaReportData.end_date}
                      {molaReportData.day_count ? ` · ${molaReportData.day_count} gün` : ""}
                    </p>
                  )}

                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    {molaReportLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                      </div>
                    ) : sortedMolaReportRows.length === 0 ? (
                      <div className={`text-center py-16 ${labelClass}`}>
                        <PauseCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>{molaSearch.trim() ? "Arama sonucu bulunamadı" : "Tarih aralığı seçip Rapor Al'a tıklayın"}</p>
                      </div>
                    ) : (
                      <div className={`overflow-x-auto rounded-xl border ${isDark ? "border-slate-700/80" : "border-amber-100"}`}>
                      <Table className="text-xs sm:text-sm">
                        <TableHeader>
                          <TableRow className={isDark ? "border-slate-700 bg-slate-800/80" : "bg-amber-50/80 border-amber-100"}>
                            <ReportSortableHead label="Agent" column="agent_name" sort={molaReportSort} onSort={(key) => setMolaReportSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                            {isSuperAdmin && (
                              <ReportSortableHead label="Firma" column="company_name" sort={molaReportSort} onSort={(key) => setMolaReportSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                            )}
                            <ReportSortableHead label="Aktif Gün" column="active_days" sort={molaReportSort} onSort={(key) => setMolaReportSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Mola Adedi" column="break_count" sort={molaReportSort} onSort={(key) => setMolaReportSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Toplam Mola" column="total_break_seconds" sort={molaReportSort} onSort={(key) => setMolaReportSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Ort. Mola" column="avg_break_seconds" sort={molaReportSort} onSort={(key) => setMolaReportSort((s) => toggleReportSort(s, key))} className={`hidden md:table-cell text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="En Uzun" column="longest_break_seconds" sort={molaReportSort} onSort={(key) => setMolaReportSort((s) => toggleReportSort(s, key))} className={`hidden lg:table-cell text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <TableHead className={`text-center whitespace-nowrap ${isDark ? "text-slate-400" : "text-gray-600"}`}>Detay</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedMolaReportRows.map((row, idx) => (
                            <TableRow
                              key={row.agent_id}
                              className={`${isDark ? "border-slate-800/50 hover:bg-slate-800/40" : "hover:bg-amber-50/60"} ${row.break_count > 0 ? (isDark ? "bg-amber-500/5" : "bg-amber-50/50") : ""} ${idx % 2 === 1 ? (isDark ? "bg-slate-900/15" : "bg-gray-50/50") : ""}`}
                            >
                              <TableCell className={`font-medium ${textClass}`}>{row.agent_name}</TableCell>
                              {isSuperAdmin && (
                                <TableCell className={isDark ? "text-slate-300" : "text-gray-600"}>{row.company_name || "-"}</TableCell>
                              )}
                              <TableCell className={`text-center font-mono ${isDark ? "text-slate-300" : ""}`}>{row.active_days}</TableCell>
                              <TableCell className="text-center">
                                {row.break_count > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => setMolaBreakModalAgent(row)}
                                    title="Mola günlerini görüntüle"
                                    className="inline-flex rounded-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
                                  >
                                    <Badge className={`cursor-pointer ${isDark ? "bg-amber-500/20 text-amber-300 border border-amber-500/30 text-base px-3 py-0.5 hover:bg-amber-500/30" : "bg-amber-100 text-amber-800 border border-amber-200 text-base px-3 py-0.5 hover:bg-amber-200"}`}>
                                      {row.break_count}
                                    </Badge>
                                  </button>
                                ) : (
                                  <span className={labelClass}>0</span>
                                )}
                              </TableCell>
                              <TableCell className={`text-center font-mono font-medium ${row.total_break_seconds > 0 ? (isDark ? "text-purple-400" : "text-purple-700") : labelClass}`}>
                                {formatTalkTime(row.total_break_seconds || 0)}
                              </TableCell>
                              <TableCell className={`hidden md:table-cell text-center font-mono ${isDark ? "text-cyan-400" : "text-cyan-700"}`}>
                                {row.break_count > 0 ? formatTalkTime(row.avg_break_seconds || 0) : "-"}
                              </TableCell>
                              <TableCell className={`hidden lg:table-cell text-center font-mono ${isDark ? "text-rose-400" : "text-rose-700"}`}>
                                {row.longest_break_seconds > 0 ? formatTalkTime(row.longest_break_seconds) : "-"}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openMolaBreakAnalysis(row)}
                                  disabled={row.active_days === 0}
                                  className={`h-7 text-[10px] sm:text-xs px-2 ${isDark ? "border-amber-700/50 text-amber-400 hover:bg-amber-500/10" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}
                                >
                                  <Clock className="w-3 h-3 mr-1" />
                                  Analiz
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                  </div>

                  {sortedMolaReportRows.length > 0 && (
                    <p className={`text-sm mt-3 ${labelClass}`}>
                      {sortedMolaReportRows.length} agent · Kolon başlığına tıklayarak sıralayın · Mola adedine tıklayarak günlük özeti görüntüleyin
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {reportsView === "tl-recordings" && (
              <TeamLeaderRecordingReportsPage
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                isAdmin={isAdmin || isSuperAdmin}
                canEditApprovals={isAdmin || isSuperAdmin || hasMenu("menu_reports_tl_approval")}
                canCreateKaliteReport={
                  hasMenu("menu_reports_tl_recordings") || hasMenu("menu_reports_mgr_kalite_degerlendirme")
                }
              />
            )}

            {MANAGER_REPORT_SPREADSHEET_VIEW_IDS.map((viewId) => (
              reportsView === viewId && MANAGER_REPORT_VIEW_TO_TYPE[viewId] ? (
                <ManagerQualityReportsPage
                  key={viewId}
                  reportType={MANAGER_REPORT_VIEW_TO_TYPE[viewId]}
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                />
              ) : null
            ))}

          {/* Mesai Late Detail Modal */}
          {mesaiLateModalAgent && (
            <PanelModal open align="start" onClose={closeMesaiLateModal}>
              <PanelModalContent
                className={`w-full flex flex-col rounded-xl shadow-2xl overflow-hidden ${panelModalSizeClasses(mesaiLateModalExpand.expanded)} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white border border-gray-200"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-rose-950/40 to-slate-900" : "border-gray-200 bg-gradient-to-r from-rose-50 to-white"}`}>
                  <div className="min-w-0">
                    <h3 className={`text-base sm:text-lg font-semibold truncate ${textClass}`}>
                      <Clock className="w-5 h-5 inline mr-2 text-rose-500 align-text-bottom" />
                      {mesaiLateModalAgent.agent_name}
                      <span className={`font-normal ${labelClass}`}> · Geç Kalma Özeti</span>
                    </h3>
                    <p className={`text-[11px] sm:text-xs mt-0.5 ${labelClass}`}>
                      {formatReportDate(mesaiReportData?.start_date || mesaiStartDate)} – {formatReportDate(mesaiReportData?.end_date || mesaiEndDate)}
                      {mesaiLateModalAgent.company_name ? ` · ${mesaiLateModalAgent.company_name}` : ""}
                    </p>
                  </div>
                  <PanelModalHeaderActions
                    expanded={mesaiLateModalExpand.expanded}
                    onToggle={mesaiLateModalExpand.toggleExpanded}
                    onClose={closeMesaiLateModal}
                    isDark={isDark}
                  />
                </div>

                <div className={`shrink-0 px-3 sm:px-4 py-3 border-b ${isDark ? "border-slate-800 bg-slate-900/80" : "border-gray-100 bg-gray-50/90"}`}>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      {
                        label: "Geç Kalma Günü",
                        value: mesaiLateModalAgent.late_count,
                        color: isDark ? "text-rose-400" : "text-rose-700",
                        bg: isDark ? "bg-rose-500/10 border-rose-500/20" : "bg-rose-50 border-rose-100",
                      },
                      {
                        label: "Toplam Gecikme",
                        value: formatLateDuration(mesaiLateModalAgent.total_late_seconds || 0).replace(" geç", ""),
                        color: isDark ? "text-amber-400" : "text-amber-700",
                        bg: isDark ? "bg-amber-500/10 border-amber-500/20" : "bg-amber-50 border-amber-100",
                      },
                      {
                        label: "Ort. Gecikme",
                        value: mesaiLateModalAgent.late_count > 0
                          ? formatLateDuration(Math.round((mesaiLateModalAgent.total_late_seconds || 0) / mesaiLateModalAgent.late_count)).replace(" geç", "")
                          : "-",
                        color: isDark ? "text-cyan-400" : "text-cyan-700",
                        bg: isDark ? "bg-cyan-500/10 border-cyan-500/20" : "bg-cyan-50 border-cyan-100",
                      },
                    ].map((stat) => (
                      <div key={stat.label} className={`rounded-lg border px-2.5 py-2 ${stat.bg}`}>
                        <p className={`text-[10px] leading-none mb-1 ${labelClass}`}>{stat.label}</p>
                        <p className={`text-sm sm:text-base font-bold font-mono leading-tight ${stat.color}`}>{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className={`text-[10px] mt-2 ${labelClass}`}>60 sn tolerans · İlk giden görüşme saati mesai başlangıcı ile karşılaştırılır</p>
                </div>

                <div className={`shrink-0 px-4 py-2 border-b ${isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-100 bg-white"}`}>
                  <p className={`text-xs font-medium ${textClass}`}>
                    Geç Kalınan Günler
                    <span className={`ml-1.5 font-normal ${labelClass}`}>
                      ({mesaiLateModalAgent.late_days?.length || mesaiLateModalAgent.late_count || 0} gün)
                    </span>
                  </p>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3">
                  {(mesaiLateModalAgent.late_days?.length > 0) ? (
                    <div className="space-y-2">
                      {mesaiLateModalAgent.late_days.map((day, dayIndex) => {
                        const theme = getMesaiLateDayTheme(dayIndex, isDark);
                        return (
                        <div
                          key={day.date}
                          className={`relative overflow-hidden rounded-lg border pl-3.5 pr-3 py-2.5 ${theme.card}`}
                        >
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${theme.stripe}`} aria-hidden />
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className={`text-sm font-bold tracking-tight ${theme.date}`}>
                                {formatReportDate(day.date)}
                                <span className={`ml-2 text-xs font-medium ${theme.day}`}>{day.day_name}</span>
                              </p>
                              <p className={`text-[11px] mt-1 ${labelClass}`}>
                                Mesai {day.scheduled_start} – {day.scheduled_end}
                              </p>
                            </div>
                            <Badge className={`shrink-0 border ${theme.badge}`}>
                              {formatLateDuration(day.late_seconds)}
                            </Badge>
                          </div>
                          <div className={`mt-2 pt-2 border-t grid grid-cols-2 gap-2 text-[11px] sm:text-xs ${theme.divider}`}>
                            <div>
                              <p className={labelClass}>Planlanan Başlangıç</p>
                              <p className={`font-mono font-medium ${textClass}`}>{day.scheduled_start}</p>
                            </div>
                            <div>
                              <p className={labelClass}>İlk Giden Görüşme</p>
                              <p className={`font-mono font-semibold ${theme.call}`}>{day.first_call_time || "-"}</p>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`text-center py-10 ${labelClass}`}>
                      <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Geç kalma gün detayı bulunamadı</p>
                      <p className="text-xs mt-1">Raporu yenileyerek tekrar deneyin</p>
                    </div>
                  )}
                </div>
              </PanelModalContent>
            </PanelModal>
          )}

          {mesaiEarlyModalAgent && (
            <PanelModal open align="start" onClose={closeMesaiEarlyModal}>
              <PanelModalContent
                className={`w-full flex flex-col rounded-xl shadow-2xl overflow-hidden ${panelModalSizeClasses(mesaiEarlyModalExpand.expanded)} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white border border-gray-200"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-orange-950/40 to-slate-900" : "border-gray-200 bg-gradient-to-r from-orange-50 to-white"}`}>
                  <div className="min-w-0">
                    <h3 className={`text-base sm:text-lg font-semibold truncate ${textClass}`}>
                      <Clock className="w-5 h-5 inline mr-2 text-orange-500 align-text-bottom" />
                      {mesaiEarlyModalAgent.agent_name}
                      <span className={`font-normal ${labelClass}`}> · Erken Çıkma Özeti</span>
                    </h3>
                    <p className={`text-[11px] sm:text-xs mt-0.5 ${labelClass}`}>
                      {formatReportDate(mesaiReportData?.start_date || mesaiStartDate)} – {formatReportDate(mesaiReportData?.end_date || mesaiEndDate)}
                      {mesaiEarlyModalAgent.company_name ? ` · ${mesaiEarlyModalAgent.company_name}` : ""}
                    </p>
                  </div>
                  <PanelModalHeaderActions
                    expanded={mesaiEarlyModalExpand.expanded}
                    onToggle={mesaiEarlyModalExpand.toggleExpanded}
                    onClose={closeMesaiEarlyModal}
                    isDark={isDark}
                  />
                </div>

                <div className={`shrink-0 px-3 sm:px-4 py-3 border-b ${isDark ? "border-slate-800 bg-slate-900/80" : "border-gray-100 bg-gray-50/90"}`}>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      {
                        label: "Erken Çıkış Günü",
                        value: mesaiEarlyModalAgent.early_leave_count || 0,
                        color: isDark ? "text-orange-400" : "text-orange-700",
                        bg: isDark ? "bg-orange-500/10 border-orange-500/20" : "bg-orange-50 border-orange-100",
                      },
                      {
                        label: "Toplam Süre",
                        value: formatEarlyLeaveDuration(mesaiEarlyModalAgent.total_early_leave_seconds || 0),
                        color: isDark ? "text-amber-400" : "text-amber-700",
                        bg: isDark ? "bg-amber-500/10 border-amber-500/20" : "bg-amber-50 border-amber-100",
                      },
                      {
                        label: "Ort. Süre",
                        value: (mesaiEarlyModalAgent.early_leave_count || 0) > 0
                          ? formatEarlyLeaveDuration(Math.round((mesaiEarlyModalAgent.total_early_leave_seconds || 0) / mesaiEarlyModalAgent.early_leave_count))
                          : "-",
                        color: isDark ? "text-cyan-400" : "text-cyan-700",
                        bg: isDark ? "bg-cyan-500/10 border-cyan-500/20" : "bg-cyan-50 border-cyan-100",
                      },
                    ].map((stat) => (
                      <div key={stat.label} className={`rounded-lg border px-2.5 py-2 ${stat.bg}`}>
                        <p className={`text-[10px] leading-none mb-1 ${labelClass}`}>{stat.label}</p>
                        <p className={`text-sm sm:text-base font-bold font-mono leading-tight ${stat.color}`}>{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className={`text-[10px] mt-2 ${labelClass}`}>Son görüşme bitişi ile mesai bitişi arasındaki süre (mola analizi ile aynı)</p>
                </div>

                <div className={`shrink-0 px-4 py-2 border-b ${isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-100 bg-white"}`}>
                  <p className={`text-xs font-medium ${textClass}`}>
                    Erken Çıkış Yapılan Günler
                    <span className={`ml-1.5 font-normal ${labelClass}`}>
                      ({mesaiEarlyModalAgent.early_leave_days?.length || mesaiEarlyModalAgent.early_leave_count || 0} gün)
                    </span>
                  </p>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3">
                  {(mesaiEarlyModalAgent.early_leave_days?.length > 0) ? (
                    <div className="space-y-2">
                      {mesaiEarlyModalAgent.early_leave_days.map((day) => (
                        <div
                          key={day.date}
                          className={`relative overflow-hidden rounded-lg border pl-3.5 pr-3 py-2.5 ${isDark ? "border-orange-500/25 bg-orange-500/5" : "border-orange-200 bg-orange-50/60"}`}
                        >
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${isDark ? "bg-orange-400" : "bg-orange-500"}`} aria-hidden />
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className={`text-sm font-bold tracking-tight ${isDark ? "text-orange-200" : "text-orange-900"}`}>
                                {formatReportDate(day.date)}
                                <span className={`ml-2 text-xs font-medium ${labelClass}`}>{day.day_name}</span>
                              </p>
                              <p className={`text-[11px] mt-1 ${labelClass}`}>
                                Mesai {day.scheduled_start} – {day.scheduled_end}
                              </p>
                            </div>
                            <Badge className={`shrink-0 border ${isDark ? "bg-orange-500/15 text-orange-300 border-orange-500/30" : "bg-orange-100 text-orange-700 border-orange-200"}`}>
                              {day.early_leave_label || formatEarlyLeaveDuration(day.early_leave_seconds)}
                            </Badge>
                          </div>
                          <div className={`mt-2 pt-2 border-t grid grid-cols-2 gap-2 text-[11px] sm:text-xs ${isDark ? "border-orange-500/15" : "border-orange-100"}`}>
                            <div>
                              <p className={labelClass}>Planlanan Bitiş</p>
                              <p className={`font-mono font-medium ${textClass}`}>{day.scheduled_end}</p>
                            </div>
                            <div>
                              <p className={labelClass}>Son Görüşme</p>
                              <p className={`font-mono font-semibold ${isDark ? "text-orange-300" : "text-orange-700"}`}>{day.last_call_time || "-"}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-10 ${labelClass}`}>
                      <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Erken çıkış gün detayı bulunamadı</p>
                      <p className="text-xs mt-1">Raporu yenileyerek tekrar deneyin</p>
                    </div>
                  )}
                </div>
              </PanelModalContent>
            </PanelModal>
          )}

          {/* Mola Break Days Summary Modal */}
          {molaBreakModalAgent && (
            <PanelModal open align="start" onClose={closeMolaBreakModal}>
              <PanelModalContent
                className={`w-full flex flex-col rounded-xl shadow-2xl overflow-hidden ${panelModalSizeClasses(molaBreakModalExpand.expanded)} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white border border-gray-200"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-amber-950/40 to-slate-900" : "border-gray-200 bg-gradient-to-r from-amber-50 to-white"}`}>
                  <div className="min-w-0">
                    <h3 className={`text-base sm:text-lg font-semibold truncate ${textClass}`}>
                      <PauseCircle className="w-5 h-5 inline mr-2 text-amber-500 align-text-bottom" />
                      {molaBreakModalAgent.agent_name}
                      <span className={`font-normal ${labelClass}`}> · Mola Özeti</span>
                    </h3>
                    <p className={`text-[11px] sm:text-xs mt-0.5 ${labelClass}`}>
                      {formatReportDate(molaReportData?.start_date || molaStartDate)} – {formatReportDate(molaReportData?.end_date || molaEndDate)}
                      {molaBreakModalAgent.company_name ? ` · ${molaBreakModalAgent.company_name}` : ""}
                    </p>
                  </div>
                  <PanelModalHeaderActions
                    expanded={molaBreakModalExpand.expanded}
                    onToggle={molaBreakModalExpand.toggleExpanded}
                    onClose={closeMolaBreakModal}
                    isDark={isDark}
                  />
                </div>

                <div className={`shrink-0 px-3 sm:px-4 py-3 border-b ${isDark ? "border-slate-800 bg-slate-900/80" : "border-gray-100 bg-gray-50/90"}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { label: "Mola Adedi", value: molaBreakModalAgent.break_count, color: isDark ? "text-amber-400" : "text-amber-700", bg: isDark ? "bg-amber-500/10 border-amber-500/20" : "bg-amber-50 border-amber-100" },
                      { label: "Toplam Süre", value: formatTalkTime(molaBreakModalAgent.total_break_seconds || 0), color: isDark ? "text-purple-400" : "text-purple-700", bg: isDark ? "bg-purple-500/10 border-purple-500/20" : "bg-purple-50 border-purple-100" },
                      { label: "Ort. Mola", value: molaBreakModalAgent.break_count > 0 ? formatTalkTime(molaBreakModalAgent.avg_break_seconds || 0) : "-", color: isDark ? "text-cyan-400" : "text-cyan-700", bg: isDark ? "bg-cyan-500/10 border-cyan-500/20" : "bg-cyan-50 border-cyan-100" },
                      { label: "Mola Günü", value: molaBreakModalAgent.break_days?.length || 0, color: isDark ? "text-emerald-400" : "text-emerald-700", bg: isDark ? "bg-emerald-500/10 border-emerald-500/20" : "bg-emerald-50 border-emerald-100" },
                    ].map((stat) => (
                      <div key={stat.label} className={`rounded-lg border px-2.5 py-2 ${stat.bg}`}>
                        <p className={`text-[10px] leading-none mb-1 ${labelClass}`}>{stat.label}</p>
                        <p className={`text-sm sm:text-base font-bold font-mono leading-tight ${stat.color}`}>{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <p className={`text-[10px] ${labelClass}`}>≥5 dk aralar + mesai sonu ≥5 dk mola sayılır · Sadece mesai içi çağrılar</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={`h-7 text-[10px] shrink-0 ${isDark ? "border-amber-700/50 text-amber-400 hover:bg-amber-500/10" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}
                      onClick={() => {
                        const agent = molaBreakModalAgent;
                        closeMolaBreakModal();
                        openMolaBreakAnalysis(agent);
                      }}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Tam Analiz
                    </Button>
                  </div>
                </div>

                <div className={`shrink-0 px-4 py-2 border-b ${isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-100 bg-white"}`}>
                  <p className={`text-xs font-medium ${textClass}`}>
                    Mola Yapılan Günler
                    <span className={`ml-1.5 font-normal ${labelClass}`}>
                      ({molaBreakModalAgent.break_days?.length || 0} gün)
                    </span>
                  </p>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3">
                  {(molaBreakModalAgent.break_days?.length > 0) ? (
                    <div className="space-y-2">
                      {molaBreakModalAgent.break_days.map((day, dayIndex) => {
                        const theme = getMesaiLateDayTheme(dayIndex, isDark);
                        return (
                          <div
                            key={day.date}
                            className={`relative overflow-hidden rounded-lg border pl-3.5 pr-3 py-2.5 ${theme.card}`}
                          >
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${theme.stripe}`} aria-hidden />
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className={`text-sm font-bold tracking-tight ${theme.date}`}>
                                  {formatReportDate(day.date)}
                                  <span className={`ml-2 text-xs font-medium ${theme.day}`}>{day.day_name}</span>
                                </p>
                                <p className={`text-[11px] mt-1 ${labelClass}`}>
                                  Mesai {day.scheduled_start} – {day.scheduled_end}
                                </p>
                              </div>
                              <Badge className={`shrink-0 border ${theme.badge}`}>
                                {day.break_count} mola
                              </Badge>
                            </div>
                            <div className={`mt-2 pt-2 border-t grid grid-cols-2 gap-2 text-[11px] sm:text-xs ${theme.divider}`}>
                              <div>
                                <p className={labelClass}>Toplam Mola</p>
                                <p className={`font-mono font-semibold ${theme.call}`}>{day.break_label || formatTalkTime(day.total_break_seconds)}</p>
                              </div>
                              <div>
                                <p className={labelClass}>En Uzun Mola</p>
                                <p className={`font-mono font-medium ${textClass}`}>{day.longest_break_seconds > 0 ? formatTalkTime(day.longest_break_seconds) : "-"}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`text-center py-10 ${labelClass}`}>
                      <PauseCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Mola gün detayı bulunamadı</p>
                      <p className="text-xs mt-1">Raporu yenileyerek tekrar deneyin</p>
                    </div>
                  )}
                </div>
              </PanelModalContent>
            </PanelModal>
          )}
          </TabsContent>

          <TabsContent value="special-agent-report" className="space-y-4">
            <SpecialAgentReportPage
              apiBase={API}
              isDark={isDark}
              textClass={textClass}
              labelClass={labelClass}
              isSuperAdmin={isSuperAdmin}
              userCompanyId={user?.company_id}
              companies={companies}
            />
          </TabsContent>

          <TabsContent value="special-agent-report-2" className="space-y-4">
            <SpecialAgentReportPageV2
              apiBase={API}
              isDark={isDark}
              textClass={textClass}
              labelClass={labelClass}
              isSuperAdmin={isSuperAdmin}
              userCompanyId={user?.company_id}
              companies={companies}
            />
          </TabsContent>

          <TabsContent value="team-leader-performance" className="space-y-4">
            <TeamLeaderPerformanceReportPage
              apiBase={API}
              isDark={isDark}
              textClass={textClass}
              labelClass={labelClass}
              isSuperAdmin={isSuperAdmin}
              userCompanyId={user?.company_id}
              cacheUserKey={user?.id}
              companies={companies}
              onOpenMrMapping={() => navigateToTab("mr-assistant-mapping")}
              onOpenMrRecords={() => navigateToTab("mr-excel-records")}
              onOpenCiroRecords={() => navigateToTab("tl-ciro-records")}
            />
          </TabsContent>

          <TabsContent value="tl-ciro-records" className="space-y-4">
            <CiroExcelRecordsPage
              apiBase={API}
              isDark={isDark}
              textClass={textClass}
              labelClass={labelClass}
              inputClass={inputClass}
              isSuperAdmin={isSuperAdmin}
              userCompanyId={user?.company_id}
              companies={companies}
            />
          </TabsContent>

          <TabsContent value="mr-excel-records" className="space-y-4">
            <MrExcelRecordsPage
              apiBase={API}
              isDark={isDark}
              textClass={textClass}
              labelClass={labelClass}
              inputClass={inputClass}
              isSuperAdmin={isSuperAdmin}
              userCompanyId={user?.company_id}
              companies={companies}
            />
          </TabsContent>

          {/* Report Daily Detail Modal */}
          {reportDetailAgent && (
            <PanelModal open align="start" onClose={closeReportDetail}>
              <PanelModalContent
                className={`panel-modal-content--report-detail w-full my-2 sm:my-4 rounded-xl shadow-2xl flex flex-col ${panelModalSizeClasses(reportDetailModalExpand.expanded, "max")} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white"}`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className={`sticky top-0 z-10 flex items-center justify-between gap-2 p-3 sm:p-4 border-b rounded-t-xl ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
                  <h3 className={`text-sm sm:text-lg font-semibold min-w-0 truncate ${textClass}`}>
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 inline mr-2 text-teal-500" />
                    {reportDetailAgent.agent_name} - Günlük Detay
                  </h3>
                  <PanelModalHeaderActions
                    expanded={reportDetailModalExpand.expanded}
                    onToggle={reportDetailModalExpand.toggleExpanded}
                    onClose={closeReportDetail}
                    isDark={isDark}
                  />
                </div>
                
                {/* Period Info + Export */}
                <div className={`px-3 sm:px-4 py-2 text-xs sm:text-sm border-b ${isDark ? "border-slate-800 bg-slate-800/30" : "border-gray-100 bg-gray-50"}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className={labelClass}>
                      📅 {formatReportDate(reportsStartDate)} - {formatReportDate(reportsEndDate)}
                      {reportDetailData?.summary?.working_days != null && (
                        <span className="ml-2">| Çalışma günü: <strong className={isDark ? "text-cyan-400" : "text-cyan-600"}>{reportDetailData.summary.working_days}</strong></span>
                      )}
                      <span className="block sm:inline sm:ml-2 mt-1 sm:mt-0 text-[10px] opacity-80">(Günlük ≥ 1 saat konuşma = çalışma günü · Tarihe tıklayarak kayıtları görüntüleyin)</span>
                    </div>
                    {!reportDetailLoading && reportDetailData?.daily_breakdown?.length > 0 && (
                      <div className="flex gap-2 shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleExportReportDetailPdf}
                          className={`h-8 text-xs ${isDark ? "border-slate-600 text-red-400 hover:bg-slate-800 hover:text-red-300" : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" />
                          PDF
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleExportReportDetailExcel}
                          className={`h-8 text-xs ${isDark ? "border-slate-600 text-emerald-400 hover:bg-slate-800 hover:text-emerald-300" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"}`}
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
                          Excel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Modal Content */}
                <div className="p-3 sm:p-6 overflow-y-auto flex-1">
                  {reportDetailLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="spinner w-8 h-8" />
                    </div>
                  ) : reportDetailData && reportDetailData.daily_breakdown && reportDetailData.daily_breakdown.length > 0 ? (
                    <div className="space-y-4">
                    <div className={`overflow-x-auto -mx-3 sm:mx-0 rounded-xl border ${isDark ? "border-slate-700/80" : "border-teal-100"}`}>
                      <Table className="text-xs sm:text-sm">
                        <TableHeader>
                          <TableRow className={isDark ? "border-slate-700 bg-slate-800/90" : "bg-teal-50/90 border-teal-100"}>
                            <ReportSortableHead label="Tarih" column="call_date" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                            <ReportSortableHead label="Toplam" column="total_calls" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Cvp" column="answered_calls" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Cvsz" column="missed_calls" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Konuşma" column="total_talk_time" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={isDark ? "text-slate-300" : "text-gray-700"} />
                            <ReportSortableHead label="<1 dk" column="short_calls" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Görüşülen çağrılar — konuşma 1–59 sn" />
                            <ReportSortableHead label="Takipsiz" column="unfollowed_missed" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} title="Tek denemeli, takipsiz cevapsız numaralar" />
                            <ReportSortableHead label="Mola Süresi" column="break_seconds" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`whitespace-nowrap ${isDark ? "text-slate-300" : "text-gray-700"}`} title="O gün mesai içi çağrılar arası ≥5 dk molaların toplam süresi" />
                            <ReportSortableHead label="Gün" column="is_working_day" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`text-center ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="İlk" column="first_call" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`hidden sm:table-cell ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Son" column="last_call" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`hidden sm:table-cell ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <ReportSortableHead label="Aralık" column="working_minutes" sort={reportDetailSort} onSort={(key) => setReportDetailSort((s) => toggleReportSort(s, key))} className={`hidden md:table-cell ${isDark ? "text-slate-300" : "text-gray-700"}`} />
                            <TableHead className={`text-center whitespace-nowrap ${isDark ? "text-slate-400" : "text-gray-600"}`}>Analiz</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortReportRows(reportDetailData.daily_breakdown, reportDetailSort).map((day, idx) => (
                            <TableRow
                              key={day.call_date ?? idx}
                              className={`${isDark ? "border-slate-700/50 hover:bg-slate-800/40" : "hover:bg-teal-50/60"} ${day.is_working_day === false ? (isDark ? "opacity-60" : "opacity-75") : ""} ${reportDetailSelectedDate === day.call_date ? (isDark ? "bg-teal-500/10" : "bg-teal-50") : ""} ${idx % 2 === 1 ? (isDark ? "bg-slate-900/15" : "bg-gray-50/50") : ""}`}
                            >
                              <TableCell
                                className={`font-mono whitespace-nowrap cursor-pointer hover:underline ${reportDetailSelectedDate === day.call_date ? (isDark ? "text-teal-300 font-semibold" : "text-teal-700 font-semibold") : (isDark ? "text-slate-200" : "")}`}
                                onClick={() => handleReportDayClick(day)}
                                title="Bu günün kayıtlarını göster"
                              >
                                {formatReportDate(day.call_date)}
                              </TableCell>
                              <ReportStatCell value={day.total_calls} unique={day.unique_total_calls} colorClass={isDark ? "text-blue-400" : "text-blue-600"} isDark={isDark} />
                              <ReportStatCell value={day.answered_calls} unique={day.unique_answered_calls} colorClass={isDark ? "text-emerald-400" : "text-emerald-600"} isDark={isDark} />
                              <ReportStatCell value={day.missed_calls || (day.total_calls - day.answered_calls)} unique={day.unique_missed_calls} colorClass={isDark ? "text-amber-400" : "text-amber-600"} isDark={isDark} />
                              <TableCell className={`whitespace-nowrap ${isDark ? "text-purple-400" : "text-purple-600"}`}>{formatTalkTime(day.total_talk_time || 0)}</TableCell>
                              <TableCell className="text-center">
                                <ReportMetricBadge value={day.short_calls} tone="slate" isDark={isDark} title="Görüşülen çağrılar — konuşma 1–59 sn" />
                              </TableCell>
                              <TableCell className="text-center">
                                <ReportMetricBadge
                                  value={day.unfollowed_missed}
                                  tone="rose"
                                  isDark={isDark}
                                  title="Tek denemeli takipsiz cevapsız"
                                  onClick={() => openUnfollowedMissedModal({
                                    agentId: reportDetailAgent.agent_id,
                                    startDate: day.call_date,
                                    endDate: day.call_date,
                                  })}
                                />
                              </TableCell>
                              <TableCell className={`whitespace-nowrap ${isDark ? "text-amber-400" : "text-amber-700"}`}>{formatTalkTime(day.break_seconds || 0)}</TableCell>
                              <TableCell className="text-center">
                                {day.is_working_day ? (
                                  <Badge className={isDark ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/30" : "bg-cyan-100 text-cyan-700 border-cyan-200"}>Çalıştı</Badge>
                                ) : (
                                  <Badge className={isDark ? "bg-slate-700 text-slate-400 border-slate-600" : "bg-gray-100 text-gray-500 border-gray-200"}>&lt;1 saat</Badge>
                                )}
                              </TableCell>
                              <TableCell className={`hidden sm:table-cell font-mono text-xs ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>{day.first_call || "-"}</TableCell>
                              <TableCell className={`hidden sm:table-cell font-mono text-xs ${isDark ? "text-amber-400" : "text-amber-600"}`}>{day.last_call || "-"}</TableCell>
                              <TableCell className={`hidden md:table-cell ${isDark ? "text-slate-300" : ""}`}>
                                {day.working_minutes > 0 
                                  ? `${Math.floor(day.working_minutes / 60)}sa ${day.working_minutes % 60}dk`
                                  : "-"}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={`h-7 px-2 text-[10px] sm:text-xs ${isDark ? "border-teal-500/30 text-teal-300 hover:bg-teal-500/10" : "border-teal-200 text-teal-700 hover:bg-teal-50"}`}
                                  onClick={(e) => { e.stopPropagation(); openCallAnalysis(day.call_date); }}
                                  title="Günlük çağrı analizi"
                                >
                                  <BarChart3 className="w-3.5 h-3.5 mr-1" />
                                  Analiz
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {reportDetailSelectedDate && (
                      <div
                        ref={reportDetailDayRecordsRef}
                        className={`rounded-lg border p-3 sm:p-4 scroll-mt-4 ${isDark ? "border-slate-700 bg-slate-800/40" : "border-gray-200 bg-gray-50"}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                          <h4 className={`text-sm font-semibold ${textClass}`}>
                            <Phone className="w-4 h-4 inline mr-2 text-teal-500" />
                            {reportDetailSelectedDate} Kayıtları
                            {!reportDetailDayRecordsLoading && (
                              <span className={`ml-2 text-xs font-normal ${labelClass}`}>
                                ({reportDetailDayRecords.length} {reportDetailUniqueOnly ? "tekil" : "kayıt"})
                              </span>
                            )}
                          </h4>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={`h-8 ${isDark ? "border-teal-500/30 text-teal-300 hover:bg-teal-500/10" : "border-teal-200 text-teal-700 hover:bg-teal-50"}`}
                              onClick={() => openCallAnalysis(reportDetailSelectedDate)}
                            >
                              <BarChart3 className="w-4 h-4 mr-1.5" />
                              Çağrı Analizi
                            </Button>
                            <label className={`inline-flex items-center gap-2 text-xs cursor-pointer ${labelClass}`}>
                              <Checkbox
                                checked={reportDetailUniqueOnly}
                                onCheckedChange={(checked) => handleReportUniqueOnlyChange(checked === true)}
                              />
                              Sadece tekil numaralar
                            </label>
                          </div>
                        </div>

                        {reportDetailDayRecordsLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <div className="spinner w-6 h-6" />
                          </div>
                        ) : reportDetailDayRecords.length > 0 ? (
                          <div className="overflow-x-auto max-h-72 overflow-y-auto -mx-1">
                            <Table className="text-xs sm:text-sm">
                              <TableHeader className={`sticky top-0 z-10 ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                                <TableRow className={isDark ? "border-slate-700" : ""}>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Saat</TableHead>
                                  <TableHead className={`text-center ${isDark ? "text-slate-400" : ""}`}>Yön</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Arayan</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Aranan</TableHead>
                                  <TableHead className={`text-center ${isDark ? "text-slate-400" : ""}`}>Durum</TableHead>
                                  <TableHead className={isDark ? "text-slate-400" : ""}>Süre</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {reportDetailDayRecords.map((record) => (
                                  <TableRow key={record.id} className={isDark ? "border-slate-700/50" : ""}>
                                    <TableCell className={`font-mono whitespace-nowrap ${isDark ? "text-slate-300" : ""}`}>
                                      {record.tarih?.split(" ")[1]?.slice(0, 5) || "-"}
                                    </TableCell>
                                    <TableCell className="text-center text-[10px] sm:text-xs">
                                      {record.yon === 1 ? (
                                        <span className={isDark ? "text-emerald-400" : "text-emerald-600"}>Gelen</span>
                                      ) : record.yon === 2 ? (
                                        <span className={isDark ? "text-blue-400" : "text-blue-600"}>Giden</span>
                                      ) : "-"}
                                    </TableCell>
                                    <TableCell className={`font-mono text-[10px] sm:text-xs ${isDark ? "text-slate-300" : ""}`}>{record.arayan_numara || "-"}</TableCell>
                                    <TableCell className={`font-mono text-[10px] sm:text-xs ${isDark ? "text-slate-300" : ""}`}>{record.aranan_numara || "-"}</TableCell>
                                    <TableCell className="text-center">
                                      <DayRecordStatus record={record} isDark={isDark} />
                                    </TableCell>
                                    <TableCell className={`font-mono whitespace-nowrap ${isDark ? "text-purple-400" : "text-purple-600"}`}>
                                      {formatDuration(record.konusma_suresi)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className={`text-center py-8 text-sm ${labelClass}`}>
                            Bu tarihte kayıt bulunamadı
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  ) : (
                    <div className={`text-center py-16 ${labelClass}`}>
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Bu tarih aralığında günlük veri bulunamadı</p>
                    </div>
                  )}
                </div>
              </PanelModalContent>
            </PanelModal>
          )}

          <AgentDayCallAnalysisModal
            isOpen={callAnalysisOpen}
            onClose={closeCallAnalysis}
            data={callAnalysisData}
            loading={callAnalysisLoading}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
          />

          <UnfollowedMissedModal
            isOpen={unfollowedMissedOpen}
            onClose={closeUnfollowedMissedModal}
            data={unfollowedMissedData}
            loading={unfollowedMissedLoading}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
          />

          {/* Break Analysis Modal (standart — günlük accordion) */}
          {breakAnalysisAgent && breakAnalysisView === "standard" && (
            <PanelModal open onClose={closeBreakAnalysis}>
              <PanelModalContent
                className={`w-full flex flex-col rounded-xl shadow-2xl overflow-hidden ${panelModalSizeClasses(breakAnalysisModalExpand.expanded)} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white border border-gray-200"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-amber-950/40 to-slate-900" : "border-gray-200 bg-gradient-to-r from-amber-50 to-white"}`}>
                  <div className="min-w-0">
                    <h3 className={`text-base sm:text-lg font-semibold truncate ${textClass}`}>
                      <Clock className="w-5 h-5 inline mr-2 text-amber-500 align-text-bottom" />
                      {breakAnalysisAgent.agent_name}
                      <span className={`font-normal ${labelClass}`}> · Mola Analizi</span>
                    </h3>
                    <p className={`text-[11px] sm:text-xs mt-0.5 ${labelClass}`}>
                      {formatReportDate(breakAnalysisPeriod?.start ?? reportsStartDate)} – {formatReportDate(breakAnalysisPeriod?.end ?? reportsEndDate)}
                    </p>
                  </div>
                  <PanelModalHeaderActions
                    expanded={breakAnalysisModalExpand.expanded}
                    onToggle={breakAnalysisModalExpand.toggleExpanded}
                    onClose={closeBreakAnalysis}
                    isDark={isDark}
                  />
                </div>

                {breakAnalysisLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="spinner w-8 h-8 mx-auto mb-3" />
                      <p className={`text-sm ${labelClass}`}>Mola analizi hesaplanıyor...</p>
                    </div>
                  </div>
                ) : breakAnalysisData ? (
                  <>
                    <div className={`shrink-0 px-3 sm:px-4 py-3 border-b ${isDark ? "border-slate-800 bg-slate-900/80" : "border-gray-100 bg-gray-50/90"}`}>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-2">
                        {[
                          { label: "Toplam Mola", hint: BREAK_ANALYSIS_HINTS.totalBreak, value: formatTalkTime(breakAnalysisData.summary?.total_break_seconds || 0), color: isDark ? "text-amber-400" : "text-amber-700", bg: isDark ? "bg-amber-500/10 border-amber-500/20" : "bg-amber-50 border-amber-100" },
                          { label: "Mola Adedi", hint: BREAK_ANALYSIS_HINTS.breakCount, value: breakAnalysisData.summary?.total_break_count || 0, color: isDark ? "text-blue-400" : "text-blue-700", bg: isDark ? "bg-blue-500/10 border-blue-500/20" : "bg-blue-50 border-blue-100" },
                          {
                            label: "Ort. Mola",
                            hint: BREAK_ANALYSIS_HINTS.avgBreak,
                            value: formatTalkTime(breakAnalysisData.summary?.avg_break_seconds || 0),
                            color: isDark ? "text-cyan-400" : "text-cyan-700",
                            bg: isDark ? "bg-cyan-500/10 border-cyan-500/20" : "bg-cyan-50 border-cyan-100",
                            sub: (
                              <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.avgHighTalkBreak} as="span">
                                60+ dk:{" "}
                                {(breakAnalysisData.summary?.high_talk_days || 0) > 0
                                  ? `${formatTalkTime(breakAnalysisData.summary?.avg_high_talk_break_seconds || 0)} (${breakAnalysisData.summary.high_talk_days} gün)`
                                  : "—"}
                              </BreakAnalysisHint>
                            ),
                          },
                          { label: "Toplam Konuşma", hint: BREAK_ANALYSIS_HINTS.totalTalk, value: formatTalkTime(breakAnalysisData.summary?.total_talk_seconds || 0), color: isDark ? "text-purple-400" : "text-purple-700", bg: isDark ? "bg-purple-500/10 border-purple-500/20" : "bg-purple-50 border-purple-100" },
                          { label: "Çağrı", hint: BREAK_ANALYSIS_HINTS.totalCalls, value: breakAnalysisData.summary?.total_calls || 0, color: isDark ? "text-emerald-400" : "text-emerald-700", bg: isDark ? "bg-emerald-500/10 border-emerald-500/20" : "bg-emerald-50 border-emerald-100" },
                          { label: SHORT_CALL_LABEL, hint: BREAK_ANALYSIS_HINTS.shortCalls, value: breakAnalysisData.summary?.short_calls || 0, color: isDark ? "text-slate-300" : "text-slate-700", bg: isDark ? "bg-slate-800/80 border-slate-700" : "bg-white border-gray-200" },
                          { label: "Takipsiz", hint: BREAK_ANALYSIS_HINTS.unfollowedMissed, value: breakAnalysisData.summary?.unfollowed_missed || 0, color: isDark ? "text-rose-400" : "text-rose-700", bg: isDark ? "bg-rose-500/10 border-rose-500/20" : "bg-rose-50 border-rose-100" },
                          {
                            label: "Aktif Gün",
                            hint: BREAK_ANALYSIS_HINTS.activeDays,
                            value: breakAnalysisData.summary?.active_days || 0,
                            color: textClass,
                            bg: isDark ? "bg-slate-800/80 border-slate-700" : "bg-white border-gray-200",
                            sub: (
                              <>
                                <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.lateDays} as="span">Geç: {breakAnalysisData.summary?.late_start_days || 0}</BreakAnalysisHint>
                                {" · "}
                                <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.earlyDays} as="span">Erken: {breakAnalysisData.summary?.early_leave_days || 0}</BreakAnalysisHint>
                              </>
                            ),
                          },
                        ].map((stat) => (
                          <BreakAnalysisHint
                            key={stat.label}
                            hint={stat.hint}
                            as="div"
                            className={`rounded-lg border px-2.5 py-2 ${stat.bg}`}
                          >
                            <p className={`text-[10px] leading-none mb-1 ${labelClass}`}>{stat.label}</p>
                            <p className={`text-sm sm:text-base font-bold font-mono leading-tight ${stat.color}`}>{stat.value}</p>
                            {stat.sub && <p className={`text-[9px] mt-1 leading-tight ${labelClass}`}>{stat.sub}</p>}
                          </BreakAnalysisHint>
                        ))}
                      </div>
                      <p className={`text-[10px] mt-2 ${labelClass}`}>≥5 dk aralar + mesai sonu ≥5 dk mola sayılır · Sadece mesai içi çağrılar · İlk görüşme = ilk giden arama</p>
                    </div>

                    <div className={`shrink-0 px-4 py-2 border-b flex items-center justify-between ${isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-100 bg-white"}`}>
                      <p className={`text-xs font-medium ${textClass}`}>
                        Günlük Detay
                        <span className={`ml-1.5 font-normal ${labelClass}`}>
                          ({breakAnalysisData.daily_breakdown?.length || 0} gün)
                        </span>
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={`h-7 text-[10px] px-2 ${isDark ? "text-slate-400 hover:text-slate-200" : ""}`}
                        onClick={() => {
                          setBreakAnalysisExpandedDay(null);
                          setBreakAnalysisUnfollowedDay(null);
                        }}
                      >
                        Tümünü kapat
                      </Button>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-3">
                      {breakAnalysisData.daily_breakdown && breakAnalysisData.daily_breakdown.length > 0 ? (
                        <div className="space-y-2 pb-2">
                          {breakAnalysisData.daily_breakdown.map((day) => {
                            const isExpanded = breakAnalysisExpandedDay === day.date;
                            return (
                              <div
                                key={day.date}
                                className={`rounded-lg border overflow-hidden transition-shadow ${
                                  isExpanded
                                    ? isDark ? "border-amber-500/40 shadow-lg shadow-amber-500/5 bg-slate-800/40" : "border-amber-300 shadow-md bg-white"
                                    : isDark ? "border-slate-700/80 bg-slate-800/20" : "border-gray-200 bg-gray-50/50"
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isExpanded) {
                                      setBreakAnalysisExpandedDay(null);
                                      if (breakAnalysisUnfollowedDay === day.date) setBreakAnalysisUnfollowedDay(null);
                                    } else {
                                      setBreakAnalysisExpandedDay(day.date);
                                      setBreakAnalysisUnfollowedDay(null);
                                    }
                                  }}
                                  className={`w-full flex items-start sm:items-center justify-between gap-3 p-3 text-left transition-colors ${isDark ? "hover:bg-slate-800/60" : "hover:bg-white"}`}
                                >
                                  <div className="flex items-start gap-2 min-w-0">
                                    <div className={`mt-0.5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                        <span className={`font-mono font-semibold text-sm ${isDark ? "text-slate-100" : "text-gray-900"}`}>
                                          {formatReportDate(day.date)}
                                        </span>
                                        <span className={`text-xs ${labelClass}`}>{day.day_name}</span>
                                      </div>
                                      {day.is_scheduled_work_day && day.scheduled_start && (
                                        <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.mesaiHours} as="p" className={`text-[10px] mt-0.5 ${labelClass}`}>
                                          Mesai {day.scheduled_start} – {day.scheduled_end}
                                        </BreakAnalysisHint>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap justify-end gap-1.5 shrink-0 max-w-[50%] sm:max-w-none">
                                    {day.has_activity ? (
                                      <>
                                        <Badge variant="outline" title={BREAK_ANALYSIS_HINTS.dailyCalls} className={`text-[10px] h-5 cursor-help ${isDark ? "border-blue-500/30 text-blue-300 bg-blue-500/10" : "border-blue-200 text-blue-700 bg-blue-50"}`}>{day.total_calls} çağrı</Badge>
                                        <Badge variant="outline" title={BREAK_ANALYSIS_HINTS.dailyBreakCount} className={`text-[10px] h-5 cursor-help ${isDark ? "border-amber-500/30 text-amber-300 bg-amber-500/10" : "border-amber-200 text-amber-700 bg-amber-50"}`}>{day.break_count} mola</Badge>
                                        <Badge variant="outline" title={BREAK_ANALYSIS_HINTS.dailyBreakTotal} className={`text-[10px] h-5 hidden sm:inline-flex cursor-help ${isDark ? "border-purple-500/30 text-purple-300 bg-purple-500/10" : "border-purple-200 text-purple-700 bg-purple-50"}`}>{formatTalkTime(day.between_break_seconds || 0)}</Badge>
                                      </>
                                    ) : (
                                      <Badge className={`text-[10px] h-5 ${isDark ? "bg-slate-700 text-slate-400" : "bg-gray-200 text-gray-600"}`}>Aktivite yok</Badge>
                                    )}
                                  </div>
                                </button>

                                {isExpanded && (
                                  <div className={`border-t ${isDark ? "border-slate-700/80 bg-slate-900/30" : "border-gray-100 bg-white"}`}>
                                    {day.has_activity ? (
                                      <div className="p-3 space-y-3">
                                        <div className={`grid grid-cols-2 lg:grid-cols-7 gap-2 rounded-lg p-2.5 text-xs ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
                                          <div>
                                            <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.firstOutgoing} className={`text-[10px] block ${labelClass}`}>İlk giden görüşme</BreakAnalysisHint>
                                            <p className={`font-mono font-semibold ${isDark ? "text-cyan-400" : "text-cyan-700"}`}>{day.first_call_time || "-"}</p>
                                          </div>
                                          <div>
                                            <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.lastCall} className={`text-[10px] block ${labelClass}`}>Son görüşme</BreakAnalysisHint>
                                            <p className={`font-mono font-semibold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{day.last_call_time || "-"}</p>
                                          </div>
                                          <div>
                                            <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.shortCalls} className={`text-[10px] block ${labelClass}`}>&lt;1 dk görüşme</BreakAnalysisHint>
                                            <div className="mt-0.5">
                                              <ReportMetricBadge value={day.short_calls} tone="slate" isDark={isDark} title="Görüşülen çağrılar — konuşma 1–59 sn" />
                                            </div>
                                          </div>
                                          <div>
                                            <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.unfollowedMissed} className={`text-[10px] block ${labelClass}`}>Takipsiz</BreakAnalysisHint>
                                            <div className="mt-0.5">
                                              <ReportMetricBadge
                                                value={day.unfollowed_missed}
                                                tone="rose"
                                                isDark={isDark}
                                                title="Tek denemeli takipsiz cevapsız"
                                                onClick={() => setBreakAnalysisUnfollowedDay((prev) => (prev === day.date ? null : day.date))}
                                              />
                                            </div>
                                          </div>
                                          <div>
                                            <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.dailyTalk} className={`text-[10px] block ${labelClass}`}>Toplam görüşme süresi (mesai içi)</BreakAnalysisHint>
                                            <p className={`font-semibold ${isDark ? "text-teal-400" : "text-teal-700"}`}>{formatTalkTime(day.total_talk_seconds || 0)}</p>
                                          </div>
                                          <div>
                                            <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.dailyOffShiftTalk} className={`text-[10px] block ${labelClass}`}>Toplam görüşme süresi (mesai dışı)</BreakAnalysisHint>
                                            <p className={`font-semibold ${isDark ? "text-rose-400" : "text-rose-700"}`}>{formatTalkTime(day.off_shift_talk_seconds || 0)}</p>
                                          </div>
                                          <div>
                                            <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.dailyTotalTalk} className={`text-[10px] block ${labelClass}`}>Toplam görüşme süresi</BreakAnalysisHint>
                                            <p className={`font-semibold ${isDark ? "text-purple-400" : "text-purple-700"}`}>{formatTalkTime((day.total_talk_seconds || 0) + (day.off_shift_talk_seconds || 0))}</p>
                                          </div>
                                        </div>

                                        {breakAnalysisUnfollowedDay === day.date && (
                                          <BreakAnalysisUnfollowedList
                                            records={day.unfollowed_missed_records}
                                            isDark={isDark}
                                            onClose={() => setBreakAnalysisUnfollowedDay(null)}
                                          />
                                        )}

                                        {day.breaks && day.breaks.filter(isMolaBreak).length > 0 && (
                                          <div className={`rounded-lg border overflow-hidden ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                                            <div className={`px-3 py-2 text-xs font-medium ${isDark ? "bg-slate-800 text-slate-300" : "bg-gray-100 text-gray-700"}`}>
                                              <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.dailyBreakCount}>Mola Detayı ({day.break_count})</BreakAnalysisHint>
                                            </div>
                                            <div className={`overflow-y-auto overscroll-contain ${breakAnalysisModalExpand.expanded ? "max-h-72" : "max-h-44"}`}>
                                              <Table className="text-xs">
                                                <TableHeader className={`sticky top-0 z-10 ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                                                  <TableRow className={isDark ? "border-slate-700" : ""}>
                                                    <TableHead className={`h-8 cursor-help ${isDark ? "text-slate-400" : ""}`} title={BREAK_ANALYSIS_HINTS.breakStart}>Başlangıç</TableHead>
                                                    <TableHead className={`h-8 cursor-help ${isDark ? "text-slate-400" : ""}`} title={BREAK_ANALYSIS_HINTS.breakEnd}>Bitiş</TableHead>
                                                    <TableHead className={`h-8 text-right cursor-help ${isDark ? "text-slate-400" : ""}`} title={BREAK_ANALYSIS_HINTS.breakDuration}>Süre</TableHead>
                                                  </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                  {day.breaks.filter(isMolaBreak).map((brk, i) => (
                                                    <TableRow key={i} className={isDark ? "border-slate-700/50" : ""}>
                                                      <TableCell className="py-2 font-mono">
                                                        {brk.start_time}
                                                        {brk.type === "early_leave" && (
                                                          <span className={`ml-1 text-[9px] ${isDark ? "text-orange-400" : "text-orange-600"}`}>(mesai sonu)</span>
                                                        )}
                                                      </TableCell>
                                                      <TableCell className="py-2 font-mono">{brk.end_time}</TableCell>
                                                      <TableCell className={`py-2 text-right font-mono ${isDark ? "text-amber-400" : "text-amber-700"}`}>{formatTalkTime(brk.duration_seconds || 0)}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </div>
                                          </div>
                                        )}

                                        {(day.late_start_seconds > 0 || day.early_leave_seconds > 0) && (
                                          <div className={`grid grid-cols-2 gap-2 text-xs ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                                            {day.late_start_seconds > 0 && (
                                              <div className={`rounded-lg px-2.5 py-2 ${isDark ? "bg-slate-800/50" : "bg-orange-50"}`}>
                                                <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.lateStart} className={`text-[10px] block ${labelClass}`}>Geç başlangıç</BreakAnalysisHint>
                                                <p className={`font-semibold ${isDark ? "text-orange-400" : "text-orange-700"}`}>{formatTalkTime(day.late_start_seconds)}</p>
                                              </div>
                                            )}
                                            {day.early_leave_seconds > 0 && (
                                              <div className={`rounded-lg px-2.5 py-2 ${isDark ? "bg-slate-800/50" : "bg-orange-50"}`}>
                                                <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.earlyLeave} className={`text-[10px] block ${labelClass}`}>Erken çıkış</BreakAnalysisHint>
                                                <p className={`font-semibold ${isDark ? "text-orange-400" : "text-orange-700"}`}>{formatTalkTime(day.early_leave_seconds)}</p>
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        <div className={`rounded-lg border-l-4 p-3 text-xs leading-relaxed ${isDark ? "border-amber-500 bg-slate-800/60 text-slate-300" : "border-amber-400 bg-amber-50/50 text-gray-700"}`}>
                                          <BreakAnalysisHint hint={BREAK_ANALYSIS_HINTS.activitySummary} className={`font-semibold mb-2 flex items-center gap-1.5 ${isDark ? "text-amber-400" : "text-amber-800"}`}>
                                            <FileText className="w-3.5 h-3.5" /> Aktivite Özeti
                                          </BreakAnalysisHint>
                                          <p className="whitespace-pre-line">{day.activity_summary}</p>
                                          {day.notes && day.notes.length > 0 && (
                                            <ul className={`mt-2 space-y-1 ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                                              {day.notes.map((note, idx) => (
                                                <li key={idx} className="flex gap-1.5">
                                                  <span className="text-amber-500 shrink-0">•</span>
                                                  <span>{note}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <p className={`p-3 text-sm ${labelClass}`}>{day.activity_summary}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className={`flex flex-col items-center justify-center py-16 ${labelClass}`}>
                          <Clock className="w-12 h-12 mb-3 opacity-30" />
                          <p className="text-sm">Bu dönemde analiz edilecek günlük veri bulunamadı</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className={`flex-1 flex flex-col items-center justify-center ${labelClass}`}>
                    <Clock className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm">Analiz verisi yüklenemedi</p>
                  </div>
                )}
              </PanelModalContent>
            </PanelModal>
          )}

          {breakAnalysisAgent && breakAnalysisView === "special" && (
            <BreakAnalysisRangeOverviewModal
              open
              onClose={closeBreakAnalysis}
              agentName={breakAnalysisAgent.agent_name}
              period={breakAnalysisPeriod}
              days={breakAnalysisData?.daily_breakdown}
              summary={breakAnalysisData?.summary}
              loading={breakAnalysisLoading}
              isDark={isDark}
              textClass={textClass}
              labelClass={labelClass}
            />
          )}

          {/* Hatlar Tab */}
          <TabsContent value="lines" className="space-y-4">
            <SubNavPills
              items={allowedLineViews}
              activeId={linesView}
              onChange={(view) => navigateToTab("lines", view)}
              isDark={isDark}
              textClass={textClass}
            />
            <Card className={cardClass}>
              <CardContent className="py-16 sm:py-24 text-center">
                <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                  {linesView === "routing" ? (
                    <GitBranch className={`w-8 h-8 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                  ) : (
                    <Settings className={`w-8 h-8 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                  )}
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${textClass}`}>
                  {linesView === "routing" ? "Yönlendirme" : "Hat Ayarları"}
                </h3>
                <p className={`text-sm max-w-md mx-auto ${labelClass}`}>
                  Bu modül yakında eklenecek. Hat tanımları, yönlendirme kuralları ve numara yönetimi buradan yapılacak.
                </p>
                <Badge variant="outline" className={`mt-4 ${isDark ? "border-blue-500/30 text-blue-300" : "border-blue-200 text-blue-700"}`}>
                  Yakında
                </Badge>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices" className="space-y-6">
            {isSuperAdmin && (
              <EcallAssignmentsPanel
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                cardClass={cardClass}
              />
            )}
            <DeviceFailedStatsPanel
              API={API}
              isDark={isDark}
              textClass={textClass}
              labelClass={labelClass}
              cardClass={cardClass}
              isSuperAdmin={isSuperAdmin}
              companyId={devicesCompanyId}
              onStatsMapChange={handleDeviceFailedStatsMapChange}
              onDeviceClick={(deviceId) => openCallLogsModalByDeviceId(deviceId, { initialTab: "failed" })}
            />
            <Card className={cardClass}>
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className={`text-lg font-semibold ${textClass}`}>
                    <Smartphone className="w-5 h-5 inline mr-2 text-pink-500" />
                    Cihazlar
                    <span className={`ml-2 text-sm font-normal ${labelClass}`}>({devicesTotal})</span>
                  </CardTitle>
                  
                  {/* Search & Filters */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    {canManageDeviceCleanup && resolvedDevicesCleanupCompanyId && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={`h-9 text-xs ${isDark ? "border-amber-500/40 text-amber-200 hover:bg-amber-500/10" : "border-amber-300 text-amber-800 hover:bg-amber-50"}`}
                        onClick={openCompanyDeviceCleanupModal}
                      >
                        <Eraser className="w-3.5 h-3.5 mr-1.5" />
                        Firma Temizlik
                      </Button>
                    )}
                    {isSuperAdmin && (
                      <Select
                        value={devicesCompanyId}
                        onValueChange={(v) => { setDevicesCompanyId(v); setDevicesPage(1); }}
                      >
                        <SelectTrigger className={`h-9 w-full sm:w-44 text-xs ${inputClass}`}>
                          <SelectValue placeholder="Firma filtresi" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="all">Tüm Firmalar</SelectItem>
                          <SelectItem value="none">Atanmamış</SelectItem>
                          {companies.filter((c) => c.status === 1).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <form onSubmit={handleDevicesSearch} className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="ID, Device ID, GSM, model..."
                      value={devicesSearch}
                      onChange={(e) => setDevicesSearch(e.target.value)}
                      className={`w-48 sm:w-64 h-9 text-sm ${inputClass}`}
                    />
                    <Button type="submit" size="sm" className="h-9 bg-pink-600 hover:bg-pink-700 text-white">
                      <Search className="w-4 h-4" />
                    </Button>
                    {devicesSearch && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { setDevicesSearch(""); setDevicesPage(1); }}
                        className={`h-9 ${isDark ? "border-slate-700" : ""}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </form>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {devicesLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="spinner w-8 h-8" />
                  </div>
                ) : devices.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <Table className="text-xs sm:text-sm">
                        <TableHeader>
                          <TableRow className={isDark ? "border-slate-800" : ""}>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>ID</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Device ID</TableHead>
                            <TableHead className={`whitespace-nowrap min-w-[140px] ${isDark ? "text-slate-400" : ""}`}>Cihaz Bilgisi</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>GSM</TableHead>
                            <TableHead className={`hidden sm:table-cell whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>İlk Sync</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Son Sync</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Firma</TableHead>
                            <TableHead className={`whitespace-nowrap ${isDark ? "text-slate-400" : ""}`}>Agent</TableHead>
                            <TableHead className={`whitespace-nowrap text-center ${isDark ? "text-slate-400" : ""}`}>Eksik Ses</TableHead>
                            {canManageDeviceCleanup && (
                              <TableHead className={`whitespace-nowrap text-center ${isDark ? "text-slate-400" : ""}`}>Temizlik</TableHead>
                            )}
                            <TableHead className={`whitespace-nowrap text-right ${isDark ? "text-slate-400" : ""}`}>İşlem</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {devices.map((device) => {
                            const assignedAgent = enrichedDeviceAgentMap[device.id];
                            const failedStat = deviceFailedStatsMap[String(device.id)];
                            const isEditingGsm = editingDeviceGsm.hasOwnProperty(device.id);
                            const gsmEmpty = !device.gsm || device.gsm === '-';
                            return (
                            <TableRow key={device.id} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                              <TableCell className={`font-mono ${isDark ? "text-slate-400" : "text-gray-500"}`}>{device.id}</TableCell>
                              <TableCell 
                                className={`font-mono text-[10px] sm:text-xs cursor-pointer hover:underline ${isDark ? "text-cyan-400" : "text-cyan-600"}`}
                                onClick={() => openCallLogsModal(device)}
                                title="Çağrı kayıtlarını görüntüle"
                              >
                                {device.device_id || '-'}
                              </TableCell>
                              <TableCell>
                                <DeviceInfoStack device={device} isDark={isDark} />
                              </TableCell>
                              <TableCell className={`font-mono ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                                {gsmEmpty || isEditingGsm ? (
                                  <div className="flex items-center gap-1">
                                    <Input 
                                      type="text"
                                      value={editingDeviceGsm[device.id] ?? ''}
                                      onChange={(e) => setEditingDeviceGsm(prev => ({...prev, [device.id]: e.target.value}))}
                                      placeholder="GSM girin veya boş bırakın"
                                      className={`h-7 w-28 text-xs ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                                    />
                                    <Button 
                                      size="sm" 
                                      onClick={() => handleUpdateDeviceGsm(device.id, editingDeviceGsm[device.id] ?? '')}
                                      disabled={!isEditingGsm && !editingDeviceGsm[device.id]}
                                      className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                                      title="Kaydet"
                                    >
                                      <CheckSquare className="w-3 h-3" />
                                    </Button>
                                    {isEditingGsm && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setEditingDeviceGsm(prev => ({ ...prev, [device.id]: "" }))}
                                          className={`h-7 px-2 text-xs ${isDark ? "border-slate-600 text-slate-300" : ""}`}
                                          title="GSM alanını boş bırak"
                                        >
                                          Boş bırak
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost"
                                          onClick={() => setEditingDeviceGsm(prev => {
                                            const newState = { ...prev };
                                            delete newState[device.id];
                                            return newState;
                                          })}
                                          className="h-7 px-2"
                                          title="İptal"
                                        >
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <span 
                                    className="cursor-pointer hover:underline" 
                                    onClick={() => setEditingDeviceGsm(prev => ({...prev, [device.id]: device.gsm === '-' ? '' : (device.gsm || '')}))}
                                    title="Düzenlemek için tıklayın"
                                  >
                                    {device.gsm}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className={`hidden sm:table-cell text-[10px] sm:text-xs ${labelClass}`}>
                                {device.first_sync ? new Date(device.first_sync).toLocaleDateString('tr-TR') : '-'}
                              </TableCell>
                              <TableCell className={`text-[10px] sm:text-xs ${isDark ? "text-teal-400" : "text-teal-600"}`}>
                                {device.last_sync ? new Date(device.last_sync).toLocaleString('tr-TR') : '-'}
                              </TableCell>
                              <TableCell className="min-w-[120px]">
                                {canManageDeviceCompany ? (
                                  <Select
                                    value={device.company_id ? String(device.company_id) : "none"}
                                    onValueChange={(v) => handleUpdateDeviceCompany(device.id, v)}
                                  >
                                    <SelectTrigger className={`h-7 w-[130px] text-[10px] sm:text-xs ${inputClass}`}>
                                      <SelectValue placeholder="Firma seç" />
                                    </SelectTrigger>
                                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                                      {isSuperAdmin && <SelectItem value="none">Atanmamış</SelectItem>}
                                      {(isSuperAdmin
                                        ? companies.filter((c) => c.status === 1)
                                        : companies.filter((c) => String(c.id) === String(user?.company_id))
                                      ).map((c) => (
                                        <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className={`text-xs ${isDark ? "text-indigo-400/90" : "text-indigo-600"}`}>
                                    {device.company_name || "-"}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {assignedAgent ? (
                                  <span className={`text-xs px-2 py-1 rounded inline-block max-w-full truncate ${isDark ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                                    {assignedAgent.agent_name}
                                  </span>
                                ) : (
                                  <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <DeviceFailedStatsBadge stat={failedStat} isDark={isDark} compact />
                              </TableCell>
                              {canManageDeviceCleanup && (
                                <TableCell className="text-center">
                                  {device.cleanup_enabled ? (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? "bg-amber-500/15 text-amber-300" : "bg-amber-50 text-amber-800"}`}>
                                      {device.cleanup_delete_days ?? "?"}g
                                    </span>
                                  ) : (
                                    <span className={`text-[10px] ${labelClass}`}>—</span>
                                  )}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className={`h-7 px-2 mt-0.5 ${isDark ? "text-amber-300 hover:bg-amber-500/10" : "text-amber-700 hover:bg-amber-50"}`}
                                    onClick={() => openDeviceCleanupModal(device)}
                                    title="Telefon ses kaydı temizlik ayarı"
                                  >
                                    <Eraser className="w-3 h-3 mr-1" />
                                    Temizlik
                                  </Button>
                                </TableCell>
                              )}
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {assignedAgent ? (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => {
                                        setSelectedDeviceForChange(device);
                                        setChangeAgentModalOpen(true);
                                      }}
                                      className={`text-xs h-7 ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""}`}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-1" /> Değiştir
                                    </Button>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      onClick={() => {
                                        setSelectedDeviceForChange(device);
                                        setChangeAgentModalOpen(true);
                                      }}
                                      className="text-xs h-7 bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      <UserPlus className="w-3 h-3 mr-1" /> Agent Ata
                                    </Button>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteDevice(device.id)}
                                    className={`h-7 px-2 ${isDark ? "text-red-400 hover:text-red-300 hover:bg-red-900/30" : "text-red-500 hover:text-red-700 hover:bg-red-50"}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Pagination - Always Show */}
                    <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                      <span className={`text-xs sm:text-sm ${labelClass}`}>
                        Toplam {devicesTotal} cihaz | Sayfa {devicesPage} / {devicesTotalPages || 1}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={devicesPage <= 1}
                          onClick={() => setDevicesPage(p => p - 1)}
                          className={`h-8 ${isDark ? "border-slate-700" : ""}`}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={devicesPage >= devicesTotalPages}
                          onClick={() => setDevicesPage(p => p + 1)}
                          className={`h-8 ${isDark ? "border-slate-700" : ""}`}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={`text-center py-16 ${labelClass}`}>
                    <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Cihaz bulunamadı</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* To-Do Tab */}
          {canAccessTodos && (
            <TabsContent value="todos" className="space-y-6">
              <div className="space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <h2 className={`text-xl font-bold flex items-center gap-2 ${textClass}`}>
                      <ListTodo className="w-6 h-6 text-blue-500" />
                      To Do List
                    </h2>
                    <p className={`text-sm mt-1 ${labelClass}`}>
                      Yazılım istekleri ve yapılacaklar
                      {canManageTodos && <span className="hidden sm:inline"> · Çift tıkla aç · Sürükleyerek taşı</span>}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {isSuperAdmin && (
                      <Select value={todosCompanyFilter} onValueChange={setTodosCompanyFilter}>
                        <SelectTrigger className={`h-9 w-full sm:w-48 text-xs ${inputClass}`}>
                          <SelectValue placeholder="Firma filtresi" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                          <SelectItem value="all">Tüm Firmalar</SelectItem>
                          {companies.filter((c) => c.status === 1).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {canManageTodos && (
                      <Button onClick={openTodoCreateModal} className={`${isDark ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-900 hover:bg-slate-800"} text-white`}>
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Ekle
                      </Button>
                    )}
                  </div>
                </div>

                {todosLoading ? (
                  <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                    {TODO_STATUSES.map((col) => {
                      const items = todosByStatus[col.id] || [];
                      const colTextClass = isDark ? col.textDark : col.textLight;
                      const isDropTarget = dragOverColumn === col.id && draggingTodoId;
                      return (
                        <div
                          key={col.id}
                          className={`rounded-xl border min-h-[420px] flex flex-col transition-colors ${
                            isDropTarget
                              ? isDark ? "border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/40" : "border-blue-400 bg-blue-50 ring-2 ring-blue-200"
                              : isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-gray-50/80"
                          }`}
                          onDragOver={(e) => handleTodoColumnDragOver(e, col.id)}
                          onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                              setDragOverColumn((prev) => (prev === col.id ? null : prev));
                            }
                          }}
                          onDrop={(e) => handleTodoColumnDrop(e, col.id)}
                        >
                          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                            <div className="flex items-center gap-2">
                              <span className={`w-2.5 h-2.5 rounded-full ${col.dotClass}`} />
                              <span className={`text-sm font-semibold ${colTextClass}`}>{col.label}</span>
                            </div>
                            <span className={`text-sm font-medium ${labelClass}`}>{items.length}</span>
                          </div>
                          <div className="p-3 space-y-3 flex-1 min-h-[120px]">
                            {items.length === 0 ? (
                              <p className={`text-center text-sm py-10 ${labelClass}`}>
                                {isDropTarget ? "Buraya bırak" : "Kayıt yok"}
                              </p>
                            ) : (
                              items.map((todo) => (
                                <div
                                  key={todo.id}
                                  draggable={canManageTodos}
                                  onDragStart={(e) => handleTodoCardDragStart(e, todo)}
                                  onDragEnd={handleTodoDragEnd}
                                  onDoubleClick={() => openTodoDetail(todo)}
                                  className={`rounded-lg border p-3 transition-opacity select-none ${
                                    draggingTodoId === todo.id ? "opacity-40" : ""
                                  } ${
                                    canManageTodos ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                                  } ${isDark ? "border-slate-700 bg-slate-800/60" : "border-gray-200 bg-white shadow-sm"}`}
                                >
                                  <h4 className={`text-sm font-semibold mb-1 ${todo.status === "tamamlandi" ? "line-through opacity-70" : ""} ${textClass}`}>
                                    {todo.title || todo.baslik || "Başlıksız"}
                                  </h4>
                                  <p className={`text-xs mb-3 line-clamp-2 ${labelClass}`}>{getTodoCardDescription(todo)}</p>
                                  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] mb-3 ${labelClass}`}>
                                    <span className="inline-flex items-center gap-1">
                                      <User className="w-3 h-3" />
                                      {todo.created_by_name || "Admin"}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {isSuperAdmin ? (todo.company_name || "Firma") : "Herkes"}
                                    </span>
                                    <span>{formatTodoShortDate(todo.created_at)}</span>
                                    <span className="inline-flex items-center gap-1">
                                      <Pencil className="w-3 h-3" />
                                      {formatTodoDateTime(todo.updated_at)}
                                    </span>
                                    {(todo.files || []).length > 0 && (
                                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${isDark ? "bg-blue-500/20 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
                                        <Paperclip className="w-3 h-3" />
                                        {todo.files.length} dosya
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex justify-end">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openTodoDetail(todo)}
                                      className={`h-7 text-xs px-2 ${isDark ? "border-slate-600 bg-slate-800/50" : ""}`}
                                    >
                                      <RotateCw className="w-3 h-3 mr-1" />
                                      Aç
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Yeni To-Do Modal */}
              <Dialog open={todoCreateModalOpen} onOpenChange={setTodoCreateModalOpen}>
                <DialogContent className={`max-w-lg ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
                  <DialogHeader>
                    <DialogTitle className={`flex items-center gap-2 ${textClass}`}>
                      <Plus className="w-5 h-5 text-blue-500" />
                      Yeni Yapılacak Ekle
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTodo} className="space-y-4">
                    <div>
                      <Label className={`text-sm font-medium ${textClass}`}>
                        Başlık <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={todoForm.baslik}
                        onChange={(e) => setTodoForm({ ...todoForm, baslik: e.target.value })}
                        className={`mt-1.5 ${inputClass}`}
                        placeholder="Kısa açıklama..."
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label className={`text-sm font-medium ${textClass}`}>Detay / Not</Label>
                      <textarea
                        value={todoForm.detay}
                        onChange={(e) => setTodoForm({ ...todoForm, detay: e.target.value })}
                        onPaste={handleTodoNotePaste}
                        rows={4}
                        className={`mt-1.5 w-full rounded-md border px-3 py-2 text-sm resize-y ${inputClass}`}
                        placeholder="Detayları yazın veya ekran görüntüsü yapıştırın (Ctrl+V)..."
                      />
                    </div>
                    <div>
                      <input
                        ref={todoFileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => { addTodoFormFiles(e.target.files); e.target.value = ""; }}
                      />
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => todoFileInputRef.current?.click()}
                        onKeyDown={(e) => e.key === "Enter" && todoFileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setTodoDragOver(true); }}
                        onDragLeave={() => setTodoDragOver(false)}
                        onDrop={handleTodoDrop}
                        className={`mt-1.5 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                          todoDragOver
                            ? isDark ? "border-blue-500 bg-blue-500/10" : "border-blue-400 bg-blue-50"
                            : isDark ? "border-slate-600 hover:border-slate-500 hover:bg-slate-800/50" : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                        }`}
                      >
                        <Image className={`w-8 h-8 mx-auto mb-2 ${labelClass}`} />
                        <p className={`text-sm font-medium ${textClass}`}>Dosya sürükle-bırak veya tıkla</p>
                        <p className={`text-xs mt-1 ${labelClass}`}>PrintScreen: Not alanında Ctrl+V</p>
                      </div>
                      {todoForm.files?.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className={`text-xs font-medium ${textClass}`}>{todoForm.files.length} dosya eklendi</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {todoForm.files.map((file, idx) => (
                              <TodoLocalFilePreview
                                key={`${file.name}-${file.size}-${idx}`}
                                file={file}
                                isDark={isDark}
                                onRemove={() => setTodoForm((prev) => ({ ...prev, files: prev.files.filter((_, i) => i !== idx) }))}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                      <Button type="button" variant="outline" onClick={() => setTodoCreateModalOpen(false)} className={isDark ? "border-slate-700" : ""}>
                        İptal
                      </Button>
                      <Button type="submit" disabled={todoSaving} className="bg-slate-900 hover:bg-slate-800 text-white">
                        {todoSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        Ekle
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              {/* To-Do Detay Modal */}
              <Dialog open={todoDetailModalOpen} onOpenChange={setTodoDetailModalOpen}>
                <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
                  <DialogHeader>
                    <DialogTitle className={textClass}>To-Do Detayı</DialogTitle>
                  </DialogHeader>
                  {selectedTodo && (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        {isSuperAdmin && (
                          <Badge variant="outline" className={isDark ? "border-indigo-500/30 text-indigo-300" : "border-indigo-200 text-indigo-700"}>
                            {selectedTodo.company_name || `Firma #${selectedTodo.company_id}`}
                          </Badge>
                        )}
                        <span className={`text-xs ${labelClass}`}>
                          {selectedTodo.created_by_name || "Admin"} · {selectedTodo.created_at ? new Date(selectedTodo.created_at).toLocaleString("tr-TR") : ""}
                        </span>
                      </div>

                      {canManageTodos ? (
                        <>
                          <div>
                            <Label className={`text-xs ${labelClass}`}>Başlık</Label>
                            <Input value={todoEditForm.baslik} onChange={(e) => setTodoEditForm({ ...todoEditForm, baslik: e.target.value })} className={`mt-1 ${inputClass}`} />
                          </div>
                          <div>
                            <Label className={`text-xs ${labelClass}`}>Durum</Label>
                            <Select value={todoEditForm.status} onValueChange={(v) => setTodoEditForm({ ...todoEditForm, status: v })}>
                              <SelectTrigger className={`mt-1 h-9 text-xs ${inputClass}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                                {TODO_STATUSES.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className={`text-xs ${labelClass}`}>Proses *</Label>
                            <textarea value={todoEditForm.proses} onChange={(e) => setTodoEditForm({ ...todoEditForm, proses: e.target.value })} rows={4} className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${inputClass}`} />
                          </div>
                          <div>
                            <Label className={`text-xs ${labelClass}`}>Sonuç</Label>
                            <textarea value={todoEditForm.sonuc} onChange={(e) => setTodoEditForm({ ...todoEditForm, sonuc: e.target.value })} rows={3} className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${inputClass}`} />
                          </div>
                        </>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className={`rounded-lg p-3 ${isDark ? "bg-slate-800/50" : "bg-violet-50/50"}`}>
                            <p className={`text-[10px] font-semibold uppercase mb-2 ${isDark ? "text-violet-400" : "text-violet-700"}`}>Proses</p>
                            <p className={`text-sm whitespace-pre-wrap ${textClass}`}>{selectedTodo.proses || "-"}</p>
                          </div>
                          <div className={`rounded-lg p-3 ${isDark ? "bg-slate-800/50" : "bg-emerald-50/50"}`}>
                            <p className={`text-[10px] font-semibold uppercase mb-2 ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>Sonuç</p>
                            <p className={`text-sm whitespace-pre-wrap ${textClass}`}>{selectedTodo.sonuc || "-"}</p>
                          </div>
                        </div>
                      )}

                      <div className={`rounded-lg p-3 ${isDark ? "bg-slate-800/50" : "bg-blue-50/50"}`}>
                        <p className={`text-[10px] font-semibold uppercase mb-3 ${isDark ? "text-blue-400" : "text-blue-700"}`}>
                          Dosyalar {(selectedTodo.files || []).length > 0 ? `(${selectedTodo.files.length})` : ""}
                        </p>

                        {(selectedTodo.files || []).length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                            {selectedTodo.files.map((file) => (
                              <TodoSavedFilePreview
                                key={file.id}
                                file={file}
                                isDark={isDark}
                                onDownload={downloadTodoFile}
                                onDelete={handleDeleteTodoFile}
                                canDelete={isSuperAdmin}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className={`text-xs mb-3 ${labelClass}`}>Henüz dosya eklenmemiş</p>
                        )}

                        {canManageTodos && (
                          <>
                            <input
                              ref={todoDetailFileInputRef}
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) => { handleAddFilesToTodo(selectedTodo.id, e.target.files); e.target.value = ""; }}
                            />
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => todoDetailFileInputRef.current?.click()}
                              onKeyDown={(e) => e.key === "Enter" && todoDetailFileInputRef.current?.click()}
                              onDragOver={(e) => { e.preventDefault(); setTodoDetailDragOver(true); }}
                              onDragLeave={() => setTodoDetailDragOver(false)}
                              onDrop={handleTodoDetailDrop}
                              className={`rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
                                todoDetailDragOver
                                  ? isDark ? "border-blue-500 bg-blue-500/10" : "border-blue-400 bg-blue-50"
                                  : isDark ? "border-slate-600 hover:border-slate-500 hover:bg-slate-800/50" : "border-gray-300 hover:border-gray-400 hover:bg-white"
                              }`}
                            >
                              <Paperclip className={`w-5 h-5 mx-auto mb-1 ${labelClass}`} />
                              <p className={`text-xs font-medium ${textClass}`}>Dosya ekle — sürükle-bırak veya tıkla</p>
                            </div>
                          </>
                        )}
                      </div>

                      <DialogFooter className="gap-2 sm:gap-0">
                        {isSuperAdmin && (
                          <Button variant="ghost" onClick={() => { handleDeleteTodo(selectedTodo.id); setTodoDetailModalOpen(false); }} className={`mr-auto ${isDark ? "text-red-400 hover:bg-red-900/30" : "text-red-600 hover:bg-red-50"}`}>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Sil
                          </Button>
                        )}
                        <Button variant="outline" onClick={() => setTodoDetailModalOpen(false)} className={isDark ? "border-slate-700" : ""}>Kapat</Button>
                        {canManageTodos && (
                          <Button onClick={handleSaveTodoDetail} disabled={todoSaving} className="bg-violet-600 hover:bg-violet-700 text-white">
                            {todoSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Kaydet
                          </Button>
                        )}
                      </DialogFooter>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </TabsContent>
          )}

          {/* Companies Tab (Super Admin) */}
          {isSuperAdmin && (
            <TabsContent value="companies" className="space-y-6">
              <SubNavPills
                items={COMPANIES_VIEWS}
                activeId={companiesView}
                onChange={(view) => navigateToTab("companies", view)}
                isDark={isDark}
                textClass={textClass}
              />
              {companiesView === "list" && (
                <CompaniesListPage
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                  onRefresh={fetchCompanies}
                  onOpenTranscriptSettings={(c) => { setTranscriptSettingsCompany(c); setCompanyTranscriptSettingsOpen(true); }}
                  onOpenCrmSettings={openCompanyCrmSettings}
                />
              )}
              {companiesView === "hospitals" && (
                <HospitalsPage
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                  companies={companies}
                  isSuperAdmin={isSuperAdmin}
                  userCompanyId={user?.company_id}
                />
              )}
              {companiesView === "doctors" && (
                <DoctorsPage
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                  companies={companies}
                  isSuperAdmin={isSuperAdmin}
                  userCompanyId={user?.company_id}
                />
              )}
            </TabsContent>
          )}

          {(isSuperAdmin || isAdmin) && (
            <TabsContent value="finance" className="space-y-6">
              <SubNavPills
                items={FINANCE_VIEWS}
                activeId={financeView}
                onChange={(view) => navigateToTab("finance", view)}
                isDark={isDark}
                textClass={textClass}
              />
              {financeView === "dashboard2" && (
                <CiroDashboard2Page
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                  companies={companies}
                  isSuperAdmin={isSuperAdmin}
                  userCompanyId={user?.company_id}
                />
              )}
              {financeView === "dashboard" && (
                <CiroDashboardPage
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                  companies={companies}
                  isSuperAdmin={isSuperAdmin}
                  userCompanyId={user?.company_id}
                />
              )}
              {financeView === "ciro" && (
                <CiroTrackingPage
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                  companies={companies}
                  isSuperAdmin={isSuperAdmin}
                  userCompanyId={user?.company_id}
                />
              )}
              {financeView === "reports" && (
                <ComingSoonPage
                  title="Finansal Raporlar"
                  description="Gelir-gider, tahsilat ve firma bazlı finansal özet raporları bu bölümde yer alacak."
                  icon={BarChart3}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                />
              )}
              {financeView === "accounting" && (
                <ComingSoonPage
                  title="Muhasebe"
                  description="Fatura, cari hesap ve muhasebe entegrasyonu modülü üzerinde çalışılıyor."
                  icon={Calculator}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                />
              )}
            </TabsContent>
          )}

          {/* OTP API Settings (Super Admin) */}
          {(isAdmin || canManageAgents) && (
            <TabsContent value="work-schedules" className="space-y-6">
              <WorkSchedulesSettings
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                cardClass={cardClass}
                inputClass={inputClass}
                companies={companies}
                isSuperAdmin={isSuperAdmin}
              />
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="otp-settings" className="space-y-6">
              <OtpApiSettings apiBase={API} isDark={isDark} textClass={textClass} labelClass={labelClass} />
              <SecurityAuditLogs apiBase={API} isDark={isDark} textClass={textClass} labelClass={labelClass} />
            </TabsContent>
          )}

          {recordingDownloadSettingsVisible && (
            <TabsContent value="recording-download-settings" className="space-y-6">
              <RecordingDownloadSettings apiBase={API} isDark={isDark} textClass={textClass} labelClass={labelClass} />
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="recording-errors" className="space-y-6">
              <RecordingErrorReportsSettings
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                onPlayRecording={openAudioPlayer}
                onOpenCdrRecord={openCdrRecordById}
              />
            </TabsContent>
          )}

          {(isSuperAdmin || isAdmin) && (
            <TabsContent value="webhook-logs" className="space-y-6">
              <WebhookPushLogsSettings
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                companies={companies}
                isSuperAdmin={isSuperAdmin}
                userCompanyId={user?.company_id}
              />
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="sesk-panel" className="space-y-6">
              <SeskPanelSettings
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
            </TabsContent>
          )}

          {/* Calendar Tab (Admin) */}
          {isAdmin && (
            <TabsContent value="calendar" className="space-y-6">
              <SubNavPills
                items={CALENDAR_VIEWS}
                activeId={calendarView}
                onChange={(view) => navigateToTab("calendar", view)}
                isDark={isDark}
                textClass={textClass}
              />
              {calendarView === "medicrm" && (
                <AppointmentCalendarPage
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                />
              )}
              {calendarView === "v2" && (
                <AppointmentCalendarV2Page
                  apiBase={API}
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  cardClass={cardClass}
                  inputClass={inputClass}
                />
              )}
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="panel-roles" className="space-y-6">
              <PanelRolesPage
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                isSuperAdmin={isSuperAdmin}
              />
            </TabsContent>
          )}

          {(isAdmin || hasPanelMenuPermission(user, "menu_reports_tl_performance")) && (
            <TabsContent value="mr-assistant-mapping" className="space-y-6">
              <MrAssistantMappingPage
                apiBase={API}
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
                isSuperAdmin={isSuperAdmin}
                userCompanyId={user?.company_id}
                companies={companies}
              />
            </TabsContent>
          )}

          {/* Users Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="users" className="space-y-6">
              <PanelTableCard isDark={isDark}>
                <PanelTableHeader
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  accent="purple"
                  icon={GitBranch}
                  title="Takım Yönetimi"
                  description="Agent gruplarını takım etiketleriyle yönetin, lider atayın"
                  toolbar={(
                    <>
                      <div className="relative w-full sm:w-auto">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                        <Input
                          placeholder="Takım, lider veya agent ara..."
                          value={teamsSearch}
                          onChange={(e) => setTeamsSearch(e.target.value)}
                          className={`pl-10 h-9 w-full sm:w-56 ${inputClass}`}
                        />
                      </div>
                      <Button
                        onClick={() => { setEditingTeam(null); setTeamModalOpen(true); }}
                        className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/20"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Takım
                      </Button>
                    </>
                  )}
                />
                <PanelTableStats isDark={isDark} items={teamTableStats} />
                <PanelTableFrame
                  isDark={isDark}
                  footer={filteredTeams.length > 0 ? (
                    <PanelTablePagination
                      isDark={isDark}
                      page={teamTablePagination.page}
                      pageSize={teamTablePagination.pageSize}
                      total={teamTablePagination.total}
                      totalPages={teamTablePagination.totalPages}
                      rangeStart={teamTablePagination.rangeStart}
                      rangeEnd={teamTablePagination.rangeEnd}
                      onPageChange={teamTablePagination.setPage}
                      onPageSizeChange={teamTablePagination.setPageSize}
                      pageSizeOptions={[10, 25, 50]}
                      loading={teamsLoading}
                    />
                  ) : null}
                >
                  <TableHeader>
                    <PanelTableHeadRow isDark={isDark}>
                      <PanelTableHead isDark={isDark}>Takım</PanelTableHead>
                      <PanelTableHead isDark={isDark}>Takım Lideri</PanelTableHead>
                      <PanelTableHead isDark={isDark} align="center">Agent</PanelTableHead>
                      <PanelTableHead isDark={isDark}>Üyeler</PanelTableHead>
                      <PanelTableHead isDark={isDark} align="right">İşlemler</PanelTableHead>
                    </PanelTableHeadRow>
                  </TableHeader>
                  <PanelTableBody>
                    {teamsLoading ? (
                      <PanelTableRow isDark={isDark} className="hover:bg-transparent">
                        <PanelTableCell isDark={isDark} colSpan={5} className="py-16 text-center">
                          <Loader2 className={`w-6 h-6 animate-spin inline mr-2 ${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
                          <span className={labelClass}>Takımlar yükleniyor...</span>
                        </PanelTableCell>
                      </PanelTableRow>
                    ) : filteredTeams.length === 0 ? (
                      <PanelTableEmpty
                        colSpan={5}
                        isDark={isDark}
                        icon={GitBranch}
                        title={teamsSearch.trim() ? "Arama sonucu bulunamadı" : "Henüz takım yok"}
                        description={teamsSearch.trim() ? "Farklı bir arama deneyin." : "Yeni takım oluşturarak agentları gruplayın."}
                      />
                    ) : teamTablePagination.paginatedItems.map((t) => (
                      <PanelTableRow key={t.id} isDark={isDark}>
                        <PanelTableCell isDark={isDark}>
                          <div className="flex items-center gap-3 min-w-[180px]">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ring-1 ${isDark ? "bg-indigo-500/15 ring-indigo-500/30 text-indigo-300" : "bg-indigo-50 ring-indigo-200 text-indigo-600"}`}>
                              <GitBranch className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <p className={`font-semibold truncate ${isDark ? "text-slate-100" : "text-gray-900"}`}>{t.ad}</p>
                              <p className={`text-[11px] mt-0.5 ${labelClass}`}>#{t.id}</p>
                            </div>
                          </div>
                        </PanelTableCell>
                        <PanelTableCell isDark={isDark}>
                          {t.lider_name ? (
                            <div className="flex items-center gap-2.5 min-w-[140px]">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold ${isDark ? "bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/30" : "bg-violet-100 text-violet-700 ring-1 ring-violet-200"}`}>
                                {t.lider_name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? "text-slate-200" : "text-gray-800"}`}>{t.lider_name}</p>
                                <p className={`text-[10px] ${labelClass}`}>Takım lideri</p>
                              </div>
                            </div>
                          ) : (
                            <Badge variant="outline" className={`text-[10px] font-normal ${isDark ? "border-slate-600 text-slate-500" : "border-gray-300 text-gray-500"}`}>
                              Lider atanmadı
                            </Badge>
                          )}
                        </PanelTableCell>
                        <PanelTableCell isDark={isDark} align="center">
                          {(t.agents || []).length > 0 ? (() => {
                            const teamAgents = t.agents || [];
                            const leftCount = teamAgents.filter(agentHasLeftCompany).length;
                            const countBadgeClass = leftCount > 0
                              ? agentLeftCountBadgeClass(isDark)
                              : (isDark ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30 hover:bg-indigo-500/25" : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100");
                            return (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    title={leftCount > 0 ? `${leftCount} agent işten ayrıldı — listeyi aç` : "Tüm agentları göster"}
                                    className="inline-flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                                  >
                                    <Badge className={`cursor-pointer transition-all hover:scale-105 tabular-nums ${countBadgeClass}`}>
                                      <Users className="w-3 h-3 mr-1 inline" />
                                      {t.agent_count || 0}
                                      {leftCount > 0 && (
                                        <span className="ml-1 text-[10px] font-normal opacity-90">({leftCount} ayrıldı)</span>
                                      )}
                                    </Badge>
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent
                                  align="center"
                                  side="bottom"
                                  sideOffset={6}
                                  className={`w-72 p-0 overflow-hidden ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"}`}
                                >
                                  <div className={`px-3 py-2.5 border-b ${isDark ? "border-slate-800 bg-slate-900/95" : "border-gray-100 bg-white"}`}>
                                    <p className={`text-sm font-semibold truncate ${isDark ? "text-slate-100" : "text-gray-900"}`}>{t.ad}</p>
                                    <p className={`text-[11px] mt-0.5 ${labelClass}`}>
                                      {(t.agents || []).length} agent
                                      {leftCount > 0 ? ` · ${leftCount} işten ayrıldı` : ""}
                                    </p>
                                  </div>
                                  <ul className="max-h-64 overflow-y-auto py-1">
                                    {teamAgents.map((a) => {
                                      const left = agentHasLeftCompany(a);
                                      return (
                                        <li
                                          key={a.id}
                                          className={`px-3 py-2 flex items-center gap-2.5 text-sm ${isDark ? "hover:bg-slate-800/80" : "hover:bg-gray-50"} ${left ? agentLeftTextClass(isDark) : (isDark ? "text-slate-200" : "text-gray-800")}`}
                                          title={left ? `İşten çıkış: ${formatAgentEmploymentDate(a.isten_cikis_tarihi)}` : undefined}
                                        >
                                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-semibold ${left
                                            ? (isDark ? "bg-rose-500/20 text-rose-300 ring-1 ring-rose-500/35" : "bg-rose-100 text-rose-700 ring-1 ring-rose-200")
                                            : (isDark ? "bg-indigo-500/20 text-indigo-300 ring-1 ring-indigo-500/30" : "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200")}`}>
                                            {(a.ad_soyad || "?").charAt(0).toUpperCase()}
                                          </div>
                                          <span className="truncate">{a.ad_soyad}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </PopoverContent>
                              </Popover>
                            );
                          })() : (
                            <Badge className={isDark ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30 tabular-nums" : "bg-indigo-50 text-indigo-700 border-indigo-200 tabular-nums"}>
                              <Users className="w-3 h-3 mr-1 inline" />
                              0
                            </Badge>
                          )}
                        </PanelTableCell>
                        <PanelTableCell isDark={isDark}>
                          {(t.agents || []).length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[320px]">
                              {(t.agents || []).slice(0, 5).map((a) => (
                                <Badge
                                  key={a.id}
                                  variant="outline"
                                  title={agentHasLeftCompany(a) ? `İşten çıkış: ${formatAgentEmploymentDate(a.isten_cikis_tarihi)}` : undefined}
                                  className={`text-[10px] font-normal px-2 py-0 h-6 ${agentHasLeftCompany(a)
                                    ? agentLeftOutlineBadgeClass(isDark)
                                    : (isDark ? "border-slate-600 bg-slate-800/50 text-slate-300" : "border-gray-200 bg-white text-gray-700")}`}
                                >
                                  {a.ad_soyad}
                                </Badge>
                              ))}
                              {(t.agents || []).length > 5 && (
                                <Badge variant="secondary" className={`text-[10px] h-6 ${isDark ? "bg-slate-700 text-slate-300" : ""}`}>
                                  +{(t.agents || []).length - 5}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className={`text-xs italic ${labelClass}`}>Henüz agent eklenmedi</span>
                          )}
                        </PanelTableCell>
                        <PanelTableCell isDark={isDark} align="right" className="!text-right">
                          <PanelTableActions>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setEditingTeam(t); setTeamModalOpen(true); }}
                              className={`h-8 w-8 ${isDark ? "text-indigo-400 hover:bg-indigo-500/10" : "text-indigo-600 hover:bg-indigo-50"}`}
                              title="Düzenle"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTeam(t.id)}
                              className={`h-8 w-8 ${isDark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50"}`}
                              title="Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </PanelTableActions>
                        </PanelTableCell>
                      </PanelTableRow>
                    ))}
                  </PanelTableBody>
                </PanelTableFrame>
              </PanelTableCard>

              <PanelTableCard isDark={isDark}>
                <PanelTableHeader
                  isDark={isDark}
                  textClass={textClass}
                  labelClass={labelClass}
                  accent="purple"
                  icon={Settings}
                  title="Kullanıcı Yönetimi"
                  description="Panel kullanıcıları, görev atamaları ve OTP erişim ayarları"
                  toolbar={(
                    <>
                      <div className="relative w-full sm:w-auto">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                        <Input
                          placeholder="Ad, kullanıcı adı, e-posta veya GSM ara..."
                          value={usersSearch}
                          onChange={(e) => setUsersSearch(e.target.value)}
                          className={`pl-10 h-9 w-full sm:w-64 ${inputClass}`}
                        />
                      </div>
                      <Button
                        onClick={() => { setEditingUser(null); setUserModalOpen(true); }}
                        className="h-9 bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-600/20"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Kullanıcı
                      </Button>
                    </>
                  )}
                />
                <PanelTableStats isDark={isDark} items={userTableStats} />
                <PanelTableFrame
                  isDark={isDark}
                  footer={filteredUsers.length > 0 ? (
                    <PanelTablePagination
                      isDark={isDark}
                      page={userTablePagination.page}
                      pageSize={userTablePagination.pageSize}
                      total={userTablePagination.total}
                      totalPages={userTablePagination.totalPages}
                      rangeStart={userTablePagination.rangeStart}
                      rangeEnd={userTablePagination.rangeEnd}
                      onPageChange={userTablePagination.setPage}
                      onPageSizeChange={userTablePagination.setPageSize}
                      pageSizeOptions={[15, 25, 50, 100]}
                    />
                  ) : null}
                >
                  <TableHeader>
                    <PanelTableHeadRow isDark={isDark}>
                      <PanelTableHead isDark={isDark}>Kullanıcı</PanelTableHead>
                      <PanelTableHead isDark={isDark}>GSM / OTP</PanelTableHead>
                      {isSuperAdmin && <PanelTableHead isDark={isDark}>Firma</PanelTableHead>}
                      <PanelTableHead isDark={isDark}>E-posta</PanelTableHead>
                      <PanelTableHead isDark={isDark}>Görev</PanelTableHead>
                      <PanelTableHead isDark={isDark} align="center">Durum</PanelTableHead>
                      <PanelTableHead isDark={isDark}>Yetkiler / Sayfalar</PanelTableHead>
                      <PanelTableHead isDark={isDark} align="right">İşlemler</PanelTableHead>
                    </PanelTableHeadRow>
                  </TableHeader>
                  <PanelTableBody>
                    {filteredUsers.length === 0 ? (
                      <PanelTableEmpty
                        colSpan={userTableColSpan}
                        isDark={isDark}
                        icon={User}
                        title={usersSearch.trim() ? "Arama sonucu bulunamadı" : "Kullanıcı bulunamadı"}
                        description={usersSearch.trim() ? "Farklı bir arama deneyin." : "Yeni kullanıcı ekleyerek başlayın."}
                      />
                    ) : userTablePagination.paginatedItems.map((u) => {
                      const phones = getUserPhonesForDisplay(u);
                      const primaryPhone = phones[0] || "";
                      const otpChannels = u.login_otp_enabled === 1
                        ? (u.login_otp_sms && u.login_otp_whatsapp ? "SMS + WA" : u.login_otp_whatsapp ? "WhatsApp" : "SMS")
                        : null;

                      return (
                        <PanelTableRow key={u.id} isDark={isDark}>
                          <PanelTableCell isDark={isDark}>
                            <div className="flex items-center gap-3 min-w-[180px]">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-sm font-semibold ring-1 ${isDark ? "bg-purple-500/15 ring-purple-500/30 text-purple-300" : "bg-purple-50 ring-purple-200 text-purple-700"}`}>
                                {(u.ad_soyad || u.kullanici_adi || "?").charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className={`font-semibold truncate ${isDark ? "text-slate-100" : "text-gray-900"}`}>{u.ad_soyad || "-"}</p>
                                <p className={`text-xs font-mono truncate mt-0.5 ${isDark ? "text-slate-500" : "text-gray-500"}`}>@{u.kullanici_adi}</p>
                              </div>
                            </div>
                          </PanelTableCell>
                          <PanelTableCell isDark={isDark}>
                            {phones.length > 0 ? (
                              <div className="space-y-1.5 min-w-[150px]">
                                {phones.length > 1 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        title="Tüm numaraları göster"
                                        className="inline-flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40"
                                      >
                                        <span className={`inline-flex items-center gap-1.5 font-mono text-sm tabular-nums tracking-wide transition-colors ${isDark ? "text-cyan-300 hover:text-cyan-200" : "text-cyan-700 hover:text-cyan-800"}`}>
                                          <Phone className="w-3.5 h-3.5 shrink-0 opacity-80" />
                                          {primaryPhone}
                                          <Badge variant="outline" className={`ml-0.5 h-5 px-1.5 text-[10px] font-medium ${isDark ? "border-cyan-500/30 text-cyan-300 bg-cyan-500/10" : "border-cyan-200 text-cyan-700 bg-cyan-50"}`}>
                                            +{phones.length - 1}
                                          </Badge>
                                        </span>
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      align="start"
                                      side="bottom"
                                      sideOffset={6}
                                      className={`w-72 p-0 overflow-hidden ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"}`}
                                    >
                                      <div className={`px-3 py-2.5 border-b ${isDark ? "border-slate-800 bg-slate-900/95" : "border-gray-100 bg-white"}`}>
                                        <p className={`text-sm font-semibold truncate ${isDark ? "text-slate-100" : "text-gray-900"}`}>{u.ad_soyad}</p>
                                        <p className={`text-[11px] mt-0.5 ${labelClass}`}>{phones.length} GSM numarası</p>
                                      </div>
                                      <ul className="max-h-56 overflow-y-auto py-1">
                                        {phones.map((phone, idx) => (
                                          <li
                                            key={`${u.id}-${phone}-${idx}`}
                                            className={`px-3 py-2 flex items-center gap-2.5 text-sm font-mono tabular-nums tracking-wide ${isDark ? "text-slate-200 hover:bg-slate-800/80" : "text-gray-800 hover:bg-gray-50"}`}
                                          >
                                            <Phone className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                                            {phone}
                                          </li>
                                        ))}
                                      </ul>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <span className={`inline-flex items-center gap-1.5 font-mono text-sm tabular-nums tracking-wide ${isDark ? "text-slate-200" : "text-gray-800"}`}>
                                    <Phone className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                                    {primaryPhone}
                                  </span>
                                )}
                                {otpChannels && (
                                  <Badge className={`text-[10px] h-5 px-1.5 font-medium w-fit ${isDark ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                                    <Shield className="w-3 h-3 mr-1 inline" />
                                    OTP · {otpChannels}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                <span className={`text-xs italic ${labelClass}`}>Numara tanımlı değil</span>
                                {otpChannels && (
                                  <Badge className={`text-[10px] h-5 px-1.5 font-medium w-fit ${isDark ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                                    <Shield className="w-3 h-3 mr-1 inline" />
                                    OTP · {otpChannels}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </PanelTableCell>
                          {isSuperAdmin && (
                            <PanelTableCell isDark={isDark}>
                              {u.is_super_admin === 1 ? (
                                <Badge className={isDark ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-amber-50 text-amber-700 border-amber-200"}>
                                  <Shield className="w-3 h-3 mr-1 inline" />
                                  Süper Admin
                                </Badge>
                              ) : (
                                <span className={`text-sm ${isDark ? "text-slate-300" : "text-gray-700"}`}>{u.company_name || u.firma_adi || "-"}</span>
                              )}
                            </PanelTableCell>
                          )}
                          <PanelTableCell isDark={isDark}>
                            {u.email ? (
                              <span className={`inline-flex items-center gap-1.5 text-sm truncate max-w-[200px] ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                                <Mail className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                                {u.email}
                              </span>
                            ) : (
                              <span className={`text-xs italic ${labelClass}`}>-</span>
                            )}
                          </PanelTableCell>
                          <PanelTableCell isDark={isDark}>
                            {u.is_admin === 1 ? (
                              <Badge className={isDark ? "bg-purple-500/15 text-purple-300 border-purple-500/30" : "bg-purple-50 text-purple-700 border-purple-200"}>
                                <Shield className="w-3 h-3 mr-1 inline" />
                                Admin
                              </Badge>
                            ) : u.gorev ? (
                              <div className="space-y-1">
                                <Badge className={isDark ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" : "bg-indigo-50 text-indigo-700 border-indigo-200"}>
                                  {gorevLabel(u.gorev)}
                                </Badge>
                                {u.panel_role_id ? (
                                  <div className={`text-[10px] ${labelClass}`}>{panelRoleLabel(panelRoles, u.panel_role_id)}</div>
                                ) : null}
                              </div>
                            ) : (
                              <Badge variant="outline" className={`text-[10px] font-normal ${isDark ? "border-slate-600 text-slate-500" : "border-gray-300 text-gray-500"}`}>
                                Eski sistem
                              </Badge>
                            )}
                          </PanelTableCell>
                          <PanelTableCell isDark={isDark} align="center">
                            <Badge className={u.status === 1
                              ? (isDark ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200")
                              : (isDark ? "bg-slate-700/50 text-slate-400 border-slate-600" : "bg-gray-100 text-gray-500 border-gray-200")
                            }>
                              {u.status === 1 ? "Aktif" : "Pasif"}
                            </Badge>
                          </PanelTableCell>
                          <PanelTableCell isDark={isDark}>
                            <UserPermissionsCell
                              user={u}
                              isDark={isDark}
                              labelClass={labelClass}
                              onViewDetail={setUserAccessDetailUser}
                            />
                          </PanelTableCell>
                          <PanelTableCell isDark={isDark} align="right" className="!text-right">
                            <PanelTableActions>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setUserAccessDetailUser(u)}
                                className={`h-8 w-8 ${isDark ? "text-indigo-400 hover:bg-indigo-500/10" : "text-indigo-600 hover:bg-indigo-50"}`}
                                title="Yetkileri gör"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isSuperAdmin && u.is_super_admin !== 1 && u.status === 1 ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCreateAdminLoginLink(u)}
                                  disabled={adminLoginLinkLoadingId === u.id}
                                  className={`h-8 w-8 ${isDark ? "text-cyan-400 hover:bg-cyan-500/10" : "text-cyan-600 hover:bg-cyan-50"}`}
                                  title="Giriş linki oluştur (15 dk, tek kullanım)"
                                >
                                  {adminLoginLinkLoadingId === u.id
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <Link2 className="w-4 h-4" />}
                                </Button>
                              ) : null}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => { setEditingUser(u); setUserModalOpen(true); }}
                                className={`h-8 w-8 ${isDark ? "text-purple-400 hover:bg-purple-500/10" : "text-purple-600 hover:bg-purple-50"}`}
                                title="Düzenle"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteUser(u.id)}
                                className={`h-8 w-8 ${isDark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50"}`}
                                title="Sil"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </PanelTableActions>
                          </PanelTableCell>
                        </PanelTableRow>
                      );
                    })}
                  </PanelTableBody>
                </PanelTableFrame>
              </PanelTableCard>
            </TabsContent>
          )}
          </div>
        </Tabs>

        <footer className={`max-w-[1536px] mx-auto px-4 sm:px-6 mt-8 text-center text-sm ${labelClass}`}>eTelekom CDR Control Panel v1.3</footer>
      </main>
    </div>
    </TooltipProvider>
  );
};

// App
function App() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved ? saved === "dark" : true;
  });

  useEffect(() => {
    localStorage.setItem("theme", isDark ? "dark" : "light");
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  return (
    <AuthProvider>
      <Toaster position="top-right" theme={isDark ? "dark" : "light"} toastOptions={{ style: isDark ? { background: '#0f172a', border: '1px solid #1e293b', color: '#f8fafc' } : {} }} />
      <AppContent isDark={isDark} onToggleTheme={() => setIsDark(!isDark)} />
    </AuthProvider>
  );
}

const AppContent = ({ isDark, onToggleTheme }) => {
  const { user, loading, login, finalizeOtpLogin, autoLoginActive, autoLoginMeta } = useAuth();
  const location = useLocation();

  if (autoLoginActive) {
    return <AutoLoginSplash isDark={isDark} deviceLabel={autoLoginMeta.deviceLabel} ipAddress={autoLoginMeta.ipAddress} />;
  }

  if (location.pathname.startsWith("/otp/")) {
    return (
      <Routes>
        <Route path="/otp/:token" element={<OtpLinkApprovePage isDark={isDark} />} />
      </Routes>
    );
  }

  if (location.pathname.startsWith("/giris/")) {
    return (
      <Routes>
        <Route path="/giris/:token" element={<AdminLoginLinkPage isDark={isDark} />} />
      </Routes>
    );
  }

  if (loading) {
    return <AppBootLoader isDark={isDark} />;
  }

  if (user) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/cdr" replace />} />
        <Route path="/login4" element={<Navigate to="/cdr" replace />} />
        <Route path="/login3" element={<Navigate to="/cdr" replace />} />
        <Route path="/:tab" element={<Dashboard isDark={isDark} onToggleTheme={onToggleTheme} />} />
      </Routes>
    );
  }

  if (location.pathname === "/login4") {
    return (
      <LoginPage4
        isDark={isDark}
        onToggleTheme={onToggleTheme}
        login={login}
        finalizeOtpLogin={finalizeOtpLogin}
      />
    );
  }

  if (location.pathname === "/login3" || location.pathname === "/login2") {
    return <Navigate to="/" replace />;
  }

  return (
    <LoginPage3
      isDark={isDark}
      onToggleTheme={onToggleTheme}
      login={login}
      finalizeOtpLogin={finalizeOtpLogin}
    />
  );
};

export default App;
